import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceDot, ReferenceLine, ResponsiveContainer, Legend,
  ScatterChart, Scatter, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import * as d3 from "d3";
import Papa from "papaparse";

// --- Fase 3: mapa y terreno reales -------------------------------------
// El mapa SVG propio (RouteMap, más abajo) queda en el archivo como
// referencia pero ya no se usa: no podía mostrar curvas de nivel ni relieve,
// y mezclaba proyección equirectangular con mosaicos Web Mercator.
const RouteMapLeaflet = React.lazy(() => import("../map/RouteMapLeaflet.jsx"));
const DigitalizadorAIP = React.lazy(() => import("../espacioAereo/DigitalizadorAIP.jsx"));
const HojaReferencia = React.lazy(() => import("../vuelo/HojaReferencia.jsx"));
/* El modo vuelo trae Leaflet y su propia hoja de estilos: se carga solo
   cuando el piloto entra, no al abrir la aplicación. */
const ModoVuelo = React.lazy(() => import("../vuelo/ModoVuelo.jsx"));
import { leerArchivoDePuntos } from "../../services/puntos/kml.js";
import { elevacionPies } from "../../services/terrain/terrarium.js";
import { clasificarRegistro, estadoNoConformidad, evaluarBowTie, resumenBowTie, PRIORIDADES_NC } from "../../domain/auditoria.js";
import { construirReporteSGS } from "../../domain/reporteAnexo2.js";
import PanelSgs from "../sgs/PanelSgs.jsx";
import PanelOEI from "../analisis/PanelOEI.jsx";
import { repartirVuelo } from "../../domain/bitacoraPiloto.js";
import LibroDeVuelo from "../vuelo/LibroDeVuelo.jsx";
import CampoCoordenada from "../comun/CampoCoordenada.jsx";
import AvisoFallasEscritura from "../comun/AvisoFallasEscritura.jsx";
import PanelFlotante from "../comun/PanelFlotante.jsx";
import {
  disposicion, altoDelMapa, panelesAbiertos, guardarPaneles, PANELES_FLOTANTES,
} from "../../domain/disposicionMapa.js";
import PanelDescargaMapas from "../map/PanelDescargaMapas.jsx";
import PanelAdministracion from "../administracion/PanelAdministracion.jsx";
import PanelEstadisticas from "../estadisticas/PanelEstadisticas.jsx";
import SelectorDeOperacion from "../comun/SelectorDeOperacion.jsx";
import { conOperacion } from "../../domain/clientes.js";
import { identidadInicial } from "../../domain/identidad.js";
import { horasDeJornada, explicacionDeJornada, FUENTE as FUENTE_JORNADA } from "../../domain/jornadaDesdeLibro.js";
import { MATRIZ_RIESGO, CRITERIOS_GRAVEDAD, CRITERIOS_PROBABILIDAD, SIGNIFICADO_NIVEL,
  clasificarTopografia, evaluarReserva, veredictoDespacho, evaluarViento, promedioDeRiesgo, aplicarMitigacion, exigeMitigacion,
  TECHO_SERVICIO_FT, VIENTO_MAXIMO_KT } from "../../domain/riesgo.js";
import { usePersistedState } from "../../services/db/persistencia.js";
import ImportarAeronave from "../importador/ImportarAeronave.jsx";
import { perfilDeRuta } from "../../services/terrain/terrarium.js";
import { limiteDePeso, corregirPorConsumoElectrico } from "../../domain/performance.js";
import { COMPARTIMENTOS_AS350B3, ESTACIONES_AS350B3 } from "../../domain/cartas/as350b3.js";
import {
  PERFIL_TIPO_BK117_B2, FLOTA_OB2240P, brazoCombustible,
  LIMITES_BK117_B2, limiteCgLateralMm, masaDentroDeLimites,
} from "../../domain/cartas/ob2240p.js";
import CampoNumero from "../../app/CampoNumero.jsx";
import Cabina from "../../app/Cabina.jsx";
import { aplicarTema, TEMA_POR_DEFECTO } from "../../domain/temaVisual.js";
import { proponerFrat, explicacionDe } from "../../domain/fratDesdeMetar.js";
import { construirAvisos, nivelDesdeEstado } from "../../domain/avisos.js";
import {
  insertarDespues, insertarEnTramoMasCercano, moverPunto, actualizarPunto,
  normalizarRuta, puntoVacio, preservarCoordenadas, nuevoIdPunto,
} from "../../domain/rutaEdicion.js";
import Cronometro from "../cronometro/Cronometro.jsx";
/* La barra de pasos de la preparación: hace visible el orden que ya existía. */
import BarraDePasos from "../preparacion/BarraDePasos.jsx";
import {
  TIPOS_DE_PUNTO, TIPO_POR_DEFECTO, sugerirTipoDeArchivo, clasificarArchivo,
} from "../../domain/tiposDePunto.js";
import { guardarVisibles as guardarMisPuntosVisibles } from "../map/capaMisPuntos.js";
/* ═══ UN NÚMERO QUE PUEDE NO EXISTIR SE ESCRIBE EN UN SOLO LUGAR ═══

   `null is not an object (evaluating 'P.elevFt.toLocaleString')`: el piloto
   importó un KMZ, el punto entró sin cota —que es el dato honesto— y seis
   pantallas la formatearon sin preguntar. Sin dato va un guion, nunca un
   cero. */
import { pies, grados, nombreDePunto } from "../../domain/formato.js";

/* Las pestañas donde se está preparando un vuelo. La barra de pasos aparece
   solo acá: en Mantenimiento o en el SGS no se prepara nada, y una barra que
   sale en todas partes deja de leerse. */
const PESTANAS_PREPARACION = new Set(["ruta", "analisis", "frat", "resumen"]);
import { cronometroVacio } from "../../domain/cronometro.js";
import {
  vueloDesdeCronometro, resumenDelDia, anularVuelo, reactivarVuelo,
} from "../../domain/diaDeVuelo.js";
import { PALETA, TIPOGRAFIA, COLOR_NIVEL } from "../../styles/paleta.js";
import { estadoEnAterrizaje, limiteEnPeso } from "../../domain/pesoBalance.js";
import {
  UNIDADES_COMBUSTIBLE, desdeLitros, aLitros, enTodasLasUnidades,
  porcentajeDeCapacidad, litrosDesdePorcentaje, cabeEnElTanque, consumoEnTodasLasUnidades,
} from "../../domain/combustible.js";
import {
  JURISDICCIONES, REGLAS_JORNADA, TIPOS_OPERACION_JORNADA, evaluarJornada,
} from "../../domain/jornada.js";
import {
  ESTADOS_COTEJO, CAMPOS_COTEJABLES, evaluarCotejo,
  acumuladoDeAeronave, resumenMesYAnio, verificarArrastre,
} from "../../domain/bitacora.js";
import { combustiblePorTramo } from "../../domain/perfilVertical.js";
import {
  PESO_VACIO_CON_CANASTA, CANASTA_STC,
  pesoVacioSegunConfiguracion, verificarCargaCanasta,
} from "../../domain/canasta.js";
import { temperaturaEnSuperficie, atmosferaEnAltura } from "../../services/meteo/openMeteo.js";
import { traerVarios, oaciValido } from "../../services/meteo/metarServicio.js";
import PanelMetar from "../meteo/PanelMetar.jsx";
import "../meteo/panelMetar.css";
import {
  interpolarEnAltura, interpolarDireccion, componentesDeViento,
  velocidadSobreSuelo, desviacionIsa, temperaturaEstimadaEnAltura, altitudDensidad,
  VELOCIDAD_CRUCERO_MAXIMA_KT, acotarVelocidadCrucero,
} from "../../domain/atmosfera.js";
import {
  calcularPerfilVertical, gradienteFtPorNm,
  ASCENSO_POR_DEFECTO, DESCENSO_POR_DEFECTO, MARGEN_LIBRAMIENTO_FT,
} from "../../domain/perfilVertical.js";

/* ═══ EMPRESA, USUARIOS Y PERFILES ═══

   El estado inicial se arma UNA vez, al cargar el módulo. `nuevaEmpresa` genera
   su identificador con la hora, así que crearlo dentro del render daría una
   empresa distinta en cada dibujo —y una lista de empresas que crece sola—. */
const IDENTIDAD_INICIAL = identidadInicial();

/* ============================================================================
   HeliCalc — Prototipo v2: perfiles de aeronave, mapa de ruta, mantenimiento y
   operaciones (SMS). Referencia de estructura de planificación: ForeFlight.
   ========================================================================== */

/* ---------------------------- Cartas AS350 B3 (digitalizadas) ---------------------------- */
const OGE_ALTS = Array.from({ length: 37 }, (_, i) => i * 500);
const OGE_TEMPS = [-20, -10, 0, 10, 20, 30, 40, 50];
const OGE_ROWS = [
  [2800,2800,2800,2800,2800,2775,2750,2610],[2800,2800,2800,2800,2790,2765,2740,2560],
  [2800,2800,2800,2800,2775,2750,2690,2520],[2800,2800,2800,2790,2765,2725,2640,2460],
  [2800,2800,2800,2775,2745,2710,2580,2420],[2800,2800,2790,2760,2725,2680,2540,2370],
  [2800,2800,2775,2750,2710,2630,2490,null],[2800,2790,2760,2725,2690,2580,2440,null],
  [2800,2775,2740,2710,2650,2530,2390,null],[2790,2760,2725,2690,2600,2480,2340,null],
  [2774,2750,2710,2650,2550,2440,2290,null],[2763,2725,2690,2610,2500,2400,2250,null],
  [2745,2710,2670,2560,2460,2350,2210,null],[2730,2690,2605,2520,2420,2300,2170,null],
  [2715,2670,2560,2470,2370,2250,2120,null],[2700,2620,2520,2420,2320,2210,2070,null],
  [2680,2570,2470,2380,2280,2170,null,null],[2620,2520,2420,2320,2240,2120,null,null],
  [2580,2470,2380,2280,2190,2070,null,null],[2520,2420,2340,2240,2150,2040,null,null],
  [2480,2380,2290,2200,2110,1990,null,null],[2420,2320,2250,2150,2060,1950,null,null],
  [2370,2280,2200,2110,2020,1910,null,null],[2320,2240,2160,2070,1980,1870,null,null],
  [2280,2190,2110,2025,1940,1840,null,null],[2240,2150,2070,1990,1900,1800,null,null],
  [2180,2110,2030,1940,1860,null,null,null],[2140,2070,1990,1910,1820,null,null,null],
  [2100,2030,1950,1870,1780,null,null,null],[2060,1995,1910,1840,1750,null,null,null],
  [2020,1950,1870,1800,1720,null,null,null],[1980,1905,1830,1760,1680,null,null,null],
  [1945,1865,1790,1720,1640,null,null,null],[1900,1825,1755,1680,1610,null,null,null],
  [1860,1785,1720,1650,1570,null,null,null],[1820,1750,1680,1615,1530,null,null,null],
  [1780,1720,1650,1580,null,null,null,null],
];
// Carta IGE real (AS350 B3, "5.6 HOVER IN GROUND EFFECT", techo 2250 kg en carga interna, Hp hasta 23 000 ft).
// IMPORTANTE: es un nomograma de líneas diagonales de OAT, no una tabla de celdas como la OGE. Los valores de
// abajo son una aproximación analítica leída visualmente de la carta (no confirmada con puntos de anclaje del
// manual) — se muestran en la app etiquetados como "estimado de gráfico, verificar contra el manual" hasta que
// se confirmen con la lectura exacta de al menos 3-4 puntos reales.
/**
 * Carta IGE — estacionario DENTRO del efecto suelo, AS 350 B3 Plus / Arriel 2B1.
 *
 * Valores de la "Tabla de Peso Máximo por Aeronave" del operador, cotejados
 * contra la Figura 5 del RFM (IGE Hovering Flight Performance).
 *
 * COBERTURA
 * ---------
 * De 1 000 a 22 000 ft y de +10 a +35 °C. El RFM cubre de 0 a 23 000 ft y de
 * −40 °C a ISA+35, así que por debajo de +10 °C la app dirá "fuera de carta":
 * es un hueco de la tabla del operador, no un límite de la aeronave.
 *
 * CELDAS ANULADAS
 * ---------------
 * Cinco celdas violan las dos reglas físicas de esta carta —el peso máximo no
 * puede subir con la temperatura ni con la altitud— y las cinco lo hacen
 * PERMITIENDO MÁS PESO, que es la dirección peligrosa:
 *
 *   15 000 ft / 20 °C = 2250   +420 kg sobre los 15 °C de la misma altitud
 *   16 500 ft / 20 °C = 1675   +25 kg sobre los 15 °C
 *   19 500 ft / 20 °C = 1430   +10 kg sobre los 15 °C
 *   21 000 ft / 20 °C = 1350   +20 kg sobre los 15 °C
 *   22 000 ft / 10 °C = 1430   +20 kg sobre los 21 000 ft
 *
 * Quedan en null. Un hueco manda al manual; un número que se sabe alto de más
 * autoriza carga que la aeronave no puede sostener. Cuatro de los cinco están
 * en la columna de 20 °C: conviene releer esa columna entera del original.
 *
 * HUECOS QUE NO SE RELLENAN
 * -------------------------
 * A 25 °C y 30 °C la tabla dice N/A a 15 000 ft pero da valores a 16 500. Eso
 * es incoherente al revés —falta un dato que debería existir— y esa dirección
 * es segura: la app dice "fuera de carta" y no autoriza nada. No se inventan.
 */
const IGE_ALTS = [
  1000, 3000, 5000, 8000, 10000, 13000, 15000,
  16500, 17000, 18000, 18500, 19500, 20000, 21000, 22000,
];
const IGE_TEMPS = [10, 15, 20, 25, 30, 35];
const IGE_ROWS = [
  [2250, 2250, 2250, 2250, 2250, 2250], //  1 000 ft
  [2250, 2250, 2250, 2250, 2250, 2250], //  3 000 ft
  [2250, 2250, 2250, 2250, 2250, 2250], //  5 000 ft
  [2250, 2250, 2250, 2250, 2250, 2250], //  8 000 ft
  [2250, 2250, 2250, 2250, 2160, null], // 10 000 ft
  [2120, 2040, 1980, 1950, null, null], // 13 000 ft
  [1920, 1830, null, null, null, null], // 15 000 ft — 20 °C anulada (decía 2250)
  [1800, 1650, null, 1550, 1520, null], // 16 500 ft — 20 °C anulada (decía 1675)
  [1770, 1620, 1580, 1540, 1500, null], // 17 000 ft
  [1700, 1550, 1500, 1420, null, null], // 18 000 ft
  [1650, 1500, 1460, 1390, 1330, null], // 18 500 ft
  [1630, 1420, null, null, null, null], // 19 500 ft — 20 °C anulada (decía 1430)
  [1470, 1420, 1390, null, null, null], // 20 000 ft
  [1410, 1330, null, null, null, null], // 21 000 ft — 20 °C anulada (decía 1350)
  [null, null, null, null, null, null], // 22 000 ft — 10 °C anulada (decía 1430)
];
/** Tabla publicada, no lectura de nomograma. */
const IGE_IS_ESTIMATE = false;

const FUEL_ALTS = [0, 2000, 4000, 6000, 8000, 10000, 12000, 14000, 16000, 18000];
const FUEL_TEMPS = [-20, -15, -10, -5, 0, 5, 10, 15, 20, 25, 30, 35, 40];
const FUEL_ROWS = [
  [170,171,172,173,174,175,175,176,174,170,165,162,158],
  [168,169,170,170,171,172,168,165,162,158,155,151,147],
  [167,167,168,167,164,161,158,155,152,148,145,140,137],
  [165,161,159,155,153,150,147,145,142,138,135,130,127],
  [154,150,148,145,142,140,137,134,132,127,124,121,null],
  [142,140,138,135,133,131,127,124,122,118,114,null,null],
  [132,130,127,125,122,120,117,114,112,108,null,null,null],
  // 14 000 ft. El valor a +25 °C figuraba como 145 l/h en una serie que venía
  // bajando 122→102: un salto del 40 % sin causa física, error de digitalización.
  // Se deja SIN DATO en vez de corregirlo por inferencia — la app avisa que hay
  // que ir al RFM en lugar de dar un número que nadie verificó.
  [122,119,117,115,113,111,108,105,102,null,null,null,null],
  [112,110,108,106,104,102,99,97,null,null,null,null,null],
  [103,101,99,97,95,93,91,89,null,null,null,null,null],
];
const TABLE_MAX_ALT = 18000;
//
// Sin este guardia, `cruiseAltFt > null` se evalúa como `cruiseAltFt > 0` y
// CUALQUIER altitud quedaba marcada como fuera de carta; y `null.toLocaleString()`
// reventaba la pantalla. Con cartas ausentes la pregunta no aplica.
const sobreTopeDeCarta = (ft) => Number.isFinite(TABLE_MAX_ALT) && ft > TABLE_MAX_ALT;
const topeDeCartaTexto = () => (Number.isFinite(TABLE_MAX_ALT) ? `${TABLE_MAX_ALT.toLocaleString()} ft` : "el tope de la carta");
const ROUTE_CEILING = 23000;

/* ---------------------------- Interpolación bilineal ---------------------------- */
function toDMS(lat, lon) {
  const fmt = (v, pos, neg) => {
    const hemi = v >= 0 ? pos : neg;
    const abs = Math.abs(v);
    const d = Math.floor(abs);
    const minFloat = (abs - d) * 60;
    const m = Math.floor(minFloat);
    const s = (minFloat - m) * 60;
    return `${d}°${String(m).padStart(2, "0")}'${s.toFixed(1).padStart(4, "0")}"${hemi}`;
  };
  return `${fmt(lat, "N", "S")} ${fmt(lon, "E", "W")}`;
}
function latDMS(lat) {
  const hemi = lat >= 0 ? "N" : "S", abs = Math.abs(lat), d = Math.floor(abs), mf = (abs - d) * 60, m = Math.floor(mf), s = (mf - m) * 60;
  return `${d}°${String(m).padStart(2, "0")}'${s.toFixed(1).padStart(4, "0")}"${hemi}`;
}
function lonDMS(lon) {
  const hemi = lon >= 0 ? "E" : "W", abs = Math.abs(lon), d = Math.floor(abs), mf = (abs - d) * 60, m = Math.floor(mf), s = (mf - m) * 60;
  return `${d}°${String(m).padStart(2, "0")}'${s.toFixed(1).padStart(4, "0")}"${hemi}`;
}

// Imágenes satelitales reales (NASA GIBS, "Blue Marble" — dominio público, sin API key) en esquema EPSG:4326
// (plate carrée), que calza directo con nuestra proyección equirectangular sin tener que reproyectar nada.
const GIBS_TILE_URL = (z, y, x) => `https://gibs.earthdata.nasa.gov/wmts/epsg4326/best/BlueMarble_NextGeneration/default/2004-08-01/500m/${z}/${y}/${x}.jpeg`;
// Orografía / relieve sombreado (misma cuadrícula de mosaicos que Blue Marble arriba, capa real de la NASA
// GIBS que muestra montañas y valles con sombreado — sin fecha porque es una capa estática, no diaria).
const GIBS_RELIEF_TILE_URL = (z, y, x) => `https://gibs.earthdata.nasa.gov/wmts/epsg4326/best/BlueMarble_ShadedRelief/default//500m/${z}/${y}/${x}.jpeg`;
// OJO: a diferencia de la mayoría de esquemas de mosaicos, en GIBS EPSG:4326 el mosaico de nivel 0 mide
// 288° (no 360°/180°) — es una particularidad documentada de GIBS para no sobremuestrear MODIS. Usar 360/180
// aquí (el supuesto "estándar") da como resultado exactamente el desalineamiento que se vio.
const GIBS_TILE_DEG_L0 = 288;
function lonLatToTile4326(lon, lat, z) {
  const scale = Math.pow(2, z) / GIBS_TILE_DEG_L0;
  return { x: Math.floor((lon + 180) * scale), y: Math.floor((90 - lat) * scale) };
}
function tile4326ToLonLat(x, y, z) {
  const tileDeg = GIBS_TILE_DEG_L0 / Math.pow(2, z);
  return { lon: x * tileDeg - 180, lat: 90 - y * tileDeg };
}

// OpenStreetMap — mosaicos de calles/nombres reales, dominio de tile.openstreetmap.org (uso ligero, sin API
// key; para uso intensivo real hay que correr un servidor propio o usar un proveedor con límites más altos).
// OJO importante: OSM usa el esquema Web Mercator estándar (distinto del EPSG:4326 de GIBS). Nuestro mapa
// dibuja todo con una proyección equirectangular (lat/lon lineal) — cerca del ecuador (Ecuador, Colombia, norte
// de Perú) el mosaico calza casi perfecto; más al sur (centro de Chile/Argentina) puede notarse un corrimiento
// creciente con la latitud. Es una aproximación honesta, no una reproyección exacta.
const OSM_TILE_URL = (z, x, y) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
function lonLatToTileMercator(lon, lat, z) {
  const n = Math.pow(2, z);
  const x = Math.floor((lon + 180) / 360 * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return { x, y };
}
function tileMercatorToLonLat(x, y, z) {
  const n = Math.pow(2, z);
  const lon = (x / n) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
  return { lon, lat: (latRad * 180) / Math.PI };
}

function bilerp(alts, temps, rows, altQ, tempQ) {
  if (altQ < alts[0] || altQ > alts[alts.length - 1]) return { value: null, outOfRange: true, reason: "altitud fuera de carta" };
  if (tempQ < temps[0] || tempQ > temps[temps.length - 1]) return { value: null, outOfRange: true, reason: "temperatura fuera de carta" };
  let ai = 0; while (ai < alts.length - 2 && alts[ai + 1] < altQ) ai++;
  let ti = 0; while (ti < temps.length - 2 && temps[ti + 1] < tempQ) ti++;
  const a0 = alts[ai], a1 = alts[ai + 1], t0 = temps[ti], t1 = temps[ti + 1];
  const q11 = rows[ai][ti], q21 = rows[ai + 1][ti], q12 = rows[ai][ti + 1], q22 = rows[ai + 1][ti + 1];
  if ([q11, q21, q12, q22].some((v) => v === null || v === undefined)) {
    return { value: null, outOfRange: true, reason: "sin dato en la carta para esa combinación — verificar en el RFM" };
  }
  const fa = a1 === a0 ? 0 : (altQ - a0) / (a1 - a0);
  const ft = t1 === t0 ? 0 : (tempQ - t0) / (t1 - t0);
  const vTop = q11 + (q12 - q11) * ft, vBot = q21 + (q22 - q21) * ft;
  return { value: Math.round((vTop + (vBot - vTop) * fa) * 10) / 10, outOfRange: false, reason: null };
}
function tableLookup(table, altQ, tempQ) {
  if (!table) return { value: null, outOfRange: true, reason: "sin carta de performance cargada para este tipo" };
  return bilerp(table.alts, table.temps, table.rows, altQ, tempQ);
}
/**
 * Serie de estacionario para una temperatura.
 *
 * DEVUELVE SI LA CARTA IGE EXISTE O NO
 * ------------------------------------
 * Antes `tableLookup(null, …)` devolvía «fuera de rango», y en el gráfico eso
 * se dibuja igual que un punto que la carta sí tiene pero no cubre. El BK117
 * no tiene carta IGE cargada, así que su curva de efecto suelo salía vacía —
 * indistinguible de «este helicóptero no puede sostener el estacionario en
 * efecto suelo acá».
 *
 * Son dos cosas MUY distintas: una es «no tenemos el dato», la otra es «el dato
 * dice que no». Confundirlas en una carta de performance es de las peores
 * formas de equivocarse.
 */
/**
 * Las horas que le corresponden al PILOTO de un asiento.
 *
 * **Es el FLIGHT TIME, no el air time.** `horasAeronave` lleva el AT —es lo
 * que suma al programa de mantenimiento— y usarlo acá le descontaba al piloto
 * las horas de rotores girando en tierra, que son tiempo de servicio y cuentan
 * para su jornada.
 *
 * El error estaba en DOS lugares: el alta manual y el registro rápido.
 *
 * Si el asiento no trae FT —uno viejo, o una carga donde solo se anotó el AT—
 * se usa el AT como respaldo: tener horas de menos es peor que no tener
 * ninguna. Pero no se inventa la diferencia entre los dos.
 */
function ftDePiloto(entry) {
  const ft = Number(entry?.flightTime);
  if (Number.isFinite(ft) && entry?.flightTime !== null && entry?.flightTime !== "") return ft;
  return Number(entry?.horasAeronave) || 0;
}

function seriesForTemp(ogeTable, igeTable, temp) {
  if (!ogeTable) return [];
  return ogeTable.alts.filter((a) => a % 1000 === 0).map((a) => {
    const oge = tableLookup(ogeTable, a, temp);
    const ige = igeTable ? tableLookup(igeTable, a, temp) : null;
    return {
      alt: a,
      oge: oge.outOfRange ? null : oge.value,
      ige: ige && !ige.outOfRange ? ige.value : null,
      /* El gráfico lo usa para decir «sin carta» en vez de dibujar una curva
         ausente. */
      sinCartaIge: !igeTable,
    };
  });
}

/* ---------------------------- Perfiles de aeronave ---------------------------- */

const AS350B3_PROFILE = {
  id: "as350b3", tipo: "Airbus AS350 B3",
  /* ═══ CANTIDAD DE MOTORES ═══

     Faltaba, y por eso el análisis OEI aparecía con el AS350 cargado
     mostrando «máximo 0 kg, excedido 2009,8 kg»: números del BK117 sobre una
     aeronave que no tiene segundo motor.

     OEI significa «un motor inoperativo, el otro sigue». En un monomotor no
     hay otro: la pregunta no existe, y responderla con un número es peor que
     no responderla. */
  motores: 1,
  emptyKg: 1300, emptyArm: 3.52,
  arms: { pilot: 1.55, paxFront: 1.55, paxRear: 2.54, bagLat: 3.2, bagRear: 4.6, external: 3.4, fuel: 3.47, canasta: 3.43 },
  fuelDensity: 0.8,
  fuelCapacityL: 540, // utilizable — confirmar contra el RFM de la matrícula
  ogeTable: { alts: OGE_ALTS, temps: OGE_TEMPS, rows: OGE_ROWS },
  igeTable: { alts: IGE_ALTS, temps: IGE_TEMPS, rows: IGE_ROWS },
  fuelTable: { alts: FUEL_ALTS, temps: FUEL_TEMPS, rows: FUEL_ROWS },
  // Envolvente de CG longitudinal REAL, tomada del Manual de Vuelo Eurocopter AS350 B3 Arriel 2B1 (Fig. 2-1,
  // secciones 2.2.1/2.2.2 carga interna, y sección 2.2 "Longitudinal CG" con carga externa). Cada punto es
  // [peso_kg, CG_m] tal como está impreso en la carta — no una aproximación. El único tramo estimado (marcado
  // abajo) es el límite trasero "with releasable load" entre 2250 y 2800 kg, porque el manual no imprime un
  // quiebre intermedio exacto ahí, solo los extremos.
  maxInternalKg: 2250,
  maxExternalKg: 2800,
  datumNote: "Datum longitudinal X: 3.40 m (133.8 in) adelante del eje del rotor principal — todas las estaciones y el CG se miden desde ahí. Datum vertical Z: 2.60 m (102.3 in) sobre el piso de cabina. Datum lateral Y: plano de simetría, izquierda negativa y derecha positiva. (RFM AS350 B3e, sección 6.2.2, figura 6-1.) El datum define desde dónde se mide, no cuánto pesa ni dónde está el CG de esta matrícula: eso sale del acta de pesaje.",
  envelope: {
    forward: [[1200, 3.17], [2000, 3.17], [2250, 3.21], [2800, 3.29]],
    rearwardInternal: [[1200, 3.50], [1750, 3.49], [2250, 3.425]],
    rearwardExternal: [[1200, 3.50], [1750, 3.49], [2250, 3.425], [2800, 3.43]], // el tramo 2250→2800 es estimado (ver nota)
  },
  // Compartimentos de equipaje con su límite de peso real — derecha/izquierda comparten el mismo brazo lateral
  // (misma estación longitudinal, distinto lado), trasero usa el brazo trasero.
  // Una sola definición, en el dominio: estaba duplicada acá y allá, y agregar
  // la canasta en un solo lado habría dejado dos listas distintas de dónde se
  // puede poner equipaje.
  cargoCompartments: COMPARTIMENTOS_AS350B3,
};
const BLANK_PROFILE_TEMPLATE = {
  tipo: "Nuevo tipo", emptyKg: 0, emptyArm: 0,
  arms: { pilot: 0, paxFront: 0, paxRear: 0, bagLat: 0, bagRear: 0, external: 0, fuel: 0 },
  fuelDensity: 0.8, fuelCapacityL: 540, ogeTable: null, igeTable: null, fuelTable: null,
  maxInternalKg: 0, maxExternalKg: 0, datumNote: "",
  envelope: { forward: [[0, 0], [1, 0]], rearwardInternal: [[0, 0], [1, 0]], rearwardExternal: [[0, 0], [1, 0]] },
  cargoCompartments: [
    { key: "derecha", label: "Derecha", maxKg: 0, armKey: "bagLat" },
    { key: "izquierda", label: "Izquierda", maxKg: 0, armKey: "bagLat" },
    { key: "trasero", label: "Trasero", maxKg: 0, armKey: "bagRear" },
  ],
};

function limitAtWeight(points, w) {
  if (!points || points.length === 0) return null;
  if (w <= points[0][0]) return points[0][1];
  if (w >= points[points.length - 1][0]) return points[points.length - 1][1];
  for (let i = 0; i < points.length - 1; i++) {
    const [w0, c0] = points[i], [w1, c1] = points[i + 1];
    if (w >= w0 && w <= w1) return c0 + (c1 - c0) * ((w - w0) / (w1 - w0));
  }
  return points[points.length - 1][1];
}

/* ---------------------------- SGS / SMS — metodología Eurocopter IN 2255-I-00 ---------------------------- */
// Matriz de riesgo Gravedad x Probabilidad (5x5), con el código de color del documento: ACEPTABLE (verde),
// MEDIO (azul), GRAVE (amarillo), INACEPTABLE (naranja), INACEPTABLE+ (rojo). Reconstruida a partir de los dos
// puntos que sí confirma el caso concreto del documento (Reducido+Mayor=GRAVE antes de mitigar;
// Raro+Menor=MEDIO después de mitigar) — el resto de las celdas sigue el gradiente estándar de esa misma matriz
// (inspirada en la de Tony Cramp/SHELL que cita el documento), no un calco pixel a pixel de la imagen.
const SMS_GRAVEDAD = ["Insignificante", "Menor", "Mayor", "Crítico", "Catastrófico"];
const SMS_PROBABILIDAD = ["Improbable", "Raro", "Reducido", "Probable", "Frecuente"];
// Matriz de la página 7 de la IN 2255-I-00. Importada del dominio para que no
// puedan existir dos versiones: esta tabla estuvo mal transcrita, con 9 de 25
// celdas más permisivas que el manual, y el error vivía duplicado acá y en
// src/domain/riesgo.js.
const SMS_MATRIX = MATRIZ_RIESGO;
const SMS_COLOR = { ACEPTABLE: "#3FAE49", MEDIO: "#2E6FBF", GRAVE: "#E8C93D", INACEPTABLE: "#E8842D", "INACEPTABLE+": "#D6392E" };
// Matriz de decisión Coste/Beneficio (Figura 3 del documento) — guía si conviene aplicar una medida de
// prevención/protección: 1-2 se puede adoptar tal cual, 3 conviene bajar el coste si se puede, más de 3
// obliga a volver a analizar el riesgo para buscar otra solución.
const SMS_COSTE = ["Bajo", "Medio", "Elevado"];
const SMS_BENEFICIO = ["Elevado", "Medio", "Bajo"];
const SMS_COSTE_BENEFICIO_MATRIX = [
  [1, 2, 3], // Coste Bajo
  [2, 3, 4], // Coste Medio
  [3, 4, 5], // Coste Elevado
];
const smsCosteBeneficioResultado = (score) => (score <= 2 ? "Se puede adoptar en el estado actual" : score === 3 ? "Si es posible, reducir el coste de la medida" : "Volver a analizar el riesgo — buscar otra solución");

const smsRiskLevel = (gravIdx, probIdx) => SMS_MATRIX[gravIdx]?.[probIdx] || "MEDIO";

// Eventos Límite (EU) — las 7 categorías de consecuencia del Anexo 1.
const SMS_EU_CATEGORIES = [
  { key: "EU1", label: "CFIT (colisión con el terreno sin pérdida de control)" },
  { key: "EU2", label: "Choque tras la pérdida de control durante el vuelo" },
  { key: "EU3", label: "Colisión en vuelo" },
  { key: "EU4", label: "Colisión en tierra" },
  { key: "EU5", label: "Salidas de la pista" },
  { key: "EU6", label: "Daños/lesiones en vuelo" },
  { key: "EU7", label: "Daños/lesiones en tierra" },
];
// Biblioteca de Eventos Indeseables (EI) — subconjunto representativo del Anexo 1 (que trae 51 en total),
// con sus categorías EU reales según la tabla del documento. Se puede agregar el resto o los propios con
// "+ evento".
const SMS_EI_LIBRARY = [
  { ref: "EI01", desc: "Aproximaciones no estabilizadas", eu: ["EU1", "EU2", "EU5", "EU6"] },
  { ref: "EI02", desc: "Cálculo erróneo del peso/centrado y anotación de estos datos en el FMS", eu: ["EU2", "EU5", "EU6"] },
  { ref: "EI03", desc: "Incursiones en pistas", eu: ["EU4", "EU5", "EU7"] },
  { ref: "EI04", desc: "Incidente relacionado con la formación de hielo o con los procedimientos de deshielo", eu: ["EU2", "EU6"] },
  { ref: "EI05", desc: "Condiciones meteorológicas peligrosas (tormentas, vientos fuertes, cizalladura, granizo, neblina…)", eu: ["EU2", "EU5", "EU6"] },
  { ref: "EI06", desc: "Fallo de un solo GTM en multi-motores (fallo, falta de combustible…)", eu: ["EU2", "EU5", "EU6"] },
  { ref: "EI07", desc: "Desvío de trayectoria en ruta", eu: ["EU1", "EU3", "EU6", "EU7"] },
  { ref: "EI08", desc: "Pérdida de separación (IFR/IFR o IFR/VFR especial) en vuelo", eu: ["EU3", "EU6"] },
  { ref: "EI09", desc: "Acción inadecuada de la tripulación (FH, reglamentación)", eu: ["EU1", "EU2", "EU3", "EU4", "EU5", "EU6", "EU7"] },
  { ref: "EI10", desc: "Fallo de las interfaces tierra/cabina (genérico)", eu: ["EU1", "EU3", "EU7"] },
  { ref: "EI11", desc: "Evento ligado a una pista contaminada", eu: ["EU5", "EU7"] },
  { ref: "EI12", desc: "Fallo sistemas de la aeronave (que no sea el fallo de un GTM)", eu: ["EU1", "EU2", "EU3", "EU4", "EU5", "EU6", "EU7"] },
  { ref: "EI13", desc: "Fuego, humo, contacto accidental de un comburente con una fuente de ignición", eu: ["EU2", "EU6", "EU7"] },
  { ref: "EI14", desc: "Eventos relacionados con trabajos/mantenimiento/atasco en la plataforma", eu: ["EU1", "EU4", "EU5", "EU7"] },
  { ref: "EI15", desc: "Eventos ligados a un incidente de mantenimiento", eu: ["EU1", "EU2", "EU3", "EU4", "EU5", "EU6", "EU7"] },
  { ref: "EI16", desc: "Daños críticos aeronave no detectados antes del vuelo", eu: ["EU1", "EU2", "EU3", "EU4", "EU5", "EU6", "EU7"] },
  { ref: "EI17", desc: "Fallo de un solo GTM en mono-motores (fallo, falta de combustible…)", eu: ["EU2", "EU6", "EU7"] },
  { ref: "EI18", desc: "Disfunción sistema de comunicación (ATC/avión, avión/equipo en tierra…)", eu: ["EU3", "EU4", "EU7"] },
  { ref: "EI19", desc: "Obstáculo desconocido de la tripulación que podría interferir con la trayectoria del helicóptero en vuelo", eu: ["EU1", "EU3", "EU6"] },
  { ref: "EI20", desc: "Colisión con aves", eu: ["EU2", "EU6", "EU7"] },
  { ref: "EI21", desc: "Entrada inesperada en IMC, pérdida de referencia en vuelo", eu: ["EU1", "EU2", "EU6"] },
  { ref: "EI22", desc: "Rebasamiento de los límites de peso y centrado que afectan al control del helicóptero", eu: ["EU2", "EU6"] },
  { ref: "EI23", desc: "Desplazamiento de la carga durante el vuelo (fallo de amarrado o falta de sujeción de la carga)", eu: ["EU2", "EU6"] },
  { ref: "EI24", desc: "Dimensiones inadaptadas de las zonas de aterrizaje (helipuerto y superficies de aterrizaje para helicópteros)", eu: ["EU1", "EU2", "EU4", "EU6"] },
  { ref: "EI25", desc: "Tipo de zonas de aterrizaje (estrechas, en pendiente, terrenos fangosos…) y/o de su entorno (hostil, urbano…)", eu: ["EU1", "EU4", "EU7"] },
  { ref: "EI26", desc: "Mala comprensión/comunicación entre las partes interesadas (fraseología tripulación/ATC, personal de tierra…)", eu: ["EU1", "EU3", "EU4", "EU5", "EU6", "EU7"] },
  { ref: "EI27", desc: "Instrucción ATC inadaptada", eu: ["EU1", "EU3", "EU4", "EU5", "EU6", "EU7"] },
  { ref: "EI28", desc: "Confusión de TWY, pista, aeródromo", eu: ["EU3", "EU4", "EU5", "EU6", "EU7"] },
  { ref: "EI29", desc: "Documentación/base de datos aeronáutica errónea o incompleta", eu: ["EU1", "EU3", "EU4", "EU6", "EU7"] },
  { ref: "EI30", desc: "Incapacidad de la tripulación que afecta a la dirección del vuelo", eu: ["EU2", "EU3", "EU4", "EU5", "EU6", "EU7"] },
  { ref: "EI31", desc: "Disfunción de uno o varios sistemas, de un componente, de un elemento de la carga que causa fuego o una explosión", eu: ["EU2", "EU6", "EU7"] },
  { ref: "EI32", desc: "Acto ilegal (sabotaje, terrorismo…)", eu: ["EU2", "EU4", "EU5", "EU6", "EU7"] },
  { ref: "EI33", desc: "Proyección de líquido (rotura tubo hidráulico, combustible…)", eu: ["EU6", "EU7"] },
  { ref: "EI34", desc: "Caída de material de a bordo, maletas no amarradas…", eu: ["EU2", "EU6"] },
  { ref: "EI35", desc: "Proyección de desechos, de escombros provocada por el soplido del rotor", eu: ["EU7"] },
  { ref: "EI36", desc: "Pérdida de piezas durante el vuelo", eu: ["EU2", "EU6", "EU7"] },
  { ref: "EI37", desc: "Pérdida de un objeto/carga externa durante el vuelo", eu: ["EU7"] },
  { ref: "EI38", desc: "Caída de una persona durante el vuelo (vuelo con las puertas abiertas)", eu: ["EU6"] },
  { ref: "EI39", desc: "Caída de una persona del helicóptero en tierra (embarque/desembarque)", eu: ["EU7"] },
  { ref: "EI40", desc: "Deterioro del RAC durante el transporte de cargas externas (utilización de la grúa, elevación…)", eu: ["EU2", "EU6", "EU7"] },
  { ref: "EI41", desc: "Mala coordinación con el personal de tierra/buque durante las operaciones de carga externa", eu: ["EU2", "EU6", "EU7"] },
  { ref: "EI42", desc: "Lesiones del personal por descarga eléctrica (electricidad estática)", eu: ["EU6", "EU7"] },
  { ref: "EI43", desc: "Pérdida de referencia en vuelo de noche durante la instrucción SAR", eu: ["EU2", "EU6", "EU7"] },
  { ref: "EI44", desc: "Heliplataforma no adaptada (alta mar)", eu: ["EU1", "EU5", "EU6", "EU7"] },
  { ref: "EI45", desc: "Incidentes durante el repostaje en heliplataforma (repostaje con motor y rotor en marcha)", eu: ["EU7"] },
  { ref: "EI46", desc: "Balizaje pista, caminos, aparcamiento… insuficiente o inadecuado", eu: ["EU4", "EU5", "EU7"] },
  { ref: "EI47", desc: "Incursión de un vehículo/aeronave/personal/animal en las servidumbres aeroportuarias", eu: ["EU4", "EU7"] },
  { ref: "EI48", desc: "Golpes al personal por las palas de los rotores", eu: ["EU7"] },
  { ref: "EI49", desc: "Ocupantes sin cinturón de seguridad durante el vuelo", eu: ["EU6"] },
  { ref: "EI50", desc: "Pérdida de sustentación (VORTEX)", eu: ["EU2", "EU6"] },
  { ref: "EI51", desc: "Alcance del umbral de reversibilidad de las órdenes", eu: ["EU2", "EU6"] },
];
// Nota: el mapeo de columnas EU por evento se reconstruyó a partir del Anexo 1 del documento (tabla con
// columnas EU1-EU7 marcadas con X); el texto de cada evento es exacto, la asignación de columnas es una
// lectura fiel de esa tabla pero conviene verificarla contra el PDF original si se usa para una auditoría formal.

// Escenarios de accidente pre-armados (Anexo 3 del documento) — cadenas de causas típicas por cada Evento
// Límite, para usarlos como referencia rápida al construir un bow-tie nuevo.
const SMS_ANEXO3_SCENARIOS = [
  { id: 1, eu: "EU1", nombre: "Colisión de alta energía con el suelo sin perder el control (CFIT)", cadena: "Documentación aeronáutica desactualizada/NOTAM no accedidos + presión por el tiempo/rutina → preparación incompleta del vuelo o error de inserción en el FMS → trayectoria errónea → degradación meteorológica, vuelo nocturno o instrucción ATC errónea → desviación de la ruta o vuelo bajo la altura mínima → CFIT." },
  { id: 2, eu: "EU2", nombre: "Pérdida de control durante el vuelo", cadena: "Determinación errónea de peso y centrado, carga mal estibada, indisciplina o fenómenos fisiológicos → rebasamiento de límites de peso/centrado o acción fuera de la envolvente de vuelo → sumado a reducción de visibilidad, meteorología peligrosa o pérdida de potencia GTM → desorientación espacial o estado inseguro del helicóptero → pérdida de control en vuelo." },
  { id: 3, eu: "EU3", nombre: "Abordaje en vuelo entre dos aeronaves", cadena: "Documentación/preparación incompleta o error en el FMS + degradación meteorológica, tráfico denso o diferencias de idioma → reducción de visibilidad, instrucción ATC errónea sin información de tráfico, o defecto del sistema anemo-barométrico → desviación de la ruta o vuelo bajo la altura autorizada → abordaje en vuelo." },
  { id: 4, eu: "EU4", nombre: "Pérdida de control en tierra", cadena: "Suelo resbaladizo, rebasamiento de límites de aterrizaje en pendiente, o falla de mantenimiento (hidráulico/frenos) → alcance de topes de mando de vuelo o acción fuera de la competencia del piloto → rodamiento en zona no carreteable o aterrizaje en superficie inadaptada → roll over estático o dinámico." },
  { id: 5, eu: "EU4", nombre: "Colisión en tierra", cadena: "Preparación incompleta, rutina, o ausencia de marcado/instrucción del área de maniobra → error en la elección de la vía de circulación o falta de manejo de la circulación en el área de tráfico → desviación de la trayectoria de rodaje → no respeto de las distancias mínimas de seguridad → colisión en tierra." },
];



const RANK = { ACEPTABLE: 0, MEDIO: 1, TOLERABLE: 1, GRAVE: 2, INTOLERABLE: 3, INACEPTABLE: 3, "INACEPTABLE+": 4 };
const RISK_GROUPS = [
  { key: "tarea", label: "Tarea", options: [
    { label: "Traslado", gravIdx: 2, probIdx: 0 },
    { label: "Mantenimiento", gravIdx: 2, probIdx: 0 },
    { label: "Entrenamiento", gravIdx: 2, probIdx: 0 },
    { label: "Línea larga", gravIdx: 2, probIdx: 1 },
    { label: "Evacuación médica", gravIdx: 2, probIdx: 1 },
    { label: "Carga externa", gravIdx: 2, probIdx: 1 },
    { label: "Carga externa LL", gravIdx: 2, probIdx: 2 },
    { label: "Electromagnetometría", gravIdx: 2, probIdx: 2 },
    { label: "Magnetometría", gravIdx: 2, probIdx: 2 },
  ]},
  // Topografía por banda de cota, para que salga sola del perfil de terreno.
  // Umbrales acordados con el operador: montaña 5 000–10 000 ft, alta montaña
  // 10 000 ft hasta el techo de servicio del AS350 B3 (23 000 ft).
  //
  // La opción 0 es el caso "todavía no sé": evalúa a GRAVE (ámbar) y no a
  // ACEPTABLE, porque no haber planificado la ruta no es lo mismo que haberla
  // planificado y que dé bien. El FRAT ya no arranca todo en verde.
  { key: "topografia", label: "Topografía (derivada del perfil de terreno)", derivable: true, options: [
    { label: "Sin ruta planificada", gravIdx: 2, probIdx: 2 },
    { label: "No montañosa — bajo 5 000 ft", gravIdx: 2, probIdx: 0 },
    { label: "Montaña — 5 000 a 10 000 ft", gravIdx: 2, probIdx: 1 },
    { label: "Alta montaña — sobre 10 000 ft", gravIdx: 2, probIdx: 2 },
    { label: "Terreno sobre el techo de servicio", gravIdx: 4, probIdx: 3 },
  ]},
  { key: "descanso", label: "Descanso de tripulación", options: [
    { label: "Adecuado", gravIdx: 2, probIdx: 0 },
    { label: "Inadecuado", gravIdx: 4, probIdx: 3 },
  ]},
  { key: "planificacion", label: "Planificación", options: [
    { label: "Adecuada", gravIdx: 2, probIdx: 0 },
    { label: "Inadecuada", gravIdx: 4, probIdx: 2 },
  ]},
  { key: "piloto", label: "Experiencia — piloto (PIC)", options: [
    { label: "≥ 500 h", gravIdx: 2, probIdx: 0 },
    { label: "≥ 250 h", gravIdx: 2, probIdx: 1 },
    { label: "< 250 h", gravIdx: 2, probIdx: 3 },
  ]},
  { key: "copiloto", label: "Experiencia — copiloto (SIC)", options: [
    { label: "Sin copiloto", gravIdx: 0, probIdx: 0 },
    { label: "≥ 250 h", gravIdx: 2, probIdx: 0 },
    { label: "≥ 120 h", gravIdx: 2, probIdx: 1 },
    { label: "< 120 h", gravIdx: 2, probIdx: 3 },
  ]},
  { key: "habilitacion", label: "Habilitación de la tripulación", options: [
    { label: "Habilitado", gravIdx: 2, probIdx: 0 },
    { label: "Sin habilitación", gravIdx: 4, probIdx: 2 },
  ]},
  { key: "meteoSalida", label: "Meteorología — salida", options: [
    { label: "CAVOK", gravIdx: 2, probIdx: 0 },
    { label: "1500' - 5KM", gravIdx: 2, probIdx: 1 },
    { label: "Sin información", gravIdx: 2, probIdx: 3 },
  ]},
  { key: "meteoRuta", label: "Meteorología — ruta", options: [
    { label: "CAVOK", gravIdx: 2, probIdx: 0 },
    { label: "1500' - 5KM", gravIdx: 2, probIdx: 1 },
    { label: "Sin información", gravIdx: 2, probIdx: 3 },
  ]},
  { key: "meteoDestino", label: "Meteorología — destino", options: [
    { label: "CAVOK", gravIdx: 2, probIdx: 0 },
    { label: "1500' - 5KM", gravIdx: 2, probIdx: 1 },
    { label: "Sin información", gravIdx: 2, probIdx: 3 },
  ]},
  // Viento derivado de la meteorología de los puntos de despegue y aterrizaje.
  // Se toma el peor de los dos, y dentro de cada uno el mayor entre viento
  // sostenido y ráfaga: un día de 20 kt con ráfagas de 35 no es un día de 20 kt.
  { key: "viento", label: "Viento (máximo aprobado: 25 kt)", derivable: true, options: [
    { label: "Sin dato de viento", gravIdx: 2, probIdx: 2 },
    { label: "Dentro del máximo de 25 kt", gravIdx: 2, probIdx: 0 },
    { label: "Sobre el máximo de 25 kt — no se sale a volar", gravIdx: 2, probIdx: 3 },
  ]},
  // Reserva de combustible: sale del cálculo, no se contesta a mano. Los
  // índices los fija evaluarReserva() en el dominio; acá solo se declaran las
  // opciones para que el grupo se vea igual que los demás.
  { key: "reserva", label: "Reserva de combustible (30 min)", derivable: true, options: [
    { label: "Sin cálculo de combustible", gravIdx: 2, probIdx: 2 },
    { label: "Reserva cubierta", gravIdx: 2, probIdx: 0 },
    { label: "Reserva cubierta con margen mínimo", gravIdx: 3, probIdx: 2 },
    { label: "Reserva NO cubierta", gravIdx: 4, probIdx: 3 },
  ]},
];
// Nota de conversión: este FRAT usaba antes su propia escala (Sumamente Improbable…Ocasional /
// Peligroso…Catastrófico, con tolerancia ACEPTABLE/TOLERABLE/INTOLERABLE). Cada opción se re-mapeó a la
// escala de Probabilidad/Gravedad del SGS (Eurocopter IN 2255-I-00): Sumamente Improbable→Improbable,
// Improbable→Raro, Remoto→Reducido, Ocasional→Probable; Peligroso→Mayor, Catastrófico→Catastrófico. El
// resultado (ACEPTABLE/MEDIO/GRAVE/INACEPTABLE/INACEPTABLE+) sale de smsRiskLevel(gravIdx, probIdx), la misma
// matriz que usa la pestaña SGS — ya no hay dos matrices de riesgo distintas en la app.


/* ---------------------------- Aeródromos de referencia ---------------------------- */
// Coordenadas verificadas contra Wikipedia/OurAirports donde se indica; el resto son valores de
// conocimiento general de aviación y quedan marcados como aproximados. FIR/TMA/CTR/frecuencia/pista
// se dejan en blanco salvo que se indique — complétalos con AIP/OpenAIP real antes de operar con esto.
const SEED_AIRPORTS = [
  // Perú
  { id: "spjc", name: "Lima — Jorge Chávez", icao: "SPJC", lat: -12.0219, lon: -77.1143, elevFt: 113, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spzo", name: "Cusco — Alejandro Velasco Astete", icao: "SPZO", lat: -13.5357, lon: -71.9388, elevFt: 10860, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spqu", name: "Arequipa — Rodríguez Ballón (aprox.)", icao: "SPQU", lat: -16.3411, lon: -71.583, elevFt: 8400, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spho", name: "Ayacucho — Alfredo Mendívil Duarte", icao: "SPHO", lat: -13.15417, lon: -74.20417, elevFt: 8917, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spjl", name: "Juliaca — Inca Manco Cápac", icao: "SPJL", lat: -15.46667, lon: -70.15833, elevFt: 12552, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spqt", name: "Iquitos — Secada Vignetta (aprox.)", icao: "SPQT", lat: -3.7847, lon: -73.3087, elevFt: 306, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spru", name: "Trujillo — Cap. FAP Carlos Martínez de Pinillos (aprox.)", icao: "SPRU", lat: -8.0819, lon: -79.1085, elevFt: 106, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spor", name: "Orcopampa", icao: "SPOR", lat: -15.3125, lon: -72.35, elevFt: 12322, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sprf", name: "San Rafael (mina)", icao: "SPRF", lat: -14.28194, lon: -70.38056, elevFt: 14422, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spoy", name: "Atico", icao: "SPOY", lat: -16.23333, lon: -73.60417, elevFt: 40, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spgl", name: "Chagual", icao: "SPGL", lat: -7.79722, lon: -77.65139, elevFt: 3967, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spqm", name: "San Lorenzo", icao: "SPQM", lat: -4.82361, lon: -76.56111, elevFt: 433, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spjr", name: "Cajamarca — Armando Revoredo Iglesias", icao: "SPJR", lat: -7.14028, lon: -78.48889, elevFt: 8781, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sppe", name: "Palmapampa", icao: "SPPE", lat: -12.76389, lon: -73.65278, elevFt: 2340, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "speq", name: "Moquegua — Cesar Torque Podesta", icao: "SPEQ", lat: -17.17917, lon: -70.93056, elevFt: 4480, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  { id: "spga", name: "Pata de Gallo (Quiruvilca)", icao: "SPGA", lat: -7.98333, lon: -78.26667, elevFt: 13720, fir: "SPIM Lima", tma: "", ctr: "", freq: "", pista: "" },
  // Ecuador (coordenadas de Wikipedia)
  { id: "seqm", name: "Quito — Mariscal Sucre", icao: "SEQM", lat: -0.14111, lon: -78.48806, elevFt: 7841, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "segu", name: "Guayaquil — José Joaquín de Olmedo", icao: "SEGU", lat: -2.15778, lon: -79.88361, elevFt: 19, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "secu", name: "Cuenca — Mariscal Lamar", icao: "SECU", lat: -2.88944, lon: -78.98444, elevFt: 8306, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "segs", name: "Baltra (Galápagos) — Seymour", icao: "SEGS", lat: -0.45389, lon: -90.26583, elevFt: 42, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "selt", name: "Latacunga — Cotopaxi Intl.", icao: "SELT", lat: -0.90694, lon: -78.61583, elevFt: 9186, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "semt", name: "Manta — Eloy Alfaro", icao: "SEMT", lat: -0.94583, lon: -80.67861, elevFt: 40, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "setu", name: "Tulcán — Luis A. Mantilla", icao: "SETU", lat: 0.80972, lon: -77.70833, elevFt: 9649, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "seca", name: "Loja — Ciudad de Catamayo", icao: "SECA", lat: -3.99583, lon: -79.37222, elevFt: 4056, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "senl", name: "Lago Agrio (Nueva Loja)", icao: "SENL", lat: 0.09306, lon: -76.86806, elevFt: 982, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "seco", name: "Coca — Francisco de Orellana", icao: "SECO", lat: -0.46111, lon: -76.9875, elevFt: 834, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sero2", name: "Machala — Santa Rosa Intl.", icao: "SERO", lat: -3.43528, lon: -79.97778, elevFt: 170, fir: "SEFG Guayaquil/Quito", tma: "", ctr: "", freq: "", pista: "" },
  // Chile
  { id: "scel", name: "Santiago — Arturo Merino Benítez", icao: "SCEL", lat: -33.393, lon: -70.7858, elevFt: 1555, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  { id: "scte", name: "Puerto Montt — El Tepual", icao: "SCTE", lat: -41.43861, lon: -73.09389, elevFt: 294, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  { id: "scie", name: "Concepción — Carriel Sur", icao: "SCIE", lat: -36.7725, lon: -73.06306, elevFt: 26, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sccii", name: "Punta Arenas — Carlos Ibáñez (aprox.)", icao: "SCCI", lat: -53.0026, lon: -70.8546, elevFt: 138, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  { id: "scip", name: "Isla de Pascua — Mataveri (aprox.)", icao: "SCIP", lat: -27.1648, lon: -109.4219, elevFt: 227, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  { id: "scda", name: "Iquique — Diego Aracena (aprox.)", icao: "SCDA", lat: -20.5352, lon: -70.1813, elevFt: 155, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  { id: "scfa", name: "Antofagasta — Andrés Sabella Gálvez (aprox.)", icao: "SCFA", lat: -23.4445, lon: -70.4451, elevFt: 455, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  { id: "scqp", name: "Temuco — La Araucanía", icao: "SCQP", lat: -38.925, lon: -72.65139, elevFt: 321, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sccf", name: "Calama — El Loa", icao: "SCCF", lat: -22.49722, lon: -68.90694, elevFt: 7631, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  { id: "scvd", name: "Valdivia — Pichoy", icao: "SCVD", lat: -39.64972, lon: -73.08611, elevFt: 59, fir: "SCEZ Santiago", tma: "", ctr: "", freq: "", pista: "" },
  // Colombia
  { id: "skbo2", name: "Bogotá — El Dorado", icao: "SKBO", lat: 4.7016, lon: -74.1469, elevFt: 8361, fir: "SKED Bogotá", tma: "", ctr: "", freq: "", pista: "" },
  { id: "skmd", name: "Medellín — José María Córdova (aprox.)", icao: "SKRG", lat: 6.1645, lon: -75.4231, elevFt: 7028, fir: "SKED Bogotá", tma: "", ctr: "", freq: "", pista: "" },
  { id: "skcl", name: "Cali — Alfonso Bonilla Aragón (aprox.)", icao: "SKCL", lat: 3.5432, lon: -76.3816, elevFt: 3162, fir: "SKED Bogotá", tma: "", ctr: "", freq: "", pista: "" },
  // Venezuela
  { id: "svmi", name: "Caracas — Simón Bolívar (aprox.)", icao: "SVMI", lat: 10.6031, lon: -66.9906, elevFt: 235, fir: "SVZM Maiquetía", tma: "", ctr: "", freq: "", pista: "" },
  // Brasil
  { id: "sbgr", name: "São Paulo — Guarulhos (aprox.)", icao: "SBGR", lat: -23.4356, lon: -46.4731, elevFt: 2459, fir: "SBBS Brasília/SBAO Atlântico", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sbgl", name: "Río de Janeiro — Galeão (aprox.)", icao: "SBGL", lat: -22.8099, lon: -43.2506, elevFt: 28, fir: "SBBS Brasília", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sbbr", name: "Brasília Intl. (aprox.)", icao: "SBBR", lat: -15.8697, lon: -47.9208, elevFt: 3497, fir: "SBBS Brasília", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sbcf", name: "Manaus — Eduardo Gomes (aprox.)", icao: "SBEG", lat: -3.0386, lon: -60.0497, elevFt: 264, fir: "SBAZ Amazônico", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sbsv", name: "Salvador — Deputado Luís Eduardo Magalhães (aprox.)", icao: "SBSV", lat: -12.9086, lon: -38.3225, elevFt: 65, fir: "SBBS Brasília", tma: "", ctr: "", freq: "", pista: "" },
  // Bolivia
  { id: "sllp2", name: "La Paz — El Alto", icao: "SLLP", lat: -16.5133, lon: -68.1925, elevFt: 13325, fir: "SLLF La Paz", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sscz", name: "Santa Cruz — Viru Viru (aprox.)", icao: "SLVR", lat: -17.6448, lon: -63.1354, elevFt: 1224, fir: "SLLF La Paz", tma: "", ctr: "", freq: "", pista: "" },
  // Paraguay / Uruguay / Argentina
  { id: "sgas", name: "Asunción — Silvio Pettirossi (aprox.)", icao: "SGAS", lat: -25.24, lon: -57.52, elevFt: 292, fir: "SGFA Asunción", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sumu", name: "Montevideo — Carrasco (aprox.)", icao: "SUMU", lat: -34.8384, lon: -56.0308, elevFt: 105, fir: "SUEO Montevideo", tma: "", ctr: "", freq: "", pista: "" },
  { id: "saez2", name: "Buenos Aires — Ezeiza", icao: "SAEZ", lat: -34.8222, lon: -58.5358, elevFt: 67, fir: "SAEF Ezeiza", tma: "", ctr: "", freq: "", pista: "" },
  { id: "saco", name: "Córdoba (aprox.)", icao: "SACO", lat: -31.3236, lon: -64.2081, elevFt: 1610, fir: "SARR Córdoba", tma: "", ctr: "", freq: "", pista: "" },
  { id: "same", name: "Mendoza — El Plumerillo (aprox.)", icao: "SAME", lat: -32.8317, lon: -68.7929, elevFt: 2310, fir: "SARR Córdoba", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sazs", name: "Bariloche (aprox.)", icao: "SAZS", lat: -41.1512, lon: -71.1575, elevFt: 2775, fir: "SAEF Ezeiza", tma: "", ctr: "", freq: "", pista: "" },
  // Guayanas
  { id: "sygo", name: "Georgetown — Cheddi Jagan (aprox.)", icao: "SYCJ", lat: 6.4985, lon: -58.2541, elevFt: 111, fir: "SYGC Georgetown", tma: "", ctr: "", freq: "", pista: "" },
  // Referencias regionales / hubs
  { id: "skbo", name: "Bogotá — El Dorado (aprox.)", icao: "SKBO", lat: 4.7016, lon: -74.1469, elevFt: 8361, fir: "SKED Bogotá", tma: "", ctr: "", freq: "", pista: "" },
  { id: "sllp", name: "La Paz — El Alto (aprox.)", icao: "SLLP", lat: -16.5133, lon: -68.1925, elevFt: 13325, fir: "SLLF La Paz", tma: "", ctr: "", freq: "", pista: "" },
  { id: "saez", name: "Buenos Aires — Ezeiza (aprox.)", icao: "SAEZ", lat: -34.8222, lon: -58.5358, elevFt: 67, fir: "SAEF Ezeiza", tma: "", ctr: "", freq: "", pista: "" },
  { id: "kmia", name: "Miami Intl.", icao: "KMIA", lat: 25.7959, lon: -80.287, elevFt: 8, fir: "KZMA Miami", tma: "", ctr: "", freq: "", pista: "" },
  { id: "lemd", name: "Madrid — Barajas (aprox.)", icao: "LEMD", lat: 40.4719, lon: -3.5626, elevFt: 2001, fir: "LECM Madrid", tma: "", ctr: "", freq: "", pista: "" },
];

// Ciudades de referencia siempre visibles, para orientación incluso muy alejado del mapa.
// Lista amplia de ciudades y pueblos reales de Sudamérica, incluida directo en la app (sin depender de
// ninguna descarga externa) — esta es la causa real de que antes "no aparecieran los pueblos": dependían
// por completo de bajar un archivo externo de ~10 MB que, si la red del entorno lo bloquea, deja el mapa sin
// ningún pueblo sin importar el zoom. Con esta lista, el mapa SIEMPRE muestra pueblos, tengas o no conexión.
const REFERENCE_CITIES = [
  // Ecuador
  { name: "Quito", lat: -0.1807, lon: -78.4678 }, { name: "Guayaquil", lat: -2.1894, lon: -79.8891 },
  { name: "Cuenca", lat: -2.9006, lon: -79.0045 }, { name: "Loja", lat: -3.9931, lon: -79.2042 },
  { name: "Ambato", lat: -1.2417, lon: -78.6197 }, { name: "Manta", lat: -0.9677, lon: -80.7089 },
  { name: "Portoviejo", lat: -1.0546, lon: -80.454 }, { name: "Machala", lat: -3.2586, lon: -79.9554 },
  { name: "Riobamba", lat: -1.6636, lon: -78.6546 }, { name: "Ibarra", lat: 0.3517, lon: -78.1223 },
  { name: "Esmeraldas", lat: 0.9682, lon: -79.6517 }, { name: "Tena", lat: -0.9938, lon: -77.8131 },
  { name: "Puyo", lat: -1.4924, lon: -78.0} , { name: "Macas", lat: -2.3089, lon: -78.1178 },
  { name: "Nueva Loja (Lago Agrio)", lat: 0.0847, lon: -76.8811 }, { name: "Coca (Puerto Francisco de Orellana)", lat: -0.4653, lon: -76.9836 },
  { name: "Santo Domingo de los Tsáchilas", lat: -0.2528, lon: -79.1719 }, { name: "Babahoyo", lat: -1.8021, lon: -79.5347 },
  { name: "Quevedo", lat: -1.0225, lon: -79.4608 }, { name: "Salinas", lat: -2.2167, lon: -80.9583 },
  { name: "Zamora", lat: -4.0653, lon: -78.9556 }, { name: "Azogues", lat: -2.7333, lon: -78.8467 },
  { name: "Tulcán", lat: 0.8114, lon: -77.7175 }, { name: "Baños de Agua Santa", lat: -1.3958, lon: -78.4247 },
  // Perú
  { name: "Lima", lat: -12.0464, lon: -77.0428 }, { name: "Cusco", lat: -13.5319, lon: -71.9675 },
  { name: "Arequipa", lat: -16.409, lon: -71.5375 }, { name: "Trujillo", lat: -8.1116, lon: -79.0287 },
  { name: "Chiclayo", lat: -6.7714, lon: -79.8409 }, { name: "Piura", lat: -5.1945, lon: -80.6328 },
  { name: "Iquitos", lat: -3.7437, lon: -73.2516 }, { name: "Pucallpa", lat: -8.3791, lon: -74.5539 },
  { name: "Tarapoto", lat: -6.4869, lon: -76.3725 }, { name: "Puerto Maldonado", lat: -12.5933, lon: -69.1891 },
  { name: "Huancayo", lat: -12.0667, lon: -75.2167 }, { name: "Ayacucho", lat: -13.1588, lon: -74.2239 },
  { name: "Puno", lat: -15.8402, lon: -70.0219 }, { name: "Tacna", lat: -18.0146, lon: -70.2534 },
  { name: "Chimbote", lat: -9.0853, lon: -78.5783 }, { name: "Cajamarca", lat: -7.1638, lon: -78.5003 },
  { name: "Ica", lat: -14.0678, lon: -75.7286 }, { name: "Huaraz", lat: -9.5277, lon: -77.5278 },
  // Colombia
  { name: "Bogotá", lat: 4.711, lon: -74.0721 }, { name: "Medellín", lat: 6.2442, lon: -75.5812 },
  { name: "Cali", lat: 3.4516, lon: -76.532 }, { name: "Cartagena", lat: 10.391, lon: -75.4794 },
  { name: "Barranquilla", lat: 10.9685, lon: -74.7813 }, { name: "Bucaramanga", lat: 7.1193, lon: -73.1227 },
  { name: "Pasto", lat: 1.2136, lon: -77.2811 }, { name: "Villavicencio", lat: 4.142, lon: -73.6266 },
  { name: "Leticia", lat: -4.2153, lon: -69.9406 }, { name: "San José del Guaviare", lat: 2.5709, lon: -72.6413 },
  { name: "Ipiales", lat: 0.8276, lon: -77.6412 }, { name: "Neiva", lat: 2.9273, lon: -75.2819 },
  // Chile
  { name: "Santiago", lat: -33.4489, lon: -70.6693 }, { name: "Valparaíso", lat: -33.0472, lon: -71.6127 },
  { name: "Concepción", lat: -36.8201, lon: -73.0444 }, { name: "Antofagasta", lat: -23.6509, lon: -70.3975 },
  { name: "La Serena", lat: -29.9027, lon: -71.2519 }, { name: "Temuco", lat: -38.7359, lon: -72.5904 },
  { name: "Puerto Montt", lat: -41.4693, lon: -72.9424 }, { name: "Punta Arenas", lat: -53.1638, lon: -70.9171 },
  { name: "Coyhaique", lat: -45.5752, lon: -72.0662 }, { name: "Iquique", lat: -20.2208, lon: -70.1431 },
  { name: "Calama", lat: -22.4667, lon: -68.9333 }, { name: "Chillán", lat: -36.6063, lon: -72.1034 },
  // Bolivia
  { name: "La Paz", lat: -16.5, lon: -68.15 }, { name: "Santa Cruz de la Sierra", lat: -17.7833, lon: -63.1821 },
  { name: "Cochabamba", lat: -17.3895, lon: -66.157 }, { name: "Sucre", lat: -19.0333, lon: -65.2627 },
  { name: "Potosí", lat: -19.5836, lon: -65.7531 }, { name: "Tarija", lat: -21.5355, lon: -64.7296 },
  { name: "Trinidad (Beni)", lat: -14.8333, lon: -64.9 }, { name: "Oruro", lat: -17.9833, lon: -67.15 },
  // Argentina
  { name: "Buenos Aires", lat: -34.6037, lon: -58.3816 }, { name: "Córdoba", lat: -31.4201, lon: -64.1888 },
  { name: "Rosario", lat: -32.9468, lon: -60.6393 }, { name: "Mendoza", lat: -32.8895, lon: -68.8458 },
  { name: "Salta", lat: -24.7859, lon: -65.4117 }, { name: "San Salvador de Jujuy", lat: -24.1858, lon: -65.2995 },
  { name: "Bariloche", lat: -41.1335, lon: -71.3103 }, { name: "Ushuaia", lat: -54.8019, lon: -68.303 },
  // Venezuela
  { name: "Caracas", lat: 10.4806, lon: -66.9036 }, { name: "Maracaibo", lat: 10.6427, lon: -71.6125 },
  { name: "Valencia", lat: 10.1621, lon: -68.0077 }, { name: "Puerto Ayacucho", lat: 5.6639, lon: -67.6236 },
  // Brasil (zona amazónica y fronteriza, relevante para operación de helicópteros)
  { name: "Manaus", lat: -3.119, lon: -60.0217 }, { name: "Tabatinga", lat: -4.2531, lon: -69.9339 },
  { name: "Rio Branco", lat: -9.9747, lon: -67.81 }, { name: "Boa Vista", lat: 2.8235, lon: -60.6758 },
  // Paraguay / Uruguay / Guyana
  { name: "Asunción", lat: -25.2637, lon: -57.5759 }, { name: "Montevideo", lat: -34.9011, lon: -56.1645 },
  { name: "Georgetown", lat: 6.8013, lon: -58.1551 },
  // Otros centros globales de referencia
  { name: "Miami", lat: 25.7617, lon: -80.1918 }, { name: "Madrid", lat: 40.4168, lon: -3.7038 },
  { name: "Ciudad de México", lat: 19.4326, lon: -99.1332 },
];

// Ciudades y pueblos pequeños en todo el mundo (dataset abierto derivado de GeoNames, licencia CC BY 4.0,
// ~130 000 localidades con población desde ~1000 habitantes). Se descarga una sola vez en el navegador y
// solo se dibuja el subconjunto visible al hacer zoom — así el mapa arranca liviano y va revelando pueblos
// a medida que te acercas, igual que en Google Maps.
const WORLD_TOWNS_URL = "https://cdn.jsdelivr.net/npm/cities.json/cities.json";

// TopoJSON público de costas/países (Natural Earth vía world-atlas). Se descarga en el navegador del
// usuario al abrir el mapa — si no hay conexión, el mapa cae a una cuadrícula lat/lon de referencia.
const WORLD_ATLAS_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
// Ríos y ejes de lagos (Natural Earth 1:110m, dominio público). Es un GeoJSON directo (no TopoJSON), así
// que sus líneas se dibujan con d3.geoPath sin pasar por el decodificador de arcos.
const RIVERS_URL = "https://raw.githubusercontent.com/martynafford/natural-earth-geojson/master/110m/physical/ne_110m_rivers_lake_centerlines.json";
function decodeTopology(topology, objectName) {
  const { arcs, transform } = topology;
  const [sx, sy] = transform ? transform.scale : [1, 1];
  const [tx, ty] = transform ? transform.translate : [0, 0];
  const decodedArcs = arcs.map((arc) => {
    let x = 0, y = 0;
    return arc.map(([dx, dy]) => { x += dx; y += dy; return [x * sx + tx, y * sy + ty]; });
  });
  function arcsToCoords(idxList) {
    let coords = [];
    idxList.forEach((idx, i) => {
      const rev = idx < 0;
      const arc = decodedArcs[rev ? ~idx : idx];
      const seg = rev ? [...arc].reverse() : arc;
      coords = i === 0 ? seg.slice() : coords.concat(seg.slice(1));
    });
    return coords;
  }
  const obj = topology.objects[objectName];
  if (!obj) return { type: "FeatureCollection", features: [] };
  const features = obj.geometries.map((g) => {
    let geometry = null;
    if (g.type === "Polygon") geometry = { type: "Polygon", coordinates: g.arcs.map(arcsToCoords) };
    else if (g.type === "MultiPolygon") geometry = { type: "MultiPolygon", coordinates: g.arcs.map((poly) => poly.map(arcsToCoords)) };
    return { type: "Feature", properties: g.properties || {}, geometry };
  }).filter((f) => f.geometry);
  return { type: "FeatureCollection", features };
}

/* ---------------------------- Estética ---------------------------- */
// Paleta y tipografía viven en src/styles/paleta.js, con el criterio de cada
// color escrito y comprobado: contraste medido contra el fondo y separación
// bajo dicromatismo. Estaban duplicadas acá con tonos distintos.
const COLORS = PALETA;
const STATUS_COLOR = COLOR_NIVEL;

function StatusPill({ status }) {
  return (
    <span style={{
      background: `${STATUS_COLOR[status]}22`, color: STATUS_COLOR[status],
      border: `1px solid ${STATUS_COLOR[status]}66`, borderRadius: 999,
      padding: "2px 10px", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 0.5,
    }}>{status}</span>
  );
}
function Field({ label, children, hint }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 10.5, color: COLORS.muted }}>{hint}</span>}
    </label>
  );
}
/**
 * Flota semilla.
 *
 * Vive fuera del componente para poder consultarla aunque el registro guardado
 * en IndexedDB sea anterior y le falten campos: así una enmienda de peso y
 * balance agregada después sigue encontrándose por matrícula.
 */
const FLOTA_SEMILLA = [{
    id: "f1", matricula: "CC-CSU", tipo: "AS 350 B3", serie: "4338", profileId: "as350b3",
    tsn: 3869.7, csn: 24602, ldg: 0, cargas: 0,
    /**
     * Estado en el momento de cargar el status report. Es la línea base contra
     * la que se verifica el arrastre de la bitácora, y NO cambia con los
     * vuelos: si se moviera junto con el TSN actual, la comprobación siempre
     * daría cero desvío y no serviría para nada.
     */
    estadoInicial: {
      referencia: "Status report al 07-07-2026",
      tsn: 3869.7, csn: 24602, ldg: 0, cargas: 0,
      motor: { tsn: 3869.7, cyNG: 9057.93, cyNF: 2758.25 },
    },
    motor: { tipo: "ARRIEL 2B1", serie: "23483", tsn: 3869.7, cyNG: 9057.93, cyNF: 2758.25 },

    // Pesaje — hoja CERTIFICACIONES del status report
    pesoBasicoVacioKg: 1345,
    fechaPesaje: "2024-02-29",
    proximoPesaje: "",            // la planilla trae el campo vacío
    // El status report NO informa el brazo ni el momento del peso vacío. Sin
    // ese dato el CG no se puede calcular con el peso real de la matrícula.
    // Queda en null a propósito: null significa "no lo tengo", y la app lo
    // dice en pantalla en vez de tapar el hueco con el valor del tipo.
    brazoVacioM: null,
    // Límites de la hoja de datos de ESTA aeronave.
    pesoMaxDespegueKg: 2250,
    pesoCeroCombustibleKg: 1823,
    // Declarado 1852 kg en la planilla. No se usa en el W&B: el módulo ya suma
    // piloto, pasajeros, equipaje y combustible por separado, y 1852 − 1345 =
    // 507 kg no se explica por equipamiento fijo (los dos kits pesan 15,6 kg
    // entre ambos), así que probablemente ya incluye dotación completa.
    // Contarlo acá sería contar dos veces a la gente.
    pesoOperacionalBasicoKg: 1852,

    /**
     * Enmienda de actualización de peso y balance Nro. 03 — 18/12/2024.
     * Eagle Worldwide Fleet Management CMA N° 492, Ing. Mauricio Torres Méndez.
     *
     * `conAccesorio` es el peso vacío CON la canasta instalada, que es como se
     * firmó el documento. `accesorio` es lo que se resta al desinstalarla:
     * peso Y momento, porque quitar 38,8 kg sin quitar su momento deja el
     * brazo equivocado.
     */
    enmiendaPesoBalance: {
      referencia: "Enmienda de peso y balance Nro. 03 — 18/12/2024",
      conAccesorio: { pesoKg: 1407.77, momentoLongKgM: 4864.674, momentoLatKgM: -31.05 },
      accesorio: {
        designacion: "FAA STC SR00213NY — Heli-Utility Basket and Long Cargo Tube",
        pesoKg: 38.8,
        momentoLongKgM: 133.084,
        momentoLatKgM: -48.5,
        capacidadMaxKg: 120,
        brazoCargaM: 133.084 / 38.8,
        brazoCargaVerificado: false,
      },
    },
  },
];


const inputStyle = {
  background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 6,
  color: COLORS.text, padding: "7px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 13.5, outline: "none",
};
/**
 * Campo derivado: se muestra pero no se edita acá.
 *
 * Un valor que se puede escribir en dos pantallas distintas termina teniendo
 * dos valores distintos. El peso vacío tiene un solo lugar de carga
 * (Mantenimiento → Aeronave, contra el pesaje) y en todas las demás pantallas
 * se lee.
 */
const soloLecturaStyle = {
  ...inputStyle,
  background: "transparent",
  borderStyle: "dashed",
  color: COLORS.cyan,
  cursor: "not-allowed",
};
const botonPuntoStyle = {
  background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan,
  borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontSize: 11,
};
function Panel({ title, eyebrow, children, right }) {
  return (
    <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.hairline}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <div>
          {eyebrow && <div style={{ fontSize: 10.5, color: COLORS.amber, letterSpacing: 1.2, textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif" }}>{eyebrow}</div>}
          <h3 style={{ margin: 0, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 20, letterSpacing: 0.3 }}>{title}</h3>
        </div>
        {right}
      </div>
      {/* ═══ EL CUERPO DEL PANEL ES EL QUE SE DESPLAZA ═══

          Veinte de las treinta y cinco tablas de la aplicación traían su
          propio contenedor con desplazamiento; las otras quince desbordaban la
          página y empujaban toda la pantalla a lo ancho.

          Ponerlo acá las cubre a las treinta y cinco de una vez, y es también
          lo que hace que la primera columna pueda quedarse fija: necesita un
          contenedor contra el cual quedarse. */}
      <div className="hc-panel__cuerpo">{children}</div>
    </div>
  );
}
function Readout({ label, value, unit, tone }) {
  const color = tone ? STATUS_COLOR[tone] : COLORS.text;
  return (
    <div style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: "10px 14px", minWidth: 128 }}>
      <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, color, lineHeight: 1.3 }}>
        {value}<span style={{ fontSize: 12, color: COLORS.muted, marginLeft: 4 }}>{unit}</span>
      </div>
    </div>
  );
}
function Banner({ tone = "TOLERABLE", children }) {
  return (
    <div style={{ background: `${STATUS_COLOR[tone]}18`, border: `1px solid ${STATUS_COLOR[tone]}55`, borderRadius: 8, padding: "10px 14px", fontSize: 12.5, color: COLORS.text, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

function FuelGauge({ fuelKg, requiredKg, unitLabel, fmtWeight }) {
  const maxScale = Math.max(fuelKg, requiredKg || 0, 1) * 1.3;
  const pct = Math.max(0, Math.min(1, fuelKg / maxScale));
  const angle = -90 + pct * 180; // -90 (izquierda) a +90 (derecha)
  const cx = 70, cy = 70, r = 58;
  const toXY = (deg) => { const rad = (deg * Math.PI) / 180; return [cx + r * Math.sin(rad), cy - r * Math.cos(rad)]; };
  const arc = (a0, a1, color) => {
    const [x0, y0] = toXY(a0), [x1, y1] = toXY(a1);
    const large = a1 - a0 > 180 ? 1 : 0;
    return <path d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`} stroke={color} strokeWidth={14} fill="none" strokeLinecap="round" />;
  };
  const [needleX, needleY] = toXY(angle);
  const reqPct = requiredKg ? Math.max(0, Math.min(1, requiredKg / maxScale)) : null;
  return (
    <svg viewBox="0 0 140 90" width="140" height="90">
      {arc(-90, -30, SMS_COLOR.INACEPTABLE)}
      {arc(-30, 30, SMS_COLOR.GRAVE)}
      {arc(30, 90, SMS_COLOR.ACEPTABLE)}
      {reqPct !== null && (() => { const [rx, ry] = toXY(-90 + reqPct * 180); return <line x1={rx} y1={ry} x2={cx + (rx - cx) * 0.7} y2={cy + (ry - cy) * 0.7} stroke="#0A1420" strokeWidth={2} />; })()}
      <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke="#E6EDF3" strokeWidth={2.5} />
      <circle cx={cx} cy={cy} r={4} fill="#E6EDF3" />
      <text x={cx} y={cy + 22} textAnchor="middle" fontSize={13} fontWeight={700} fill="#E6EDF3" fontFamily="'JetBrains Mono', monospace">{fmtWeight(fuelKg).toFixed(0)}</text>
      <text x={cx} y={cy + 34} textAnchor="middle" fontSize={8.5} fill="#9AA5B1" fontFamily="'Inter', sans-serif">{unitLabel} embarcado</text>
    </svg>
  );
}

function BowTieDiagram({ event, euCategories }) {
  if (!event) return null;
  const causas = (event.precursores || "").split(/\n|;/).map((s) => s.trim()).filter(Boolean);
  const consecuencias = euCategories.filter((c) => event.euCategorias.includes(c.key));
  const rows = Math.max(causas.length, consecuencias.length, 1);
  const rowH = 42;
  const height = Math.max(180, rows * rowH + 60);
  const midY = height / 2;
  const boxW = 190, gap = 26;
  const leftX = 10, centerX = leftX + boxW + gap + 60, rightX = centerX + 130 + gap;

  const wrapLines = (text, maxChars) => {
    const words = text.split(" ");
    const lines = []; let cur = "";
    words.forEach((w) => {
      if ((cur + " " + w).trim().length > maxChars) { lines.push(cur.trim()); cur = w; }
      else cur = (cur + " " + w).trim();
    });
    if (cur) lines.push(cur);
    return lines.slice(0, 3);
  };

  return (
    <svg viewBox={`0 0 ${rightX + boxW + 20} ${height}`} width="100%" style={{ background: "#0A1420", borderRadius: 8 }}>
      {causas.map((c, i) => {
        const y = 30 + i * rowH;
        const lines = wrapLines(c, 26);
        return (
          <g key={i}>
            <rect x={leftX} y={y} width={boxW} height={34} rx={5} fill="#151B22" stroke="#3A4652" />
            {lines.map((l, li) => <text key={li} x={leftX + 8} y={y + 14 + li * 11} fontSize={9.5} fill="#D8DCE0" fontFamily="'Inter', sans-serif">{l}</text>)}
            <line x1={leftX + boxW} y1={y + 17} x2={centerX - 55} y2={midY} stroke="#5A6672" strokeWidth={1} />
          </g>
        );
      })}
      <g>
        <ellipse cx={centerX} cy={midY} rx={62} ry={40} fill="#E8842D" stroke="#0A1420" strokeWidth={1.5} />
        {wrapLines(event.descripcion, 16).map((l, li, arr) => (
          <text key={li} x={centerX} y={midY - (arr.length - 1) * 6 + li * 12} fontSize={9.5} fontWeight={700} fill="#1A1204" textAnchor="middle" fontFamily="'Inter', sans-serif">{l}</text>
        ))}
        <text x={centerX} y={midY + 30} fontSize={8} fill="#0A1420" textAnchor="middle" fontFamily="'Inter', sans-serif" fontStyle="italic">Evento Temido</text>
      </g>
      {consecuencias.map((c, i) => {
        const y = 30 + i * rowH;
        const lines = wrapLines(c.label, 24);
        return (
          <g key={c.key}>
            <line x1={centerX + 55} y1={midY} x2={rightX} y2={y + 17} stroke="#5A6672" strokeWidth={1} />
            <rect x={rightX} y={y} width={boxW} height={34} rx={5} fill="#2A1518" stroke="#7A3A3A" />
            <text x={rightX + 8} y={y + 12} fontSize={8.5} fill="#E8A0A0" fontFamily="'JetBrains Mono', monospace" fontWeight={700}>{c.key}</text>
            {lines.map((l, li) => <text key={li} x={rightX + 8} y={y + 23 + li * 10} fontSize={8.5} fill="#D8DCE0" fontFamily="'Inter', sans-serif">{l}</text>)}
          </g>
        );
      })}
      <text x={leftX} y={16} fontSize={10} fill="#9AA5B1" fontFamily="'Inter', sans-serif" fontWeight={700}>PRECURSORES (causas)</text>
      <text x={rightX} y={16} fontSize={10} fill="#9AA5B1" fontFamily="'Inter', sans-serif" fontWeight={700} textAnchor="start">EVENTOS LÍMITE (consecuencias)</text>
    </svg>
  );
}

function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#1D2732";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
  }, []);

  const pos = (ev) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = ev.touches ? ev.touches[0] : ev;
    return { x: (p.clientX - rect.left) * (canvasRef.current.width / rect.width), y: (p.clientY - rect.top) * (canvasRef.current.height / rect.height) };
  };
  const start = (ev) => { drawing.current = true; last.current = pos(ev); };
  const move = (ev) => {
    if (!drawing.current) return;
    ev.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(ev);
    ctx.strokeStyle = "#FFFFFF"; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const canvas = canvasRef.current, ctx = canvas.getContext("2d");
    ctx.fillStyle = "#1D2732"; ctx.fillRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef} width={320} height={100}
        style={{ width: "100%", maxWidth: 320, height: 100, borderRadius: 6, border: `1px solid ${COLORS.hairline}`, cursor: "crosshair", touchAction: "none" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <button onClick={clear} type="button" style={{ marginTop: 4, background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.muted, borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontSize: 11 }}>Borrar firma</button>
    </div>
  );
}

/* ---------------------------- Mapa (D3, sin dependencias de red) ---------------------------- */
// Open-Elevation (API pública gratuita) — a nivel de módulo (no dentro de un componente) para que tanto el
// mapa (clic/cursor) como el perfil de ruta en el componente principal puedan usarla por igual. Este era en
// realidad el motivo real de que el perfil de ruta nunca se generara: antes solo existía DENTRO de RouteMap,
// así que la función que arma el perfil (en el componente principal) llamaba a algo que no existía ahí.
async function fetchElevation(lat, lon) {
  try {
    // Límite de tiempo explícito: sin esto, si la red bloquea la consulta sin devolver ni éxito ni error
    // (se queda esperando en silencio), la promesa nunca se resuelve — y por eso el recuadro del mapa se
    // quedaba diciendo "consultando altitud…" para siempre, sin caer nunca al respaldo.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);
    const r = await fetch(`https://api.open-elevation.com/api/v1/lookup?locations=${lat},${lon}`, { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!r.ok) throw new Error("http " + r.status);
    const data = await r.json();
    const meters = data?.results?.[0]?.elevation;
    return Number.isFinite(meters) ? Math.round(meters * 3.28084) : null;
  } catch {
    return null;
  }
}

function RouteMap({ waypoints, airports, airways, airwayLayers, onAddPoint, onInsertAtSegment, onResetRoute }) {
  const svgRef = useRef(null);
  const gRef = useRef(null);
  const [transform, setTransform] = useState({ k: 1, x: 0, y: 0 });
  const [selected, setSelected] = useState(null);
  const [worldGeo, setWorldGeo] = useState(null);
  const [mapStatus, setMapStatus] = useState("loading");
  const [clicked, setClicked] = useState(null); // { lat, lon, elevFt, status }
  const [confirmResetMap, setConfirmResetMap] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showTowns, setShowTowns] = useState(true);
  const [basemap, setBasemap] = useState("satellite"); // "vector" | "satellite" | "osm"
  const [towns, setTowns] = useState(null);
  const [townsStatus, setTownsStatus] = useState("idle");
  const [rivers, setRivers] = useState(null);
  const [riversStatus, setRiversStatus] = useState("loading");
  const width = 760, height = 480;

  // Mercator (no equirectangular): así los mosaicos de OpenStreetMap (que vienen en Mercator) calzan
  // correctamente en cualquier latitud, no solo cerca del ecuador. Se acota a ±85° porque Mercator no puede
  // representar los polos (fitSize necesitaría escala infinita si se lo pidiera con la esfera completa).
  const projection = useMemo(() => d3.geoMercator().fitSize([width, height], {
    type: "Polygon",
    coordinates: [[[-180, 85], [180, 85], [180, -85], [-180, -85], [-180, 85]]],
  }), []);
  const graticule = useMemo(() => d3.geoGraticule().step([10, 10])(), []);
  const path = useMemo(() => d3.geoPath(projection), [projection]);
  // project() se necesita bien temprano porque orographyPaths (más abajo) lo usa dentro de un useMemo que se
  // ejecuta durante el render — declararlo tarde causaba "Cannot access 'project' before initialization"
  // (un error real de orden de declaración en JS, no algo del entorno de StackBlitz).
  const project = (lon, lat) => projection([lon, lat]);

  useEffect(() => {
    let cancelled = false;
    fetch(RIVERS_URL).then((r) => { if (!r.ok) throw new Error("http " + r.status); return r.json(); })
      .then((geo) => { if (cancelled) return; setRivers(geo); setRiversStatus("ok"); })
      .catch(() => { if (!cancelled) setRiversStatus("error"); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(WORLD_ATLAS_URL).then((r) => { if (!r.ok) throw new Error("http " + r.status); return r.json(); })
      .then((topo) => { if (cancelled) return; setWorldGeo(decodeTopology(topo, "countries")); setMapStatus("ok"); })
      .catch(() => { if (!cancelled) setMapStatus("error"); });
    return () => { cancelled = true; };
  }, []);

  // Ciudades y pueblos pequeños (dataset abierto ~130 000 localidades, varios MB) — se descarga solo si el
  // usuario lo pide explícitamente con el interruptor de abajo, para no gastar datos móviles sin permiso.
  useEffect(() => {
    if (!showTowns || towns || townsStatus === "loading") return;
    setTownsStatus("loading");
    fetch(WORLD_TOWNS_URL).then((r) => { if (!r.ok) throw new Error("http " + r.status); return r.json(); })
      .then((data) => {
        const norm = data.map((d) => ({ name: d.name, lat: Number(d.lat), lon: Number(d.lng ?? d.lon) })).filter((d) => Number.isFinite(d.lat) && Number.isFinite(d.lon));
        setTowns(norm);
        setTownsStatus("ok");
      })
      .catch(() => setTownsStatus("error"));
  }, [showTowns, towns, townsStatus]);


  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const zoom = d3.zoom().scaleExtent([1, 200])
      .on("zoom", (ev) => { if (gRef.current) gRef.current.setAttribute("transform", `translate(${ev.transform.x},${ev.transform.y}) scale(${ev.transform.k})`); })
      .on("end", (ev) => setTransform(ev.transform));
    svg.call(zoom);
    return () => svg.on(".zoom", null);
  }, []);

  // Base de coordenadas + altitud ya cargada (la tabla de aeródromos, con lat/lon/elevFt juntos) — esta es
  // la fuente primaria y confiable. La API de elevación en vivo (Open-Elevation) es solo un afinamiento
  // opcional cuando el punto está lejos de cualquier aeródromo conocido.
  const haversineKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371, dLat = (lat2 - lat1) * Math.PI / 180, dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };
  const nearestKnownElevation = (lat, lon) => {
    let best = null, bestKm = Infinity;
    for (const a of airports) {
      const d = haversineKm(lat, lon, a.lat, a.lon);
      if (d < bestKm) { bestKm = d; best = a; }
    }
    if (!best) return null;
    return { elevFt: best.elevFt, source: best.icao || best.name, km: bestKm };
  };

  const resolveElevation = async (lat, lon) => {
    const near = nearestKnownElevation(lat, lon);
    // ANTES: si había un aeródromo conocido a menos de 15 km, se devolvía SIEMPRE su elevación fija, sin
    // siquiera intentar la API — por eso al mover el cursor dentro de esos ~15 km alrededor de cualquier
    // aeródromo, la altitud se quedaba pegada en el mismo número sin importar el punto real bajo el cursor.
    // Ahora primero se intenta la elevación real del punto exacto; el aeródromo cercano queda solo como
    // respaldo si la API no responde.
    const apiFt = await fetchElevation(lat, lon);
    if (apiFt !== null) return { elevFt: apiFt, source: "Open-Elevation" };
    if (near && near.km <= 15) return { elevFt: near.elevFt, source: `estimado del aeródromo más cercano (${near.source}, ${near.km.toFixed(1)} km — sin conexión, ajustar a mano)` };
    if (near) return { elevFt: near.elevFt, source: `estimado del aeródromo más cercano (${near.source}, ${near.km.toFixed(0)} km — ajustar a mano)` };
    return { elevFt: 0, source: "sin dato — ingresar a mano" };
  };

  // ---- Orografía vectorial (curvas de nivel + bandas de color por altitud) ----
  // Antes esto era una imagen satelital de relieve (mosaicos de NASA GIBS) que se volvía a pedir todo el
  // tiempo mientras se movía el mapa — de ahí la inestabilidad. Ahora se genera UNA sola vez, a pedido, con
  // una cuadrícula real de elevaciones consultadas para la vista actual, y d3.contours() arma las curvas de
  // nivel de verdad — el resultado queda fijo (no se recalcula solo) hasta que se vuelva a pedir.
  const [terrainContours, setTerrainContours] = useState(null); // { bounds, cols, rows, thresholdStep, polygons: [...] }
  const [terrainStatus, setTerrainStatus] = useState("idle"); // idle | loading | ok | error
  const HYPSOMETRIC_COLORS = ["#1F5C3A", "#3E7D3F", "#6B9A4A", "#A6A550", "#C9954A", "#A9713C", "#8A5A3A", "#EDEDED"];
  const generateOrography = async () => {
    setTerrainStatus("loading");
    try {
      const c1 = projection.invert([-transform.x / transform.k, -transform.y / transform.k]);
      const c2 = projection.invert([(width - transform.x) / transform.k, (height - transform.y) / transform.k]);
      if (!c1 || !c2) throw new Error("vista inválida");
      const lonMin = Math.max(-180, Math.min(c1[0], c2[0])), lonMax = Math.min(180, Math.max(c1[0], c2[0]));
      const latMin = Math.max(-85, Math.min(c1[1], c2[1])), latMax = Math.min(85, Math.max(c1[1], c2[1]));
      const cols = 18, rows = 13; // cuadrícula más liviana — antes 26x18 generaba demasiados puntos para recalcular en cada cuadro al mover el mapa
      const points = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const lon = lonMin + (c / (cols - 1)) * (lonMax - lonMin);
          const lat = latMax - (r / (rows - 1)) * (latMax - latMin); // fila 0 = arriba = latitud mayor
          points.push({ lon, lat });
        }
      }
      const values = await Promise.all(points.map(async (p) => {
        const { elevFt } = await resolveElevation(p.lat, p.lon);
        return Math.max(0, elevFt);
      }));
      const maxV = Math.max(...values, 1);
      const step = Math.max(250, Math.ceil(maxV / 7 / 250) * 250); // ~7 bandas, redondeado a 250 ft
      const thresholds = Array.from({ length: 8 }, (_, i) => i * step);
      const polygons = d3.contours().size([cols, rows]).thresholds(thresholds)(values);
      setTerrainContours({ bounds: { lonMin, lonMax, latMin, latMax }, cols, rows, polygons });
      setTerrainStatus("ok");
    } catch {
      setTerrainStatus("error");
    }
  };
  // Convierte una coordenada de la cuadrícula (0..cols-1, 0..rows-1) a lon/lat real, usando los límites
  // guardados en el momento en que se generó — así el dibujo queda anclado a lugares reales del mapa.
  const gridToLonLat = (gx, gy) => {
    if (!terrainContours) return [0, 0];
    const { lonMin, lonMax, latMin, latMax } = terrainContours.bounds;
    const { cols, rows } = terrainContours;
    const lon = lonMin + (gx / (cols - 1)) * (lonMax - lonMin);
    const lat = latMax - (gy / (rows - 1)) * (latMax - latMin);
    return [lon, lat];
  };
  // Los trazos de las curvas de nivel se calculan UNA sola vez aquí (con useMemo, dependiendo solo de
  // terrainContours) — antes se recalculaban en cada cuadro mientras se arrastraba el mapa (porque estaban
  // directo en el render), cientos de puntos por vez, y eso era probablemente lo que hacía que el mapa se
  // sintiera inestable y terminara en el error de React al mover el mapa.
  const orographyPaths = useMemo(() => {
    if (!terrainContours) return [];
    const { polygons } = terrainContours;
    const maxValue = Math.max(...polygons.map((p) => p.value), 1);
    return polygons.map((poly, pi) => {
      const colorIdx = Math.min(HYPSOMETRIC_COLORS.length - 1, Math.round((poly.value / maxValue) * (HYPSOMETRIC_COLORS.length - 1)));
      const d = poly.coordinates.map((ring) => {
        const pts = ring.map(([gx, gy]) => {
          const [lon, lat] = gridToLonLat(gx, gy);
          return project(lon, lat);
        }).filter(Boolean);
        if (pts.length < 3) return "";
        return "M" + pts.map((p) => p.join(",")).join("L") + "Z";
      }).join(" ");
      return { key: pi, d, color: HYPSOMETRIC_COLORS[colorIdx] };
    });
  }, [terrainContours]);

  const handleMapClick = async (ev) => {
    try {
      if (ev.target.closest("[data-marker]")) return; // no interceptar clics sobre marcadores
      if (!svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const sx = ((ev.clientX - rect.left) / rect.width) * width;
      const sy = ((ev.clientY - rect.top) / rect.height) * height;
      const ux = (sx - transform.x) / transform.k, uy = (sy - transform.y) / transform.k;
      const lineHit = checkRouteLineHover(ux, uy, sx, sy);
      const lonLat = projection.invert([ux, uy]);
      if (!lonLat) return;
      const [lon, lat] = lonLat;
      setSelected(null);
      // El punto queda listo para agregarse de inmediato con lat/lon reales (cálculo local, no depende de red).
      setClicked({ lat, lon, elevFt: 0, elevSource: "buscando en la base…", status: "loading", insertAfterId: lineHit.hit ? lineHit.afterId : null });
      const { elevFt, source } = await resolveElevation(lat, lon);
      setClicked((c) => (c && c.lat === lat && c.lon === lon ? { ...c, elevFt, elevSource: source, status: "ok" } : c));
    } catch (err) {
      // Un error acá NO lo agarra el error boundary de React (los manejadores de eventos quedan fuera de eso) —
      // por eso antes se veía pantalla en blanco sin ningún aviso al hacer clic. Esto evita que se propague mal.
      console.error("Error al hacer clic en el mapa:", err);
    }
  };

  // Verificar altitud de CUALQUIER punto del terreno con solo pasar el cursor encima (no solo aeródromos).
  // Antes se cancelaba la consulta de red en cada píxel de movimiento del mouse (debounce puro), así que casi
  // nunca alcanzaba a completarse — de ahí el "0 / consultando / sin información" permanente. Ahora es un
  // throttle real: como máximo una consulta cada 300 ms, sin cancelar las que ya están en curso, descartando
  // solo las respuestas que quedaron obsoletas (con un ID de solicitud, no comparando lat/lon exactos).
  const [hoverPoint, setHoverPoint] = useState(null);
  const hoverReqId = useRef(0);
  const hoverLastFetch = useRef(0);
  const handleMapMouseMove = (ev) => {
    try {
      if (ev.target.closest("[data-marker]")) { setHoverPoint(null); setHoverLeg(null); return; }
      if (!svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const sx = ((ev.clientX - rect.left) / rect.width) * width;
      const sy = ((ev.clientY - rect.top) / rect.height) * height;
      const ux = (sx - transform.x) / transform.k, uy = (sy - transform.y) / transform.k;
      if (checkRouteLineHover(ux, uy, sx, sy).hit) { setHoverPoint(null); return; }
      const lonLat = projection.invert([ux, uy]);
      if (!lonLat) { setHoverPoint(null); return; }
      const [lon, lat] = lonLat;
      // Esto es barato (sin red) y se actualiza siempre, para que el recuadro siga al cursor de inmediato.
      setHoverPoint((prev) => ({ lat, lon, elevFt: prev ? prev.elevFt : null, status: prev ? prev.status : "loading", screenX: sx, screenY: sy }));
      const now = Date.now();
      if (now - hoverLastFetch.current < 300) return; // throttle: no dispara una consulta nueva por cada píxel
      hoverLastFetch.current = now;
      const myReqId = ++hoverReqId.current;
      resolveElevation(lat, lon).then(({ elevFt, source }) => {
        if (myReqId !== hoverReqId.current) return; // ya hay una consulta más nueva en curso, se descarta esta
        setHoverPoint((h) => (h ? { ...h, elevFt, status: "ok", isLive: source === "Open-Elevation" } : h));
      }).catch((err) => console.error("Error de altitud al pasar el cursor:", err));
    } catch (err) {
      console.error("Error al mover el cursor sobre el mapa:", err);
    }
  };
  const handleMapMouseLeave = () => {
    setHoverPoint(null);
    hoverReqId.current++; // invalida cualquier consulta de elevación que siga en curso
    setHoverLeg(null);
  };

  // Recorrer la línea de ruta con el cursor: al pasar cerca de cualquier tramo entre waypoints, se despliega
  // un recuadro con el tramo (de/a) y su distancia — sin necesidad de hacer clic.
  const [hoverLeg, setHoverLeg] = useState(null);
  function distToSegment(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
  }
  const checkRouteLineHover = (ux, uy, sx, sy) => {
    const defined = waypoints.filter((w) => w.lat !== null && w.lon !== null);
    const thresholdLocal = 8 / transform.k;
    for (let i = 0; i < defined.length - 1; i++) {
      const a = defined[i], b = defined[i + 1];
      const pa = project(a.lon, a.lat), pb = project(b.lon, b.lat);
      if (!pa || !pb) continue;
      const d = distToSegment(ux, uy, pa[0], pa[1], pb[0], pb[1]);
      if (d <= thresholdLocal) {
        const distNm = haversineKm(a.lat, a.lon, b.lat, b.lon) / 1.852;
        setHoverLeg({ from: a.name, to: b.name, distNm, screenX: sx, screenY: sy, afterId: a.id });
        return { hit: true, afterId: a.id };
      }
    }
    setHoverLeg(null);
    return { hit: false, afterId: null };
  };

  // Búsqueda de lugares — Nominatim (OpenStreetMap, geocodificación abierta y gratuita con política de uso).
  const runSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=6`, {
        headers: { Accept: "application/json" },
      });
      const data = await r.json();
      setResults(data.map((d) => ({ name: d.display_name, lat: Number(d.lat), lon: Number(d.lon) })));
    } catch {
      setResults([]);
    }
    setSearching(false);
  };

  const pickSearchResult = async (r) => {
    setResults([]);
    setQuery(r.name);
    setSelected(null);
    setClicked({ lat: r.lat, lon: r.lon, elevFt: 0, elevSource: "buscando en la base…", status: "loading", name: r.name });
    const { elevFt, source } = await resolveElevation(r.lat, r.lon);
    setClicked({ lat: r.lat, lon: r.lon, elevFt, elevSource: source, status: "ok", name: r.name });
  };

  const AIRWAY_COLOR = { VFR: "#5FD07A", IFR: "#C77FE0" };
  const visibleAirways = airways.filter((w) => airwayLayers[w.type] && airwayLayers[w.level]);
  const showCityLabels = transform.k > 2.2;
  const OCEAN = "#3C4650";

  // Franjas de color por latitud (verde ecuatorial → banda árida subtropical → verde templado → gris polar),
  // como estilización tipo "relieve" (no es dato real de elevación satelital). Se aplica como un degradado
  // SVG único para toda la tierra, para que se vea continuo en vez de un color plano por país.
  const latStops = [85, 60, 35, 20, 8, 0, -8, -20, -35, -60, -85];
  const latColor = { 85: "#D7DCD4", 60: "#7C8F5E", 35: "#8C8A57", 20: "#B79B63", 8: "#7F9A55", 0: "#4C7A3D", "-8": "#5C8A48", "-20": "#B0955E", "-35": "#8C8A57", "-60": "#7C8F5E", "-85": "#D7DCD4" };
  const gradId = "terrainGrad";
  const yTop = project(0, 85)[1], yBottom = project(0, -85)[1];

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={query} onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && runSearch()}
          placeholder="Buscar lugar, ciudad o aeródromo… (Nominatim/OSM)"
          style={{ flex: 1, minWidth: 180, background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 6, color: COLORS.text, padding: "8px 10px", fontSize: 13 }}
        />
        <button onClick={runSearch} disabled={searching} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}>
          {searching ? "Buscando…" : "Buscar"}
        </button>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11.5, color: COLORS.muted, whiteSpace: "nowrap" }}>
          mapa base:
          <select value={basemap} onChange={(e) => setBasemap(e.target.value)} style={{ background: COLORS.panelAlt, color: COLORS.text, border: `1px solid ${COLORS.hairline}`, borderRadius: 6, padding: "3px 6px", fontSize: 11.5 }}>
            <option value="satellite">Satelital (NASA)</option>
            <option value="orografia">Orografía / relieve (NASA)</option>
            <option value="osm">Calles (OpenStreetMap)</option>
            <option value="vector">Vectorial (sin imágenes)</option>
          </select>
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11.5, color: COLORS.muted, whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={showTowns} onChange={(e) => setShowTowns(e.target.checked)} />
          pueblos y ciudades {townsStatus === "loading" ? "(sumando más desde internet…)" : townsStatus === "error" ? "(usando la lista incluida — sin conexión para sumar más)" : townsStatus === "ok" ? "(lista incluida + internet)" : ""}
        </label>
        {basemap === "orografia" && (
          <button
            onClick={generateOrography}
            disabled={terrainStatus === "loading"}
            style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "4px 10px", cursor: terrainStatus === "loading" ? "default" : "pointer", fontSize: 11.5, fontWeight: 600 }}
          >
            {terrainStatus === "loading" ? "consultando relieve…" : terrainContours ? "↻ regenerar orografía aquí" : "▲ generar orografía aquí"}
          </button>
        )}
      </div>
      {basemap === "orografia" && terrainStatus === "error" && (
        <p style={{ fontSize: 11, color: COLORS.caution, margin: "4px 0 0" }}>No se pudo consultar el relieve — revisa la conexión e intenta de nuevo.</p>
      )}
      {basemap === "orografia" && !terrainContours && terrainStatus !== "loading" && (
        <p style={{ fontSize: 11, color: COLORS.muted, margin: "4px 0 0" }}>Encuadra la zona que te interesa y toca "generar orografía aquí" — se calcula una sola vez para esa vista, no se recarga solo al mover el mapa (por eso antes se sentía inestable).</p>
      )}
      {results.length > 0 && (
        <div style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, marginBottom: 8, maxHeight: 160, overflowY: "auto" }}>
          {results.map((r, i) => (
            <div key={i} onClick={() => pickSearchResult(r)} style={{ padding: "7px 10px", fontSize: 12.5, cursor: "pointer", borderTop: i ? `1px solid ${COLORS.hairline}` : "none" }}>
              {r.name}
            </div>
          ))}
        </div>
      )}
      <svg ref={svgRef} width="100%" viewBox={`0 0 ${width} ${height}`} onClick={handleMapClick} onMouseMove={handleMapMouseMove} onMouseLeave={handleMapMouseLeave} style={{ background: OCEAN, borderRadius: 8, border: `1px solid ${COLORS.hairline}`, cursor: "crosshair", touchAction: "none" }}>
        <defs>
          <linearGradient id={gradId} gradientUnits="userSpaceOnUse" x1={0} y1={yTop} x2={0} y2={yBottom}>
            {latStops.map((lat) => (
              <stop key={lat} offset={`${((project(0, lat)[1] - yTop) / (yBottom - yTop)) * 100}%`} stopColor={latColor[lat]} />
            ))}
          </linearGradient>
        </defs>
        <g ref={gRef} transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
          <rect x={-width * 5} y={-height * 5} width={width * 11} height={height * 11} fill={OCEAN} stroke="#20272E" />
          {worldGeo ? (
            worldGeo.features.map((f, i) => <path key={i} d={path(f)} fill={`url(#${gradId})`} stroke="#1E2B22" strokeWidth={0.5 / transform.k} opacity={0.95} />)
          ) : (
            <path d={path(graticule)} fill="none" stroke="#4A5560" strokeWidth={0.6} />
          )}
          {basemap === "satellite" && (() => {
            // Nivel elegido para que cada mosaico (288°/2^z de ancho) se vea en pantalla a un tamaño
            // razonable (~300 px) según el zoom actual — ya con la base de 288° correcta, no 360°.
            const worldPx = 2 * Math.PI * projection.scale() * transform.k;
            const z = Math.max(0, Math.min(9, Math.round(Math.log2((worldPx * GIBS_TILE_DEG_L0) / (360 * 300)))));
            const c1 = projection.invert([-transform.x / transform.k, -transform.y / transform.k]);
            const c2 = projection.invert([(width - transform.x) / transform.k, (height - transform.y) / transform.k]);
            if (!c1 || !c2) return null;
            const lonMin = Math.max(-180, Math.min(c1[0], c2[0])), lonMax = Math.min(180, Math.max(c1[0], c2[0]));
            const latMin = Math.max(-90, Math.min(c1[1], c2[1])), latMax = Math.min(90, Math.max(c1[1], c2[1]));
            const t1 = lonLatToTile4326(lonMin, latMax, z), t2 = lonLatToTile4326(lonMax, latMin, z);
            const maxCol = Math.ceil(360 / (GIBS_TILE_DEG_L0 / Math.pow(2, z))) - 1;
            const maxRow = Math.ceil(180 / (GIBS_TILE_DEG_L0 / Math.pow(2, z))) - 1;
            const xs = []; for (let x = Math.max(0, t1.x - 1); x <= Math.min(maxCol, t2.x + 1) && xs.length < 10; x++) xs.push(x);
            const ys = []; for (let y = Math.max(0, t1.y - 1); y <= Math.min(maxRow, t2.y + 1) && ys.length < 10; y++) ys.push(y);
            return xs.flatMap((tx) => ys.map((ty) => {
              const nw = tile4326ToLonLat(tx, ty, z), se = tile4326ToLonLat(tx + 1, ty + 1, z);
              const pNw = project(nw.lon, nw.lat), pSe = project(se.lon, se.lat);
              if (!pNw || !pSe) return null;
              const w = pSe[0] - pNw[0], h = pSe[1] - pNw[1];
              // Guarda real: un array de coordenadas siempre es "verdadero" en JS aunque tenga NaN/Infinity
              // adentro (por ejemplo cerca de los polos) — el chequeo de arriba no lo detectaba, y esos valores
              // inválidos llegaban tal cual a los atributos del SVG.
              if (![pNw[0], pNw[1], w, h].every(Number.isFinite) || w <= 0 || h <= 0) return null;
              return (
                <image key={`${z}-${tx}-${ty}`} href={GIBS_TILE_URL(z, ty, tx)}
                  x={pNw[0]} y={pNw[1]} width={w} height={h}
                  preserveAspectRatio="none"
                  /* `onError` SÍ funciona sobre SVGImageElement: la regla de
                     eslint usa una lista blanca que solo contempla <img>.

                     Y hace falta: sin él, una carta que no carga deja un
                     recuadro roto encima del gráfico, y un piloto no puede
                     distinguir «la imagen no cargó» de «la carta dice esto». */
                  // eslint-disable-next-line react/no-unknown-property
                  onError={(e) => { e.target.style.display = "none"; }} />
              );
            }));
          })()}
          {basemap === "orografia" && orographyPaths.map(({ key, d, color }) => (
            <path key={key} d={d} fill={color} fillOpacity={0.85} stroke="#0A1420" strokeWidth={0.4 / transform.k} strokeOpacity={0.3} />
          ))}
          {basemap === "osm" && (() => {
            // Mosaicos reales de OpenStreetMap (calles, nombres de lugares) — vienen en Web Mercator, mientras
            // que este mapa dibuja todo en proyección equirectangular; cerca del ecuador calzan casi perfecto,
            // más al sur/norte puede notarse un corrimiento leve. Se posicionan con la misma función project()
            // que usa el resto del mapa, así que quedan lo más alineados posible sin reproyectar todo el mapa.
            const worldPx = 2 * Math.PI * projection.scale() * transform.k;
            const z = Math.max(0, Math.min(17, Math.round(Math.log2(worldPx / 300))));
            const c1 = projection.invert([-transform.x / transform.k, -transform.y / transform.k]);
            const c2 = projection.invert([(width - transform.x) / transform.k, (height - transform.y) / transform.k]);
            if (!c1 || !c2) return null;
            const lonMin = Math.max(-180, Math.min(c1[0], c2[0])), lonMax = Math.min(180, Math.max(c1[0], c2[0]));
            const latMin = Math.max(-85, Math.min(c1[1], c2[1])), latMax = Math.min(85, Math.max(c1[1], c2[1]));
            const t1 = lonLatToTileMercator(lonMin, latMax, z), t2 = lonLatToTileMercator(lonMax, latMin, z);
            const n = Math.pow(2, z);
            const xs = []; for (let x = Math.max(0, t1.x - 1); x <= Math.min(n - 1, t2.x + 1) && xs.length < 12; x++) xs.push(x);
            const ys = []; for (let y = Math.max(0, t1.y - 1); y <= Math.min(n - 1, t2.y + 1) && ys.length < 12; y++) ys.push(y);
            return xs.flatMap((tx) => ys.map((ty) => {
              const nw = tileMercatorToLonLat(tx, ty, z), se = tileMercatorToLonLat(tx + 1, ty + 1, z);
              const pNw = project(nw.lon, nw.lat), pSe = project(se.lon, se.lat);
              if (!pNw || !pSe) return null;
              const w = pSe[0] - pNw[0], h = pSe[1] - pNw[1];
              if (![pNw[0], pNw[1], w, h].every(Number.isFinite) || w <= 0 || h <= 0) return null;
              return (
                <image key={`osm-${z}-${tx}-${ty}`} href={OSM_TILE_URL(z, tx, ty)}
                  x={pNw[0]} y={pNw[1]} width={w} height={h}
                  preserveAspectRatio="none"
                  /* `onError` SÍ funciona sobre SVGImageElement: la regla de
                     eslint usa una lista blanca que solo contempla <img>.

                     Y hace falta: sin él, una carta que no carga deja un
                     recuadro roto encima del gráfico, y un piloto no puede
                     distinguir «la imagen no cargó» de «la carta dice esto». */
                  // eslint-disable-next-line react/no-unknown-property
                  onError={(e) => { e.target.style.display = "none"; }} />
              );
            }));
          })()}
          {rivers && rivers.features.map((f, i) => (
            <path key={i} d={path(f)} fill="none" stroke="#3E7FB0" strokeWidth={1 / transform.k} opacity={0.75} />
          ))}
          {showTowns && towns && transform.k > 0.8 && (() => {
            // Solo se dibuja lo visible en pantalla (no las ~130 000 localidades a la vez) y con un tope
            // duro de marcadores, para que no se vuelva lento ni en el navegador ni en un teléfono.
            const corners = [projection.invert([-transform.x / transform.k, -transform.y / transform.k]), projection.invert([(width - transform.x) / transform.k, (height - transform.y) / transform.k])];
            if (!corners[0] || !corners[1]) return null;
            const lonMin = Math.min(corners[0][0], corners[1][0]), lonMax = Math.max(corners[0][0], corners[1][0]);
            const latMin = Math.min(corners[0][1], corners[1][1]), latMax = Math.max(corners[0][1], corners[1][1]);
            let shown = 0;
            return towns.filter((t) => t.lon >= lonMin && t.lon <= lonMax && t.lat >= latMin && t.lat <= latMax).slice(0, 500).map((t, i) => {
              const p = project(t.lon, t.lat);
              if (!p) return null;
              shown++;
              return (
                <g
                  key={`${t.name}-${i}`} data-marker="1" transform={`translate(${p[0]},${p[1]})`}
                  style={{ cursor: "pointer" }}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setClicked(null);
                    setSelected({ kind: "waypoint", data: { name: t.name, lat: t.lat, lon: t.lon, elevFt: 0, tempC: 15 } });
                    resolveElevation(t.lat, t.lon).then(({ elevFt }) => {
                      setSelected((s) => (s && s.data && s.data.lat === t.lat && s.data.lon === t.lon ? { ...s, data: { ...s.data, elevFt } } : s));
                    }).catch(() => {});
                  }}
                >
                  <circle r={2.4 / transform.k} fill="#D8DCE0" stroke="#0A1420" strokeWidth={0.6 / transform.k} />
                  {transform.k > 4 && <text x={4 / transform.k} y={2.5 / transform.k} fontSize={8 / transform.k} fill="#D8DCE0" stroke="#1A1A1A" strokeWidth={2 / transform.k} paintOrder="stroke" fontFamily="'Inter', sans-serif">{t.name}</text>}
                </g>
              );
            });
          })()}
          {REFERENCE_CITIES.map((c) => {
            const p = project(c.lon, c.lat);
            if (!p) return null;
            return (
              <g
                key={c.name} data-marker="1" transform={`translate(${p[0]},${p[1]})`}
                style={{ cursor: "pointer" }}
                onClick={(ev) => {
                  ev.stopPropagation();
                  setClicked(null);
                  setSelected({ kind: "waypoint", data: { name: c.name, lat: c.lat, lon: c.lon, elevFt: 0, tempC: 15 } });
                  resolveElevation(c.lat, c.lon).then(({ elevFt }) => {
                    setSelected((s) => (s && s.data && s.data.lat === c.lat && s.data.lon === c.lon ? { ...s, data: { ...s.data, elevFt } } : s));
                  }).catch(() => {});
                }}
              >
                <circle r={2.6 / transform.k} fill="#FFFFFF" stroke="#1A1A1A" strokeWidth={0.6 / transform.k} />
                {showCityLabels && (
                  <text x={6 / transform.k} y={3 / transform.k} fontSize={10 / transform.k} fill="#FFFFFF" stroke="#1A1A1A" strokeWidth={2.5 / transform.k} paintOrder="stroke" fontFamily="'Barlow Condensed', sans-serif" fontWeight={700} letterSpacing={0.3}>{c.name.toUpperCase()}</text>
                )}
              </g>
            );
          })}
          {visibleAirways.map((w) => {
            const pts = w.points.map((p) => project(p.lon, p.lat)).filter(Boolean);
            if (pts.length < 2) return null;
            const d = "M" + pts.map((p) => p.join(",")).join("L");
            return <path key={w.id} d={d} fill="none" stroke={AIRWAY_COLOR[w.type] || "#FFFFFF"} strokeWidth={(w.level === "alta" ? 1.6 : 1) / transform.k} strokeDasharray={w.level === "alta" ? "" : "2 2"} opacity={0.9} />;
          })}
          {airports.map((a) => {
            const p = project(a.lon, a.lat);
            if (!p) return null;
            return (
              <g key={a.id} data-marker="1" transform={`translate(${p[0]},${p[1]})`} style={{ cursor: "pointer" }} onClick={(ev) => { ev.stopPropagation(); setClicked(null); setSelected({ kind: "airport", data: a }); }}>
                <circle r={2.6 / transform.k} fill="#2E9BF2" stroke="#0A1420" strokeWidth={0.8 / transform.k} />
                <text x={4.5 / transform.k} y={2.4 / transform.k} fontSize={6.5 / transform.k} fill="#FFFFFF" stroke="#0A1420" strokeWidth={1.4 / transform.k} paintOrder="stroke" fontFamily="'JetBrains Mono', monospace" fontWeight={700}>{a.icao}</text>
                {showCityLabels && (
                  <text x={4.5 / transform.k} y={10.5 / transform.k} fontSize={6 / transform.k} fill="#2E9BF2" stroke="#0A1420" strokeWidth={1.4 / transform.k} paintOrder="stroke" fontFamily="'JetBrains Mono', monospace">{pies(a.elevFt)}</text>
                )}
              </g>
            );
          })}
          {clicked && (() => {
            const p = project(clicked.lon, clicked.lat);
            if (!p) return null;
            return (
              <g data-marker="1" transform={`translate(${p[0]},${p[1]})`}>
                <line x1={-7 / transform.k} y1={0} x2={7 / transform.k} y2={0} stroke="#FFD400" strokeWidth={1.4 / transform.k} />
                <line x1={0} y1={-7 / transform.k} x2={0} y2={7 / transform.k} stroke="#FFD400" strokeWidth={1.4 / transform.k} />
                <circle r={7 / transform.k} fill="none" stroke="#FFD400" strokeWidth={1.2 / transform.k} />
                <text x={10 / transform.k} y={10 / transform.k} fontSize={9 / transform.k} fill="#FFD400" stroke="#0A1420" strokeWidth={2 / transform.k} paintOrder="stroke" fontFamily="'JetBrains Mono', monospace">
                  {clicked.status === "loading" ? "…" : `${Math.round(clicked.elevFt).toLocaleString()} ft`}
                </text>
              </g>
            );
          })()}
          {waypoints.map((w, i) => {
            if (w.lat === null || w.lon === null) return null;
            const p = project(w.lon, w.lat);
            if (!p) return null;
            const next = waypoints[i + 1];
            const pn = next && next.lat !== null && next.lon !== null ? project(next.lon, next.lat) : null;
            return (
              <g key={w.id}>
                {pn && <line x1={p[0]} y1={p[1]} x2={pn[0]} y2={pn[1]} stroke="#FF3EA5" strokeWidth={1.8 / transform.k} strokeDasharray="4 3" />}
                <g data-marker="1" transform={`translate(${p[0]},${p[1]})`} style={{ cursor: "pointer" }} onClick={(ev) => { ev.stopPropagation(); setClicked(null); setSelected({ kind: "waypoint", data: w }); }}>
                  <circle r={5 / transform.k} fill="#FF3EA5" stroke="#0A1420" strokeWidth={1 / transform.k} />
                  <text x={7 / transform.k} y={-6 / transform.k} fontSize={10.5 / transform.k} fill="#FFFFFF" stroke="#0A1420" strokeWidth={2.5 / transform.k} paintOrder="stroke" fontFamily="'Barlow Condensed', sans-serif" fontWeight={700}>{w.name}</text>
                  <text x={7 / transform.k} y={7 / transform.k} fontSize={9 / transform.k} fill="#FF3EA5" stroke="#0A1420" strokeWidth={2 / transform.k} paintOrder="stroke" fontFamily="'JetBrains Mono', monospace">{Math.round(w.elevFt).toLocaleString()} ft</text>
                </g>
              </g>
            );
          })}
        </g>
        {hoverLeg && (
          <g transform={`translate(${Math.min(hoverLeg.screenX + 14, width - 190)},${Math.max(hoverLeg.screenY - 12, 14)})`} style={{ pointerEvents: "none" }}>
            <rect x={0} y={-14} width={186} height={30} rx={4} fill="#0A1420" opacity={0.92} stroke="#FF3EA5" />
            <text x={6} y={-2} fontSize={10} fill="#FFFFFF" fontFamily="'Barlow Condensed', sans-serif" fontWeight={700}>{hoverLeg.from} → {hoverLeg.to}</text>
            <text x={6} y={10} fontSize={10.5} fill="#FF3EA5" fontFamily="'JetBrains Mono', monospace" fontWeight={700}>{hoverLeg.distNm.toFixed(1)} NM</text>
          </g>
        )}
        {!hoverLeg && hoverPoint && (
          <g transform={`translate(${Math.min(hoverPoint.screenX + 14, width - 190)},${Math.max(hoverPoint.screenY - 10, 14)})`} style={{ pointerEvents: "none" }}>
            <rect x={0} y={-14} width={186} height={42} rx={4} fill="#0A1420" opacity={0.92} stroke="#3A4652" />
            <text x={6} y={-2} fontSize={10} fill="#D8DCE0" fontFamily="'JetBrains Mono', monospace">{hoverPoint.lat.toFixed(4)}, {hoverPoint.lon.toFixed(4)}</text>
            <text x={6} y={10} fontSize={10.5} fill="#FFD400" fontFamily="'JetBrains Mono', monospace" fontWeight={700}>
              {hoverPoint.elevFt === null ? "consultando altitud…" : `${Math.round(hoverPoint.elevFt).toLocaleString()} ft`}
            </text>
            {hoverPoint.elevFt !== null && (
              <text x={6} y={22} fontSize={8.5} fill={hoverPoint.isLive ? "#7FD858" : "#E8A33D"} fontFamily="'Inter', sans-serif">
                {hoverPoint.isLive ? "● en vivo (Open-Elevation)" : "● respaldo — sin conexión a la API"}
              </text>
            )}
          </g>
        )}
      </svg>
      <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11, color: COLORS.muted, flexWrap: "wrap" }}>
        <span>● <span style={{ color: "#FF3EA5" }}>magenta</span> = ruta</span>
        <span>pasa el cursor sobre la línea de ruta para ver el tramo y su distancia, o haz clic sobre ella para insertar un punto ahí (esquivar montañas)</span>
        <span>● <span style={{ color: "#2E9BF2" }}>azul</span> = aeródromos (clic para agregar a la ruta)</span>
        <span>● <span style={{ color: "#FFD400" }}>amarillo</span> = punto marcado (clic en cualquier parte del mapa)</span>
        <span>pasa el cursor sobre cualquier punto para ver su altitud</span>
        <span>● <span style={{ color: AIRWAY_COLOR.VFR }}>verde</span> = vías VFR</span>
        <span>● <span style={{ color: AIRWAY_COLOR.IFR }}>violeta</span> = vías IFR</span>
        <span>rueda del mouse: zoom · arrastrar: desplazar · clic: marcar altitud</span>
        {mapStatus === "loading" && <span style={{ color: COLORS.caution }}>cargando costas/países…</span>}
        {mapStatus === "error" && <span style={{ color: COLORS.caution }}>sin conexión — mostrando cuadrícula de referencia</span>}
      </div>
      {selected && (
        <div style={{ marginTop: 10, background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 12, fontSize: 12.5 }}>
          {selected.kind === "airport" ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <b>{selected.data.icao} — {selected.data.name}</b>
                {onAddPoint && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => onAddPoint(selected.data.lat, selected.data.lon, selected.data.elevFt, selected.data.icao || selected.data.name, "origin")} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5, fontWeight: 600 }}>Fijar origen</button>
                    <button onClick={() => onAddPoint(selected.data.lat, selected.data.lon, selected.data.elevFt, selected.data.icao || selected.data.name, "destination")} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5, fontWeight: 600 }}>Fijar destino</button>
                    <button onClick={() => onAddPoint(selected.data.lat, selected.data.lon, selected.data.elevFt, selected.data.icao || selected.data.name, "waypoint")} style={{ background: COLORS.panel, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5 }}>+ intermedio</button>
                  </div>
                )}
              </div>
              <div style={{ color: COLORS.muted, marginTop: 4 }}>
                lat/lon: {selected.data.lat.toFixed(4)}, {selected.data.lon.toFixed(4)} ({toDMS(selected.data.lat, selected.data.lon)}) · elevación: {selected.data.elevFt} ft<br />
                FIR: {selected.data.fir || "—"} · TMA: {selected.data.tma || "—"} · CTR: {selected.data.ctr || "—"}<br />
                Frecuencia: {selected.data.freq || "— (completar)"} · Pista: {selected.data.pista || "— (completar)"}
              </div>
            </>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <b>{selected.data.name}</b>
                {onAddPoint && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button onClick={() => onAddPoint(selected.data.lat, selected.data.lon, selected.data.elevFt, selected.data.name, "origin")} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5, fontWeight: 600 }}>Fijar origen</button>
                    <button onClick={() => onAddPoint(selected.data.lat, selected.data.lon, selected.data.elevFt, selected.data.name, "destination")} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5, fontWeight: 600 }}>Fijar destino</button>
                    <button onClick={() => onAddPoint(selected.data.lat, selected.data.lon, selected.data.elevFt, selected.data.name, "waypoint")} style={{ background: COLORS.panel, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5 }}>+ intermedio</button>
                  </div>
                )}
              </div>
              <div style={{ color: COLORS.muted, marginTop: 4 }}>
                lat/lon: {selected.data.lat.toFixed(4)}, {selected.data.lon.toFixed(4)} ({toDMS(selected.data.lat, selected.data.lon)}) · elevación: {selected.data.elevFt === 0 ? "consultando…" : `${selected.data.elevFt} ft`}
              </div>
            </>
          )}
        </div>
      )}
      {clicked && (
        <div style={{ marginTop: 10, background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 12, fontSize: 12.5 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <b>{clicked.name || "Punto marcado"}</b>
            {onAddPoint && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {clicked.insertAfterId && onInsertAtSegment ? (
                  <>
                    <button onClick={() => onInsertAtSegment(clicked.insertAfterId, clicked.lat, clicked.lon, clicked.elevFt, clicked.name)} style={{ background: "#FF3EA5", border: "none", color: "#1A1204", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5, fontWeight: 700 }}>
                      ↷ insertar en la ruta aquí (evitar obstáculo)
                    </button>
                    {onResetRoute && (
                      confirmResetMap ? (
                        <>
                          <span style={{ fontSize: 11.5, color: COLORS.danger, alignSelf: "center" }}>¿Seguro?</span>
                          <button onClick={() => { onResetRoute(); setClicked(null); setConfirmResetMap(false); }} style={{ background: COLORS.danger, border: "none", color: "#fff", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5, fontWeight: 700 }}>Sí, borrar</button>
                          <button onClick={() => setConfirmResetMap(false)} style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.muted, borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5 }}>Cancelar</button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmResetMap(true)} style={{ background: "transparent", border: `1px solid ${COLORS.danger}`, color: COLORS.danger, borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5 }}>
                          🗑 borrar ruta
                        </button>
                      )
                    )}
                  </>
                ) : (
                  <button onClick={() => onAddPoint(clicked.lat, clicked.lon, clicked.elevFt, clicked.name, "waypoint")} style={{ background: COLORS.panel, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5 }}>+ intermedio</button>
                )}
                <button onClick={() => onAddPoint(clicked.lat, clicked.lon, clicked.elevFt, clicked.name, "origin")} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5, fontWeight: 600 }}>Fijar origen</button>
                <button onClick={() => onAddPoint(clicked.lat, clicked.lon, clicked.elevFt, clicked.name, "destination")} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "5px 8px", cursor: "pointer", fontSize: 11.5, fontWeight: 600 }}>Fijar destino</button>
              </div>
            )}
          </div>
          <div style={{ color: COLORS.muted, marginTop: 6, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span>lat/lon: {clicked.lat.toFixed(4)}, {clicked.lon.toFixed(4)} ({toDMS(clicked.lat, clicked.lon)})</span>
            <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
              elevación (ft):
              <CampoNumero
                valor={clicked.elevFt}
                alCambiar={(n) => setClicked((c) => ({ ...c, elevFt: n, status: "manual" }))}
                style={{ width: 80, background: COLORS.panel, border: `1px solid ${COLORS.hairline}`, borderRadius: 4, color: COLORS.text, padding: "2px 6px", fontFamily: "'JetBrains Mono', monospace" }} />
            </label>
            {clicked.status === "loading" && <span style={{ color: COLORS.caution }}>consultando elevación…</span>}
            {clicked.status === "manual" && <span style={{ color: COLORS.caution }}>sin dato automático — edítala a mano</span>}
          </div>
          <div style={{ fontSize: 10.5, color: COLORS.muted, marginTop: 4 }}>
            La coordenada se toma directamente del clic (siempre exacta). La elevación intenta traerse sola vía Open-Elevation
            — si esa API falla o está caída, queda en 0 y editable a mano; en ningún caso bloquea agregar el punto a la ruta.
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================ App ================================ */
function HeliCalcProto({ alRegistrarIr } = {}) {
  // "hoja" dejó de ser una pestaña propia: vive dentro de Resumen. Arrancar en
  // una clave inexistente dejaba la pantalla en blanco al abrir la app.
  const [tab, setTab] = useState("resumen");

  /* ═══ EL CASCARÓN ES QUIEN TIENE EL BOTÓN DE ADMINISTRACIÓN ═══

     El botón vive arriba, en la barra del aviso legal —es el único lugar de la
     pantalla que está a la vista en TODAS las herramientas—, así que el
     cascarón necesita poder cambiar de pestaña. Se le presta `setTab`, que es
     estable, una sola vez: el legacy sigue siendo dueño de su estado y el
     cascarón no necesita saber cómo está escrito por dentro. */
  useEffect(() => { alRegistrarIr?.(setTab); }, [alRegistrarIr]);
  const [dashFilterTipo, setDashFilterTipo] = useState("todos");
  const [dashFilterOperacion, setDashFilterOperacion] = useState("todas");
  const [dashFilterPeriodo, setDashFilterPeriodo] = useState("todo");

  // ---- Perfiles de aeronave ----
  /* Un perfil por TIPO de aeronave. Cada uno trae su envolvente de CG, sus
     brazos de estación y sus cartas de performance; la matrícula elige cuál
     usar. Agregar un tipo nuevo es agregar una entrada acá. */
  /**
   * Fusiona el catálogo guardado con el de fábrica.
   *
   * DOS REGLAS, Y LA SEGUNDA COSTÓ UN ERROR
   *
   * 1. Los tipos que el operador no tiene guardados se agregan. Sin esto, un
   *    tipo nuevo nunca llega a quien ya usó la app.
   *
   * 2. En los tipos que SÍ están guardados, las CARTAS DE PERFORMANCE vienen
   *    siempre de fábrica. La primera versión conservaba el perfil guardado
   *    entero, y una copia vieja —con las cartas en null porque se guardó
   *    antes de digitalizarlas— pisaba las de fábrica: el AS350 perdió su
   *    carta de consumo y todos los tramos decían "fuera de carta".
   *
   *    Las cartas son datos del fabricante, no preferencias del usuario. No
   *    hay nada que el operador pueda "editar" en ellas que deba sobrevivir a
   *    una corrección de la aplicación.
   */
  const CAMPOS_DE_FABRICA = ["ogeTable", "igeTable", "fuelTable", "oeiHogeTable", "envelope", "estacionesPax", "cargoCompartments", "tanques"];

  const fusionarPorId = (guardado, deFabrica) => {
    if (!Array.isArray(guardado)) return deFabrica;
    const porId = new Map(deFabrica.map((x) => [x.id, x]));
    const conservados = guardado.map((g) => {
      const fab = porId.get(g?.id);
      if (!fab) return g; // tipo propio del operador: se respeta entero
      const mezcla = { ...g };
      for (const campo of CAMPOS_DE_FABRICA) {
        if (fab[campo] !== undefined) mezcla[campo] = fab[campo];
      }
      return mezcla;
    });
    const ids = new Set(guardado.map((x) => x?.id).filter(Boolean));
    return [...conservados, ...deFabrica.filter((x) => !ids.has(x.id))];
  };

  const [profiles, setProfiles] = usePersistedState(
    "perfiles", [AS350B3_PROFILE, PERFIL_TIPO_BK117_B2], fusionarPorId,
  );
  const [activeProfileId, setActiveProfileId] = useState("as350b3");
  /**
   * La matrícula activa se PERSISTE.
   *
   * Era `useState` suelto: al refrescar la página —o al recargarse la PWA sola—
   * volvía a "CC-CSU" en silencio. Toda la aplicación cuelga de la matrícula
   * (peso vacío, envolvente, cartas, mantenimiento), así que eso significaba
   * seguir trabajando sobre OTRO helicóptero sin ninguna señal en pantalla.
   * Lo destapó la prueba del importador: se importaba una matrícula nueva, se
   * refrescaba, y la app calculaba con la anterior.
   */
  const [matricula, setMatricula] = usePersistedState("matriculaActiva", "CC-CSU");
  /* Tema día/noche. En automático se revisa cada media hora: el vuelo que
     empieza de día termina de noche, y cambiar de tema a mano en el aire es
     una cosa menos que el piloto tiene que acordarse de hacer. */
  const [tema, setTema] = useState(() => {
    try { return localStorage.getItem("hcTema") || TEMA_POR_DEFECTO; } catch { return TEMA_POR_DEFECTO; }
  });

  useEffect(() => {
    aplicarTema(tema);
    try { localStorage.setItem("hcTema", tema); } catch { /* modo privado */ }
    if (tema !== "auto") return undefined;
    const reloj = setInterval(() => aplicarTema("auto"), 30 * 60 * 1000);
    return () => clearInterval(reloj);
  }, [tema]);



  /* ─────────────────────────────────────────────────────────────────────────
   * FLOTA — una entrada por matrícula.
   *
   * El peso vacío vive acá y no en el perfil. El perfil describe el TIPO
   * (envolvente de CG, cartas de performance, brazos de asiento); el peso
   * vacío describe ESTE helicóptero y sale de un pesaje con fecha. Dos AS350 B3
   * idénticos de fábrica pesan distinto según equipamiento, pintura y
   * reparaciones. Usar el valor genérico del tipo hacía que la app calculara
   * una aeronave con el valor genérico del tipo cuando su pesaje real dice otro:
   * el error va siempre hacia el lado optimista.
   *
   * Los valores concretos salen de la hoja "CERTIFICACIONES" del status report
   * de cada matrícula, y viven en src/datos/privado/ (no se publica).
   * ──────────────────────────────────────────────────────────────────────── */
  const [fleet, setFleet] = usePersistedState(
    "flota", [...FLOTA_SEMILLA, FLOTA_OB2240P], fusionarPorId, "aeronaves",
  );

  /**
   * El perfil de tipo sigue a la MATRÍCULA elegida.
   *
   * Antes el tipo se elegía por separado, y nada impedía calcular una OB-2240P
   * —un BK117 bimotor de 3350 kg— con la envolvente y las cartas del AS350 B3.
   * El resultado habría sido coherente en pantalla y equivocado en todo.
   *
   * Ahora la matrícula manda: cada entrada de flota dice de qué tipo es, y el
   * perfil se resuelve de ahí. El selector de tipo queda solo para matrículas
   * que todavía no lo declaren.
   */
  const aeronaveDeMatricula = Array.isArray(fleet) ? fleet.find((f) => f.matricula === matricula) : null;
  const perfilIdEfectivo = aeronaveDeMatricula?.perfilId || activeProfileId;
  const activeProfile =
    (Array.isArray(profiles) ? profiles.find((p) => p.id === perfilIdEfectivo) || profiles[0] : null) ||
    AS350B3_PROFILE;

/** La aeronave de la flota que corresponde a la matrícula activa, si existe. */
  const aeronaveActiva = Array.isArray(fleet) ? fleet.find((f) => f.matricula === matricula) : null;

  /**
   * Peso vacío y brazo efectivos, con su procedencia.
   *
   * Prioridad: pesaje de la matrícula > valor genérico del tipo. La procedencia
   * se propaga a la pantalla porque un piloto tiene que poder distinguir un
   * número pesado en balanza de uno heredado del modelo.
   */
  /* ─────────────────────────────────────────────────────────────────────────
   * CONFIGURACIÓN DE CANASTA.
   *
   * Cambiar la configuración cambia el peso vacío Y su brazo: quitar la canasta
   * no es restar 38,8 kg dejando el brazo igual, hay que restar también su
   * momento y volver a dividir.
   * ──────────────────────────────────────────────────────────────────────── */
  const [canastaInstalada, setCanastaInstalada] = usePersistedState("canastaInstalada", true);
  /**
   * La enmienda de peso y balance pertenece a UNA matrícula.
   *
   * Antes esto era una comparación contra la cadena "CC-CSU" escrita en el
   * código. Ahora es un dato de la aeronave: cada matrícula lleva la suya, y
   * agregar otra máquina con su propia enmienda no exige tocar el programa.
   */
  /**
   * La enmienda puede no estar en el registro guardado.
   *
   * La flota se persiste en IndexedDB. Un operador que ya usó la app tiene
   * grabado un registro anterior a que existiera este campo, así que
   * `aeronaveActiva.enmiendaPesoBalance` viene vacío y la pantalla decía "para
   * CC-CSU no se aplica" con la enmienda cargada delante. Se busca por
   * matrícula en las enmiendas conocidas del código como respaldo.
   */
  const enmienda = aeronaveActiva?.enmiendaPesoBalance
    || (FLOTA_SEMILLA.find((f) => f.matricula === matricula)?.enmiendaPesoBalance ?? null);
  const enmiendaAplicable = Boolean(enmienda);
  const MATRICULA_CON_ENMIENDA = fleet.find((f) => f.enmiendaPesoBalance)?.matricula || "—";
  /** ¿Está el accesorio instalado Y la aeronave tiene enmienda que lo contemple? */
  const accesorioInstalado = enmiendaAplicable && canastaInstalada;

  const configuracionCanasta = enmiendaAplicable
    ? pesoVacioSegunConfiguracion(canastaInstalada, enmienda.conAccesorio, enmienda.accesorio)
    : { valido: false, pesoKg: null, brazoM: null, configuracion: null };

  const pesoVacio = (() => {
    // La configuración de canasta manda sobre el pesaje genérico cuando la
    // aeronave tiene la enmienda de peso y balance cargada.
    const delPesaje = configuracionCanasta.valido
      ? configuracionCanasta.pesoKg
      : (Number.isFinite(aeronaveActiva?.pesoBasicoVacioKg) ? aeronaveActiva.pesoBasicoVacioKg : null);
    const brazoDelPesaje = configuracionCanasta.valido
      ? configuracionCanasta.brazoM
      : (Number.isFinite(aeronaveActiva?.brazoVacioM) ? aeronaveActiva.brazoVacioM : null);
    return {
      kg: delPesaje ?? activeProfile.emptyKg,
      arm: brazoDelPesaje ?? activeProfile.emptyArm,
      pesoDelPesaje: delPesaje !== null,
      brazoDelPesaje: brazoDelPesaje !== null,
      fecha: aeronaveActiva?.fechaPesaje || null,
    };
  })();


  const addProfile = () => {
    const id = `tipo_${Date.now()}`;
    setProfiles((ps) => [...ps, { ...BLANK_PROFILE_TEMPLATE, id, arms: { ...BLANK_PROFILE_TEMPLATE.arms }, envelope: { ...BLANK_PROFILE_TEMPLATE.envelope } }]);
    setActiveProfileId(id);
  };
  const updateProfile = (patch) => setProfiles((ps) => ps.map((p) => (p.id === activeProfileId ? { ...p, ...patch } : p)));
  const updateArm = (key, val) => setProfiles((ps) => ps.map((p) => (p.id === activeProfileId ? { ...p, arms: { ...p.arms, [key]: val } } : p)));
  const updateCargoCompartment = (key, maxKg) => setProfiles((ps) => ps.map((p) => (p.id === activeProfileId ? { ...p, cargoCompartments: p.cargoCompartments.map((c) => (c.key === key ? { ...c, maxKg } : c)) } : p)));
  const updateEnvelope = (key, val) => setProfiles((ps) => ps.map((p) => (p.id === activeProfileId ? { ...p, envelope: { ...p.envelope, [key]: val } } : p)));

  // ---- Ruta (con coordenadas) ----
  // Origen y destino son casilleros fijos que empiezan en blanco (sin coordenadas) — no se eliminan, solo se
  // "fijan" haciendo clic en el mapa, buscando un lugar, o eligiéndolos de la lista. Los intermedios sí se
  // pueden agregar y quitar libremente entre ambos.
  const BLANK_ORIGIN = puntoVacio("origin");
  const BLANK_DEST = puntoVacio("destination");

  /* ─────────────────────────────────────────────────────────────────────────
   * LA RUTA — con portero.
   *
   * Toda la aplicación da por sentado que esto es un arreglo de dos o más
   * puntos, el primero "origin" y el último "destination": está escrito así en
   * decenas de lugares (`waypoints[0]`, `waypoints[waypoints.length - 1]`).
   * Ese supuesto no estaba garantizado en ningún lado, y alcanzaba con una
   * ruta guardada en formato viejo o con una escritura inesperada para que la
   * app se cayera entera con "undefined is not an object (evaluating
   * 'waypoints[0]')": pantalla de error, sin forma de volver, con el vuelo a
   * medio planificar.
   *
   * Ahora TODA escritura pasa por `normalizarRuta`. Es un envoltorio de tres
   * líneas alrededor de `useState`, y convierte el supuesto en un hecho: no
   * hay camino —ni el de hoy ni el que se escriba mañana— que pueda dejar la
   * ruta en un estado que rompa el render.
   * ──────────────────────────────────────────────────────────────────────── */
  const [waypoints, setWaypointsCrudo] = useState(() => normalizarRuta([BLANK_ORIGIN, BLANK_DEST]));

  /* Rastro de las veces que algo intentó borrar una coordenada sin pedirlo.
     No es para el piloto: es para poder decir QUIÉN lo hizo la próxima vez que
     aparezca. Se guardan pocas y sin datos de vuelo. */
  const rastroRutaRef = useRef([]);

  /**
   * @param {*} valor          la ruta nueva, o una función que la calcula
   * @param {object} [opciones]
   * @param {boolean} [opciones.intencional] true cuando el cambio lo pidió el
   *   piloto: borrar la ruta, cargar otra, editar coordenadas a mano. Sin esta
   *   marca, un punto que ya tenía posición no la puede perder.
   */
  const setWaypoints = useCallback((valor, opciones = {}) => {
    setWaypointsCrudo((previo) => {
      const propuesta = normalizarRuta(typeof valor === "function" ? valor(previo) : valor);
      const { ruta, restaurados } = preservarCoordenadas(previo, propuesta, opciones);
      if (restaurados.length > 0) {
        const aviso = `se evitó borrar la posición de: ${restaurados.join(", ")}`;
        rastroRutaRef.current = [
          ...rastroRutaRef.current.slice(-4),
          { cuando: new Date().toISOString().slice(11, 19), aviso, pila: new Error().stack || "" },
        ];
        console.warn("[HeliCalc]", aviso, new Error().stack);
      }
      return ruta;
    });
  }, []);

  /* ─────────────────────────────────────────────────────────────────────────
   * MIS PUNTOS — base propia del operador.
   *
   * Distinta de la base de aeródromos: aquella es comunitaria (OurAirports) y
   * de solo lectura. Esta la escribe el operador, y guarda lo que ninguna base
   * pública tiene: zonas de aterrizaje de faena, plataformas, puntos de
   * referencia visual, helipuertos de hospital.
   *
   * Cada punto recuerda de dónde salió (mapa, carga manual o archivo). Un punto
   * que vino de un KMZ de un tercero no merece la misma confianza que uno que
   * el piloto midió, y sin esa marca los dos se ven igual a los seis meses.
   * ──────────────────────────────────────────────────────────────────────── */
  const [misPuntos, setMisPuntos] = usePersistedState("misPuntos", []);
  const [importacion, setImportacion] = useState(null);
  const [importando, setImportando] = useState(false);

  /* ═══ EL TIPO SE ELIGE UNA VEZ, PARA TODO EL ARCHIVO ═══

     Un KMZ trae puntos de UNA cosa: 41 torres de una línea, o los helipuertos
     de la región. Clasificarlos de a uno son 41 clics para repetir el mismo
     dato. */
  const [tipoDeLaImportacion, setTipoDeLaImportacion] = useState(TIPO_POR_DEFECTO);

  /* La pista que da el nombre del archivo y el de los puntos. Se MUESTRA; no
     se aplica sola. */
  const sugerencia = useMemo(
    () => (importacion ? sugerirTipoDeArchivo(importacion.archivo, importacion.puntos) : null),
    [importacion],
  );

  const guardarPunto = (punto) => {
    setMisPuntos((ps) => [...ps, {
      id: `pt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      nombre: (punto.nombre || "PUNTO").toUpperCase(),
      lat: punto.lat, lon: punto.lon,
      elevFt: Number.isFinite(punto.elevFt) ? punto.elevFt : null,
      origen: punto.origen || "mapa",
      archivo: punto.archivo || null,
      creado: new Date().toISOString(),
    }]);
  };
  const actualizarPunto = (id, patch) => setMisPuntos((ps) => ps.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const borrarPunto = (id) => setMisPuntos((ps) => ps.filter((x) => x.id !== id));
  const borrarPorArchivo = (archivo) => setMisPuntos((ps) => ps.filter((x) => x.archivo !== archivo));

  /** Lee el archivo y lo deja en vista previa. No toca la base todavía. */
  const elegirArchivoDePuntos = async (archivo) => {
    if (!archivo) return;
    setImportando(true);
    try {
      const leido = await leerArchivoDePuntos(archivo);
      setImportacion(leido);

      /* ═══ LA ASIMETRÍA ═══

         Un tipo que NO afirma que se puede posar se preselecciona: si la
         pista erró, el error es conservador.

         Un tipo OPERATIVO no se preselecciona NUNCA, por más clara que sea la
         pista. Adivinar hacia «acá se puede posar» es el error que esta
         aplicación no puede cometer: un piloto que ve 41 marcas de helipuerto
         sobre 41 torres tiene 41 trampas en el mapa. La sugerencia se muestra
         igual, y el operador la elige a mano si corresponde. */
      const s = sugerirTipoDeArchivo(leido.archivo, leido.puntos);
      setTipoDeLaImportacion(s?.sePreselecciona ? s.tipo : TIPO_POR_DEFECTO);
    } finally {
      setImportando(false);
    }
  };

  /** Recién acá entran a la base, con el archivo de origen anotado. */
  const confirmarImportacion = () => {
    if (!importacion?.puntos?.length) return;
    const ahora = Date.now();
    setMisPuntos((ps) => [...ps, ...importacion.puntos.map((punto, i) => ({
      id: `pt_${ahora}_${i}`,
      nombre: punto.nombre.toUpperCase(),
      lat: punto.lat, lon: punto.lon,
      elevFt: null,
      origen: "archivo",
      archivo: importacion.archivo,
      /* El tipo del archivo entero, elegido antes de guardar. */
      tipo: tipoDeLaImportacion,
      creado: new Date().toISOString(),
    }))]);
    setImportacion(null);
    setTipoDeLaImportacion(TIPO_POR_DEFECTO);
  };

  /**
   * Lleva un punto guardado a la ruta, resolviendo antes su elevación.
   *
   * Los puntos importados de KML no traen cota: la altitud del archivo se
   * descarta a propósito. Si se los insertara con elevación cero, un helipuerto
   * a 2850 ft entraría como si estuviera al nivel del mar y los márgenes OGE
   * saldrían holgados por más de 800 m de error. Se consulta el modelo digital
   * de terreno, que es la misma fuente que usa el perfil de ruta. Si no hay red
   * el punto entra igual, pero con la cota en cero visible en la tabla para que
   * el piloto la corrija a mano.
   */
  const usarPuntoEnRuta = async (punto, destino) => {
    let elevFt = punto.elevFt;
    if (!Number.isFinite(elevFt)) {
      try { elevFt = await elevacionPies(punto.lat, punto.lon); } catch { elevFt = null; }
    }
    if (destino === "waypoint") addIntermediateWaypoint(punto.lat, punto.lon, elevFt, punto.nombre);
    else setEndpoint(destino, punto.lat, punto.lon, elevFt, punto.nombre);
  };

  /* ═══ VER LOS PUNTOS EN EL MAPA ═══

     Se guardaban y se listaban en una tabla, y nunca se veían sobre el
     terreno. Con 41 estructuras de una línea de transmisión, 41 filas de
     coordenadas no dicen por dónde pasa la línea ni dónde conviene el acopio.

     El contador sube para pedirle al mapa que encuadre: encenderlos sin mover
     la vista deja al piloto mirando un mapa vacío con los puntos fuera de
     pantalla, creyendo que la función no anda. */
  const [encuadrarMisPuntos, setEncuadrarMisPuntos] = useState(0);

  const verMisPuntosEnMapa = () => {
    guardarMisPuntosVisibles(true);
    setEncuadrarMisPuntos((n) => n + 1);
    setTab("ruta");
  };

  /**
   * Pone el mismo tipo a todos los puntos de un archivo ya cargado.
   *
   * Los 41 puntos del usuario ya estaban guardados sin tipo cuando esto se
   * escribió: sin esta vía habría que clasificarlos de a uno, o borrar el
   * archivo y volver a importarlo perdiendo lo que hubiera editado a mano.
   *
   * El conteo se muestra: un «listo» sobre cero puntos es peor que un error.
   */
  const [aviso, setAviso] = useState(null);

  const clasificarTodoElArchivo = (archivo, tipo) => {
    setMisPuntos((ps) => {
      const r = clasificarArchivo(ps, archivo, tipo);
      const t = TIPOS_DE_PUNTO.find((x) => x.id === tipo);
      setAviso(r.cuantos > 0
        ? `${r.cuantos} punto(s) de ${archivo} quedaron como «${t?.nombre || tipo}».`
        : `No se cambió ningún punto de ${archivo}.`);
      return r.puntos;
    });
  };

  /** Archivos ya importados, para poder borrar una carga completa de una vez. */
  const archivosImportados = [...new Set(misPuntos.filter((x) => x.archivo).map((x) => x.archivo))];
  /**
   * Invierte la ruta para el tramo de regreso.
   *
   * Se da vuelta el orden completo, así los waypoints intermedios se recorren
   * al revés. El perfil de terreno se descarta: es el del sentido anterior y
   * dejarlo sería mostrar la orografía de la ida bajo la vuelta.
   */
  const invertirRuta = () => {
    setWaypoints((ws) => {
      const invertidos = [...ws].reverse().map((w, i) => ({ ...w, id: `wp_${Date.now()}_${i}` }));
      // Los casilleros fijos conservan su identidad de extremo.
      if (invertidos.length >= 2) {
        invertidos[0] = { ...invertidos[0], id: "origin" };
        invertidos[invertidos.length - 1] = { ...invertidos[invertidos.length - 1], id: "destination" };
      }
      return invertidos;
    }, { intencional: true });
    setRouteProfile(null);
    setRouteProfileStatus("idle");
    setTodManualNm(null);
    setInicioAscensoNm(0);
    setMeteoPuntos((m) => ({ origin: m.destination, destination: m.origin }));
  };

  const resetRoute = () => {
    setWaypoints([{ ...BLANK_ORIGIN }, { ...BLANK_DEST }], { intencional: true });
    setRouteProfile(null);
    setRouteProfileStatus("idle");
    setRouteProfileSource(null);
    setPilotConfirmed(false);
  };
  const [confirmResetRoute, setConfirmResetRoute] = useState(false);
  const [manualWp, setManualWp] = useState({ name: "", lat: "", lon: "" });
  const addManualWaypoint = () => {
    const lat = Number(manualWp.lat), lon = Number(manualWp.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    addPointToRoute(lat, lon, 0, manualWp.name || nombreDePunto(lat, lon));
    setManualWp({ name: "", lat: "", lon: "" });
  };
  const setEndpoint = (slotId, lat, lon, elevFt, name) => setWaypoints((ws) => ws.map((w) => (w.id === slotId ? { ...w, lat, lon, elevFt: Number.isFinite(elevFt) ? elevFt : null, name: name || w.name } : w)), { intencional: true });
  const [isLocalFlight, setIsLocalFlight] = useState(false);
  useEffect(() => {
    if (!isLocalFlight) return;
    setWaypoints((ws) => {
      const o = ws[0];
      if (!o || o.lat === null) return ws;
      return ws.map((w, i) => (i === ws.length - 1 ? { ...w, lat: o.lat, lon: o.lon, elevFt: o.elevFt, name: `${o.name} (regreso)` } : w));
    });
  }, [isLocalFlight, waypoints[0]?.lat, waypoints[0]?.lon, waypoints[0]?.elevFt, waypoints[0]?.name]);
  const addIntermediateWaypoint = (lat, lon, elevFt, name) => setWaypoints((ws) => [
    ...ws.slice(0, -1),
    { id: Date.now(), name: name || nombreDePunto(lat, lon) || "PUNTO", lat, lon, elevFt: Number.isFinite(elevFt) ? elevFt : null, tempC: 15, legNm: 20 },
    ws[ws.length - 1],
  ]);
  // Inserta el nuevo punto justo después del waypoint indicado (no siempre al final) — es lo que permite
  // "doblar" la ruta exactamente donde se hizo clic sobre la línea, para esquivar una montaña u obstáculo.
  /* El `id` llega desde afuera cuando el punto nace de estirar la línea: el
     mapa necesita conocerlo de antemano para pegarle la elevación cuando el
     servicio de terreno conteste, medio segundo después de soltar. */
  const insertWaypointAfter = (afterId, lat, lon, elevFt, name, id) => setWaypoints((ws) => {
    const nuevo = { id, lat, lon, elevFt, name };
    const conElAncla = insertarDespues(ws, afterId, nuevo);
    if (conElAncla !== ws) return conElAncla;

    /* El id de anclaje no está en la ruta. Pasa si la lista cambió entre que
       se agarró la línea y que se soltó. Sin esta salida el punto se perdía en
       silencio y el piloto veía que la línea "volvía" a donde estaba, sin
       ninguna explicación. Se ubica por geografía, que no depende de ids. */
    const porGeografia = insertarEnTramoMasCercano(ws, nuevo);
    if (porGeografia !== ws) {
      console.warn(`[HeliCalc] el punto de anclaje «${afterId}» no estaba en la ruta; se insertó por cercanía`);
      return porGeografia;
    }
    console.warn(`[HeliCalc] no se pudo insertar el punto (ancla «${afterId}», ruta de ${ws.length})`);
    return ws;
  });
  /** Reubica un punto intermedio arrastrándolo en el mapa. */
  const moverPuntoDeRuta = (id, lat, lon) => setWaypoints((ws) => moverPunto(ws, id, lat, lon));
  /** Completa un punto con un dato que llegó después (la elevación medida). */
  const actualizarPuntoDeRuta = (id, cambios) => setWaypoints((ws) => actualizarPunto(ws, id, cambios));
  const [cruiseAltFt, setCruiseAltFt] = useState(15000);
  const [cruiseTempC, setCruiseTempC] = useState(5);
  const [groundspeedKt, setGroundspeedKt] = useState(100);

  /* ─────────────────────────────────────────────────────────────────────────
   * PERFIL VERTICAL — ascenso, crucero y descenso.
   *
   * Valores acordados con el operador, todos editables: son perfiles de
   * operación del piloto, no límites del fabricante.
   * ──────────────────────────────────────────────────────────────────────── */
  const [ascenso, setAscenso] = usePersistedState("perfilAscenso", ASCENSO_POR_DEFECTO);
  const [descenso, setDescenso] = usePersistedState("perfilDescenso", DESCENSO_POR_DEFECTO);
  const [margenLibramientoFt, setMargenLibramientoFt] = usePersistedState("margenLibramiento", MARGEN_LIBRAMIENTO_FT);
  /** Inicio de descenso forzado por el piloto. null = lo calcula la app. */
  const [todManualNm, setTodManualNm] = useState(null);
  /** Milla en la que arranca el ascenso. 0 = desde el despegue mismo. */
  const [inicioAscensoNm, setInicioAscensoNm] = useState(0);

  /* Temperatura de superficie traída de Open-Meteo, por punto. */
  const [meteoPuntos, setMeteoPuntos] = useState({ origin: null, destination: null });
  const [meteoCargando, setMeteoCargando] = useState(null);

  /* METAR de los aeródromos de despegue y aterrizaje.

     Solo tiene sentido cuando el punto ES un aeródromo: el nombre del waypoint
     guarda el código OACI cuando se elige uno del mapa o de la lista. Para una
     coordenada suelta no existe METAR, y el panel lo dice sin alarma. */
  const [metar, setMetar] = useState({});
  const [metarCargando, setMetarCargando] = useState(false);

  const oaciDespegue = oaciValido(waypoints[0]?.name) ? waypoints[0].name.toUpperCase() : null;
  const oaciAterrizaje = oaciValido(waypoints[waypoints.length - 1]?.name)
    ? waypoints[waypoints.length - 1].name.toUpperCase() : null;

  const consultarMetar = useCallback(async () => {
    const codigos = [oaciDespegue, oaciAterrizaje].filter(Boolean);
    if (codigos.length === 0) return;
    setMetarCargando(true);
    try {
      setMetar(await traerVarios(codigos));
    } finally {
      setMetarCargando(false);
    }
  }, [oaciDespegue, oaciAterrizaje]);

  /* Se consulta solo cuando cambian los aeródromos. El servicio ya cachea
     cinco minutos, así que volver a la pestaña no dispara pedidos nuevos. */
  useEffect(() => { consultarMetar(); }, [consultarMetar]);

  const traerTemperatura = async (slotId) => {
    const wp = slotId === "origin" ? waypoints[0] : waypoints[waypoints.length - 1];
    if (!wp || wp.lat === null) {
      setMeteoPuntos((m) => ({ ...m, [slotId]: { ok: false, error: "El punto no está fijado todavía." } }));
      return;
    }
    setMeteoCargando(slotId);
    const r = await temperaturaEnSuperficie(wp.lat, wp.lon, wp.elevFt);
    setMeteoPuntos((m) => ({ ...m, [slotId]: r }));
    // La temperatura entra al waypoint, que es de donde la leen performance y
    // peso y balance. El valor se aplica tal como lo da el modelo: la
    // corrección por diferencia de elevación se informa, no se aplica sola.
    if (r.ok) updateWp(wp.id, { tempC: Math.round(r.temperaturaC) });
    setMeteoCargando(null);
  };

  // Perfil de ruta (terreno) — muestrea puntos a lo largo de los tramos y consulta su elevación real
  // (misma API abierta que ya usa el mapa) para graficar un perfil de obstáculos aproximado.
  const [routeProfile, setRouteProfile] = useState(null);
  const [routeProfileStatus, setRouteProfileStatus] = useState("idle");
  const [routeProfileSource, setRouteProfileSource] = useState(null);
  const haversineKmApp = (lat1, lon1, lat2, lon2) => {
    const R = 6371, dLat = (lat2 - lat1) * Math.PI / 180, dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };
  const nearestAirportElevFt = (lat, lon) => {
    let best = null, bestKm = Infinity;
    for (const a of airports) {
      const d = haversineKmApp(lat, lon, a.lat, a.lon);
      if (d < bestKm) { bestKm = d; best = a; }
    }
    return best && bestKm <= 30 ? best.elevFt : null;
  };
  // Perfil de elevación leído del modelo digital de elevación real (Terrarium,
  // ~30 m). Sustituye a la versión anterior, que ante un fallo de red rellenaba
  // con el aeródromo más cercano o interpolaba en línea recta entre origen y
  // destino: eso producía un perfil que parecía terreno y no lo era (AD-004).
  // Ahora un punto que no se puede leer queda como hueco y se informa.
  const generateRouteProfile = async () => {
    const defined = waypoints.filter((w) => w.lat !== null && w.lon !== null);
    if (defined.length < 2) { setRouteProfileStatus("error"); return; }
    setRouteProfileStatus("loading");
    setRouteProfileSource(null);

    try {
      const { muestras, huecos } = await perfilDeRuta(defined, { pasoNm: 0.5, maxMuestras: 500 });

      const conDato = muestras.filter((m) => m.elevFt !== null);
      if (conDato.length === 0) {
        setRouteProfileStatus("error");
        setRouteProfileSource({ usedApi: 0, usedNearest: 0, usedInterp: 0, total: muestras.length });
        return;
      }

      // Pendiente entre cada punto y el anterior, en porcentaje. Se salta si
      // alguno de los dos extremos es un hueco: no se calcula sobre un vacío.
      const profile = muestras.map((m, i) => {
        const base = { distNm: Math.round(m.distNm * 10) / 10, elevFt: m.elevFt, lat: m.lat, lon: m.lon };
        if (i === 0) return { ...base, slopePct: 0 };
        const prev = muestras[i - 1];
        if (m.elevFt === null || prev.elevFt === null) return { ...base, slopePct: 0 };
        const distFt = Math.max(1, (m.distNm - prev.distNm) * 6076.12);
        return { ...base, slopePct: Math.round(((m.elevFt - prev.elevFt) / distFt) * 1000) / 10 };
      });

      setRouteProfile(profile);
      setRouteProfileStatus(huecos === 0 ? "ok" : "ok-degraded");
      setRouteProfileSource({
        usedApi: conDato.length,
        usedNearest: 0,
        usedInterp: 0,
        huecos,
        total: muestras.length,
      });

      // Altitud de crucero sugerida: 1000 ft sobre la cima del terreno,
      // redondeado a 500 ft. Es una sugerencia, no un mínimo reglamentario.
      // `|| 0` metía el nivel del mar en el máximo cuando una cota faltaba, lo
      // que no cambia el resultado (0 nunca es el máximo) pero sí lo enmascara:
      // se prefiere no incluir el dato ausente antes que sustituirlo.
      const cotasConocidas = [
        ...conDato.map((m) => m.elevFt),
        origin.elevFt, destination.elevFt,
      ].filter(Number.isFinite);
      const maxTerrainFt = cotasConocidas.length ? Math.max(...cotasConocidas) : null;
      if (maxTerrainFt === null) {
        setRouteProfileStatus("error");
        return;
      }
      setCruiseAltFt(Math.min(ROUTE_CEILING, Math.ceil((maxTerrainFt + 1000) / 500) * 500));
      setCruiseTempC(Math.round(((origin.tempC || 15) + (destination.tempC || 15)) / 2));
    } catch (e) {
      console.error("No se pudo generar el perfil de ruta:", e);
      setRouteProfileStatus("error");
    }
  };

  // Planes de vuelo (rutas) guardados — usa el almacenamiento persistente del artefacto si está disponible
  // (personal, no compartido); si esta app corre fuera de Claude.ai, no existe window.storage y esto queda
  // solo en memoria de la sesión — para producción hace falta guardar esto en tu propio backend.
  const ROUTES_STORAGE_KEY = "helicalc:savedRoutes";
  const [savedRoutes, setSavedRoutesState] = useState([]);
  const [newRouteName, setNewRouteName] = useState("");
  useEffect(() => {
    (async () => {
      try {
        if (typeof window !== "undefined" && window.storage) {
          const res = await window.storage.get(ROUTES_STORAGE_KEY, false);
          if (res && res.value) setSavedRoutesState(JSON.parse(res.value));
        }
      } catch { /* sin planes guardados aún, o almacenamiento no disponible en este entorno */ }
    })();
  }, []);
  const persistRoutes = async (routes) => {
    setSavedRoutesState(routes);
    try {
      if (typeof window !== "undefined" && window.storage) await window.storage.set(ROUTES_STORAGE_KEY, JSON.stringify(routes), false);
    } catch { /* no se pudo guardar — sigue disponible solo en esta sesión */ }
  };
  const saveCurrentRoute = () => {
    if (!newRouteName.trim()) return;
    const entry = {
      id: `r_${Date.now()}`, name: newRouteName.trim(), waypoints, cruiseAltFt, cruiseTempC, groundspeedKt,
      activeProfileId, matricula, assignedPilotId, savedAt: new Date().toISOString(),
    };
    persistRoutes([entry, ...savedRoutes]);
    setNewRouteName("");
  };
  const loadRoute = (r) => {
    // Una ruta guardada con otro formato —o a la que le falta el arreglo— pasa
    // igual por el portero, así cargarla nunca puede dejar la app sin ruta.
    setWaypoints(r?.waypoints, { intencional: true });
    setCruiseAltFt(r.cruiseAltFt);
    setCruiseTempC(r.cruiseTempC);
    setGroundspeedKt(r.groundspeedKt);
    if (r.activeProfileId) setActiveProfileId(r.activeProfileId);
    if (r.matricula) setMatricula(r.matricula);
    if (r.assignedPilotId) setAssignedPilotId(r.assignedPilotId);
  };
  const deleteRoute = (id) => persistRoutes(savedRoutes.filter((r) => r.id !== id));

  // Informes de auditoría estilo BARS: una foto completa del análisis de ESTE vuelo (ruta, pesos OGE/IGE,
  // combustible, matriz de riesgo completa, aeronave, piloto y su firma) — separado del plan de vuelo guardado
  // y del registro de horas/ciclos, porque esto es específicamente el respaldo para una auditoría.
  const AUDIT_STORAGE_KEY = "helicalc:auditReports";
  const [auditReports, setAuditReportsState] = useState([]);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        if (typeof window !== "undefined" && window.storage) {
          const res = await window.storage.get(AUDIT_STORAGE_KEY, false);
          if (res && res.value) setAuditReportsState(JSON.parse(res.value));
        }
      } catch { /* sin informes guardados aún, o almacenamiento no disponible en este entorno */ }
    })();
  }, []);
  const persistAuditReports = async (reports) => {
    setAuditReportsState(reports);
    try {
      if (typeof window !== "undefined" && window.storage) await window.storage.set(AUDIT_STORAGE_KEY, JSON.stringify(reports), false);
    } catch { /* no se pudo guardar — sigue disponible solo en esta sesión */ }
  };
  const deleteAuditReport = (id) => persistAuditReports(auditReports.filter((r) => r.id !== id));
  /* Edición a mano: el piloto puede borrar una coordenada del campo y volver
     a escribirla, así que acá la red de seguridad no se interpone. */
  const updateWp = (id, patch) => setWaypoints((ws) => ws.map((w) => (w.id === id ? { ...w, ...patch } : w)), { intencional: true });
  const addWp = () => setWaypoints((ws) => [
    ...ws.slice(0, -1),
    { id: Date.now(), name: `WAYPOINT ${ws.length - 1}`, lat: null, lon: null, elevFt: 0, tempC: 15, legNm: 20 },
    ws[ws.length - 1],
  ]);
  const removeWp = (id) => setWaypoints((ws) => (id === "origin" || id === "destination" ? ws : ws.filter((w) => w.id !== id)), { intencional: true });
  // Distancia real por tramo, calculada de las coordenadas (no del campo "Tramo (NM)" que se podía escribir a
  // mano y quedaba desactualizado apenas cambiabas el origen/destino en el mapa).
  const haversineNm = (a, b) => {
    if (!a || !b || a.lat === null || b.lat === null) return 0;
    const R_NM = 3440.065;
    const dLat = (b.lat - a.lat) * Math.PI / 180, dLon = (b.lon - a.lon) * Math.PI / 180;
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return 2 * R_NM * Math.asin(Math.sqrt(s));
  };
  const legDistancesNm = waypoints.map((w, i) => (i === 0 ? 0 : haversineNm(waypoints[i - 1], w)));
  const totalNm = legDistancesNm.reduce((s, d) => s + d, 0);
  /**
   * Velocidad de crucero pedida, acotada al máximo de la aeronave.
   *
   * El campo es la velocidad PROPIA (TAS). La velocidad sobre el suelo sale
   * después, del triángulo de velocidades con el viento en altura: son cosas
   * distintas y confundirlas da tiempos de vuelo equivocados en las dos
   * direcciones.
   */
  const velocidadPedida = acotarVelocidadCrucero(groundspeedKt, VELOCIDAD_CRUCERO_MAXIMA_KT);
  const tasKt = velocidadPedida.kt ?? 0;

  /* ─────────────────────────────────────────────────────────────────────────
   * ATMÓSFERA EN RUTA.
   *
   * Temperatura y viento al NIVEL DE CRUCERO, del pronóstico en altura. No se
   * extrapola desde la superficie salvo que no haya otra cosa: la atmósfera
   * real rara vez sigue el gradiente estándar, y una inversión térmica lo
   * invierte por completo.
   * ──────────────────────────────────────────────────────────────────────── */
  const [atmosferaRuta, setAtmosferaRuta] = usePersistedState("atmosferaRuta", null);
  const [atmosferaCargando, setAtmosferaCargando] = useState(false);

  const traerAtmosferaEnRuta = async () => {
    // Se consulta en el punto medio de la ruta: es más representativo del
    // crucero que cualquiera de los dos extremos.
    const conCoord = waypoints.filter((w) => w.lat !== null);
    if (conCoord.length === 0) {
      setAtmosferaRuta({ ok: false, error: "Fija al menos un punto de la ruta." });
      return;
    }
    const medio = conCoord[Math.floor(conCoord.length / 2)];
    setAtmosferaCargando(true);
    const r = await atmosferaEnAltura(medio.lat, medio.lon);
    setAtmosferaRuta(r ? { ...r, consultadoEn: new Date().toISOString(), punto: medio.name } : null);
    setAtmosferaCargando(false);
  };

  /* eteMin se define más abajo, después del viento: el tiempo de vuelo real
     depende de la velocidad SOBRE EL SUELO, no de la propia. */

  /** Condiciones interpoladas al nivel de crucero elegido. */
  const atmosferaCrucero = (() => {
    if (!atmosferaRuta?.ok || !Number.isFinite(cruiseAltFt)) return null;
    const temperatura = interpolarEnAltura(atmosferaRuta.niveles, cruiseAltFt, "temperatura");
    const velocidad = interpolarEnAltura(atmosferaRuta.niveles, cruiseAltFt, "velocidad");
    const direccion = interpolarDireccion(atmosferaRuta.niveles, cruiseAltFt);
    if (temperatura === null) return null;
    return {
      temperaturaC: Math.round(temperatura * 10) / 10,
      vientoKt: velocidad === null ? null : Math.round(velocidad),
      direccionGrados: direccion === null ? null : Math.round(direccion),
      desviacionIsaC: desviacionIsa(cruiseAltFt, temperatura),
      horaUtc: atmosferaRuta.horaUtc,
      punto: atmosferaRuta.punto,
    };
  })();

  /**
   * Efecto del viento sobre la ruta.
   *
   * La velocidad sobre el suelo no es la propia menos el viento de frente: con
   * viento cruzado hay que apuntar contra la deriva y eso también cuesta
   * velocidad. Se resuelve con el triángulo de velocidades.
   */
  const vientoEnRuta = (() => {
    if (!atmosferaCrucero || !Number.isFinite(atmosferaCrucero.vientoKt) || !Number.isFinite(atmosferaCrucero.direccionGrados)) return null;
    // Rumbo directo entre despegue y aterrizaje. Con waypoints intermedios
    // cada tramo tiene su propio rumbo y su propia componente; acá se informa
    // el del tramo directo, y la app lo dice para que nadie lo tome por el
    // promedio real de una ruta quebrada.
    const o = waypoints[0];
    const d = waypoints[waypoints.length - 1];
    if (!o || !d || o.lat === null || d.lat === null || !(tasKt > 0)) return null;
    const rad = (g) => (g * Math.PI) / 180;
    const dLon = rad(d.lon - o.lon);
    const y = Math.sin(dLon) * Math.cos(rad(d.lat));
    const x = Math.cos(rad(o.lat)) * Math.sin(rad(d.lat)) - Math.sin(rad(o.lat)) * Math.cos(rad(d.lat)) * Math.cos(dLon);
    const rumbo = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
    const comp = componentesDeViento(rumbo, atmosferaCrucero.direccionGrados, atmosferaCrucero.vientoKt);
    const gs = velocidadSobreSuelo(tasKt, rumbo, atmosferaCrucero.direccionGrados, atmosferaCrucero.vientoKt);
    return {
      ...comp,
      rumboDirecto: Math.round(rumbo),
      velocidadSueloKt: gs,
      eteConVientoMin: gs && totalNm > 0 ? Math.round((totalNm / gs) * 60) : null,
      variosTramos: waypoints.filter((w) => w.lat !== null).length > 2,
    };
  })();

  /**
   * Velocidad sobre el suelo efectiva y tiempo de vuelo.
   *
   * Si hay viento en altura, el tiempo sale de la velocidad SOBRE EL SUELO. Sin
   * viento cargado se usa la propia, que es lo mismo que suponer calma — y la
   * app lo dice, porque suponer calma en una ruta de cordillera subestima el
   * tiempo y con él el combustible requerido.
   */
  const velocidadSueloEfectivaKt = vientoEnRuta?.velocidadSueloKt ?? tasKt;
  const eteMin = velocidadSueloEfectivaKt > 0 ? (totalNm / velocidadSueloEfectivaKt) * 60 : 0;
  const eteConViento = Boolean(vientoEnRuta?.velocidadSueloKt);
  const origin = waypoints[0];
  const destination = waypoints[waypoints.length - 1];

  /* El perfil vertical se recalcula solo cuando cambia algo que lo afecta.
     Sin memoizar se rehacía en cada render —o sea en cada tecla de cualquier
     campo de la app— y eso se siente como que la pantalla se traba. */
  const perfilVertical = useMemo(() => calcularPerfilVertical({
    perfilTerreno: routeProfile || [],
    totalNm,
    // Sin `?? 0`: una cota desconocida hacía que el perfil se calculara como si
    // el punto estuviera al nivel del mar. Para un helipuerto a 9 000 ft eso
    // produce un ascenso y un consumo completamente equivocados, y con aspecto
    // de estar bien. Ahora la falta de cota invalida el cálculo y se ve.
    elevSalidaFt: origin?.elevFt,
    elevLlegadaFt: destination?.elevFt,
    cruceroFt: cruiseAltFt,
    ascenso, descenso,
    margenFt: margenLibramientoFt,
    todManualNm,
    inicioAscensoNm,
  }), [routeProfile, totalNm, origin?.elevFt, destination?.elevFt, cruiseAltFt, ascenso, descenso, margenLibramientoFt, todManualNm, inicioAscensoNm]);

  /**
   * Rango real del eje X del gráfico de perfil.
   *
   * El eje va de 0 a la ÚLTIMA MUESTRA del perfil de terreno, que no es lo
   * mismo que `totalNm`: la distancia total la calcula el legado sumando
   * tramos, y las muestras las genera el servicio de terreno con su propia
   * cuenta y además se ralean cuando la ruta es larga. Basta una décima de
   * diferencia para que un extremo quede fuera del dominio.
   *
   * Y cuando un extremo de un `ReferenceLine segment` cae fuera del dominio,
   * Recharts NO recorta el segmento: lo descarta entero. Por eso el ascenso
   * —cuyos dos extremos caen bien adentro— se dibujaba y el descenso, que
   * termina justo en el borde, no.
   */
  const rangoEjeX = (() => {
    if (!Array.isArray(routeProfile) || routeProfile.length === 0) return { min: 0, max: totalNm };
    const distancias = routeProfile.map((p) => p.distNm).filter(Number.isFinite);
    return distancias.length
      ? { min: Math.min(...distancias), max: Math.max(...distancias) }
      : { min: 0, max: totalNm };
  })();
  /** Sujeta una distancia al rango dibujable, para que el segmento no se caiga. */
  const enEje = (nm) => Math.min(rangoEjeX.max, Math.max(rangoEjeX.min, nm));

  /**
   * Cotas de los extremos para dibujar la senda.
   *
   * La segunda causa por la que un segmento no se dibuja: una coordenada que
   * no es un número. Si el punto de aterrizaje se fijó desde "mis puntos" y la
   * consulta de elevación falló, `elevFt` queda en null y Recharts descarta el
   * segmento entero, sin decir nada.
   *
   * Se cae a la última cota del perfil de terreno, que sí existe, y si tampoco
   * la hay el gráfico lo declara en vez de quedarse mudo.
   */
  const cotaExtremo = (wp, indiceMuestra) => {
    if (Number.isFinite(wp?.elevFt)) return wp.elevFt;
    const muestra = routeProfile?.[indiceMuestra];
    return Number.isFinite(muestra?.elevFt) ? muestra.elevFt : null;
  };
  const cotaSalidaGrafico = cotaExtremo(origin, 0);
  const cotaLlegadaGrafico = cotaExtremo(destination, (routeProfile?.length || 1) - 1);
  const sendaDibujable = perfilVertical.valido
    && Number.isFinite(cotaSalidaGrafico)
    && Number.isFinite(cotaLlegadaGrafico)
    && Number.isFinite(cruiseAltFt);


  // ---- Aeródromos ----
  const [airports, setAirports] = useState(SEED_AIRPORTS);
  const [newAirport, setNewAirport] = useState({ name: "", icao: "", lat: "", lon: "", elevFt: "", fir: "", tma: "", ctr: "", freq: "", pista: "" });
  const addAirport = () => {
    if (!newAirport.name || newAirport.lat === "" || newAirport.lon === "") return;
    setAirports((as) => [...as, { ...newAirport, id: `apt_${Date.now()}`, lat: Number(newAirport.lat), lon: Number(newAirport.lon), elevFt: Number(newAirport.elevFt) || 0 }]);
    setNewAirport({ name: "", icao: "", lat: "", lon: "", elevFt: "", fir: "", tma: "", ctr: "", freq: "", pista: "" });
  };
  const [importInfo, setImportInfo] = useState("");
  const importAirportsCsv = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        // Mapeo flexible: acepta encabezados de OurAirports (ident, name, latitude_deg, longitude_deg, elevation_ft)
        // o encabezados propios (icao, name, lat, lon, elevFt).
        const rows = res.data.map((r, i) => ({
          id: `imp_${Date.now()}_${i}`,
          icao: r.icao || r.ident || r.gps_code || "",
          name: r.name || r.airport_name || "",
          lat: Number(r.lat ?? r.latitude_deg), lon: Number(r.lon ?? r.longitude_deg),
          elevFt: Number(r.elevFt ?? r.elevation_ft) || 0,
          fir: r.fir || "", tma: r.tma || "", ctr: r.ctr || "", freq: r.freq || r.frequency || "", pista: r.pista || r.runway || "",
        })).filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lon));
        setAirports((as) => [...as, ...rows]);
        setImportInfo(`Se importaron ${rows.length} aeródromos de ${file.name}.`);
      },
      error: (err) => setImportInfo(`Error al leer el CSV: ${err.message}`),
    });
    e.target.value = "";
  };

  // ---- Vías aéreas (VFR/IFR, baja/alta) — el prototipo no trae datos reales, solo la capa e importador ----
  const [airways, setAirways] = useState([]);
  const [airwayLayers, setAirwayLayers] = useState({ VFR: true, IFR: true, baja: true, alta: true });
  const importAirwaysCsv = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        // Encabezados esperados: id, name, type(VFR/IFR), level(baja/alta), points ("lat1,lon1;lat2,lon2;...")
        const rows = res.data.map((r, i) => ({
          id: r.id || `awy_${Date.now()}_${i}`, name: r.name || "", type: (r.type || "VFR").toUpperCase(),
          level: (r.level || "baja").toLowerCase(),
          points: (r.points || "").split(";").map((pt) => pt.split(",").map(Number)).filter((p) => p.length === 2 && p.every(Number.isFinite)).map(([lat, lon]) => ({ lat, lon })),
        })).filter((r) => r.points.length >= 2);
        setAirways((aw) => [...aw, ...rows]);
        setImportInfo(`Se importaron ${rows.length} vías aéreas de ${file.name}.`);
      },
      error: (err) => setImportInfo(`Error al leer el CSV: ${err.message}`),
    });
    e.target.value = "";
  };


  // Fuentes reales, abiertas y descargables (dominio público CC0, espejo estático de OurAirports.com):
  //  https://davidmegginson.github.io/ourairports-data/airports.csv
  //  https://davidmegginson.github.io/ourairports-data/airport-frequencies.csv
  //  https://davidmegginson.github.io/ourairports-data/runways.csv
  // Se descargan y se cargan aquí como CSV; frecuencias/pistas se unen por el código (ident/icao).
  const importFrequenciesCsv = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const byIcao = {};
        res.data.forEach((r) => {
          const icao = r.airport_ident || r.icao;
          if (!icao) return;
          const label = `${r.type || ""} ${r.frequency_mhz || r.frequency || ""}`.trim();
          if (!label) return;
          byIcao[icao] = byIcao[icao] ? `${byIcao[icao]}; ${label}` : label;
        });
        let matched = 0;
        setAirports((as) => as.map((a) => {
          if (byIcao[a.icao]) { matched++; return { ...a, freq: a.freq ? `${a.freq}; ${byIcao[a.icao]}` : byIcao[a.icao] }; }
          return a;
        }));
        setImportInfo(`Frecuencias unidas a ${matched} aeródromos ya cargados (de ${file.name}). Importa primero los aeródromos.`);
      },
      error: (err) => setImportInfo(`Error al leer el CSV: ${err.message}`),
    });
    e.target.value = "";
  };
  const importRunwaysCsv = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const byIcao = {};
        res.data.forEach((r) => {
          const icao = r.airport_ident || r.icao;
          if (!icao) return;
          const label = `${r.le_ident || ""}/${r.he_ident || ""} ${r.length_ft ? Math.round(r.length_ft) + "ft" : ""}`.trim();
          if (!label) return;
          byIcao[icao] = byIcao[icao] ? `${byIcao[icao]}; ${label}` : label;
        });
        let matched = 0;
        setAirports((as) => as.map((a) => {
          if (byIcao[a.icao]) { matched++; return { ...a, pista: a.pista ? `${a.pista}; ${byIcao[a.icao]}` : byIcao[a.icao] }; }
          return a;
        }));
        setImportInfo(`Pistas unidas a ${matched} aeródromos ya cargados (de ${file.name}). Importa primero los aeródromos.`);
      },
      error: (err) => setImportInfo(`Error al leer el CSV: ${err.message}`),
    });
    e.target.value = "";
  };
  const applyAirportToWp = (wpId, airportId) => {
    const apt = airports.find((a) => a.id === airportId);
    if (!apt) { updateWp(wpId, { airportId: "" }); return; }
    updateWp(wpId, { airportId, name: apt.icao || apt.name, lat: apt.lat, lon: apt.lon, elevFt: apt.elevFt });
  };
  const addPointToRoute = (lat, lon, elevFt, name, target) => {
    /* ═══ UN PUNTO SIN COORDENADAS NO ES UN PUNTO ═══

       El nombre por defecto se armaba con `lat.toFixed(2)` directo, que es el
       mismo accidente del `elevFt.toLocaleString()` del KMZ: con la coordenada
       ausente revienta la pantalla entera en vez de rechazar un punto.

       `nombreDePunto` devuelve null sin coordenadas, y acá eso corta: agregar
       a la ruta un punto sin posición sería agregar algo que no se puede
       dibujar ni medir. */
    const automatico = nombreDePunto(lat, lon);
    if (!name && !automatico) return;
    const cleanName = (name || automatico).toString().toUpperCase().slice(0, 24);
    if (target === "origin") { setEndpoint("origin", lat, lon, elevFt, cleanName); return; }
    if (target === "destination") { setEndpoint("destination", lat, lon, elevFt, cleanName); return; }
    if (target === "waypoint") { addIntermediateWaypoint(lat, lon, elevFt, cleanName); return; }
    if (waypoints[0]?.lat === null) { setEndpoint("origin", lat, lon, elevFt, cleanName); return; }
    if (waypoints[waypoints.length - 1]?.lat === null) { setEndpoint("destination", lat, lon, elevFt, cleanName); return; }
    addIntermediateWaypoint(lat, lon, elevFt, cleanName);
  };

  // ---- Peso y Balance ----
  const [pilotKg, setPilotKg] = useState(170);
  // Manifiesto de pasajeros: nombre, documento de identidad, estación (delante/atrás), peso, y equipaje CON el
  // compartimento de carga elegido por el pasajero (no se reparte solo) — cada compartimento empieza en 0 al
  // iniciar la planificación (se recalcula siempre desde el manifiesto, nunca queda un valor residual).
  const [passengers, setPassengers] = useState([]);
  /**
   * Un pasajero nuevo nace en la PRIMERA estación del tipo activo.
   *
   * Antes nacía en "atras", que solo existe en el AS350. En el BK117 esa clave
   * no corresponde a ninguna estación: el pasajero pesaba pero no aportaba
   * momento, y el CG salía adelantado sin que nada avisara.
   */
  const addPassenger = () => setPassengers((ps) => [...ps, {
    id: `pax_${Date.now()}`,
    nombre: "", documento: "",
    station: estacionesDelTipo[0]?.key || "atras",
    pesoKg: 75, equipajeKg: 0,
    compartimento: activeProfile.cargoCompartments[0]?.key || "",
  }]);
  const removePassenger = (id) => setPassengers((ps) => ps.filter((p) => p.id !== id));

  /**
   * Al cambiar de tipo, los pasajeros en estaciones que el tipo nuevo no tiene
   * se reubican en la primera.
   *
   * Sin esto, pasar de AS350 a BK117 dejaba a todos en "atras" —una estación
   * inexistente en el BK117— y su peso entraba al total sin momento: el CG
   * salía adelantado y el vuelo parecía más seguro de lo que era.
   */
  useEffect(() => {
    const validas = new Set(estacionesDelTipo.map((e) => e.key));
    setPassengers((ps) => {
      if (ps.every((p) => validas.has(p.station))) return ps;
      const primera = estacionesDelTipo[0]?.key;
      return ps.map((p) => (validas.has(p.station) ? p : { ...p, station: primera }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile.id]);
  /**
   * Estaciones de asiento DEL TIPO ACTIVO.
   *
   * Antes había dos claves escritas en el código —"delante" y "atras"—, que es
   * la forma del AS350. El BK117 tiene NUEVE estaciones con brazos de 3,492 a
   * 5,229 m: con dos bloques no entra, y dos personas en la fila de popa
   * mueven el momento 278 kg·m más que dos adelante.
   *
   * Ahora el manifiesto se construye a partir del perfil: cada tipo declara
   * sus estaciones y la tabla se dibuja con las que tenga.
   */
  const estacionesDelTipo = Array.isArray(activeProfile.estacionesPax) && activeProfile.estacionesPax.length > 0
    ? activeProfile.estacionesPax
    : ESTACIONES_AS350B3;

  /** Peso por estación, cualquiera sea la cantidad de estaciones del tipo. */
  const pesoPorEstacion = estacionesDelTipo.reduce((acc, e) => {
    acc[e.key] = passengers
      .filter((p) => p.station === e.key)
      .reduce((s, p) => s + (Number(p.pesoKg) || 0), 0);
    return acc;
  }, {});

  // Compatibilidad con el resto de la app, que todavía habla de delante/atrás.
  const paxFrontKg = pesoPorEstacion.delante ?? 0;
  const paxRearKg = pesoPorEstacion.atras ?? 0;
  const totalBaggageKg = passengers.reduce((s, p) => s + (Number(p.equipajeKg) || 0), 0);
  // Total por compartimento (siempre recalculado desde cero a partir del manifiesto actual) + estado de sobrepeso.
  /**
   * Totales por compartimento.
   *
   * La canasta es un ACCESORIO: existe como compartimento solo cuando está
   * instalada, y su total suma dos cosas —el equipaje que los pasajeros le
   * asignan y la carga suelta que se escribe en el manifiesto—.
   *
   * Antes había dos caminos separados: el compartimento sumaba el equipaje y
   * la carga suelta entraba al peso y balance por su cuenta. Resultado: el
   * límite de 120 kg solo miraba la carga suelta, así que 100 kg sueltos más
   * 100 kg de equipaje asignado pasaban el control con 200 kg en la canasta.
   */
  const compartmentTotals = activeProfile.cargoCompartments
    .map((c) => {
      const kg = passengers.filter((p) => p.compartimento === c.key).reduce((s, p) => s + (Number(p.equipajeKg) || 0), 0);
      return { ...c, kg, overweight: kg > c.maxKg };
    });
  // Cuánto le queda libre a un compartimento SIN contar lo que ya carga este mismo pasajero — se usa para topar
  // el equipaje que se puede escribir, en vez de solo avisar después de que ya se pasó del límite.
  const remainingCapacityFor = (passengerId, compartimentoKey) => {
    const compartimento = activeProfile.cargoCompartments.find((c) => c.key === compartimentoKey);
    if (!compartimento) return Infinity;
    const usedByOthers = passengers.filter((p) => p.compartimento === compartimentoKey && p.id !== passengerId).reduce((s, p) => s + (Number(p.equipajeKg) || 0), 0);
    return Math.max(0, compartimento.maxKg - usedByOthers);
  };
  const [externalCargoKg, setExternalCargoKg] = useState(0);
  const [fuelLiters, setFuelLiters] = useState(540);
  /* ------------------------------------------------------------------------
     UNIDADES — un solo sistema para toda la aplicación.

     Antes había DOS selectores independientes: uno de combustible (L/gal/kg/lb)
     y otro de peso derivado. Por eso se podía cargar en litros y leer el
     requerido en kilos, en la misma pantalla. Mezclar unidades de combustible
     es una de las causas clásicas de accidente — el Gimli Glider se quedó sin
     combustible en vuelo exactamente por confundir kilos con libras.

     Ahora hay un solo sistema (métrico o imperial) que rige TODA la app, y el
     combustible se muestra SIEMPRE en volumen y masa a la vez, con la densidad
     usada a la vista. No hay que elegir ni recordar en qué unidad se estaba.
     ------------------------------------------------------------------------ */
  const [unitSystem, setUnitSystem] = usePersistedState("sistemaUnidades", "metrico");
  const GAL_TO_L = 3.785411784;
  const LB_PER_KG = 2.2046226218;

  const esImperial = unitSystem === "imperial";
  const weightUnitLabel = esImperial ? "lb" : "kg";
  const volUnitLabel = esImperial ? "gal US" : "L";
  const fmtWeight = (kg) => (esImperial ? kg * LB_PER_KG : kg);
  const fmtVol = (litros) => (esImperial ? litros / GAL_TO_L : litros);
  const volToLiters = (v) => (esImperial ? Number(v) * GAL_TO_L : Number(v));
  const weightToKg = (w) => (esImperial ? Number(w) / LB_PER_KG : Number(w));

  /* ─────────────────────────────────────────────────────────────────────────
   * COMBUSTIBLE — cuatro unidades, un solo punto de ajuste.
   *
   * El dato vive en LITROS y se convierte al mostrar. Guardar en la unidad
   * elegida obligaría a reconvertir en cada cambio de unidad, y cada
   * reconversión arrastra redondeo: 540 L → gal → lb → L terminaría en 539,8.
   *
   * Este panel es el ÚNICO lugar donde se ajusta el combustible. El campo que
   * había en "Carga → Pesos" quedó de solo lectura: dos controles para el
   * mismo dato es cómo se termina cargando en litros y leyendo en kilos.
   * ──────────────────────────────────────────────────────────────────────── */
  const [unidadCombustible, setUnidadCombustible] = usePersistedState("unidadCombustible", "L");
  const fuelCapacityL = Number(activeProfile.fuelCapacityL) || 540;
  const densidadKgL = Number(activeProfile.fuelDensity) || null;
  const fuelPct = porcentajeDeCapacidad(fuelLiters, fuelCapacityL) ?? 0;

  const unidadActual = UNIDADES_COMBUSTIBLE.find((u) => u.clave === unidadCombustible) || UNIDADES_COMBUSTIBLE[0];
  /** Litros a la unidad elegida en este panel, redondeado para mostrar. */
  const enUnidadCrucero = (litros) => {
    const v = desdeLitros(litros, unidadActual.clave, densidadKgL);
    return v === null ? "s/d" : Math.round(v * 10) / 10;
  };
  const fuelInputValue = (() => {
    const v = desdeLitros(fuelLiters, unidadActual.clave, densidadKgL);
    return v === null ? null : Math.round(v * 10) / 10;
  })();
  const fuelInputLabel = unidadActual.simbolo;

  /** Capacidad del tanque en las cuatro unidades, para mostrarla de referencia. */
  const capacidadTodas = enTodasLasUnidades(fuelCapacityL, densidadKgL);
  const embarcadoTodas = enTodasLasUnidades(fuelLiters, densidadKgL);
  const chequeoTanque = cabeEnElTanque(fuelLiters, fuelCapacityL);

  const setFuelFromInput = (v) => {
    const litros = aLitros(Number(v), unidadActual.clave, densidadKgL);
    if (litros === null) return;
    setFuelLiters(Math.max(0, litros));
  };
  const setFuelFromPct = (pct) => {
    const litros = litrosDesdePorcentaje(Number(pct), fuelCapacityL);
    if (litros !== null) setFuelLiters(litros);
  };

  const fuelKg = fuelLiters * activeProfile.fuelDensity;
  const items = [
    { label: "Vacío", kg: pesoVacio.kg, arm: pesoVacio.arm },
    { label: "Piloto", kg: pilotKg, arm: activeProfile.arms.pilot },
    /* Una partida por estación del tipo, con SU brazo. Para el AS350 son dos;
       para el BK117, nueve. */
    ...estacionesDelTipo.map((e) => ({
      label: `Pax ${e.label.toLowerCase()}`,
      kg: pesoPorEstacion[e.key] || 0,
      arm: e.brazoM,
    })),
    /* Brazo propio del compartimento si el formato lo publica; si no, el
       genérico del perfil. El BK117 publica un brazo por compartimento —4,34 m
       la canasta, 5,835 m el stowage de popa— y agruparlos bajo un solo brazo
       lateral perdería casi un metro y medio de diferencia. */
    ...compartmentTotals.map((c) => ({
      label: `Equipaje ${c.label.toLowerCase()}`,
      kg: c.kg,
      arm: Number.isFinite(c.brazoM) ? c.brazoM : activeProfile.arms[c.armKey],
    })),

    { label: "Carga externa", kg: externalCargoKg, arm: activeProfile.arms.external },
    {
      label: "Combustible",
      kg: fuelKg,
      /* En el BK117 el brazo del tanque NO es constante: va de 4,798 m casi
         vacío a 4,563 m lleno. Usar el valor fijo del perfil daría un momento
         equivocado en todo el rango, y el error crece con lo embarcado. */
      arm: activeProfile.brazoCombustibleVariable
        ? (brazoCombustible(fuelKg) ?? activeProfile.arms.fuel)
        : activeProfile.arms.fuel,
    },
  ];
  const totalKg = items.reduce((s, it) => s + it.kg, 0);
  const totalMoment = items.reduce((s, it) => s + it.kg * it.arm, 0);
  const cg = totalKg > 0 ? totalMoment / totalKg : 0;

  const env = activeProfile.envelope;
  const usingExternal = externalCargoKg > 0;
  const maxAllowedKg = usingExternal ? activeProfile.maxExternalKg : activeProfile.maxInternalKg;
  const rearwardPoints = usingExternal ? env.rearwardExternal : env.rearwardInternal;
  const forwardPoints = env.forward.filter((p) => p[0] <= (usingExternal ? activeProfile.maxExternalKg : activeProfile.maxInternalKg));
  const fwdLimitSeries = env ? forwardPoints.map(([w, c]) => ({ w, fwd: c })) : [];
  const aftLimitSeries = env ? rearwardPoints.map(([w, c]) => ({ w, aft: c })) : [];
  // Un solo trazo cerrado (carga interna, como en la Fig. 2-1 del manual): sigue el límite delantero subiendo,
  // el tope de peso, el límite trasero bajando, y cierra de vuelta al punto de partida.
  const closedEnvelopePoints = env ? [
    ...env.forward.filter((p) => p[0] <= activeProfile.maxInternalKg).map(([w, c]) => ({ x: c, y: w })),
    ...[...env.rearwardInternal].reverse().map(([w, c]) => ({ x: c, y: w })),
    { x: env.forward[0][1], y: env.forward[0][0] },
  ] : [];
  // Extensión con carga externa (hasta 2800 kg, con carga liberable) — mismo trazo cerrado pero con los puntos
  // hasta 2800 kg del manual.
  const closedEnvelopeExternalPoints = env ? [
    ...env.forward.filter((p) => p[0] <= activeProfile.maxExternalKg).map(([w, c]) => ({ x: c, y: w })),
    ...[...env.rearwardExternal].reverse().map(([w, c]) => ({ x: c, y: w })),
    { x: env.forward[0][1], y: env.forward[0][0] },
  ] : [];
  /**
   * Masa mínima aprobada.
   *
   * La aplicación solo comprobaba el MÁXIMO. Un helicóptero demasiado liviano
   * también está fuera de límites: el BK117 no puede volar por debajo de
   * 1700 kg (RFM 2.6.2), y un vuelo de traslado sin carga ni pasajeros puede
   * caer ahí sin que nada avise.
   */
  const masaMinimaKg = activeProfile.masaMinimaKg ?? null;
  const chequeoMasa = masaMinimaKg !== null && Number.isFinite(totalKg)
    ? (totalKg < masaMinimaKg
      ? { dentro: false, motivo: `${Math.round(totalKg)} kg está por debajo de la masa mínima aprobada de ${masaMinimaKg} kg para ${activeProfile.tipo}.` }
      : { dentro: true, motivo: null })
    : { dentro: null, motivo: null };

  const insideEnvelope = env ? (() => {
    const fwdLimit = limitAtWeight(env.forward, totalKg);
    const aftLimit = limitAtWeight(rearwardPoints, totalKg);
    if (fwdLimit === null || aftLimit === null) return null;
    return cg >= Math.min(fwdLimit, aftLimit) && cg <= Math.max(fwdLimit, aftLimit) && totalKg <= maxAllowedKg;
  })() : null;

  /**
   * Performance en un punto.
   *
   * Sin carta digitalizada para el tipo NO se devuelve un número: se devuelve
   * "sin carta" con el motivo. Un tipo nuevo entra a la aplicación con las
   * cartas en null, y hasta que alguien las cargue del RFM la app tiene que
   * decir que no sabe, no inventar un peso máximo.
   */
  const sinCarta = (cual) => ({
    value: null,
    outOfRange: true,
    reason: `sin carta ${cual} digitalizada para ${activeProfile.tipo}`,
    sinCarta: true,
  });
  const perfAt = (wp) => {
    if (wp.lat === null) {
      return {
        oge: { value: null, outOfRange: true, reason: "punto sin definir" },
        ige: { value: null, outOfRange: true, reason: "punto sin definir" },
      };
    }
    return {
      oge: activeProfile.ogeTable ? tableLookup(activeProfile.ogeTable, wp.elevFt, wp.tempC) : sinCarta("OGE"),
      ige: activeProfile.igeTable ? tableLookup(activeProfile.igeTable, wp.elevFt, wp.tempC) : sinCarta("IGE"),
    };
  };
  const perfOrigin = perfAt(origin);
  const perfDestination = perfAt(destination);

  /**
   * Peso máximo REAL en cada punto: el menor entre lo que dan las cartas y el
   * máximo estructural. La carta de estacionario llega a 2800 kg porque
   * contempla carga externa; con carga interna el límite es otro y manda ese.
   */
  const maximoEstructuralKg = usingExternal ? activeProfile.maxExternalKg : activeProfile.maxInternalKg;

  /**
   * Consumo eléctrico sobre 50 A.
   *
   * La carta IGE del RFM (Figura 5) resta 50 kg al peso máximo en esa
   * condición. Con faro de búsqueda, equipo calefaccionado o cabrestante se
   * pasa de 50 A sin dificultad, y 50 kg son media persona.
   */
  const [consumoElectricoAlto, setConsumoElectricoAlto] = usePersistedState("consumoElectricoAlto", false);
  const conCorreccionElectrica = (kg) => corregirPorConsumoElectrico(kg, consumoElectricoAlto).pesoKg;
  const limitePunto = (perf) => limiteDePeso({
    ogeKg: perf.oge.outOfRange ? null : conCorreccionElectrica(perf.oge.value),
    igeKg: perf.ige.outOfRange ? null : conCorreccionElectrica(perf.ige.value),
    maximoEstructuralKg,
  });
  const limiteOrigen = limitePunto(perfOrigin);
  const limiteDestino = limitePunto(perfDestination);
  const wbStatus = (perf) => {
    if (perf.oge.outOfRange || perf.oge.value === null) return "TOLERABLE";
    if (totalKg > perf.oge.value) return "INTOLERABLE";
    return "ACEPTABLE";
  };

  // ---- Performance explorador ----
  // El nivel de vuelo y temperatura de Performance son los MISMOS de "Planificación de ruta de vuelo" (cruiseAltFt/
  // cruiseTempC) — no hay un segundo valor independiente; ajustar el slider aquí también actualiza la ruta.
  const chartData = useMemo(() => seriesForTemp(activeProfile.ogeTable, activeProfile.igeTable, cruiseTempC), [activeProfile, cruiseTempC]);
  const ogeExpl = tableLookup(activeProfile.ogeTable, cruiseAltFt, cruiseTempC);
  const igeExpl = tableLookup(activeProfile.igeTable, cruiseAltFt, cruiseTempC);

  // ---- Combustible ----
  const [reserveMin, setReserveMin] = useState(30);
  const fuelRateAt = tableLookup(activeProfile.fuelTable, cruiseAltFt, cruiseTempC);
  const autonomyMin = fuelRateAt.outOfRange ? null : (fuelKg / fuelRateAt.value) * 60;
  const usableMin = autonomyMin !== null ? autonomyMin - reserveMin : null;
  const requiredKg = fuelRateAt.outOfRange ? null : ((eteMin + reserveMin) / 60) * fuelRateAt.value;

  /* ─────────────────────────────────────────────────────────────────────────
   * PESO Y BALANCE AL ATERRIZAJE.
   *
   * El de despegue no dice si el aterrizaje es legal. Quemar combustible no
   * solo aliviana: mueve el CG, porque el tanque tiene su propio brazo. Y la
   * envolvente cambia con el peso, así que sus límites hay que releerlos al
   * peso de llegada. Se puede despegar dentro y llegar fuera.
   *
   * Solo se descuenta el combustible de la ETAPA, no la reserva: la reserva
   * llega al destino dentro del tanque y sigue pesando.
   * ──────────────────────────────────────────────────────────────────────── */
  const combustibleEtapaKg = fuelRateAt.outOfRange ? null : (eteMin / 60) * fuelRateAt.value;
  const wbDespegue = {
    items: items.map((i) => ({ ...i, etiqueta: i.label })),
    pesoTotalKg: totalKg,
    momentoTotal: totalMoment,
    cg,
    conCargaExterna: usingExternal,
    limiteDelantero: env ? limiteEnPeso(activeProfile.envelope?.forward, totalKg) : null,
    limiteTrasero: env ? limiteEnPeso(usingExternal ? activeProfile.envelope?.rearwardExternal : activeProfile.envelope?.rearwardInternal, totalKg) : null,
    dentroDeEnvolvente: insideEnvelope,
  };
  const wbAterrizaje = estadoEnAterrizaje(
    activeProfile, wbDespegue, combustibleEtapaKg, activeProfile.arms?.fuel,
  );
  const fuelMargin = requiredKg !== null ? fuelKg - requiredKg : null;

  // ---- Riesgo FRAT ----
  const [riskChoices, setRiskChoices] = useState(() => { const init = {}; RISK_GROUPS.forEach((g) => (init[g.key] = 0)); return init; });

  // Registro del SGS (Anexo 2 del IN 2255-I-00): un evento indeseable por fila, con sus precursores, defensas,
  // responsable, estado de implementación, y el nivel de riesgo antes y después de aplicar las medidas.
  // El registro de eventos indeseables se persiste: estaba en useState, así que
  // se vaciaba en cada recarga. Un reporte mensual o anual sobre una cartera
  // que no sobrevive a cerrar la pestaña no es un reporte, y la app además
  // mostraba "0 eventos" como si eso significara que no hay riesgos.
  const [smsEvents, setSmsEvents] = usePersistedState("eventosSgs", [], undefined, "eventosSgs");
  const addSmsEvent = (fromLibrary) => setSmsEvents((es) => [...es, {
    id: `sms_${Date.now()}`,
    ref: fromLibrary?.ref || "",
    descripcion: fromLibrary?.desc || "Nuevo evento indeseable",
    euCategorias: fromLibrary?.eu || [],
    // El bow-tie empieza en el PELIGRO, no en el evento: el evento temido es el
    // momento en que se pierde su control (SKYbrary/EUROCONTROL).
    peligro: "",
    precursores: "",
    // Dos de las ocho preguntas de la metodología: cómo pueden fallar las
    // propias barreras, y qué las sostiene. Una barrera que nadie verifica no
    // es una barrera, es una suposición.
    factoresEscalada: "",
    controlEscalada: "",
    medidasControl: "", medidasRecuperacion: "", medidasMitigacion: "",
    responsable: "",
    implementado: "No",
    gravedadInicial: 2, probabilidadInicial: 2, // Mayor / Reducido por defecto — ajustar caso por caso
    gravedadActual: 2, probabilidadActual: 2,
    costeIdx: 0, beneficioIdx: 0, // Bajo / Elevado por defecto
    // Sin fecha de alta no se puede armar un reporte mensual ni anual. Los
    // eventos viejos que no la tengan igual entran en todos los períodos: el
    // reporte los cuenta aparte en vez de dejarlos caer.
    creado: new Date().toISOString().slice(0, 10),
    revisadoEl: new Date().toISOString().slice(0, 10),
  }]);

  const updateSmsEvent = (id, patch) => setSmsEvents((es) => es.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeSmsEvent = (id) => setSmsEvents((es) => es.filter((e) => e.id !== id));
  const [smsBowTieId, setSmsBowTieId] = useState(null);

  /* ─────────────────────────────────────────────────────────────────────────
   * AUDITORÍAS Y NO CONFORMIDADES.
   *
   * Autoevaluación interna que reproduce la lógica de colores del programa
   * BARS. NO es un registro BARS: esa designación la emite la BARS Program
   * Office a partir de auditorías de auditores acreditados. Acá sirve para
   * llegar preparado a una auditoría real, nada más.
   * ──────────────────────────────────────────────────────────────────────── */
  const [auditorias, setAuditorias] = usePersistedState("auditorias", [], undefined, "auditorias");
  const [noConformidades, setNoConformidades] = usePersistedState("noConformidades", [], undefined, "noConformidades");
  const addAuditoria = () => setAuditorias((as) => [...as, {
    id: `aud_${Date.now()}`, fecha: new Date().toISOString().slice(0, 10),
    tipo: as.length === 0 ? "inicial" : "renovacion", antesDelVencimiento: true,
    auditor: "", empresaAuditora: "", alcance: "",
  }]);
  const updateAuditoria = (id, patch) => setAuditorias((as) => as.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const removeAuditoria = (id) => setAuditorias((as) => as.filter((a) => a.id !== id));
  const addNoConformidad = () => setNoConformidades((ns) => [...ns, {
    id: `nc_${Date.now()}`, referencia: "", prioridad: "P1", descripcion: "",
    plazoHasta: "", cerradaEl: null, responsable: "",
  }]);
  const updateNoConformidad = (id, patch) => setNoConformidades((ns) => ns.map((n) => (n.id === id ? { ...n, ...patch } : n)));
  const removeNoConformidad = (id) => setNoConformidades((ns) => ns.filter((n) => n.id !== id));

  const registroBars = clasificarRegistro({
    auditorias,
    noConformidades: noConformidades.map((n) => ({ ...n, plazoHasta: n.plazoHasta || null })),
  });
  const bowTie = resumenBowTie(smsEvents);

  // ---- Política de seguridad y responsable del SGS (Pilar 1 de un SMS: Política y objetivos) ----
  const [smsPolicy, setSmsPolicy] = usePersistedState("politicaSgs", { responsable: "", declaracion: "" });

  /* Período del reporte. Por defecto, el mes en curso. */
  const hoyISO = new Date().toISOString().slice(0, 10);
  const [periodoReporte, setPeriodoReporte] = useState({
    desde: `${hoyISO.slice(0, 7)}-01`,
    hasta: hoyISO,
    operador: "",
  });
  const aplicarPeriodo = (clave) => {
    const hoy = new Date();
    const anio = hoy.getFullYear();
    const mes = String(hoy.getMonth() + 1).padStart(2, "0");
    if (clave === "mes") setPeriodoReporte((p) => ({ ...p, desde: `${anio}-${mes}-01`, hasta: hoyISO }));
    if (clave === "anio") setPeriodoReporte((p) => ({ ...p, desde: `${anio}-01-01`, hasta: hoyISO }));
    if (clave === "todo") setPeriodoReporte((p) => ({ ...p, desde: "", hasta: "" }));
  };

  // ---- Reportes voluntarios de seguridad / ASR (Pilar 2: fuente interna de identificación de peligros) ----
  // Mismo motivo: un reporte voluntario de seguridad que desaparece al recargar
  // enseña a la tripulación a no reportar.
  const [smsReports, setSmsReports] = usePersistedState("reportesAsr", []);
  /* ═══ LAS FUNCIONES DEL REGISTRO VIEJO SE QUITARON ═══

     `addSmsReport` creaba reportes en el depósito anterior. Mientras existiera,
     el sistema no se consolidaba nunca: se migraba, y al siguiente uso volvía a
     haber dos listas.

     `removeSmsReport` era peor: BORRABA un reporte de seguridad sin dejar
     rastro. En el registro único no existe borrado — un reporte se CIERRA con
     un motivo, porque la confianza del que reporta se basa en que lo que
     escribió queda.

     `smsReports` se conserva solo para leer y migrar. */

  // ---- Indicadores de seguridad / KPIs (Pilar 3: Garantía de la seguridad) ----
  const smsKpis = {
    total: smsEvents.length,
    implementadosSi: smsEvents.filter((e) => e.implementado === "Sí").length,
    implementadosParcial: smsEvents.filter((e) => e.implementado === "Parcial").length,
    implementadosNo: smsEvents.filter((e) => e.implementado === "No").length,
    reportesAbiertos: smsReports.filter((r) => r.estado !== "Convertido a EI").length,
  };
  const smsNivelCounts = (campo) => {
    const counts = { ACEPTABLE: 0, MEDIO: 0, GRAVE: 0, INACEPTABLE: 0, "INACEPTABLE+": 0 };
    smsEvents.forEach((e) => {
      const nivel = campo === "inicial" ? smsRiskLevel(e.gravedadInicial, e.probabilidadInicial) : smsRiskLevel(e.gravedadActual, e.probabilidadActual);
      counts[nivel] = (counts[nivel] || 0) + 1;
    });
    return counts;
  };
  const smsEuCounts = () => {
    const counts = {};
    SMS_EU_CATEGORIES.forEach((c) => (counts[c.key] = 0));
    smsEvents.forEach((e) => e.euCategorias.forEach((k) => { counts[k] = (counts[k] || 0) + 1; }));
    return counts;
  };

  /* ───────────────────────────────────────────────────────────────────────
   * FRAT — factores derivados de la planificación.
   *
   * Topografía y reserva de combustible no se contestan a mano: salen del
   * perfil de terreno y del cálculo de combustible. Un FRAT enteramente manual
   * se contesta rápido y mal, siempre con la opción de siempre.
   *
   * Cuando el dato no está, el factor cae en la opción 0, que evalúa a GRAVE
   * (ámbar). Sin dato no es sin riesgo.
   * ────────────────────────────────────────────────────────────────────── */
  /**
   * Cota máxima del terreno a lo largo de la ruta.
   *
   * Se toma el mayor entre las cotas del perfil y las elevaciones de los puntos
   * de despegue y aterrizaje: un helipuerto en altura sigue siendo terreno alto
   * aunque el perfil todavía no se haya generado.
   *
   * Devuelve null —no -Infinity— cuando no hay ninguna cota leída. El reduce
   * con semilla -Infinity producía un número que la app mostraba como si fuera
   * una cota real y pintaba el terreno en verde.
   */
  const cotaMaximaRutaFt = (() => {
    const cotas = [];
    if (Array.isArray(routeProfile)) {
      routeProfile.forEach((p) => { if (Number.isFinite(p.elevFt)) cotas.push(p.elevFt); });
    }
    waypoints.forEach((w) => { if (w.lat !== null && Number.isFinite(w.elevFt)) cotas.push(w.elevFt); });
    return cotas.length ? Math.max(...cotas) : null;
  })();

  /* Viento del FRAT: el peor de los dos puntos, ráfaga incluida. */
  const vientoDeLosPuntos = [meteoPuntos.origin, meteoPuntos.destination]
    .filter((m) => m && m.ok && Number.isFinite(m.vientoParaRiesgoKt))
    .map((m) => m.vientoParaRiesgoKt);
  const vientoPeorKt = vientoDeLosPuntos.length ? Math.max(...vientoDeLosPuntos) : null;
  const vientoAuto = evaluarViento(vientoPeorKt, VIENTO_MAXIMO_KT);

  const topografiaAuto = clasificarTopografia(cotaMaximaRutaFt, TECHO_SERVICIO_FT);
  const reservaAuto = evaluarReserva(fuelMargin, reserveMin);
  const reservaIndice = RISK_GROUPS.find((g) => g.key === "reserva")
    .options.findIndex((o) => o.gravIdx === reservaAuto.gravedadIdx && o.probIdx === reservaAuto.probabilidadIdx);

  /** Índice efectivo: manda lo derivado sobre lo elegido a mano. */
  /* ─────────────────────────────────────────────────────────────────────────
   * EL METAR ALIMENTA LAS CASILLAS DE METEOROLOGÍA
   *
   * Sigue el mismo patrón que `topografia` y `viento`, que ya se derivan solos:
   * la propuesta manda mientras el piloto no conteste a mano, y la casilla se
   * muestra marcada como automática con la explicación debajo.
   *
   * `metarManual` guarda las casillas que el piloto tocó: una vez que decidió,
   * su respuesta no se pisa más. Él vio el cielo.
   * ──────────────────────────────────────────────────────────────────────── */
  const [metarManual, setMetarManual] = useState({});

  const propuestasMeteo = useMemo(
    /* `traerVarios` devuelve las respuestas indexadas por CÓDIGO OACI, no por
       `origin`/`destination`: buscarlas por esos nombres devolvía undefined y
       la casilla nunca se completaba. */
    () => proponerFrat({
      salida: oaciDespegue ? metar?.[oaciDespegue.toUpperCase()]?.crudo : null,
      destino: oaciAterrizaje ? metar?.[oaciAterrizaje.toUpperCase()]?.crudo : null,
    }),
    [metar, oaciDespegue, oaciAterrizaje],
  );

  const indiceDeGrupo = (g) => {
    if (g.key === "topografia") return topografiaAuto.indice;
    if (g.key === "reserva") return reservaIndice >= 0 ? reservaIndice : 0;
    if (g.key === "viento") return vientoAuto.indice;

    /* Meteorología de salida y destino: la propuesta del METAR manda mientras
       el piloto no haya contestado a mano. La de RUTA nunca se propone — un
       METAR describe un aeródromo, no el tramo entre los dos. */
    if ((g.key === "meteoSalida" || g.key === "meteoDestino") && !metarManual[g.key]) {
      const p = propuestasMeteo[g.key];
      if (p) return p.indice;
    }

    return riskChoices[g.key];
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * MITIGACIÓN POR FACTOR.
   *
   * La sección 8 de la IN 2255-I-00 dice que un riesgo de GRAVE para arriba
   * requiere una medida que lo baje al menos a MEDIO. Que un factor esté en
   * GRAVE no significa que el vuelo no salga: significa que hace falta una
   * medida y que quede registrada.
   *
   * La prevención baja la probabilidad; la protección baja la gravedad. Sin
   * texto declarado no baja nada.
   * ──────────────────────────────────────────────────────────────────────── */
  const [mitigaciones, setMitigaciones] = usePersistedState("mitigacionesFrat", {});
  const editarMitigacion = (clave, campo, valor) =>
    setMitigaciones((m) => ({ ...m, [clave]: { ...(m[clave] || {}), [campo]: valor } }));

  const selectedRisks = RISK_GROUPS.map((g) => {
    const opt = g.options[indiceDeGrupo(g)];
    const medidas = mitigaciones[g.key] || {};
    const mit = aplicarMitigacion({ gravedadIdx: opt.gravIdx, probabilidadIdx: opt.probIdx }, medidas);
    return {
      group: g, opt, medidas, mit,
      nivelInicial: mit.nivelInicial,
      // El nivel que cuenta para el veredicto es el RESIDUAL: el que queda
      // después de aplicar lo que el piloto declaró que va a hacer.
      nivel: mit.nivelResidual || smsRiskLevel(opt.gravIdx, opt.probIdx),
    };
  });
  /* Carga de riesgo promedio de los factores del FRAT. Información, no criterio
     de despacho: ver la nota del panel. */
  /**
   * Tipo de tarea del vuelo, tal como se respondió en el FRAT.
   *
   * Varias pantallas mostraban `worst.idx`, que es el NOMBRE DEL GRUPO del
   * factor dominante ("Tarea", "Topografía"…), no la respuesta. Decían "Tarea"
   * cuando lo que hace falta saber es cuál: traslado, mantenimiento,
   * entrenamiento, línea larga, evacuación médica, carga externa,
   * magnetometría…
   */
  const tipoDeTarea = selectedRisks.find((r) => r.group.key === "tarea")?.opt.label || "sin definir";

  const promedioFrat = promedioDeRiesgo(selectedRisks.map((r) => r.nivel));
  const promedioInicial = promedioDeRiesgo(selectedRisks.map((r) => r.nivelInicial));
  const factoresQueExigenMedida = selectedRisks.filter((r) => exigeMitigacion(r.nivel));


  /**
   * Peor factor del FRAT y su descripción.
   *
   * Antes esto guardaba solo el NOMBRE DEL GRUPO ("Tarea", "Topografía") y,
   * ante empate, se quedaba con el primero de la lista. Con todos los factores
   * en MEDIO la hoja de vuelo decía "Factor dominante: Tarea", que no es un
   * factor dominante: es el primer renglón del formulario.
   *
   * Ahora se nombra el grupo Y la respuesta —"Topografía: Alta montaña"— y si
   * varios comparten el peor nivel se dice cuántos, porque en ese caso no hay
   * un dominante y fingir que lo hay manda al piloto a mirar el lugar
   * equivocado.
   */
  const worstFrat = (() => {
    const evaluados = selectedRisks.filter((r) => Number.isFinite(RANK[r.nivel]));
    if (evaluados.length === 0) return { nivel: null, idx: "", empatados: 0, detalle: "Sin factores evaluados." };

    const peorRango = Math.max(...evaluados.map((r) => RANK[r.nivel]));
    const enElPeor = evaluados.filter((r) => RANK[r.nivel] === peorRango);
    const nivel = enElPeor[0].nivel;

    // Con todo en el nivel más benigno no hay "factor dominante" que nombrar.
    if (peorRango <= RANK.ACEPTABLE) {
      return { nivel, idx: "", empatados: enElPeor.length, detalle: "Ningún factor por encima de ACEPTABLE." };
    }

    const nombrar = (r) => `${r.group.label.replace(/\s*\([^)]*\)/, "")}: ${r.opt.label}`;
    const idx = enElPeor.length === 1
      ? nombrar(enElPeor[0])
      : `${nombrar(enElPeor[0])} (y ${enElPeor.length - 1} más en ${nivel})`;

    return {
      nivel, idx,
      empatados: enElPeor.length,
      detalle: enElPeor.map(nombrar).join(" · "),
    };
  })();
  // Combustible y altitud de crucero pasan a ser dos elementos más de la MISMA matriz Gravedad×Probabilidad del
  // SGS (antes usaban su propia escala aparte de 3 niveles) — así toda la app comparte una sola matriz de riesgo.
  // El combustible dejó de evaluarse por fuera del FRAT: ahora es el grupo
  // "reserva", con los mismos índices que el resto. Antes, cuando el cálculo no
  // se podía hacer, este factor devolvía ACEPTABLE — o sea que "no sé cuánto
  // combustible necesito" contaba como ausencia de riesgo.
  const fuelNivel = selectedRisks.find((r) => r.group.key === "reserva").nivel;
  const altNivel = smsRiskLevel(2, sobreTopeDeCarta(cruiseAltFt) ? 2 : 0);
  const worst = [worstFrat, { nivel: fuelNivel, idx: fuelNivel !== "ACEPTABLE" ? "Combustible" : worstFrat.idx }, { nivel: altNivel, idx: altNivel !== "ACEPTABLE" ? "Altitud de crucero" : worstFrat.idx }]
    .reduce((w, r) => (RANK[r.nivel] > RANK[w.nivel] ? r : w), worstFrat);

  // ---- Mantenimiento y Operaciones ----
  // La estructura sigue las hojas MIM / STATUS INSP. AIRCRAFT & ENG. / PLAN DE
  // REEMP. AERONAVE de un status report real. Los datos concretos de la flota
  // viven en src/datos/privado/, que no se publica.

  // Registro de cumplimiento de inspecciones (hoja "STATUS INSP. AIRCRAFT & ENG."): referencia AMM/PM,
  // frecuencia en horas, último cumplimiento (horas/fecha) y próximo cumplimiento — el remanente se
  // recalcula en vivo contra el TSN actual de la aeronave, no queda fijo como en el Excel estático.
  const [inspectionItems, setInspectionItems] = useState([
    { id: "i1", aircraftId: "f1", ref: "3.1.2", designacion: "Frequency adapter GVI — AMM 05-40-00, 6-7", frecuenciaHoras: 10, ultimoHoras: 3780.8, ultimoFecha: "2026-05-07", proximoHoras: 3790.8, obs: "" },
    { id: "i2", aircraftId: "f1", ref: "3.1.2", designacion: "Tail rotor blade — laminated pitch half bearing GVI — AMM 05-40-00, 6-7", frecuenciaHoras: 20, ultimoHoras: 3780.9, ultimoFecha: "2026-04-05", proximoHoras: 3800.9, obs: "" },
    { id: "i3", aircraftId: "f1", ref: "3.1.2", designacion: "TR blade GVI — AMM 64-10-00, 6-2", frecuenciaHoras: 30, ultimoHoras: 3780.9, ultimoFecha: "2026-04-05", proximoHoras: 3810.9, obs: "" },
    { id: "i4", aircraftId: "f1", ref: "3.1.5", designacion: "Forward cowling / Tail rotor drive shaft — AMM 53-31-00, 6-3", frecuenciaHoras: 50, ultimoHoras: 3716.7, ultimoFecha: "2026-02-23", proximoHoras: 3766.7, obs: "" },
    { id: "i5", aircraftId: "f1", ref: "3.1.5", designacion: "Starflex star GVI — AMM 05-40-00, 6-7", frecuenciaHoras: 50, ultimoHoras: 3716.7, ultimoFecha: "2026-02-23", proximoHoras: 3766.7, obs: "" },
    { id: "i6", aircraftId: "f1", ref: "3.1.7", designacion: "Frame 1400 kg cargo swing — AMM 25-91-01, 6-1", frecuenciaHoras: 100, ultimoHoras: 3716.7, ultimoFecha: "2026-02-18", proximoHoras: 3816.7, obs: "" },
    { id: "i7", aircraftId: "f1", ref: "3.1.5", designacion: "Transmission Deck GVI — AMM 53-10-00, 6-1", frecuenciaHoras: 100, ultimoHoras: 3716.7, ultimoFecha: "2026-02-18", proximoHoras: 3816.7, obs: "" },
  ]);
  const addInspectionItem = () => setInspectionItems((ms) => [...ms, { id: `i_${Date.now()}`, aircraftId: fleet[0]?.id, ref: "", designacion: "Nueva inspección", frecuenciaHoras: 0, ultimoHoras: 0, ultimoFecha: "", proximoHoras: 0, obs: "", documentos: [] }]);
  const attachInspectionDoc = (itemId, doc) => setInspectionItems((ms) => ms.map((m) => (m.id === itemId ? { ...m, documentos: [...(m.documentos || []), doc] } : m)));

  // Certificaciones, programa de mantenimiento e AD's repetitivos — extraídos de las hojas "CERTIFICACIONES" y
  // "AD REPETITIVOS" del status report. El remanente se recalcula en vivo contra
  // el TSN de referencia del reporte — apenas registres un vuelo, baja solo.
  const REPORT_TSN_REF = 3869.7;
  const [hourComplianceItems, setHourComplianceItems] = useState([
    { id: "hc1", aircraftId: "f1", item: "Célula — inspección 150 h / 12 meses", motor: false, intervaloHoras: 150, remHorasAlReporte: -3.0 },
    { id: "hc2", aircraftId: "f1", item: "Célula — inspección 750 h / 24 meses", motor: false, intervaloHoras: 750, remHorasAlReporte: 165.1 },
    { id: "hc3", aircraftId: "f1", item: "Célula — inspección 1500 h / 48 meses", motor: false, intervaloHoras: 1500, remHorasAlReporte: 915.1 },
    { id: "hc4", aircraftId: "f1", item: "Motor Arriel 2B1 — inspección 150 h", motor: true, intervaloHoras: 150, remHorasAlReporte: -3.0 },
    // El Excel calcula este remanente contra un TSO vacío (el motor nunca fue
    // overhauleado), así que da 300 − 3869.7 = −3569.7: no es un vencimiento de
    // 3569 horas, es una fórmula sin dato. Queda en null hasta que alguien
    // aporte el último cumplimiento real de la inspección de 300 h. Un número
    // que se sabe falso es peor que un hueco: el hueco al menos se ve.
    { id: "hc5", aircraftId: "f1", item: "Motor Arriel 2B1 — inspección 300 h", motor: true, intervaloHoras: 300, remHorasAlReporte: null },
    { id: "hc6", aircraftId: "f1", item: "Motor Arriel 2B1 — inspección 600 h", motor: true, intervaloHoras: 600, remHorasAlReporte: 12.0 },
    { id: "hc7", aircraftId: "f1", item: "Motor Arriel 2B1 — TBO (3500 h)", motor: true, intervaloHoras: 3500, remHorasAlReporte: 1793.2 },
    { id: "hc8", aircraftId: "f1", item: "AD 2023-0187 — ATA 76 microswitches control motor (repetitivo 660 h)", motor: false, intervaloHoras: 660, remHorasAlReporte: 289.5 },
  ]);
  const [dateComplianceItems, setDateComplianceItems] = useState([
    { id: "dc1", aircraftId: "f1", item: "Altímetro piloto (S/N 452859)", fecha: "2026-06-14" },
    { id: "dc2", aircraftId: "f1", item: "Compás piloto (S/N AZ1980707)", fecha: "2027-04-07" },
    { id: "dc3", aircraftId: "f1", item: "Transponder GTX327 (S/N 83727783)", fecha: "2026-06-14" },
    { id: "dc4", aircraftId: "f1", item: "Pitot-estático", fecha: "2026-06-14" },
    { id: "dc5", aircraftId: "f1", item: "ELT — inspección anual (406AF-H)", fecha: "2026-06-13" },
    { id: "dc6", aircraftId: "f1", item: "ELT — vencimiento de batería", fecha: "2032-03-31" },
    { id: "dc7", aircraftId: "f1", item: "Programa mantenimiento — ítem 24 meses", fecha: "2026-06-14" },
  ]);
  const addHourComplianceItem = () => setHourComplianceItems((ms) => [...ms, { id: `hc_${Date.now()}`, aircraftId: fleet[0]?.id, item: "Nuevo ítem", motor: false, intervaloHoras: 0, remHorasAlReporte: 0 }]);
  const addDateComplianceItem = () => setDateComplianceItems((ms) => [...ms, { id: `dc_${Date.now()}`, aircraftId: fleet[0]?.id, item: "Nuevo ítem", fecha: "" }]);
  const removeHourComplianceItem = (id) => setHourComplianceItems((ms) => ms.filter((m) => m.id !== id));
  const removeDateComplianceItem = (id) => setDateComplianceItems((ms) => ms.filter((m) => m.id !== id));

  // Informe resumen de status: junta TODAS las fuentes de alerta (horas, calendario, inspecciones AMM,
  // componentes de vida límite) en una sola lista ordenada por urgencia — VENCIDO primero — para imprimir.
  const RANK_STATUS = { INTOLERABLE: 0, TOLERABLE: 1, ACEPTABLE: 2 };
  // Bandas de alarma pedidas: urgente si quedan ≤50 h (o ≤6 meses), próximo entre 50-150 h (o 6-12 meses),
  // al día por encima de eso. Se usan igual para AD's, inspecciones y reemplazos (horas o componentes).
  const hourAlarmStatus = (remaining) => {
    if (remaining <= 0) return { estado: "INTOLERABLE", label: "VENCIDO" };
    if (remaining <= 50) return { estado: "INTOLERABLE", label: "URGENTE (≤50 h)" };
    if (remaining <= 150) return { estado: "TOLERABLE", label: "PRÓXIMO (50–150 h)" };
    return { estado: "ACEPTABLE", label: "AL DÍA" };
  };
  const dateAlarmStatus = (days) => {
    if (days === null) return { estado: "TOLERABLE", label: "SIN FECHA" };
    if (days <= 0) return { estado: "INTOLERABLE", label: "VENCIDO" };
    if (days <= 180) return { estado: "INTOLERABLE", label: "URGENTE (≤6 meses)" };
    if (days <= 365) return { estado: "TOLERABLE", label: "PRÓXIMO (6–12 meses)" };
    return { estado: "ACEPTABLE", label: "AL DÍA" };
  };
  const buildStatusReport = (aircraftId) => {
    const ac = fleet.find((f) => f.id === aircraftId);
    if (!ac) return [];
    const rows = [];
    hourComplianceItems.filter((m) => m.aircraftId === aircraftId).forEach((m) => {
      const currentTsn = m.motor ? ac.motor?.tsn : ac.tsn;
      if (!Number.isFinite(m.remHorasAlReporte) || !Number.isFinite(currentTsn)) {
        rows.push({ grupo: "Certificación / AD (horas)", item: m.item, dato: "sin dato de cumplimiento", estado: "TOLERABLE" });
        return;
      }
      const rem = m.remHorasAlReporte - (currentTsn - REPORT_TSN_REF);
      const a = hourAlarmStatus(rem);
      rows.push({ grupo: "Certificación / AD (horas)", item: m.item, dato: `${rem.toFixed(1)} h restantes — ${a.label}`, estado: a.estado });
    });
    dateComplianceItems.filter((m) => m.aircraftId === aircraftId).forEach((m) => {
      const dias = m.fecha ? Math.ceil((new Date(m.fecha).getTime() - Date.now()) / 86400000) : null;
      const a = dateAlarmStatus(dias);
      rows.push({ grupo: "Certificación (fecha)", item: m.item, dato: m.fecha ? `vence ${m.fecha} (${dias} d) — ${a.label}` : "sin fecha", estado: a.estado });
    });
    inspectionItems.filter((m) => m.aircraftId === aircraftId).forEach((m) => {
      const rem = m.proximoHoras - ac.tsn;
      const a = hourAlarmStatus(rem);
      rows.push({ grupo: "Inspección AMM/ALS", item: m.designacion, dato: `${rem.toFixed(1)} h restantes — ${a.label}`, estado: a.estado });
    });
    lifeComponents.filter((m) => m.aircraftId === aircraftId).forEach((m) => {
      const usadas = ac.tsn - m.tsnInstalacion;
      const rem = m.limiteHoras ? m.limiteHoras - usadas : null;
      const a = rem === null ? { estado: "TOLERABLE", label: m.metodo } : hourAlarmStatus(rem);
      rows.push({ grupo: "Componente vida límite / reemplazo", item: `${m.descripcion} (P/N ${m.pn || "s/d"}, S/N ${m.sn || "s/d"})`, dato: rem === null ? a.label : `${rem.toFixed(1)} h restantes — ${a.label}`, estado: a.estado });
    });
    return rows.sort((a, b) => RANK_STATUS[a.estado] - RANK_STATUS[b.estado]);
  };

  // Plan de reemplazo de componentes con vida límite / TBO (hoja "PLAN DE REEMP. AERONAVE"): P/N, S/N,
  // método (OTL/SLL/OC), horas al instalar y límite — el remanente también se recalcula contra el TSN actual.
  const [lifeComponents, setLifeComponents] = useState([
    { id: "c1", aircraftId: "f1", ata: "25-66", descripcion: "Battery pack (ELT)", pn: "S1820516-99", sn: "TM0000350360", metodo: "OTL", limiteHoras: null, fechaInstalacion: "2025-11-10", tsnInstalacion: 3499.2 },
    { id: "c2", aircraftId: "f1", ata: "26-22", descripcion: "Cabin fire extinguisher", pn: "S262A10T1001", sn: "ACW07911", metodo: "OTL", limiteHoras: null, fechaInstalacion: "2024-11-15", tsnInstalacion: 3284.8 },
    { id: "c3", aircraftId: "f1", ata: "62-10", descripcion: "Unequipped blade pin", pn: "350A31-1771-21", sn: "M53748", metodo: "SLL", limiteHoras: 5000, fechaInstalacion: "2007-11-23", tsnInstalacion: 0 },
    { id: "c4", aircraftId: "f1", ata: "62-10", descripcion: "Unequipped blade pin", pn: "350A31-1771-21", sn: "M53729", metodo: "SLL", limiteHoras: 5000, fechaInstalacion: "2007-11-23", tsnInstalacion: 0 },
  ]);
  const addLifeComponent = () => setLifeComponents((ms) => [...ms, { id: `c_${Date.now()}`, aircraftId: fleet[0]?.id, ata: "", descripcion: "Nuevo componente", pn: "", sn: "", metodo: "OTL", limiteHoras: null, fechaInstalacion: "", tsnInstalacion: 0, documentos: [] }]);
  const attachComponentDoc = (itemId, doc) => setLifeComponents((ms) => ms.map((m) => (m.id === itemId ? { ...m, documentos: [...(m.documentos || []), doc] } : m)));

  /* ═══ EL PILOTO DE FÁBRICA ARRANCA EN CERO ═══

     Traía `horasTotales: 3200` y `horasUltimos30d: 40`. Números inventados,
     puestos para que la pantalla no se viera vacía en el desarrollo.

     Un piloto que abre la aplicación por primera vez veía 3200 horas que no
     voló, y los límites de jornada se calculaban contra 40 horas que tampoco
     existían. Sus horas reales se cargan con el arrastre. */
  const [pilots, setPilots] = usePersistedState("pilotos", [
    { id: "p1", nombre: "Piloto", apellido: "1", licencia: "", aptoMedicoVencimiento: "", horasTotales: 0, horasUltimos30d: 0, finServicioISO: "", jurisdiccion: JURISDICCIONES[0] },
  ], undefined, "pilotos");
  const addPilot = () => setPilots((ps) => [...ps, { id: `p_${Date.now()}`, nombre: "Nuevo", apellido: "Piloto", licencia: "", aptoMedicoVencimiento: "", horasTotales: 0, horasUltimos30d: 0, finServicioISO: "", jurisdiccion: JURISDICCIONES[0] }]);

  /* ─────────────────────────────────────────────────────────────────────────
   * MANTENIMIENTO DE LA AERONAVE ACTIVA — resumen para la hoja de vuelo.
   *
   * La pestaña Mantenimiento ya tiene todo: programa por horas, ítems de
   * calendario, inspecciones AMM y componentes de vida límite. Pero la hoja de
   * vuelo no los miraba: mostraba "—" y pintaba el bloque en verde. Un
   * helicóptero con la inspección de 150 h vencida se veía igual que uno al día.
   *
   * Acá se reduce todo a los dos números que un piloto necesita antes de
   * despegar: cuántas horas quedan hasta el vencimiento por horas más cercano,
   * y cuántos días hasta el vencimiento de calendario más cercano. Junto con
   * cuál es el ítem que manda, porque "quedan 3 horas" sin decir de qué no
   * sirve para nada.
   *
   * Se recorren las MISMAS fuentes que el informe de status de la pestaña
   * Mantenimiento y con las mismas fórmulas, para que los dos lados no puedan
   * dar números distintos.
   * ──────────────────────────────────────────────────────────────────────── */
  const resumenMantenimiento = (aircraftId) => {
    const vacio = {
      horas: null, itemHoras: null,
      dias: null, itemDias: null,
      hayDatos: false,
    };
    const ac = fleet.find((f) => f.id === aircraftId);
    if (!ac) return vacio;

    const porHoras = [];

    // Programa de mantenimiento y AD's repetitivos: el remanente del reporte se
    // corrige por las horas voladas desde que se emitió.
    hourComplianceItems.filter((m) => m.aircraftId === aircraftId).forEach((m) => {
      const tsn = m.motor ? ac.motor?.tsn : ac.tsn;
      if (!Number.isFinite(tsn) || !Number.isFinite(m.remHorasAlReporte)) return;
      porHoras.push({ rem: m.remHorasAlReporte - (tsn - REPORT_TSN_REF), item: m.item });
    });

    // Inspecciones AMM/ALS: el próximo cumplimiento está en horas absolutas.
    inspectionItems.filter((m) => m.aircraftId === aircraftId).forEach((m) => {
      if (!Number.isFinite(m.proximoHoras) || !Number.isFinite(ac.tsn)) return;
      porHoras.push({ rem: m.proximoHoras - ac.tsn, item: m.designacion });
    });

    // Componentes con vida límite en horas. Los de método OC / sin límite en
    // horas no entran: no tienen un número que comparar.
    lifeComponents.filter((m) => m.aircraftId === aircraftId).forEach((m) => {
      if (!Number.isFinite(m.limiteHoras) || !Number.isFinite(ac.tsn) || !Number.isFinite(m.tsnInstalacion)) return;
      porHoras.push({ rem: m.limiteHoras - (ac.tsn - m.tsnInstalacion), item: m.descripcion });
    });

    const porFecha = dateComplianceItems
      .filter((m) => m.aircraftId === aircraftId && m.fecha)
      .map((m) => ({ dias: Math.ceil((new Date(m.fecha).getTime() - Date.now()) / 86400000), item: m.item }))
      .filter((x) => Number.isFinite(x.dias));

    const criticoHoras = porHoras.length ? porHoras.reduce((a, b) => (b.rem < a.rem ? b : a)) : null;
    const criticoFecha = porFecha.length ? porFecha.reduce((a, b) => (b.dias < a.dias ? b : a)) : null;

    return {
      horas: criticoHoras ? criticoHoras.rem : null,
      itemHoras: criticoHoras ? criticoHoras.item : null,
      dias: criticoFecha ? criticoFecha.dias : null,
      itemDias: criticoFecha ? criticoFecha.item : null,
      hayDatos: porHoras.length > 0 || porFecha.length > 0,
    };
  };
  const mantenimientoActivo = resumenMantenimiento(aeronaveActiva?.id);

  const removePilot = (id) => setPilots((ps) => (ps.length > 1 ? ps.filter((p) => p.id !== id) : ps));
  const [assignedPilotId, setAssignedPilotId] = useState("p1");

  // Base de datos de mecánicos y supervisores — igual que pilotos, para seleccionarlos en vez de escribirlos
  // a mano cada vez en la bitácora de discrepancias.
  const [mechanics, setMechanics] = useState([
    { id: "m1", nombre: "Mecánico", apellido: "1", licencia: "" },
  ]);
  const addMechanic = () => setMechanics((ms) => [...ms, { id: `mec_${Date.now()}`, nombre: "Nuevo", apellido: "Mecánico", licencia: "" }]);
  const removeMechanic = (id) => setMechanics((ms) => (ms.length > 1 ? ms.filter((m) => m.id !== id) : ms));

  const [supervisors, setSupervisors] = useState([
    { id: "s1", nombre: "Supervisor", apellido: "1", licencia: "" },
  ]);
  const addSupervisor = () => setSupervisors((ss) => [...ss, { id: `sup_${Date.now()}`, nombre: "Nuevo", apellido: "Supervisor", licencia: "" }]);
  const removeSupervisor = (id) => setSupervisors((ss) => (ss.length > 1 ? ss.filter((s) => s.id !== id) : ss));
  const [pilotConfirmed, setPilotConfirmed] = useState(false);

  /* ═══════════════════════════════════════════════════════════════════════
     LOS HECHOS DE LA PREPARACIÓN

     Lo que la barra de pasos necesita saber, sacado de los cálculos que ya
     existen. **Cada hecho es `true`, `false` o `null`, y los tres significan
     cosas distintas**: cumplido, incumplido, y no se sabe.

     `null` NO es `false`. Que el OGE caiga fuera de la carta no quiere decir
     que la performance no alcance: quiere decir que la carta no llega hasta
     ahí y nadie puede afirmar nada. Decir «falta» sobre eso sería inventar un
     veredicto; decir «listo» sería peor.
     ═══════════════════════════════════════════════════════════════════════ */
  const hechosPreparacion = useMemo(() => ({
    /* Ruta */
    origen: Number.isFinite(origin?.lat) && Number.isFinite(origin?.lon),
    destino: Number.isFinite(destination?.lat) && Number.isFinite(destination?.lon),
    distancia: Number.isFinite(totalNm) && totalNm > 0,
    /* El perfil del terreno se está buscando: todavía no se sabe. */
    perfilTerreno: routeProfileStatus === "loading" ? null
      : routeProfileStatus === "error" ? false
        : Array.isArray(routeProfile) && routeProfile.length > 0,
    nivelCrucero: Number.isFinite(cruiseAltFt) && cruiseAltFt > 0,

    /* Peso y balance */
    aeronave: Boolean(matricula && activeProfile),
    /* El peso vacío GENÉRICO del tipo no es el de esta matrícula. No es falso
       —hay un número y el cálculo corre— pero nadie pesó este helicóptero: es
       sin dato, y una aeronave genérica no puede disimular que no está
       verificada. */
    pesoVacio: pesoVacio.pesoDelPesaje ? true : null,
    carga: items.length > 0 && totalKg > 0,
    dentroEnvolvente: insideEnvelope,
    cgAterrizaje: wbAterrizaje.valido ? wbAterrizaje.dentroDeEnvolvente : null,
    /* Fuera del rango de la carta no es «no alcanza»: es que la carta no
       llega. */
    performance: perfOrigin.oge.outOfRange ? null : true,

    /* Combustible */
    embarcado: Number.isFinite(fuelKg) && fuelKg > 0,
    requerido: requiredKg === null ? null : true,
    alcanza: requiredKg === null ? null : fuelKg >= requiredKg,

    /* FRAT */
    tipoDeTarea: tipoDeTarea !== "sin definir",
    nivelAceptable: RANK[worst.nivel] < RANK.INACEPTABLE,
    medidasDeclaradas: factoresQueExigenMedida.length === 0,

    /* Hoja */
    piloto: pilots.length > 0,
    confirmado: pilotConfirmed,
  }), [
    origin, destination, totalNm, routeProfileStatus, routeProfile, cruiseAltFt,
    matricula, activeProfile, pesoVacio, items, totalKg, insideEnvelope,
    wbAterrizaje, perfOrigin, fuelKg, requiredKg, tipoDeTarea, worst,
    factoresQueExigenMedida, pilots, pilotConfirmed,
  ]);

  /* Las reglas de jornada viven en el dominio, una por jurisdicción. Antes eran
     cuatro copias idénticas de 10/14/8 y el selector de país no cambiaba nada:
     elegir Perú o EE.UU. daba exactamente el mismo resultado. */
  const [dutyTime, setDutyTime] = usePersistedState("dutyTime", {});
  const editarDuty = (pilotId, campo, valor) =>
    setDutyTime((d) => ({ ...d, [pilotId]: { ...(d[pilotId] || {}), [campo]: valor } }));

  /** Horas desde el fin del servicio anterior, si está registrado. */
  const horasDescansoDe = (p) => {
    if (!p.finServicioISO) return null;
    const h = (Date.now() - new Date(p.finServicioISO).getTime()) / 3600000;
    return Number.isFinite(h) ? Math.round(h * 10) / 10 : null;
  };

  /** Evaluación de jornada de un piloto contra su jurisdicción. */
  const jornadaDe = (p) => {
    const d = dutyTime[p.id] || {};
    return evaluarJornada({
      jurisdiccion: p.jurisdiccion,
      tipoOperacion: d.tipoOperacion || "estandar",
      cantidadPilotos: Number(d.cantidadPilotos) || 1,
      /* Las horas de la jornada SALEN DEL LIBRO del piloto mientras él no
         conteste a mano. Antes se tecleaban teniendo el dato al lado: eso era
         una segunda versión de la verdad, y si las dos difieren nadie se
         entera.

         Lo declarado a mano manda: él sabe si voló algo que la aplicación no
         registró. */
      horasVueloJornada: horasDeJornada(
        libroPiloto, p.id,
        new Date().toISOString().slice(0, 10),
        d.horasVueloJornada,
      ).horas,
      horasPsv: Number.isFinite(Number(d.horasPsv)) && d.horasPsv !== "" ? Number(d.horasPsv) : null,
      horasDescansoPrevio: horasDescansoDe(p),
    });
  };

  const [flightLog, setFlightLog] = usePersistedState("bitacora", [], undefined, "bitacora");

  /* Libro de vuelo del PILOTO, separado de la bitácora de la aeronave.

     Son dos registros distintos con dos números distintos: el libro del piloto
     lleva el flight time —que incluye rotores en tierra y cuenta para su
     jornada— y la bitácora de mantenimiento lleva el air time. */
  const [libroPiloto, setLibroPiloto] = usePersistedState("libroPiloto", []);

  /* ═══ DISPOSICIÓN DEL MAPA ═══

     En ruta y vuelo el mapa ocupa el alto disponible y los controles flotan
     encima. En pantalla chica vuelve al apilado: una capa flotante ahí tapa
     más mapa del que deja ver, y un piloto moviendo paneles para ver dónde
     está es un piloto mirando la pantalla en vez de afuera. */
  const [ventana, setVentana] = useState(() => ({
    ancho: typeof window !== "undefined" ? window.innerWidth : 0,
    alto: typeof window !== "undefined" ? window.innerHeight : 0,
  }));

  useEffect(() => {
    const medir = () => setVentana({ ancho: window.innerWidth, alto: window.innerHeight });
    window.addEventListener("resize", medir);
    /* En el iPad, rotar no siempre dispara `resize` a tiempo. */
    window.addEventListener("orientationchange", medir);
    return () => {
      window.removeEventListener("resize", medir);
      window.removeEventListener("orientationchange", medir);
    };
  }, []);

  const [paneles, setPaneles] = useState(panelesAbiertos);

  /* La disposición de ESTA pestaña. Las de formulario siguen apiladas. */
  const dispo = useMemo(() => disposicion(tab, ventana), [tab, ventana]);

  const alternarPanel = useCallback((clave) => {
    setPaneles((p) => {
      const nuevo = { ...p, [clave]: !p[clave] };
      guardarPaneles(nuevo);
      return nuevo;
    });
  }, []);
  const [newLog, setNewLog] = useState({
    aircraftId: fleet[0]?.id, pilotId: pilots[0]?.id, nVuelo: "", horasAeronave: 0, flightTime: "", horasMotor: 0,
    ciclosNG: 0, ciclosNF: 0, ldg: 0, cargas: 0, frat: "ACEPTABLE", adjunto: null,
    empresaId: null, clienteId: null,
    pasajeros: 0, cargaInternaKg: 0, cargaExternaKg: 0, combustibleUsadoKg: 0, tipoOperacion: "Traslado",
  });
  const addLogEntry = () => {
    const aircraft = fleet.find((f) => f.id === newLog.aircraftId);

    /* Antes de armar nada: para quién se volvió. Un asiento sin empresa se
       contaría en «(sin empresa)» junto a los traslados propios —un dato que
       falta disfrazado de hecho— y el informe lo mostraría como si fuera una
       decisión. */
    const resuelta = conLaOperacion({}, parDeOperacion(newLog));
    if (!resuelta.ok) return setMotivoOperacion(resuelta.motivo);
    setMotivoOperacion(null);

    const entry = {
      id: `l_${Date.now()}`, fecha: new Date().toISOString().slice(0, 10), ...newLog,
      /* La empresa RESUELTA, no la que haya quedado en el formulario: con
         cliente, la empresa es la del cliente. */
      empresaId: resuelta.asiento.empresaId,
      clienteId: resuelta.asiento.clienteId,
      horasAeronave: Number(newLog.horasAeronave) || 0,
      /* El FT del piloto va aparte: si no se declara queda null, y
         `ftDePiloto` usa el AT como respaldo sin inventar la diferencia. */
      flightTime: newLog.flightTime === "" || newLog.flightTime === null
        ? null : (Number(newLog.flightTime) || null),
      horasMotor: Number(newLog.horasMotor) || 0,
      ciclosNG: Number(newLog.ciclosNG) || 0, ciclosNF: Number(newLog.ciclosNF) || 0,
      ldg: Number(newLog.ldg) || 0, cargas: Number(newLog.cargas) || 0,
      pasajeros: Number(newLog.pasajeros) || 0, cargaInternaKg: Number(newLog.cargaInternaKg) || 0,
      cargaExternaKg: Number(newLog.cargaExternaKg) || 0, combustibleUsadoKg: Number(newLog.combustibleUsadoKg) || 0,
      tipoAeronave: aircraft?.tipo || "",
    };
    // Sin fecha el asiento no se puede ubicar en ningún mes, y el Duty Time se
    // arma por mes y por año.
    entry.fecha = entry.fecha || new Date().toISOString();
    entry.cotejo = { campos: {}, tecnico: "", fechaCotejo: null, observacion: "" };
    setFlightLog((l) => [entry, ...l]);
    /* El alta manual también alimenta el LIBRO DEL PILOTO: si no, un vuelo
       cargado a mano no contaría para su jornada ni aparecería en su libro.
       Va el FLIGHT TIME, que es el número del piloto. */
    {
      const { piloto } = repartirVuelo({
        ...entry, flightTime: ftDePiloto(entry), airTime: entry.horasAeronave,
      });
      if (piloto) setLibroPiloto((l) => [piloto, ...l]);
    }
    // El vuelo registrado (con los mismos campos que la bitácora física: horas aeronave/motor, NG, NF, LDG,
    // cargas) suma directo a la aeronave EXACTA por matrícula/serie en Mantenimiento — así se arma una base de
    // datos verificable para inspecciones programadas por horas o calendario, cruzable contra el adjunto escaneado.
    // Además guarda pasajeros/carga/combustible/tipo de operación y de aeronave para el histórico del Dashboard.
    setFleet((fs) => fs.map((f) => (f.id === entry.aircraftId ? {
      ...f,
      tsn: sumarContador(f.tsn, entry.horasAeronave),
      csn: sumarContador(f.csn, entry.ldg),
      ldg: (f.ldg || 0) + entry.ldg,
      cargas: (f.cargas || 0) + entry.cargas,
      motor: {
        ...f.motor,
        tsn: sumarContador(f.motor?.tsn, entry.horasMotor),
        cyNG: sumarContador(f.motor?.cyNG, entry.ciclosNG),
        cyNF: sumarContador(f.motor?.cyNF, entry.ciclosNF),
      },
    } : f)));
    setPilots((ps) => ps.map((p) => (p.id === entry.pilotId ? { ...p, horasTotales: p.horasTotales + ftDePiloto(entry), horasUltimos30d: p.horasUltimos30d + ftDePiloto(entry) } : p)));
    setNewLog((l) => ({ ...l, nVuelo: "", horasAeronave: 0, flightTime: "", horasMotor: 0, ciclosNG: 0, ciclosNF: 0, ldg: 0, cargas: 0, adjunto: null, pasajeros: 0, cargaInternaKg: 0, cargaExternaKg: 0, combustibleUsadoKg: 0 }));
  };
  /**
   * Bitácoras manuscritas escaneadas, por matrícula y agrupadas por año-mes.
   *
   * Solo se agregan y se consultan. No hay edición ni borrado: el PDF es el
   * documento firmado. Si un dato está mal, lo que se corrige es el asiento
   * virtual, y el cotejo deja constancia de la diferencia.
   */
  /**
   * Cronómetro del vuelo en curso.
   *
   * Se persiste: si la app se cierra o el teléfono se queda sin batería en
   * medio del vuelo, al volver el cronómetro sigue donde estaba. Como guarda
   * instantes y no un contador, recupera el tiempo transcurrido entero.
   */
  const [cronometro, setCronometro] = usePersistedState("cronometroVuelo", cronometroVacio());
  /** Vuelos cronometrados de la jornada, antes de pasar a la bitácora. */
  const [vuelosDelDia, setVuelosDelDia] = usePersistedState("vuelosDelDia", []);
  const totalDelDia = resumenDelDia(vuelosDelDia);
  /** Cuántos registros del cronómetro se borraron: sale en el papel del día. */
  const [vuelosEliminados, setVuelosEliminados] = usePersistedState("vuelosEliminadosDelDia", 0);

  /* Ayudantes de la tabla del día. La unidad de combustible es la misma que se
     eligió en Análisis de vuelo: una sola preferencia para toda la app. */
  /**
   * Hora de reloj, en dos cifras.
   *
   * `toLocaleTimeString` en español devuelve "9:49:52" SIN cero delante, así
   * que cortar los cinco primeros caracteres daba "9:49:" — con el dos puntos
   * colgando y la hora sin alinear en la columna. Se piden las dos cifras
   * explícitamente.
   */
  /**
   * Horas de un contador, o "sin cargar".
   *
   * Toda lectura de contadores de una aeronave pasa por acá. Cero NO es lo
   * mismo que sin dato: cero significaría una aeronave a estrenar, y una
   * matrícula recién incorporada tiene historial que nadie cargó todavía.
   */
  const hs = (v) => (Number.isFinite(v) ? v.toFixed(1) : "sin cargar");

  /**
   * Suma un vuelo a un contador de la aeronave.
   *
   * Si el contador está SIN CARGAR, el resultado sigue sin cargar. `null + 1.5`
   * en JavaScript da 1.5, no un error: una matrícula recién incorporada
   * empezaría a acumular horas desde cero en silencio, y su TSN diría 1,5 h
   * cuando la aeronave lleva miles. El desfase se arrastraría a todos los
   * remanentes de inspección.
   */
  const sumarContador = (actual, aporte) => {
    if (!Number.isFinite(actual)) return null;
    return actual + (Number.isFinite(aporte) ? aporte : 0);
  };

  const hora = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("es", { hour12: false, hour: "2-digit", minute: "2-digit" });
  };

  /**
   * Unidad con que se carga el combustible en el día de vuelo.
   *
   * Propia de este panel, no la de Análisis de vuelo: el planificador puede
   * estar razonando en litros mientras el operario de plataforma entrega el
   * ticket en galones. Obligar a convertir de cabeza es cómo se cargan mal los
   * números. El dato se guarda siempre en litros.
   */
  const [unidadCombustibleDia, setUnidadCombustibleDia] = usePersistedState("unidadCombustibleDia", "L");
  const simboloDia = UNIDADES_COMBUSTIBLE.find((u) => u.clave === unidadCombustibleDia)?.simbolo ?? "L";
  const enUnidad = (litros) => {
    if (!Number.isFinite(litros)) return "";
    const v = desdeLitros(litros, unidadCombustibleDia, densidadKgL);
    return v === null ? "" : Math.round(v * 10) / 10;
  };
  const aLitrosDesdeUnidad = (texto) => {
    if (texto === "" || texto === null) return null;
    const l = aLitros(Number(texto), unidadCombustibleDia, densidadKgL);
    // Se guarda con más resolución que la mostrada: redondear a la décima de
    // litro al ENTRAR haría que 143 gal y 143,04 gal se guardaran igual, y la
    // diferencia reaparecería como error al volver a mostrar.
    return l === null ? null : Math.round(l * 1000) / 1000;
  };
  const consumoVuelo = (v) => (
    Number.isFinite(v.combustibleInicialL) && Number.isFinite(v.combustibleFinalL)
      ? Math.round((v.combustibleInicialL - v.combustibleFinalL) * 10) / 10
      : null
  );
  const editarVueloDelDia = (id, patch) =>
    setVuelosDelDia((vs) => vs.map((v) => (v.id === id ? { ...v, ...patch } : v)));

  const [bitacorasEscaneadas, setBitacorasEscaneadas] = usePersistedState("bitacorasEscaneadas", {});
  const adjuntarBitacoras = async (aircraftId, archivos) => {
    const lista = Array.from(archivos || []);
    for (const archivo of lista) {
      const datos = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => res(null);
        r.readAsDataURL(archivo);
      });
      if (!datos) continue;
      const hoy = new Date();
      setBitacorasEscaneadas((b) => ({
        ...b,
        [aircraftId]: [...(b[aircraftId] || []), {
          id: `bit_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          nombre: archivo.name,
          tamanoKb: Math.round(archivo.size / 1024),
          periodo: `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`,
          subidoEl: hoy.toISOString(),
          datos,
        }],
      }));
    }
  };

  /** Marca de cotejo campo por campo contra la bitácora de papel. */
  const marcarCotejo = (logId, clave, coincide) => setFlightLog((l) => l.map((v) => (v.id === logId
    ? { ...v, cotejo: { ...(v.cotejo || {}), campos: { ...(v.cotejo?.campos || {}), [clave]: coincide } } }
    : v)));
  const editarCotejo = (logId, campo, valor) => setFlightLog((l) => l.map((v) => (v.id === logId
    ? { ...v, cotejo: { ...(v.cotejo || {}), [campo]: valor } }
    : v)));

  /**
   * Estado de partida de cada aeronave, para poder verificar el arrastre.
   *
   * Es el TSN/CSN que tenía cuando se cargó el status report, antes de que la
   * bitácora empezara a sumar. Sin ese punto de partida no hay forma de saber
   * si el status actual es la suma de los vuelos o si alguien lo tocó a mano.
   */
  const estadoInicialDe = (f) =>
    f?.estadoInicial
    || FLOTA_SEMILLA.find((x) => x.matricula === f?.matricula)?.estadoInicial
    || null;

  const smsTally = flightLog.reduce((acc, l) => { acc[l.frat] = (acc[l.frat] || 0) + 1; return acc; }, {});

  // ---- Discrepancias de mantenimiento (formato bitácora, con adjuntos escaneados) ----
  const [discrepancies, setDiscrepancies] = usePersistedState("discrepancias", []);
  const [newDiscrepancy, setNewDiscrepancy] = useState({ aircraftId: fleet[0]?.id, folio: "", fecha: new Date().toISOString().slice(0, 10), descripcion: "", accionCorrectiva: "", mecanicoId: "m1", supervisorId: "s1" });
  const addDiscrepancy = () => {
    setDiscrepancies((ds) => [{ id: `d_${Date.now()}`, ...newDiscrepancy, documentos: [], cerrada: false }, ...ds]);
    setNewDiscrepancy((d) => ({ ...d, folio: "", descripcion: "", accionCorrectiva: "" }));
  };
  /**
   * Levantar o reabrir una discrepancia. NUNCA borrarla.
   *
   * Una discrepancia borrada desaparece del historial de aeronavegabilidad sin
   * dejar rastro de que existió, y ese historial es justamente lo que un
   * auditor viene a mirar. Se levanta —con quién y cuándo— o se reabre, pero el
   * asiento queda. Por eso no hay botón de eliminar en esta tabla.
   */
  const toggleDiscrepancyCerrada = (id, quien) => setDiscrepancies((ds) => ds.map((d) => (d.id === id
    ? (d.cerrada
        ? { ...d, cerrada: false, cerradaPor: null, cerradaEl: null }
        : { ...d, cerrada: true, cerradaPor: quien || "sin registrar", cerradaEl: new Date().toISOString() })
    : d)));
  const attachDiscrepancyDoc = (id, doc) => setDiscrepancies((ds) => ds.map((d) => (d.id === id ? { ...d, documentos: [...d.documentos, doc] } : d)));

  const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const [showReport, setShowReport] = useState(false);

  /* ═══ EMPRESA, USUARIOS Y PERFILES ═══

     Los datos se guardan en IndexedDB, como todo lo demás. El perfil con el que
     se MIRA la pantalla no se guarda, y es a propósito: es una herramienta para
     ver el modelo mientras no hay entrada, no una sesión. Un perfil «elegido»
     que sobrevive al refresco es exactamente lo que un día va a parecer una
     sesión sin serlo, y nadie lo va a revisar. */
  const [identidad, setIdentidad] = usePersistedState("identidad", IDENTIDAD_INICIAL);
  const [perfilQueMira, setPerfilQueMira] = useState("company_admin");

  /* El catálogo de clientes se guarda APARTE de la identidad, y no por
     prolijidad: la identidad es quién entra y qué puede hacer; los clientes son
     datos de operación. Van juntos en la pantalla porque se administran juntos,
     no porque sean lo mismo. */
  const [clientes, setClientes] = usePersistedState("clientes", [], undefined, "clientes");

  /* ═══ PARA QUIÉN SE VUELA ═══

     La operación del vuelo que se está preparando: la empresa y el cliente. NO
     es una preferencia que se recuerde sola y decida por su cuenta: se elige por
     vuelo, y el cierre del cronómetro la muestra ANTES de firmar el asiento.

     Son dos selecciones y no una, a propósito: la del vuelo que se prepara —acá
     abajo— y la de cada asiento del libro de papel, porque una planilla de
     veinte renglones puede tener veinte clientes distintos. */
  const [operacion, setOperacion] = usePersistedState("operacion", { empresaId: null, clienteId: null });
  const [motivoOperacion, setMotivoOperacion] = useState(null);

  /* La empresa es la elegida, o la activa: el que está cargando pertenece a una
     empresa, y preguntárselo en cada vuelo es pedir un dato que ya se sabe. */
  const parDeOperacion = (elegido) => ({
    empresaId: elegido?.empresaId || identidad?.empresaActiva || null,
    clienteId: elegido?.clienteId || null,
  });

  /** El par resuelto, o el motivo. Un solo camino en la pantalla hacia
      `conOperacion`, que es la única función que estampa el par en un asiento. */
  const conLaOperacion = (asiento, par) => conOperacion(asiento, par, {
    clientes, empresas: identidad?.empresas || [],
  });

  /** Lo que se muestra en el cierre del cronómetro, en una línea. `null` cuando
      todavía no hay empresa: ahí el cierre avisa que el vuelo quedaría sin decir
      para quién se voló. */
  const etiquetaOperacion = (() => {
    const par = parDeOperacion(operacion);
    if (!par.empresaId) return null;
    const empresa = (identidad?.empresas || []).find((e) => e.id === par.empresaId)?.nombre;
    const cliente = (clientes || []).find((c) => c.id === par.clienteId)?.nombre;
    return [empresa, cliente].filter(Boolean).join(" · ");
  })();


  /* Las claves son también los nombres de icono (ver app/iconos). `corto` es
     lo que entra debajo de un icono de 22 px sin abreviarse mal; `enConsola`
     marca las cuatro que quedan al alcance del pulgar en teléfono — las diez
     siguen accesibles desde el botón "Todas". */
  const TABS = [
    { key: "ruta", label: "Planificación de ruta", corto: "Ruta", grupo: "Planificación", enConsola: true },
    { key: "analisis", label: "Análisis de vuelo (W&B · Performance · Combustible)", corto: "Análisis", grupo: "Planificación", enConsola: true },
    { key: "espacio", label: "Espacio aéreo (AIP)", corto: "Espacio", grupo: "Planificación" },
    { key: "frat", label: "Riesgo (FRAT)", corto: "FRAT", grupo: "Planificación", enConsola: true },
    { key: "resumen", label: "Resumen y hoja de vuelo", corto: "Resumen", grupo: "Planificación", enConsola: true },
    { key: "vuelo", label: "Modo vuelo (GPS)", corto: "Vuelo", grupo: "Planificación", enConsola: true },
    /* EMPRESA no va en la lista de herramientas: es administración, no vuelo.
       Se entra desde el botón de la barra de arriba (ver DisclaimerBar), y así
       no queda duplicada ni escondida detrás del cajón «Todas». El bloque de
       más abajo la sigue dibujando igual. */
    { key: "empresa", label: "Empresa, usuarios y perfiles", corto: "Empresa", grupo: "Gestión", fueraDeLaLista: true },
    { key: "estadisticas", label: "Estadísticas de horas de vuelo", corto: "Estad.", grupo: "Gestión" },
    { key: "operaciones", label: "Operaciones", corto: "Oper.", grupo: "Gestión" },
    { key: "mantenimiento", label: "Mantenimiento", corto: "Mant.", grupo: "Gestión" },
    { key: "sms", label: "SGS / SMS", corto: "SGS", grupo: "Gestión" },
    { key: "importar", label: "Importar aeronave", corto: "Importar", grupo: "Gestión" },
    { key: "dashboard", label: "Dashboard", corto: "Tablero", grupo: "Gestión" },
    /* CONSULTA no es un módulo: es un proceso. Acá no se llena nada —matrices,
       criterios, escenarios, glosario y la biblioteca de los 51 eventos— y
       estaba todo metido entre los formularios del SGS, que sí son de carga.
       Mezclar lo que se mira con lo que se llena es parte de por qué el SGS
       tenía trece paneles. */
    { key: "consulta", label: "Consulta y referencia", corto: "Consulta", grupo: "Gestión" },
  ];

  /* ─────────────────────────────────────────────────────────────────────────
   * BLOQUE DE DATOS Y PANEL DE AVISOS
   *
   * Nada de esto se calcula acá: son los mismos valores que ya usa cada
   * pestaña. Lo único nuevo es que se muestran SIEMPRE y en un solo lugar, en
   * vez de solamente adentro de la herramienta que los produjo.
   * ──────────────────────────────────────────────────────────────────────── */
  const limiteVigenteKg = Number.isFinite(limiteDestino?.limiteKg)
    ? limiteDestino.limiteKg
    : (Number.isFinite(limiteOrigen?.limiteKg) ? limiteOrigen.limiteKg : null);

  const avisosDeCabina = construirAvisos({
    pesoBalance: {
      pesoTotalKg: totalKg,
      limiteKg: maximoEstructuralKg,
      dentroDeEnvolvente: insideEnvelope,
      dentroEnAterrizaje: wbAterrizaje?.valido ? wbAterrizaje.dentroDeEnvolvente : null,
    },
    performance: { pesoTotalKg: totalKg, limiteKg: limiteVigenteKg },
    combustible: { aBordoKg: fuelKg, requeridoKg: requiredKg },
    mantenimiento: { filas: buildStatusReport(aeronaveActiva?.id) },
    /* `promedioFrat` es el objeto que devuelve promedioDeRiesgo(); el nivel
       legible es `nivelEquivalente`, redondeado hacia arriba. */
    frat: { nivel: promedioFrat.nivelEquivalente, factoresQueExigenMedida: factoresQueExigenMedida.length },
  });

  const estadoDePeso = (() => {
    if (!Number.isFinite(maximoEstructuralKg)) return undefined;
    if (totalKg > maximoEstructuralKg) return "alarma";
    if (maximoEstructuralKg - totalKg < 40) return "precaucion";
    return "ok";
  })();

  const nivelFrat = nivelDesdeEstado(promedioFrat.nivelEquivalente);

  /* Que el panel de avisos esté abierto o cerrado se recuerda entre sesiones:
     es una preferencia de cómo trabaja cada quien, no un estado del vuelo. */
  const [panelAvisosVisible, setPanelAvisosVisible] = usePersistedState("panelAvisosVisible", true);

  /* ═══ LOS GRUPOS DEL MENÚ SE PLIEGAN, Y SE RECUERDAN ═══

     Quien trabaja siempre en Planificación pliega Gestión una vez y no la
     vuelve a ver; el menú de trece herramientas queda en la mitad. Se guarda
     cuáles están plegados y no cuáles abiertos: así, un grupo NUEVO —una
     herramienta que se agrega mañana— entra visible en vez de aparecer
     escondido sin que nadie lo note. */
  const [gruposCerrados, setGruposCerrados] = usePersistedState("menuGruposCerrados", []);
  const alternarGrupo = (nombre) => setGruposCerrados((v) => (
    v.includes(nombre) ? v.filter((n) => n !== nombre) : [...v, nombre]
  ));

  const datosDeCabina = [
    {
      rotulo: `Peso de despegue (máx. ${fmtWeight(maximoEstructuralKg).toFixed(0)})`,
      valor: fmtWeight(totalKg).toFixed(0),
      unidad: weightUnitLabel,
      estado: estadoDePeso,
    },
    {
      rotulo: "CG longitudinal",
      valor: totalKg > 0 ? cg.toFixed(3) : "—",
      unidad: "m",
      estado: insideEnvelope === false ? "alarma" : (insideEnvelope ? "ok" : undefined),
    },
    {
      rotulo: "Combustible",
      valor: fmtWeight(fuelKg).toFixed(0),
      unidad: weightUnitLabel,
      estado: fuelMargin === null ? undefined : (fuelMargin < 0 ? "alarma" : "ok"),
    },
    {
      rotulo: "Riesgo (FRAT)",
      valor: promedioFrat.nivelEquivalente || "sin evaluar",
      estado: nivelFrat === "alarma" ? "alarma" : (nivelFrat === "precaucion" ? "precaucion" : "ok"),
    },
    { rotulo: "Ruta", valor: `${origin.name} → ${destination.name}` },
  ];

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, color: COLORS.text, fontFamily: "'Inter', sans-serif" }}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap" />
      <style>{`
        /* Móvil (Android/iOS) y web: reglas de responsividad globales para el prototipo. */
        table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        input, select, textarea, button { font-family: inherit; }
        @media (max-width: 720px) {
          /* iOS Safari hace zoom automático al enfocar un campo con font-size menor a 16px — esto lo evita. */
          input, select, textarea { font-size: 16px !important; }
          /* Colapsa a una sola columna las grillas de 2 y 3 columnas más comunes del prototipo. */
          [style*="grid-template-columns: 1fr 1fr"],
          [style*="grid-template-columns: 1.2fr 1fr"],
          [style*="grid-template-columns: 1fr 1.2fr"],
          [style*="grid-template-columns: 1fr 1.4fr"],
          [style*="grid-template-columns: 1.4fr 1fr"],
          [style*="grid-template-columns: 1fr 1fr 1fr"] { grid-template-columns: 1fr !important; }
          [style*="repeat(4, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
          /* Botones y casilleros con más margen para el dedo.
             34 px era MENOS que el mínimo táctil de 44, y como esta hoja se
             inyecta después de la global, le ganaba por orden de cascada: la
             pantalla chica —justo el iPad— quedaba con los botones más chicos
             que la grande. */
          button { min-height: 44px; }
        }
      `}</style>

      {/* ── Armazón de cabina ───────────────────────────────────────────────
          La navegación dejó de ser dos filas de botones con el nombre largo de
          cada pestaña. Ahora es un riel de iconos al costado —consola inferior
          en teléfono—, con el bloque de datos siempre a la vista arriba y los
          avisos de TODOS los módulos juntos a la derecha.

          Los grupos siguen siendo los mismos: PLANIFICACIÓN es lo que se hace
          antes de despegar para este vuelo, GESTIÓN lo que vive entre vuelos.
          Separarlos evita que un piloto apurado entre a Mantenimiento buscando
          la performance de hoy. */}
      <Cabina
        tema={tema}
        onTema={setTema}
        herramientas={TABS}
        activa={tab}
        alCambiar={setTab}
        matricula={matricula}
        tipo={activeProfile.tipo}
        datos={datosDeCabina}
        avisos={avisosDeCabina}
        panelVisible={panelAvisosVisible}
        alCambiarPanel={setPanelAvisosVisible}
        gruposCerrados={gruposCerrados}
        alCambiarGrupo={alternarGrupo}
      >

      {/* ═══ EL CAMINO DE LA PREPARACIÓN, A LA VISTA ═══

          El orden ya existía —no se calcula el peso y balance sin saber qué se
          carga— pero vivía en la cabeza del piloto, y si se salteaba un paso
          nada lo advertía hasta que del otro lado faltaba el dato.

          Va SOLO en las pestañas de la preparación. En Mantenimiento o en el
          SGS no se está preparando un vuelo, y una barra que aparece en todas
          partes deja de leerse. */}
      {PESTANAS_PREPARACION.has(tab) && (
        <BarraDePasos hechos={hechosPreparacion} pasoActivo={tab} alIr={setTab} />
      )}

      {/* ---------------- ESTADÍSTICAS DE HORAS ---------------- */}
      {tab === "estadisticas" && (
        <PanelEstadisticas
          bitacora={flightLog}
          aeronaves={fleet}
          pilotos={pilots}
          empresas={identidad?.empresas || []}
          clientes={clientes}
        />
      )}

      {/* ---------------- EMPRESA, USUARIOS Y PERFILES ---------------- */}
      {tab === "empresa" && (
        <PanelAdministracion
          estado={identidad}
          alCambiar={setIdentidad}
          clientes={clientes}
          alCambiarClientes={setClientes}
          perfilQueMira={perfilQueMira}
          alCambiarPerfil={setPerfilQueMira}
        />
      )}

      {/* ---------------- RUTA ---------------- */}
      {tab === "dashboard" && (() => {
        const tareaOpt = RISK_GROUPS.find((g) => g.key === "tarea")?.options[riskChoices.tarea];
        const hourAlerts = hourComplianceItems.map((m) => {
          const ac = fleet.find((f) => f.id === m.aircraftId);
          const currentTsn = m.motor ? ac?.motor?.tsn : ac?.tsn;
          if (!Number.isFinite(m.remHorasAlReporte)) return { item: m.item, estado: "TOLERABLE", label: "SIN DATO", rem: null };
          const rem = currentTsn !== undefined ? m.remHorasAlReporte - (currentTsn - REPORT_TSN_REF) : m.remHorasAlReporte;
          return { item: m.item, ...hourAlarmStatus(rem), rem };
        });
        const dateAlerts = dateComplianceItems.map((m) => {
          const dias = m.fecha ? Math.ceil((new Date(m.fecha).getTime() - Date.now()) / 86400000) : null;
          return { item: m.item, ...dateAlarmStatus(dias), dias };
        });
        const mantVencidos = [...hourAlerts, ...dateAlerts].filter((a) => a.estado === "INTOLERABLE");
        const mantProximos = [...hourAlerts, ...dateAlerts].filter((a) => a.estado === "TOLERABLE");
        const pilotAsignado = pilots.find((p) => p.id === assignedPilotId);
        const medDias = pilotAsignado?.aptoMedicoVencimiento ? Math.ceil((new Date(pilotAsignado.aptoMedicoVencimiento).getTime() - Date.now()) / 86400000) : null;
        const compartimentosSobrepeso = compartmentTotals.filter((c) => c.overweight);

        // Histórico de operaciones: se arma SOLO de flightLog (la bitácora real de Operaciones/Resumen) — nunca
        // de la planificación, que es solo una estimación. De la planificación únicamente se hereda el tipo de
        // vuelo (Traslado, Magnetometría, Carga externa LL, etc.) al registrar; pasajeros, carga y combustible
        // real quedan fijados recién cuando el vuelo se registra como ejecutado en la bitácora.
        const tiposAeronaveDisponibles = [...new Set(flightLog.map((l) => l.tipoAeronave).filter(Boolean))];
        const tiposOperacionDisponibles = [...new Set(flightLog.map((l) => l.tipoOperacion).filter(Boolean))];
        const dentroDelPeriodo = (fechaStr) => {
          if (dashFilterPeriodo === "todo") return true;
          const fecha = new Date(fechaStr);
          const ahora = new Date();
          const dias = (ahora.getTime() - fecha.getTime()) / 86400000;
          if (dashFilterPeriodo === "dia") return dias <= 1;
          if (dashFilterPeriodo === "semana") return dias <= 7;
          if (dashFilterPeriodo === "mes") return dias <= 31;
          if (dashFilterPeriodo === "semestre") return dias <= 183;
          if (dashFilterPeriodo === "anio") return dias <= 366;
          return true;
        };
        const historicoFiltrado = flightLog.filter((l) =>
          (dashFilterTipo === "todos" || l.tipoAeronave === dashFilterTipo) &&
          (dashFilterOperacion === "todas" || l.tipoOperacion === dashFilterOperacion) &&
          dentroDelPeriodo(l.fecha)
        );
        const historico = {
          vuelos: historicoFiltrado.length,
          pasajeros: historicoFiltrado.reduce((s, l) => s + (l.pasajeros || 0), 0),
          cargaInterna: historicoFiltrado.reduce((s, l) => s + (l.cargaInternaKg || 0), 0),
          cargaExterna: historicoFiltrado.reduce((s, l) => s + (l.cargaExternaKg || 0), 0),
          combustible: historicoFiltrado.reduce((s, l) => s + (l.combustibleUsadoKg || 0), 0),
          horas: historicoFiltrado.reduce((s, l) => s + (l.horasAeronave || 0), 0),
        };
        // Desglose por aeronave (matrícula) y por tipo de helicóptero — toma TODOS los vuelos de la bitácora,
        // sin el filtro de arriba, para que ningún dato ingresado quede afuera del total.
        const porAeronave = {};
        const porTipo = {};
        flightLog.forEach((l) => {
          const matriculaL = fleet.find((f) => f.id === l.aircraftId)?.matricula || "sin matrícula";
          if (!porAeronave[matriculaL]) porAeronave[matriculaL] = { vuelos: 0, combustible: 0, horas: 0, pasajeros: 0, cargaInterna: 0, cargaExterna: 0 };
          porAeronave[matriculaL].vuelos += 1;
          porAeronave[matriculaL].combustible += l.combustibleUsadoKg || 0;
          porAeronave[matriculaL].horas += l.horasAeronave || 0;
          porAeronave[matriculaL].pasajeros += l.pasajeros || 0;
          porAeronave[matriculaL].cargaInterna += l.cargaInternaKg || 0;
          porAeronave[matriculaL].cargaExterna += l.cargaExternaKg || 0;
          const tipoL = l.tipoAeronave || "sin tipo";
          if (!porTipo[tipoL]) porTipo[tipoL] = { vuelos: 0, combustible: 0, horas: 0, pasajeros: 0, cargaInterna: 0, cargaExterna: 0 };
          porTipo[tipoL].vuelos += 1;
          porTipo[tipoL].combustible += l.combustibleUsadoKg || 0;
          porTipo[tipoL].horas += l.horasAeronave || 0;
          porTipo[tipoL].pasajeros += l.pasajeros || 0;
          porTipo[tipoL].cargaInterna += l.cargaInternaKg || 0;
          porTipo[tipoL].cargaExterna += l.cargaExternaKg || 0;
        });
        const totalGeneral = {
          vuelos: flightLog.length,
          combustible: flightLog.reduce((s, l) => s + (l.combustibleUsadoKg || 0), 0),
          horas: flightLog.reduce((s, l) => s + (l.horasAeronave || 0), 0),
          pasajeros: flightLog.reduce((s, l) => s + (l.pasajeros || 0), 0),
          cargaInterna: flightLog.reduce((s, l) => s + (l.cargaInternaKg || 0), 0),
          cargaExterna: flightLog.reduce((s, l) => s + (l.cargaExternaKg || 0), 0),
        };
        return (
        <div style={{ display: "grid", gap: 16 }}>
          {(() => {
            const COLOR_REG = {
              "ORO": "#D4AF37", "PLATA": "#B8BFC7", "VERDE": COLORS.ok,
              "ÁMBAR": COLORS.amber, "FUERA DE PLAZO": COLORS.danger, "SIN AUDITORÍA": COLORS.muted,
            };
            const c = COLOR_REG[registroBars.color];
            const porPrioridad = PRIORIDADES_NC.map((pr) => {
              const propias = noConformidades.filter((n) => n.prioridad === pr.clave);
              const estados = propias.map((n) => estadoNoConformidad({ ...n, plazoHasta: n.plazoHasta || null }));
              return {
                ...pr, total: propias.length,
                abiertas: estados.filter((e) => e.abierta).length,
                vencidas: estados.filter((e) => e.abierta && e.enPlazo === false).length,
                sinPlazo: estados.filter((e) => e.enPlazo === null).length,
              };
            });
            return (
              <Panel eyebrow="Auditoría de seguridad" title="Autoevaluación previa — lógica de colores del programa BARS" right={
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={addAuditoria} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ auditoría</button>
                  <button onClick={addNoConformidad} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ no conformidad</button>
                </div>
              }>
                <div style={{ background: `${c}18`, border: `2px solid ${c}`, borderRadius: 10, padding: "14px 18px", marginBottom: 14 }}>
                  <div style={{ fontSize: 10.5, color: COLORS.muted, letterSpacing: 1.2, textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif" }}>
                    Cómo llegarías hoy a una auditoría
                  </div>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 30, color: c, lineHeight: 1.2 }}>
                    {registroBars.color}
                  </div>
                  <p style={{ fontSize: 12.5, color: COLORS.text, margin: "4px 0 0" }}>{registroBars.motivo}</p>
                  {registroBars.propio && (
                    <p style={{ fontSize: 11, color: COLORS.amber, margin: "6px 0 0" }}>
                      Este estado no figura en la descripción del programa: es un criterio propio de la app. Una P1
                      abierta con el plazo vencido no puede verse igual que una abierta y en término.
                    </p>
                  )}
                  <p style={{ fontSize: 11, color: COLORS.muted, margin: "10px 0 0", lineHeight: 1.5 }}>
                    <strong>Esto NO es un registro BARS.</strong> La designación la emite la BARS Program Office a
                    partir de auditorías realizadas por auditores acreditados de una Compañía de Auditoría
                    registrada, y el informe es propiedad del operador que la solicita. Acá solo se reproduce la
                    lógica de colores para ver cómo llegarías a esa auditoría. No sirve como evidencia.
                  </p>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
                  {porPrioridad.map((pr) => (
                    <Readout key={pr.clave}
                      label={`${pr.clave} — abiertas / total`}
                      value={`${pr.abiertas} / ${pr.total}`} unit=""
                      tone={pr.vencidas > 0 ? "INTOLERABLE" : pr.abiertas > 0 ? "TOLERABLE" : "ACEPTABLE"} />
                  ))}
                  <Readout label="Auditorías registradas" value={auditorias.length} unit="" />
                  <Readout label="Renovaciones en término" value={auditorias.filter((a) => a.tipo === "renovacion" && a.antesDelVencimiento).length} unit="" />
                </div>
                {porPrioridad.some((pr) => pr.sinPlazo > 0) && (
                  <Banner tone="TOLERABLE">
                    Hay no conformidades sin plazo de cierre cargado. El plazo lo fija el informe de auditoría; sin
                    él la app no puede decir si están en término, y no lo inventa.
                  </Banner>
                )}

                {auditorias.length > 0 && (
                  <div className="hc-tabla-ancha" style={{ overflowX: "auto", marginTop: 14 }}>
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Fecha</th><th>Tipo</th><th>¿Antes del vencimiento?</th><th>Empresa auditora</th><th>Auditor</th><th></th></tr></thead>
                      <tbody>{auditorias.map((a) => (
                        <tr key={a.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                          <td><input type="date" style={{ ...inputStyle, padding: "3px 6px" }} value={a.fecha} onChange={(e) => updateAuditoria(a.id, { fecha: e.target.value })} /></td>
                          <td>
                            <select style={{ ...inputStyle, padding: "3px 6px" }} value={a.tipo} onChange={(e) => updateAuditoria(a.id, { tipo: e.target.value })}>
                              <option value="inicial">Inicial</option><option value="renovacion">Renovación</option>
                            </select>
                          </td>
                          <td style={{ textAlign: "center" }}><input type="checkbox" checked={a.antesDelVencimiento} onChange={(e) => updateAuditoria(a.id, { antesDelVencimiento: e.target.checked })} /></td>
                          <td><input style={{ ...inputStyle, padding: "3px 6px", width: 150 }} value={a.empresaAuditora} onChange={(e) => updateAuditoria(a.id, { empresaAuditora: e.target.value })} /></td>
                          <td><input style={{ ...inputStyle, padding: "3px 6px", width: 130 }} value={a.auditor} onChange={(e) => updateAuditoria(a.id, { auditor: e.target.value })} /></td>
                          <td><button onClick={() => removeAuditoria(a.id)} style={{ background: "transparent", border: "none", color: COLORS.danger, cursor: "pointer" }}>✕</button></td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}

                {noConformidades.length > 0 && (
                  <div className="hc-tabla-ancha" style={{ overflowX: "auto", marginTop: 14 }}>
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Ref.</th><th>Prio.</th><th>Descripción</th><th>Plazo</th><th>Cerrada el</th><th>Estado</th><th></th></tr></thead>
                      <tbody>{noConformidades.map((n) => {
                        const est = estadoNoConformidad({ ...n, plazoHasta: n.plazoHasta || null });
                        const tono = !est.abierta && est.enPlazo !== false ? "ACEPTABLE" : est.enPlazo === false ? "INTOLERABLE" : "TOLERABLE";
                        return (
                          <tr key={n.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                            <td><input style={{ ...inputStyle, padding: "3px 6px", width: 70 }} value={n.referencia} onChange={(e) => updateNoConformidad(n.id, { referencia: e.target.value })} /></td>
                            <td>
                              <select style={{ ...inputStyle, padding: "3px 6px" }} value={n.prioridad} onChange={(e) => updateNoConformidad(n.id, { prioridad: e.target.value })}>
                                {PRIORIDADES_NC.map((pr) => <option key={pr.clave} value={pr.clave} title={pr.descripcion}>{pr.clave}</option>)}
                              </select>
                            </td>
                            <td><input style={{ ...inputStyle, padding: "3px 6px", width: 240 }} value={n.descripcion} onChange={(e) => updateNoConformidad(n.id, { descripcion: e.target.value })} /></td>
                            <td><input type="date" style={{ ...inputStyle, padding: "3px 6px" }} value={n.plazoHasta || ""} onChange={(e) => updateNoConformidad(n.id, { plazoHasta: e.target.value })} /></td>
                            <td><input type="date" style={{ ...inputStyle, padding: "3px 6px" }} value={n.cerradaEl || ""} onChange={(e) => updateNoConformidad(n.id, { cerradaEl: e.target.value || null })} /></td>
                            <td><StatusPill status={tono} /> <span style={{ fontSize: 10, color: COLORS.muted }}>{est.etiqueta}</span></td>
                            <td><button onClick={() => removeNoConformidad(n.id)} style={{ background: "transparent", border: "none", color: COLORS.danger, cursor: "pointer" }}>✕</button></td>
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  </div>
                )}
              </Panel>
            );
          })()}

          <Panel eyebrow="Anexo 2 · bow-tie" title="Integridad de barreras — qué falta para que la ficha esté completa">
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0 }}>
              Las ocho preguntas de la metodología bow-tie (SKYbrary/EUROCONTROL) aplicadas a la ficha de
              seguimiento del Anexo 2. No mide cuántos eventos hay: mide cuántos están realmente cerrados como
              análisis. Un evento sin peligro nombrado y sin factores de escalada es una lista de buenos
              propósitos, no un bow-tie.
            </p>
            {bowTie.eventos === 0 ? (
              <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0 }}>Sin eventos en el registro del SGS todavía.</p>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
                  <Readout label="Completitud media" value={`${bowTie.promedio} %`} unit="" tone={bowTie.promedio >= 80 ? "ACEPTABLE" : bowTie.promedio >= 50 ? "TOLERABLE" : "INTOLERABLE"} />
                  <Readout label="Fichas completas" value={`${bowTie.completos} / ${bowTie.eventos}`} unit="" tone={bowTie.completos === bowTie.eventos ? "ACEPTABLE" : "TOLERABLE"} />
                  <Readout label="Sin peligro nombrado" value={bowTie.sinPeligro} unit="" tone={bowTie.sinPeligro > 0 ? "TOLERABLE" : "ACEPTABLE"} />
                  <Readout label="Sin factores de escalada" value={bowTie.sinEscalada} unit="" tone={bowTie.sinEscalada > 0 ? "TOLERABLE" : "ACEPTABLE"} />
                  <Readout label="Sin responsable" value={bowTie.sinResponsable} unit="" tone={bowTie.sinResponsable > 0 ? "INTOLERABLE" : "ACEPTABLE"} />
                  <Readout label="Medidas sin implementar" value={bowTie.sinImplementar} unit="" tone={bowTie.sinImplementar > 0 ? "INTOLERABLE" : "ACEPTABLE"} />
                </div>
                <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}>
                    <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Evento</th><th>Peligro</th><th>Amenazas</th><th>F. escalada</th><th>Bow-tie</th><th>Qué falta</th></tr></thead>
                    <tbody>{bowTie.detalle.map(({ evento, bt }) => (
                      <tr key={evento.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                        <td style={{ padding: "5px 0", maxWidth: 220 }}>{evento.descripcion}</td>
                        <td style={{ color: evento.peligro ? COLORS.text : COLORS.amber }}>{evento.peligro || "sin nombrar"}</td>
                        <td style={{ textAlign: "center" }}>{bt.amenazas}</td>
                        <td style={{ textAlign: "center", color: bt.factoresEscalada === 0 ? COLORS.amber : COLORS.text }}>{bt.factoresEscalada}</td>
                        <td style={{ color: bt.porcentaje === 100 ? COLORS.ok : bt.porcentaje >= 50 ? COLORS.amber : COLORS.danger, fontWeight: 700 }}>{bt.porcentaje} %</td>
                        <td style={{ fontSize: 10.5, color: COLORS.muted, maxWidth: 300 }}>{bt.faltantes.map((f) => f.pregunta).join(" ") || "—"}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                {bowTie.detalle.some((d) => d.bt.barrerasSinSostenimiento) && (
                  <Banner tone="TOLERABLE">
                    Hay eventos con medidas de control declaradas pero sin nada que asegure que esas medidas no
                    fallen. Es la séptima pregunta del bow-tie, y la que más se saltea: una barrera que nadie
                    verifica no es una barrera.
                  </Banner>
                )}
              </>
            )}
          </Panel>

          <Banner tone="TOLERABLE">
            Vista consolidada de los datos clave de cada módulo para esta planificación — pensada para decidir
            rápido antes de operar y para tener a mano lo que importa de cara a futuras planificaciones,
            operaciones, SGS y mantenimiento.
          </Banner>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
            <Panel eyebrow="Planificación" title={`${origin.name} → ${destination.name}`}>
              <div style={{ display: "grid", gap: 6 }}>
                <Readout label="Distancia / ETE" value={`${totalNm.toFixed(1)} NM`} unit={`${eteMin.toFixed(0)} min`} />
                <Readout label="Nivel de crucero (Hp)" value={cruiseAltFt.toLocaleString()} unit={`${cruiseTempC}°C`} />
                <Readout label="Planificación confirmada" value={pilotConfirmed ? "Sí" : "No"} unit="" tone={pilotConfirmed ? "ACEPTABLE" : "TOLERABLE"} />
              </div>
            </Panel>

            <Panel eyebrow="Tipo de operación" title={tareaOpt?.label || "Sin definir"}>
              <div style={{ display: "grid", gap: 6 }}>
                <Readout label="Matrícula" value={matricula} unit={activeProfile.tipo} />
                <Readout label="Tipo de tarea" value={tipoDeTarea} unit="" />
                <Readout
                  label="FRAT (matriz SGS)"
                  value={worst.nivel === "ACEPTABLE" ? "OK" : `${worst.nivel}${worst.idx ? ` — ${worst.idx}` : ""}`}
                  unit="" tone={worst.nivel}
                />
              </div>
            </Panel>

            <Panel eyebrow="Peso y Balance" title="Peso bruto y envolvente">
              <div style={{ display: "grid", gap: 6 }}>
                <Readout label="Peso bruto" value={fmtWeight(totalKg).toFixed(0)} unit={weightUnitLabel} tone={insideEnvelope === false ? "INTOLERABLE" : "ACEPTABLE"} />
                <Readout label="Dentro de envolvente" value={insideEnvelope === null ? "s/d" : insideEnvelope ? "Sí" : "No"} unit="" tone={insideEnvelope === false ? "INTOLERABLE" : "ACEPTABLE"} />
                <Readout label="Pasajeros / equipaje" value={passengers.length} unit={`${fmtWeight(totalBaggageKg).toFixed(0)} ${weightUnitLabel} eq.`} />
                {compartimentosSobrepeso.length > 0 && <Readout label="Compartimentos en sobrepeso" value={compartimentosSobrepeso.map((c) => c.label).join(", ")} unit="" tone="INTOLERABLE" />}
              </div>
            </Panel>

            <Panel eyebrow="Combustible" title="Embarcado vs. requerido">
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <FuelGauge fuelKg={fuelKg} requiredKg={requiredKg} unitLabel={weightUnitLabel} fmtWeight={fmtWeight} />
                <div style={{ display: "grid", gap: 6, flex: 1, minWidth: 140 }}>
                  <Readout label="Embarcado" value={fmtWeight(fuelKg).toFixed(0)} unit={weightUnitLabel} />
                  <Readout label="Requerido" value={requiredKg === null ? "s/d" : fmtWeight(requiredKg).toFixed(0)} unit={weightUnitLabel} />
                  <Readout label="Margen" value={fuelMargin === null ? "s/d" : fmtWeight(fuelMargin).toFixed(0)} unit={weightUnitLabel} tone={fuelMargin !== null && fuelMargin < 0 ? "INTOLERABLE" : fuelMargin !== null && fuelMargin < 30 ? "TOLERABLE" : "ACEPTABLE"} />
                  <Readout label="Autonomía utilizable" value={usableMin === null ? "s/d" : (usableMin / 60).toFixed(2)} unit="h" tone={usableMin !== null && usableMin < 0 ? "INTOLERABLE" : "ACEPTABLE"} />
                </div>
              </div>
            </Panel>

            <Panel eyebrow="Operaciones" title={pilotAsignado ? `${pilotAsignado.nombre} ${pilotAsignado.apellido}` : "Sin piloto asignado"}>
              <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
                <Readout label="Licencia" value={pilotAsignado?.licencia || "s/d"} unit="" />
                <Readout label="Apto médico" value={medDias === null ? "sin fecha" : `${medDias} d`} unit="" tone={medDias === null ? "TOLERABLE" : medDias <= 0 ? "INTOLERABLE" : medDias <= 30 ? "TOLERABLE" : "ACEPTABLE"} />
              </div>
              <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Horas voladas por piloto (últimos 30 días)</div>
              <div style={{ height: 110 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pilots.map((p) => ({ nombre: p.nombre, horas: p.horasUltimos30d }))} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <XAxis dataKey="nombre" tick={{ fontSize: 9, fill: COLORS.muted }} />
                    <YAxis tick={{ fontSize: 9, fill: COLORS.muted }} />
                    <Tooltip contentStyle={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, fontSize: 11 }} />
                    <Bar dataKey="horas" fill={COLORS.cyan} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel eyebrow="Mantenimiento" title={`${fleet[0]?.matricula || "—"} — TSN ${fleet[0]?.tsn?.toFixed(1) || "—"} h`}>
              <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
                <Readout label="Ítems vencidos" value={mantVencidos.length} unit="" tone={mantVencidos.length > 0 ? "INTOLERABLE" : "ACEPTABLE"} />
                <Readout label="Ítems próximos" value={mantProximos.length} unit="" tone={mantProximos.length > 0 ? "TOLERABLE" : "ACEPTABLE"} />
                {mantVencidos[0] && <Readout label="Más urgente" value={mantVencidos[0].item} unit="" tone="INTOLERABLE" />}
              </div>
              <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Horas restantes por ítem (control por horas voladas)</div>
              <div style={{ height: 110 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourAlerts.map((a) => ({ item: a.item.length > 14 ? a.item.slice(0, 14) + "…" : a.item, horas: Math.max(0, a.rem) }))} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <XAxis type="number" tick={{ fontSize: 9, fill: COLORS.muted }} />
                    <YAxis type="category" dataKey="item" tick={{ fontSize: 9, fill: COLORS.muted }} width={90} />
                    <Tooltip contentStyle={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, fontSize: 11 }} />
                    <Bar dataKey="horas" radius={[0, 3, 3, 0]}>
                      {hourAlerts.map((a, i) => <Cell key={i} fill={a.estado === "INTOLERABLE" ? SMS_COLOR.INACEPTABLE : a.estado === "TOLERABLE" ? SMS_COLOR.GRAVE : SMS_COLOR.ACEPTABLE} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel eyebrow="SGS" title="Gestión de la seguridad">
              <div style={{ display: "grid", gap: 6 }}>
                <Readout label="Eventos registrados" value={smsKpis.total} unit="" />
                <Readout label="Sin implementar" value={smsKpis.implementadosNo} unit="" tone={smsKpis.implementadosNo > 0 ? "GRAVE" : "ACEPTABLE"} />
                <Readout label="Reportes ASR sin procesar" value={smsKpis.reportesAbiertos} unit="" tone={smsKpis.reportesAbiertos > 0 ? "MEDIO" : "ACEPTABLE"} />
                <Readout label="Eventos en Grave/Inaceptable(+) (actual)" value={smsNivelCounts("actual").GRAVE + smsNivelCounts("actual").INACEPTABLE + smsNivelCounts("actual")["INACEPTABLE+"]} unit="" tone={(smsNivelCounts("actual").GRAVE + smsNivelCounts("actual").INACEPTABLE + smsNivelCounts("actual")["INACEPTABLE+"]) > 0 ? "GRAVE" : "ACEPTABLE"} />
              </div>
            </Panel>
          </div>

          <Panel eyebrow="Histórico" title="Estadísticas de operaciones (todos los vuelos registrados)">
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
              <Field label="Filtrar por tipo de aeronave">
                <select style={inputStyle} value={dashFilterTipo} onChange={(e) => setDashFilterTipo(e.target.value)}>
                  <option value="todos">Todos los tipos</option>
                  {tiposAeronaveDisponibles.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Filtrar por tipo de operación">
                <select style={inputStyle} value={dashFilterOperacion} onChange={(e) => setDashFilterOperacion(e.target.value)}>
                  <option value="todas">Todas las operaciones</option>
                  {tiposOperacionDisponibles.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Período">
                <select style={inputStyle} value={dashFilterPeriodo} onChange={(e) => setDashFilterPeriodo(e.target.value)}>
                  <option value="dia">Diario (últimas 24 h)</option>
                  <option value="semana">Semanal (últimos 7 días)</option>
                  <option value="mes">Mensual (últimos 31 días)</option>
                  <option value="semestre">Semestral (últimos 6 meses)</option>
                  <option value="anio">Anual (últimos 12 meses)</option>
                  <option value="todo">Todo el histórico</option>
                </select>
              </Field>
            </div>
            {flightLog.length === 0 ? (
              <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0 }}>
                Todavía no hay vuelos registrados — este histórico se arma solo apenas registres vuelos en
                "Operaciones" o con "Registrar este vuelo" en Resumen.
              </p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                <Readout label="N° de vuelos" value={historico.vuelos} unit="" />
                <Readout label="Pasajeros transportados" value={historico.pasajeros} unit="" />
                <Readout label="Carga interna total" value={fmtWeight(historico.cargaInterna).toFixed(0)} unit={weightUnitLabel} />
                <Readout label="Carga externa total" value={fmtWeight(historico.cargaExterna).toFixed(0)} unit={weightUnitLabel} />
                <Readout label="Combustible usado total" value={fmtWeight(historico.combustible).toFixed(0)} unit={weightUnitLabel} />
                <Readout label="Horas totales" value={historico.horas.toFixed(1)} unit="h" />
              </div>
            )}
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 0 }}>
              Estos números salen únicamente de la bitácora real (lo que se registra en Operaciones o con el
              botón de Resumen después de que el vuelo se ejecutó) — nunca de la planificación, que es solo una
              estimación. De la planificación se hereda únicamente el tipo de vuelo (Magnetometría, Línea larga,
              Carga externa, etc.); pasajeros, carga interna/externa y combustible quedan fijados recién al
              registrar el vuelo como ejecutado, listos para reportar a la autoridad aeronáutica.
            </p>
          </Panel>

          <Panel eyebrow="Consumo acumulado" title="Combustible y horas — por aeronave, por tipo, y total general">
            {flightLog.length === 0 ? (
              <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0 }}>Sin vuelos registrados todavía — esta tabla toma absolutamente todos los vuelos que se vayan cargando, sin importar el filtro de arriba.</p>
            ) : (
              <div style={{ display: "grid", gap: 18 }}>
                <div>
                  <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Por aeronave (matrícula)</div>
                  <table style={{ width: "100%", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                    <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Matrícula</th><th>Vuelos</th><th>Horas</th><th>Combustible ({weightUnitLabel})</th><th>Pax</th><th>C. interna</th><th>C. externa</th></tr></thead>
                    <tbody>{Object.entries(porAeronave).map(([matriculaK, d]) => (
                      <tr key={matriculaK} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                        <td style={{ padding: "4px 0" }}>{matriculaK}</td><td>{d.vuelos}</td><td>{d.horas.toFixed(1)}</td>
                        <td>{fmtWeight(d.combustible).toFixed(0)}</td><td>{d.pasajeros}</td>
                        <td>{fmtWeight(d.cargaInterna).toFixed(0)}</td><td>{fmtWeight(d.cargaExterna).toFixed(0)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                <div>
                  <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Por tipo de helicóptero</div>
                  <table style={{ width: "100%", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                    <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Tipo</th><th>Vuelos</th><th>Horas</th><th>Combustible ({weightUnitLabel})</th><th>Pax</th><th>C. interna</th><th>C. externa</th></tr></thead>
                    <tbody>{Object.entries(porTipo).map(([tipoK, d]) => (
                      <tr key={tipoK} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                        <td style={{ padding: "4px 0" }}>{tipoK}</td><td>{d.vuelos}</td><td>{d.horas.toFixed(1)}</td>
                        <td>{fmtWeight(d.combustible).toFixed(0)}</td><td>{d.pasajeros}</td>
                        <td>{fmtWeight(d.cargaInterna).toFixed(0)}</td><td>{fmtWeight(d.cargaExterna).toFixed(0)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 12 }}>
                  <Readout label="TOTAL general — vuelos" value={totalGeneral.vuelos} unit="" />
                  <Readout label="TOTAL general — horas" value={totalGeneral.horas.toFixed(1)} unit="h" />
                  <Readout label="TOTAL general — combustible" value={fmtWeight(totalGeneral.combustible).toFixed(0)} unit={weightUnitLabel} />
                  <Readout label="TOTAL general — pasajeros" value={totalGeneral.pasajeros} unit="" />
                  <Readout label="TOTAL general — carga interna" value={fmtWeight(totalGeneral.cargaInterna).toFixed(0)} unit={weightUnitLabel} />
                  <Readout label="TOTAL general — carga externa" value={fmtWeight(totalGeneral.cargaExterna).toFixed(0)} unit={weightUnitLabel} />
                </div>
              </div>
            )}
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 0 }}>
              Esta tabla toma absolutamente todos los vuelos de la bitácora, sin filtrar por período — cada vuelo
              que registres (de cualquier aeronave, cualquier tipo) se suma solo a su fila correspondiente y al
              total general.
            </p>
          </Panel>
        </div>
        );
      })()}

      {tab === "ruta" && (
        <div style={{ display: "grid", gap: 16 }}>
          <Panel eyebrow="Aeronave y tripulación" title="Para esta ruta" right={
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => {
                  const id = `f_${Date.now()}`;
                  setFleet((f) => [...f, { id, matricula: "NUEVA", tipo: activeProfile.tipo, serie: "", profileId: activeProfileId, tsn: 0, csn: 0, ldg: 0, cargas: 0, motor: { tipo: "", serie: "", tsn: 0, cyNG: 0, cyNF: 0 }, pesoBasicoVacioKg: null, brazoVacioM: null, fechaPesaje: "" }]);
                  setMatricula("NUEVA");
                }}
                style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}
              >+ nueva aeronave</button>
              <button
                onClick={() => {
                  const id = `p_${Date.now()}`;
                  setPilots((ps) => [...ps, { id, nombre: "Nuevo", apellido: "Piloto", licencia: "", aptoMedicoVencimiento: "", horasTotales: 0, horasUltimos30d: 0, finServicioISO: "", jurisdiccion: JURISDICCIONES[0] }]);
                  setAssignedPilotId(id);
                }}
                style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}
              >+ nuevo piloto</button>
            </div>
          }>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
              <Field label="Aeronave (de la base de flota)">
                <select
                  style={{ ...inputStyle, width: "100%" }}
                  value={fleet.find((f) => f.matricula === matricula)?.id || ""}
                  onChange={(e) => {
                    const f = fleet.find((x) => x.id === e.target.value);
                    if (!f) return;
                    setMatricula(f.matricula);
                    if (f.profileId) setActiveProfileId(f.profileId);
                  }}
                >
                  <option value="">— elegir de la flota —</option>
                  {fleet.map((f) => <option key={f.id} value={f.id}>{f.matricula} ({f.tipo}{f.serie ? `, S/N ${f.serie}` : ""})</option>)}
                </select>
              </Field>
              <Field label="Tipo de aeronave">
                <select style={{ ...inputStyle, width: "100%" }} value={activeProfileId} onChange={(e) => setActiveProfileId(e.target.value)}>
                  {profiles.map((p) => <option key={p.id} value={p.id}>{p.tipo}</option>)}
                </select>
              </Field>
              <Field label="Matrícula">
                <input style={{ ...inputStyle, width: "100%" }} value={matricula} onChange={(e) => setMatricula(e.target.value.toUpperCase())} />
              </Field>
              <Field label="Piloto asignado">
                <select style={{ ...inputStyle, width: "100%" }} value={assignedPilotId} onChange={(e) => setAssignedPilotId(e.target.value)}>
                  {pilots.map((p) => <option key={p.id} value={p.id}>{p.nombre} {p.apellido}</option>)}
                </select>
              </Field>
              <Field label="Nombre del piloto">
                <input
                  style={{ ...inputStyle, width: "100%" }} placeholder="Nombre"
                  value={pilots.find((p) => p.id === assignedPilotId)?.nombre || ""}
                  onChange={(e) => setPilots((ps) => ps.map((p) => (p.id === assignedPilotId ? { ...p, nombre: e.target.value } : p)))}
                />
              </Field>
              <Field label="Apellido del piloto">
                <input
                  style={{ ...inputStyle, width: "100%" }} placeholder="Apellido"
                  value={pilots.find((p) => p.id === assignedPilotId)?.apellido || ""}
                  onChange={(e) => setPilots((ps) => ps.map((p) => (p.id === assignedPilotId ? { ...p, apellido: e.target.value } : p)))}
                />
              </Field>
              {/* El tipo de vuelo es el factor "Tarea" del FRAT. Se edita acá o
                  allá indistintamente: es el mismo dato y una sola fuente.
                  Cambiarlo mueve el nivel de riesgo, el resumen, la hoja de
                  vuelo y el registro del vuelo ejecutado. */}
              <Field label="Tipo de vuelo (tarea)" hint="Es el factor «Tarea» del FRAT: cambiarlo acá lo cambia en toda la aplicación.">
                <select
                  style={{ ...inputStyle, width: "100%" }}
                  value={riskChoices.tarea}
                  onChange={(e) => setRiskChoices((c) => ({ ...c, tarea: Number(e.target.value) }))}
                >
                  {RISK_GROUPS.find((g) => g.key === "tarea").options.map((o, i) => (
                    <option key={o.label} value={i}>{o.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Licencia del piloto">
                <input
                  style={{ ...inputStyle, width: "100%" }} placeholder="N° licencia"
                  value={pilots.find((p) => p.id === assignedPilotId)?.licencia || ""}
                  onChange={(e) => setPilots((ps) => ps.map((p) => (p.id === assignedPilotId ? { ...p, licencia: e.target.value } : p)))}
                />
              </Field>
            </div>
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 0 }}>
              Tipo, matrícula y piloto son los mismos de "Peso y balance" y "Operaciones → Tripulación"
              (es la misma base de datos — editar el piloto o su licencia aquí también lo actualiza allá, y viceversa).
              Para crear o eliminar pilotos, ve a "Operaciones".
            </p>
          </Panel>
          <Banner tone="TOLERABLE">
            Planifica haciendo clic en un aeródromo del mapa (queda añadido como próximo punto de ruta) o eligiéndolo
            en la lista de abajo. Las costas/países se cargan desde un mapa base real (Natural Earth); los aeródromos
            marcados en cian están verificados o son de conocimiento aeronáutico general — usa la pestaña "Aeródromos"
            para importar un dataset real completo (OurAirports) o agregar los tuyos.
          </Banner>
          <Panel eyebrow="Planificación visual" title="Mapa de ruta" right={
            <div style={{ display: "flex", gap: 10, fontSize: 11.5, flexWrap: "wrap" }}>
              {["VFR", "IFR"].map((t) => (
                <label key={t} style={{ display: "flex", gap: 4, alignItems: "center", color: COLORS.muted }}>
                  <input type="checkbox" checked={airwayLayers[t]} onChange={(e) => setAirwayLayers((l) => ({ ...l, [t]: e.target.checked }))} />{t}
                </label>
              ))}
            </div>
          }>
            {/* ═══ EL MAPA OCUPA EL ALTO DISPONIBLE ═══

                Antes era el 70 % del alto con los paneles empujándolo hacia
                abajo. En vuelo se mira el mapa y poco más: ese reparto
                desperdicia justo lo que más importa.

                El alto va por variable CSS y no por `calc()` fijo: descontar
                la cabecera con un número se rompe cada vez que la cabecera
                cambia de tamaño, y en el iPad cambia al rotar. */}
            <div
              className={dispo.modo === "capas" ? "hc-mapa-lleno" : undefined}
              style={dispo.modo === "capas"
                ? { "--hc-alto-mapa": `${altoDelMapa({ alto: ventana.alto, cabeceraPx: 132, pestañasPx: 0 })}px` }
                : undefined}
            >
              <React.Suspense fallback={<div style={{ padding: 24, color: COLORS.muted, fontSize: 13 }}>Cargando mapa…</div>}>
                <RouteMapLeaflet waypoints={waypoints} airports={airports} airways={airways} airwayLayers={airwayLayers} onAddPoint={addPointToRoute} onInsertAtSegment={insertWaypointAfter} onMoverPunto={moverPuntoDeRuta} onActualizarPunto={actualizarPuntoDeRuta} onResetRoute={resetRoute} onGuardarPunto={guardarPunto} misPuntos={misPuntos} encuadrarMisPuntos={encuadrarMisPuntos} />
              </React.Suspense>

              {/* Los paneles flotan sobre el mapa solo cuando hay lugar. El
                  módulo lo decide; acá no se supone nada. */}
              {dispo.modo === "capas" && (
                <PanelFlotante
                  titulo="Ruta"
                  resumen={`${waypoints.filter((w) => Number.isFinite(w.lat)).length} puntos · ${totalNm.toFixed(0)} NM`}
                  lado="izquierda"
                  abierto={paneles.ruta}
                  onAlternar={() => alternarPanel("ruta")}
                >
                  <ol className="panel-flotante__lista">
                    {waypoints.map((w, i) => (
                      <li key={w.id}>
                        <strong>{i === 0 ? "ORI" : (i === waypoints.length - 1 ? "DES" : i)}</strong>
                        {" "}{w.name || "(sin definir)"}
                      </li>
                    ))}
                  </ol>
                </PanelFlotante>
              )}
            </div>
          </Panel>

          <Panel eyebrow="Perfil de ruta" title="Altitud del terreno bajo la ruta" right={
            <button onClick={generateRouteProfile} disabled={routeProfileStatus === "loading"} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontWeight: 600, fontSize: 12.5 }}>
              {routeProfileStatus === "loading" ? "Generando…" : "🏔 Generar perfil"}
            </button>
          }>
            {routeProfileStatus === "idle" && <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0 }}>Fija origen y destino y pulsa "Generar perfil" para ver la altitud del terreno a lo largo de la ruta.</p>}
            {routeProfileStatus === "error" && <p style={{ fontSize: 12.5, color: COLORS.caution, margin: 0 }}>No se pudo generar el perfil — verifica que origen y destino estén fijados.</p>}
            {(routeProfileStatus === "ok" || routeProfileStatus === "ok-degraded") && routeProfile && (
              <>
                <div style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={routeProfile}
                      margin={{ top: 6, right: 12, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="terrainFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#8C6A3D" stopOpacity={0.95} />
                          <stop offset="100%" stopColor="#4A3620" stopOpacity={0.95} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={COLORS.hairline} />
                      <XAxis dataKey="distNm" type="number" domain={[0, "dataMax"]} stroke={COLORS.muted} tick={{ fontSize: 11 }} label={{ value: "NM", position: "insideBottomRight", offset: -2, fill: COLORS.muted, fontSize: 11 }} />
                      <YAxis stroke={COLORS.muted} tick={{ fontSize: 11 }} label={{ value: "ft", angle: -90, position: "insideLeft", fill: COLORS.muted, fontSize: 11 }} domain={[
                        // Arrancaba en 0. Con el aterrizaje a −13 ft el extremo
                        // del descenso quedaba fuera del dominio y la línea se
                        // recortaba: por eso el ascenso se veía y el descenso no.
                        (dataMin) => Math.min(dataMin, cotaSalidaGrafico ?? dataMin, cotaLlegadaGrafico ?? dataMin) - 200,
                        (dataMax) => Math.max(dataMax, cruiseAltFt) + 1000,
                      ]} />
                      <Tooltip
                        contentStyle={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}` }}
                        content={({ active, payload, label }) => {
                          if (!active || !payload || !payload.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 6, padding: "6px 10px", fontSize: 11.5 }}>
                              <div>{label} NM</div>
                              <div style={{ color: "#B8935A", fontWeight: 700 }}>{pies(d.elevFt)}</div>
                              <div style={{ color: Math.abs(d.slopePct) >= 10 ? COLORS.danger : Math.abs(d.slopePct) >= 5 ? COLORS.caution : COLORS.muted }}>
                                pendiente: {d.slopePct > 0 ? "+" : ""}{d.slopePct}%
                              </div>
                            </div>
                          );
                        }}
                      />
                      <Area type="monotone" dataKey="elevFt" name="Terreno" stroke="#B8935A" strokeWidth={1.5} fill="url(#terrainFill)" />
                      <ReferenceLine y={cruiseAltFt} stroke="#FF3EA5" strokeDasharray="6 4" strokeWidth={1.6} label={{ value: `Crucero ${cruiseAltFt.toLocaleString()} ft`, fill: "#FF3EA5", fontSize: 10.5, position: "insideTopRight" }} />
                      {/* Ascenso y descenso como segmentos rectos entre dos puntos
                          conocidos. La versión anterior los dibujaba como series de
                          datos con huecos y no aparecían: un segmento recto no
                          depende de que las millas del terreno y las de la senda
                          coincidan. */}
                      {sendaDibujable && perfilVertical.inicioAscensoNm > 0 && (
                        <ReferenceLine
                          segment={[
                            { x: rangoEjeX.min, y: cotaSalidaGrafico },
                            { x: enEje(perfilVertical.inicioAscensoNm), y: cotaSalidaGrafico },
                          ]}
                          stroke="#FF3EA5" strokeWidth={2.4} strokeDasharray="7 5"
                        />
                      )}
                      {sendaDibujable && (
                        <ReferenceLine
                          segment={[
                            { x: enEje(perfilVertical.inicioAscensoNm), y: cotaSalidaGrafico },
                            { x: enEje(perfilVertical.tocNm), y: cruiseAltFt },
                          ]}
                          stroke="#FF3EA5" strokeWidth={2.4} strokeDasharray="7 5"
                          label={{ value: `↗ ${perfilVertical.regimenAscensoRequeridoFtMin} ft/min`, fill: "#FF3EA5", fontSize: 10.5, position: "insideTopLeft" }}
                        />
                      )}
                      {sendaDibujable && (
                        <ReferenceLine
                          segment={[
                            { x: enEje(perfilVertical.todNm), y: cruiseAltFt },
                            { x: rangoEjeX.max, y: cotaLlegadaGrafico },
                          ]}
                          stroke="#FF3EA5" strokeWidth={2.4} strokeDasharray="7 5"
                          label={{ value: `↘ ${perfilVertical.regimenDescensoRequeridoFtMin} ft/min`, fill: "#FF3EA5", fontSize: 10.5, position: "insideBottomRight" }}
                        />
                      )}
                      {perfilVertical.valido && perfilVertical.inicioAscensoNm > 0 && (
                        <ReferenceLine x={enEje(perfilVertical.inicioAscensoNm)} stroke="#5BD1D7" strokeDasharray="2 3" label={{ value: "Inicio ascenso", fill: "#5BD1D7", fontSize: 10, position: "top" }} />
                      )}
                      {perfilVertical.valido && (
                        <ReferenceLine x={enEje(perfilVertical.tocNm)} stroke="#5BD1D7" strokeDasharray="2 3" label={{ value: "TOC", fill: "#5BD1D7", fontSize: 10, position: "top" }} />
                      )}
                      {perfilVertical.valido && (
                        <ReferenceLine x={enEje(perfilVertical.todNm)} stroke="#5BD1D7" strokeDasharray="2 3" label={{ value: "TOD", fill: "#5BD1D7", fontSize: 10, position: "top" }} />
                      )}
                      <ReferenceDot x={0} y={origin.elevFt} r={5} fill="#2E9BF2" stroke="#0A1420" />
                      <ReferenceDot x={routeProfile[routeProfile.length - 1]?.distNm} y={destination.elevFt} r={5} fill="#2E9BF2" stroke="#0A1420" />
                      {(() => {
                        const peak = routeProfile.reduce((max, p) => (p.elevFt > max.elevFt ? p : max), routeProfile[0]);
                        return <ReferenceDot x={peak.distNm} y={peak.elevFt} r={4} fill="#FFD400" stroke="#0A1420" label={{ value: `▲ ${Math.round(peak.elevFt).toLocaleString()} ft`, fill: "#FFD400", fontSize: 10.5, position: "top" }} />;
                      })()}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                {(() => {
                  const steepest = routeProfile.reduce((max, p) => (Math.abs(p.slopePct) > Math.abs(max.slopePct) ? p : max), routeProfile[0]);
                  return (
                    <div style={{ display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap" }}>
                      <Readout label="Pendiente más pronunciada" value={`${steepest.slopePct > 0 ? "+" : ""}${steepest.slopePct}%`} unit={`en NM ${steepest.distNm}`} tone={Math.abs(steepest.slopePct) >= 10 ? "INTOLERABLE" : Math.abs(steepest.slopePct) >= 5 ? "TOLERABLE" : "ACEPTABLE"} />
                    </div>
                  );
                })()}
                <p style={{ fontSize: 10.5, color: COLORS.muted, marginTop: 4, marginBottom: 0 }}>
                  {routeProfile.length} puntos muestreados a lo largo de la ruta (~1 cada 3 km) — mientras más
                  puntos, más se parece este perfil al de Google Earth en vez de una línea recta entre pocos hitos.
                  Pasa el cursor sobre el gráfico para ver la pendiente exacta (% de grado) en cualquier tramo.
                </p>
                <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 8, marginBottom: 0 }}>
                  Perfil aproximado: interpola en línea recta entre waypoints y consulta elevación real por tramos
                  (Open-Elevation) — no reemplaza una carta topográfica oficial ni detecta obstáculos artificiales
                  (torres, líneas de tendido, etc.), solo relieve del terreno. La línea magenta punteada es tu nivel
                  de crucero actual.
                </p>
                {routeProfileSource && (
                  <p style={{ fontSize: 10.5, color: routeProfileSource.huecos ? COLORS.caution : COLORS.muted, marginTop: 4, marginBottom: 0 }}>
                    {routeProfileSource.usedApi}/{routeProfileSource.total} puntos leídos del modelo digital de
                    elevación real (SRTM ~30 m, error vertical típico ±10-16 ft)
                    {routeProfileSource.huecos ? `. ${routeProfileSource.huecos} punto(s) sin dato — se dibujan como hueco, no se rellenan` : ""}.
                    El modelo describe el terreno: <strong>no incluye torres, antenas ni tendidos eléctricos</strong>.
                  </p>
                )}
                <p style={{ fontSize: 11, color: COLORS.cyan, marginTop: 6, marginBottom: 0 }}>
                  Nivel de vuelo sugerido ({cruiseAltFt.toLocaleString()} ft) y temperatura ({cruiseTempC}°C) ya quedaron
                  cargados solos en "Análisis de vuelo → Combustible" (terreno más alto + 1000 ft, promedio de
                  temperatura origen/destino) — ajústalos ahí si tu crucero real es distinto.
                </p>
              </>
            )}
          </Panel>

          <Panel eyebrow="Perfil vertical" title="Ascenso, crucero y descenso" right={
            (todManualNm !== null || inicioAscensoNm > 0) && (
              <button onClick={() => { setTodManualNm(null); setInicioAscensoNm(0); }} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>
                ↺ volver al descenso calculado
              </button>
            )
          }>
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0, marginBottom: 12 }}>
              La senda cian sobre el perfil de terreno. El descenso arranca en el último punto que deja la
              trayectoria libre de obstáculos: se calcula hacia atrás desde el punto de aterrizaje y, si el
              terreno se mete en el camino, se retrasa. Todos los valores son editables — son perfiles de
              operación del piloto, no límites del fabricante.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
              <Field label="Ascenso (ft/min)"><CampoNumero step={50} style={inputStyle} valor={ascenso.regimenFtMin} alCambiar={(n) => setAscenso((a) => ({ ...a, regimenFtMin: n }))} /></Field>
              <Field label="Ascenso (kt)"><CampoNumero step={5} style={inputStyle} valor={ascenso.velocidadKt} alCambiar={(n) => setAscenso((a) => ({ ...a, velocidadKt: n }))} /></Field>
              <Field label="Descenso (ft/min)"><CampoNumero step={50} style={inputStyle} valor={descenso.regimenFtMin} alCambiar={(n) => setDescenso((d) => ({ ...d, regimenFtMin: n }))} /></Field>
              <Field label="Descenso (kt)"><CampoNumero step={5} style={inputStyle} valor={descenso.velocidadKt} alCambiar={(n) => setDescenso((d) => ({ ...d, velocidadKt: n }))} /></Field>
              <Field label="Margen sobre el terreno (ft)" hint="Parámetro de la app, no un mínimo reglamentario.">
                <CampoNumero step={100} style={inputStyle} valor={margenLibramientoFt} alCambiar={(n) => setMargenLibramientoFt(n)} />
              </Field>
              <Field label="Inicio de ascenso (NM)" hint="0 = sube desde el despegue. Sirve para salir nivelado de un valle antes de trepar.">
                <CampoNumero step={0.5} min={0} style={inputStyle}
                  valor={inicioAscensoNm}
                  alCambiar={(n) => setInicioAscensoNm(Math.max(0, n || 0))} />
              </Field>
              <Field label="Inicio de descenso (NM)" hint={todManualNm === null ? "vacío = lo calcula la app" : "fijado a mano"}>
                <CampoNumero step={0.5} style={inputStyle}
                  placeholder={perfilVertical.valido ? String(perfilVertical.todNm) : ""}
                  valor={todManualNm === null ? "" : todManualNm}
                  alCambiar={(n) => setTodManualNm(n === null ? null : n)} permitirVacio />
              </Field>
            </div>

            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: -4, marginBottom: 12 }}>
              Ascenso y descenso se dibujan en <span style={{ color: "#FF3EA5" }}>magenta punteado</span> sobre el
              perfil de terreno. Los regímenes se calculan solos para librar los obstáculos: si los valores
              elegidos no alcanzan, la app usa los que sí y lo avisa. La primera{" "}
              {perfilVertical.transicionAscensoNm ?? "—"} NM tras el despegue no las cubre este cálculo: ahí el
              libramiento es la trayectoria de despegue, visual y con la carta del helipuerto.
            </p>
            {!perfilVertical.valido ? (
              <Banner tone="INTOLERABLE">{perfilVertical.avisos.join(" ")}</Banner>
            ) : (
              <>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                  <Readout
                    label="Régimen de ascenso necesario"
                    value={perfilVertical.regimenAscensoRequeridoFtMin} unit="ft/min"
                    tone={perfilVertical.ascensoAjustado ? "TOLERABLE" : "ACEPTABLE"}
                  />
                  <Readout label="Gradiente de ascenso" value={perfilVertical.gradienteAscensoFtNm} unit="ft/NM" />
                  <Readout label="Top of climb" value={perfilVertical.tocNm} unit={`NM · ${perfilVertical.minutosAscenso} min`} />
                  <Readout label="Top of descent" value={perfilVertical.todNm} unit={`NM · ${perfilVertical.minutosDescenso} min`} tone={perfilVertical.ajustadoPorTerreno ? "TOLERABLE" : undefined} />
                  <Readout label="Gradiente de descenso" value={perfilVertical.gradienteDescensoFtNm} unit="ft/NM" />
                  <Readout
                    label="Régimen de descenso necesario"
                    value={perfilVertical.regimenDescensoRequeridoFtMin} unit="ft/min"
                    tone={perfilVertical.regimenDescensoRequeridoFtMin > descenso.regimenFtMin + 20 ? "TOLERABLE" : "ACEPTABLE"}
                  />
                  <Readout label="Libra el terreno" value={perfilVertical.libraElTerreno ? "Sí" : "No"} unit="" tone={perfilVertical.libraElTerreno ? "ACEPTABLE" : "INTOLERABLE"} />
                  {perfilVertical.obstaculoCrucero && (
                    <Readout label="Crucero mínimo necesario" value={Math.round(perfilVertical.obstaculoCrucero.terrenoFt + margenLibramientoFt).toLocaleString()} unit="ft" tone="INTOLERABLE" />
                  )}
                </div>

                {/* La cuenta completa, escrita. Ver "no libra" con mil pies de
                    aire sobre la cima en el gráfico parece un error de la app,
                    y no lo es: lo que falta es el margen que el piloto fijó.
                    Sin mostrar la aritmética, el aviso no se entiende. */}
                {perfilVertical.obstaculoCrucero && (
                  <Banner tone="INTOLERABLE">
                    <strong>Por qué dice que no libra.</strong>{" "}
                    Cima en ruta {Math.round(perfilVertical.obstaculoCrucero.terrenoFt).toLocaleString()} ft
                    {" + "}margen {margenLibramientoFt.toLocaleString()} ft
                    {" = "}<strong>{Math.round(perfilVertical.obstaculoCrucero.terrenoFt + margenLibramientoFt).toLocaleString()} ft necesarios</strong>.
                    {" "}El crucero está en {cruiseAltFt.toLocaleString()} ft:
                    {" "}faltan <strong>{Math.round(perfilVertical.obstaculoCrucero.faltanteFt)} ft</strong>.
                    {margenLibramientoFt > MARGEN_LIBRAMIENTO_FT && (
                      <>
                        {" "}El margen de {margenLibramientoFt.toLocaleString()} ft lo fijaste tú: es un parámetro
                        de la app, no un mínimo reglamentario. Con el valor por defecto de {MARGEN_LIBRAMIENTO_FT} ft
                        harían falta {Math.round(perfilVertical.obstaculoCrucero.terrenoFt + MARGEN_LIBRAMIENTO_FT).toLocaleString()} ft
                        {" "}y {cruiseAltFt >= perfilVertical.obstaculoCrucero.terrenoFt + MARGEN_LIBRAMIENTO_FT ? "sí libraría" : "tampoco libraría"}.
                      </>
                    )}
                  </Banner>
                )}
                <div style={{ display: "none" }}>
                </div>
                {(() => {
                  const consumoEn = (altFt) => {
                    const r = tableLookup(activeProfile.fuelTable, altFt, cruiseTempC);
                    return r.outOfRange ? null : r.value;
                  };
                  const c = combustiblePorTramo(perfilVertical, consumoEn, groundspeedKt);
                  if (!c.valido) return null;
                  return (
                    <div style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 12, margin: "12px 0" }}>
                      <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                        Combustible por tramo
                      </div>
                      <table style={{ width: "100%", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                        <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Tramo</th><th>Minutos</th><th>Altitud media</th><th>Consumo</th><th>Combustible</th></tr></thead>
                        <tbody>
                          {c.tramos.map((t) => (
                            <tr key={t.clave} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                              <td style={{ padding: "4px 0" }}>{t.etiqueta}</td>
                              <td>{Math.round(t.minutos)} min</td>
                              <td>{Math.round(t.altitudFt).toLocaleString()} ft</td>
                              <td>{t.kgPorHora === null ? <span style={{ color: COLORS.amber }}>fuera de carta</span> : `${t.kgPorHora.toFixed(0)} kg/h`}</td>
                              <td style={{ color: t.kg === null ? COLORS.amber : COLORS.text }}>
                                {t.kg === null ? "s/d" : `${fmtWeight(t.kg).toFixed(1)} ${weightUnitLabel}`}
                              </td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: `2px solid ${COLORS.hairline}`, fontWeight: 700 }}>
                            <td style={{ padding: "5px 0" }}>Total</td>
                            <td>{c.minutosTotales} min</td><td /><td />
                            <td style={{ color: c.totalKg === null ? COLORS.amber : COLORS.cyan }}>
                              {c.totalKg === null ? "no sumable" : `${fmtWeight(c.totalKg).toFixed(1)} ${weightUnitLabel}`}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      {c.avisos.map((a, i) => (
                        <p key={i} style={{ fontSize: 10.5, color: COLORS.amber, margin: "6px 0 0" }}>⚠ {a}</p>
                      ))}
                      <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "8px 0 0", lineHeight: 1.5 }}>
                        Cada tramo se lee de la carta a su altitud media, con la temperatura de crucero. Es una
                        estimación: el consumo real depende del régimen de potencia, y la carta no está indexada
                        por eso. Si falta un tramo, el total no se suma — un total parcial sería menor que el real.
                      </p>
                    </div>
                  );
                })()}
                {perfilVertical.valido && !sendaDibujable && (
                  <Banner tone="TOLERABLE">
                    La senda no se puede dibujar sobre el perfil: falta la cota de alguno de los extremos
                    {!Number.isFinite(cotaSalidaGrafico) && " (punto de despegue)"}
                    {!Number.isFinite(cotaLlegadaGrafico) && " (punto de aterrizaje)"}
                    {" "}o el nivel de crucero. Los números del perfil vertical de abajo sí están calculados;
                    lo que falta es poder ubicarlos en el gráfico. Genera el perfil de terreno o carga la
                    elevación del punto a mano.
                  </Banner>
                )}
                {perfilVertical.avisos.map((a, i) => (
                  <Banner key={i} tone={a.includes("por encima de la senda") || a.includes("NO libra") ? "INTOLERABLE" : "TOLERABLE"}>{a}</Banner>
                ))}
                {!routeProfile && (
                  <Banner tone="TOLERABLE">
                    Todavía no generaste el perfil de terreno: la senda está calculada, pero
                    <strong> nadie comprobó que libre nada</strong>. Pulsa "Generar perfil" arriba.
                  </Banner>
                )}
              </>
            )}
          </Panel>

          <Panel
            eyebrow="Confirmación del piloto"
            title="Planificación lista para verificar y guardar"
            right={<StatusPill status={waypoints[0].lat !== null && waypoints[waypoints.length - 1].lat !== null && wbStatus(perfOrigin) !== "INTOLERABLE" && wbStatus(perfDestination) !== "INTOLERABLE" && RANK[worst.nivel] < RANK.INACEPTABLE ? (pilotConfirmed ? "ACEPTABLE" : "TOLERABLE") : "INTOLERABLE"} />}
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 14 }}>
              <Readout label="Despegue fijado" value={origin.lat !== null ? origin.name : "sin definir"} unit="" tone={origin.lat !== null ? "ACEPTABLE" : "INTOLERABLE"} />
              <Readout label="Aterrizaje fijado" value={destination.lat !== null ? destination.name : "sin definir"} unit="" tone={destination.lat !== null ? "ACEPTABLE" : "INTOLERABLE"} />
              <Readout label="Peso bruto (de W&B)" value={fmtWeight(totalKg).toFixed(0)} unit={weightUnitLabel} />
              <Readout label="Performance en origen (OGE)" value={perfOrigin.oge.outOfRange ? "s/d" : "OK"} unit="" tone={wbStatus(perfOrigin)} />
              <Readout label="Performance en destino (OGE)" value={perfDestination.oge.outOfRange ? "s/d" : "OK"} unit="" tone={wbStatus(perfDestination)} />
              <Readout
                label="Peso / CG al aterrizaje"
                value={wbAterrizaje.valido ? `${fmtWeight(wbAterrizaje.pesoTotalKg).toFixed(0)} ${weightUnitLabel} · ${wbAterrizaje.cg.toFixed(3)} m` : "s/d"}
                unit=""
                tone={wbAterrizaje.dentroDeEnvolvente === false ? "INTOLERABLE" : wbAterrizaje.dentroDeEnvolvente === null ? "TOLERABLE" : "ACEPTABLE"}
              />
              <Readout label="Tipo de tarea" value={tipoDeTarea} unit="" />
              <Readout
                label="Matriz de riesgo (FRAT/SGS)"
                value={worst.nivel === "ACEPTABLE" ? "OK" : `${worst.nivel}${worst.idx ? ` — ${worst.idx}` : ""}`}
                unit="" tone={worst.nivel}
              />
            </div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 10 }}>
              <input type="checkbox" checked={pilotConfirmed} onChange={(e) => setPilotConfirmed(e.target.checked)} />
              El piloto ({pilots.find((p) => p.id === assignedPilotId)?.nombre} {pilots.find((p) => p.id === assignedPilotId)?.apellido}) verifica y confirma origen, destino, peso, performance y FRAT de esta planificación.
            </label>
            {origin.lat === null || destination.lat === null ? (
              <Banner tone="INTOLERABLE">Faltan puntos por fijar — usa el mapa, la búsqueda por nombre, o coordenadas manuales para definir origen y destino antes de confirmar.</Banner>
            ) : pilotConfirmed ? (
              <Banner tone="ACEPTABLE">✓ Planificación confirmada por el piloto — guarda el plan de vuelo abajo, o ve a "Resumen" para guardar el informe de auditoría con firma.</Banner>
            ) : (
              <Banner tone="TOLERABLE">Revisa los datos de arriba y marca la casilla para confirmar la planificación antes de guardarla.</Banner>
            )}
          </Panel>

          <Panel eyebrow="Planes de vuelo" title="Guardar / cargar rutas" right={
            <div style={{ display: "flex", gap: 8 }}>
              <input value={newRouteName} onChange={(e) => setNewRouteName(e.target.value)} placeholder="Nombre del plan…" style={{ ...inputStyle, width: 160 }} />
              <button onClick={saveCurrentRoute} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontWeight: 600, fontSize: 12.5 }}>Guardar ruta actual</button>
            </div>
          }>
            {savedRoutes.length === 0 ? (
              <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0 }}>Todavía no guardaste ningún plan de vuelo.</p>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {savedRoutes.map((r) => (
                  <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: "8px 12px" }}>
                    <div style={{ fontSize: 12.5 }}>
                      <b>{r.name}</b>
                      <span style={{ color: COLORS.muted, marginLeft: 8 }}>{r.waypoints[0]?.name} → {r.waypoints[r.waypoints.length - 1]?.name} · {r.waypoints.length} puntos</span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => loadRoute(r)} style={{ background: COLORS.panel, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12 }}>Cargar</button>
                      <button onClick={() => deleteRoute(r.id)} style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.danger, borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12 }}>Eliminar</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel eyebrow="Plan de vuelo" title="Puntos de ruta" right={
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: COLORS.muted }}>
                <input type="checkbox" checked={isLocalFlight} onChange={(e) => setIsLocalFlight(e.target.checked)} />vuelo local (regreso al origen)
              </label>
              {confirmResetRoute ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: COLORS.danger }}>¿Borrar toda la ruta?</span>
                  <button onClick={() => { resetRoute(); setConfirmResetRoute(false); }} className="no-print" style={{ background: COLORS.danger, border: "none", color: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Sí, borrar</button>
                  <button onClick={() => setConfirmResetRoute(false)} className="no-print" style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.muted, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12 }}>Cancelar</button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmResetRoute(true)}
                  className="no-print"
                  style={{ background: "transparent", border: `1px solid ${COLORS.danger}`, color: COLORS.danger, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12 }}
                >
                  🗑 Borrar ruta y empezar de nuevo
                </button>
              )}
            </div>
          }>
            <div style={{ display: "grid", gap: 10 }}>
              {waypoints.map((w, i) => {
                const isEndpoint = w.id === "origin" || w.id === "destination";
                const isBlank = w.lat === null || w.lon === null;
                return (
                <div key={w.id} style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr 0.9fr 0.8fr 0.8fr 0.7fr auto", gap: 8, alignItems: "end" }}>
                  <Field label={i === 0 ? "Punto de despegue" : i === waypoints.length - 1 ? "Punto de aterrizaje" : `Waypoint ${i}`}>
                    <input style={inputStyle} value={w.name} onChange={(e) => updateWp(w.id, { name: e.target.value.toUpperCase() })} />
                  </Field>
                  {/* ═══ GRADOS, MINUTOS Y SEGUNDOS ═══

                      Pedían decimal, y la aviación no trabaja así: la AIP
                      publica 020742S y las cartas van en grados y minutos. El
                      piloto tenía que convertir en la cabeza antes de
                      escribir, y una cuenta a mano es un error esperando.

                      El campo acepta las dos formas y muestra la de la carta. */}
                  <Field label="Latitud">
                    <CampoCoordenada
                      eje="latitud"
                      style={inputStyle}
                      valor={isBlank ? null : w.lat}
                      alCambiar={(n) => updateWp(w.id, { lat: n })}
                    />
                  </Field>
                  <Field label="Longitud">
                    <CampoCoordenada
                      eje="longitud"
                      style={inputStyle}
                      valor={isBlank ? null : w.lon}
                      alCambiar={(n) => updateWp(w.id, { lon: n })}
                    />
                  </Field>
                  <Field label="Elev. (ft)"><CampoNumero style={inputStyle} valor={isBlank ? "" : w.elevFt} alCambiar={(n) => updateWp(w.id, { elevFt: n })} /></Field>
                  <Field label="Temp (°C)"><CampoNumero disabled={isBlank} style={{ ...inputStyle, opacity: isBlank ? 0.4 : 1 }} valor={w.tempC} alCambiar={(n) => updateWp(w.id, { tempC: n })} /></Field>
                  <Field label={i === 0 ? "—" : "Tramo (NM)"}>
                    <input type="number" disabled style={{ ...inputStyle, opacity: i === 0 ? 0.35 : 0.7 }} value={i === 0 ? "" : legDistancesNm[i].toFixed(1)} />
                  </Field>
                  <button onClick={() => removeWp(w.id)} disabled={isEndpoint} title={isEndpoint ? "El despegue y el aterrizaje no se eliminan — cambialos desde el mapa" : "Quitar waypoint"} style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: isEndpoint ? COLORS.hairline : COLORS.muted, borderRadius: 6, padding: "7px 10px", cursor: isEndpoint ? "not-allowed" : "pointer" }}>✕</button>
                </div>
                );
              })}
              <p style={{ fontSize: 11.5, color: COLORS.muted, margin: 0 }}>
                El punto de despegue y el de aterrizaje son casilleros fijos (no se eliminan) — quedan "sin definir"
                hasta que toques un punto del mapa y elijas "Despegue" o "Aterrizaje", o busques un lugar. Los waypoints
                intermedios se agregan y se quitan libremente, y se numeran solos según su orden en la ruta.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr auto", gap: 8, alignItems: "end", background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 10 }}>
                <Field label="Nombre del punto"><input style={inputStyle} value={manualWp.name} onChange={(e) => setManualWp((m) => ({ ...m, name: e.target.value }))} /></Field>
                <Field label="Latitud">
                  <CampoCoordenada
                    eje="latitud"
                    style={inputStyle}
                    valor={manualWp.lat === "" || manualWp.lat === null ? null : Number(manualWp.lat)}
                    alCambiar={(n) => setManualWp((m) => ({ ...m, lat: n === null ? "" : n }))}
                  />
                </Field>
                <Field label="Longitud">
                  <CampoCoordenada
                    eje="longitud"
                    style={inputStyle}
                    valor={manualWp.lon === "" || manualWp.lon === null ? null : Number(manualWp.lon)}
                    alCambiar={(n) => setManualWp((m) => ({ ...m, lon: n === null ? "" : n }))}
                  />
                </Field>
                <button onClick={addManualWaypoint} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}>+ agregar punto manual</button>
              </div>
              <div style={{ display: "flex", gap: 8, justifySelf: "start" }}>
                <button onClick={addWp} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "7px 12px", cursor: "pointer", fontSize: 13 }}>+ waypoint</button>
                <button
                  onClick={invertirRuta}
                  title="Invertir la ruta para el tramo de regreso: el aterrizaje pasa a ser despegue y los waypoints se recorren al revés"
                  style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.amber, borderRadius: 6, padding: "7px 12px", cursor: "pointer", fontSize: 13 }}
                >
                  ⇄ ruta de vuelta
                </button>
              </div>
            </div>
          </Panel>

          <Panel eyebrow="Mis puntos" title="Base propia de puntos y zonas de aterrizaje" right={
            <span style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {/* ═══ VERLOS SOBRE EL TERRENO ═══

                Con los puntos solo en una tabla, planificar exige leer 41
                pares de coordenadas y armarse el mapa en la cabeza. El botón
                los enciende Y ENCUADRA la vista: encenderlos sin mover el mapa
                deja los puntos fuera de pantalla y parece que no funcionó. */}
            {misPuntos.length > 0 && (
              <button
                type="button"
                className="no-print"
                onClick={verMisPuntosEnMapa}
                style={{ background: "transparent", border: `1px solid ${COLORS.cyan}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}
              >
                Ver en el mapa ({misPuntos.length})
              </button>
            )}
            <label className="no-print" style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>
              {importando ? "Leyendo…" : "Importar KML / KMZ"}
              <input
                type="file" accept=".kml,.kmz" style={{ display: "none" }} disabled={importando}
                onChange={(e) => { elegirArchivoDePuntos(e.target.files?.[0]); e.target.value = ""; }}
              />
            </label>
            </span>
          }>
            <p style={{ fontSize: 11.5, color: COLORS.muted, margin: "0 0 12px" }}>
              Puntos que no están en ninguna base pública: zonas de faena, plataformas, helipuertos de hospital,
              referencias visuales. Se agregan tocando un punto del mapa y eligiendo "★ Guardar en mis puntos",
              cargando coordenadas a mano, o importando un KML/KMZ de Google Earth. Cada punto recuerda de dónde
              vino: un punto de un archivo de terceros no merece la misma confianza que uno que mediste tú.
            </p>

            {importacion && (
              <div style={{ background: COLORS.panelAlt, border: `1px solid ${importacion.puntos.length ? COLORS.cyan : COLORS.danger}`, borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <strong style={{ fontSize: 13 }}>{importacion.archivo}</strong>
                <p style={{ fontSize: 12.5, color: COLORS.text, margin: "6px 0" }}>
                  {importacion.puntos.length
                    ? `${importacion.puntos.length} punto(s) leídos. Todavía no se guardaron.`
                    : "No se pudo importar ningún punto."}
                </p>
                {importacion.avisos.map((a, i) => (
                  <p key={i} style={{ fontSize: 11.5, color: COLORS.amber, margin: "3px 0" }}>⚠ {a}</p>
                ))}
                {importacion.puntos.length > 0 && (
                  <ul style={{ fontSize: 11.5, color: COLORS.muted, margin: "8px 0", paddingLeft: 18, maxHeight: 120, overflowY: "auto", fontFamily: "'JetBrains Mono', monospace" }}>
                    {importacion.puntos.slice(0, 12).map((x, i) => (
                      <li key={i}>{x.nombre} — {x.lat.toFixed(4)}, {x.lon.toFixed(4)}</li>
                    ))}
                    {importacion.puntos.length > 12 && <li>… y {importacion.puntos.length - 12} más</li>}
                  </ul>
                )}
                <p style={{ fontSize: 11, color: COLORS.muted, margin: "6px 0" }}>
                  La altitud del archivo no se importa: en Google Earth casi siempre es cero y se vería igual que una
                  cota real. La elevación se consulta contra el modelo digital de terreno cuando el punto entra en una ruta.
                </p>
                {/* ═══ QUÉ TRAE ESTE ARCHIVO ═══

                    Se elige UNA vez para los 41 puntos. El nombre del archivo
                    y el de los puntos dan una pista, pero no son un dato: un
                    KMZ llamado «Plataformas.kmz» puede traer las bases de las
                    torres, que son plataformas y no son lugares para posar.
                    Por eso la pista se MUESTRA y el operador confirma. */}
                {importacion.puntos.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${COLORS.hairline}` }}>
                    <label style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 12.5 }}>
                      <span>Estos {importacion.puntos.length} puntos son:</span>
                      <select
                        style={{ ...inputStyle, padding: "5px 8px", minWidth: 190 }}
                        value={tipoDeLaImportacion}
                        onChange={(e) => setTipoDeLaImportacion(e.target.value)}
                      >
                        {TIPOS_DE_PUNTO.map((t) => (
                          <option key={t.id} value={t.id}>{t.nombre}</option>
                        ))}
                      </select>
                    </label>

                    {sugerencia && (
                      <p style={{ fontSize: 11.5, color: COLORS.amber, margin: "6px 0 0", lineHeight: 1.5 }}>
                        Parece que son <strong>{sugerencia.nombre}</strong>. {sugerencia.motivo}
                        {/* Un tipo operativo afirma que ahí se puede posar, y
                            eso no se adivina: se dice por qué hay que elegirlo
                            a mano. */}
                        {sugerencia.operativo && (
                          <>
                            {" "}<strong>No se eligió solo</strong>: ese tipo dice que ahí se
                            puede posar, y eso lo confirma el operador, no el nombre de un archivo.
                          </>
                        )}
                      </p>
                    )}

                    <p style={{ fontSize: 11, color: COLORS.muted, margin: "6px 0 0", lineHeight: 1.5 }}>
                      Se puede cambiar después, uno por uno o todo el archivo de nuevo.
                    </p>
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  {importacion.puntos.length > 0 && (
                    <button onClick={confirmarImportacion} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "7px 12px", cursor: "pointer", fontWeight: 600, fontSize: 12.5 }}>
                      Guardar {importacion.puntos.length} punto(s)
                    </button>
                  )}
                  <button onClick={() => setImportacion(null)} style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.muted, borderRadius: 6, padding: "7px 12px", cursor: "pointer", fontSize: 12.5 }}>Cancelar</button>
                </div>
              </div>
            )}

            {/* ═══ UN ARCHIVO, UN TIPO ═══

                41 torres no se clasifican de a una. Cada archivo cargado trae
                su propio selector: el próximo KMZ puede ser de helipuertos o
                de waypoints, y cada uno se resuelve por separado sin tocar los
                demás. */}
            {archivosImportados.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 11.5, color: COLORS.muted }}>Archivos cargados:</span>
                {archivosImportados.map((arch) => {
                  const cuantos = misPuntos.filter((x) => x.archivo === arch).length;
                  const delArchivo = misPuntos.filter((x) => x.archivo === arch);
                  /* El tipo que ya tienen, si todos comparten uno: así el
                     selector muestra el estado real en vez de volver al
                     genérico y hacer creer que no se clasificaron. */
                  const tipos = [...new Set(delArchivo.map((x) => x.tipo || TIPO_POR_DEFECTO))];
                  const comun = tipos.length === 1 ? tipos[0] : "";
                  const pista = sugerirTipoDeArchivo(arch, delArchivo);

                  return (
                    <div key={arch} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: "8px 10px" }}>
                      <strong style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>{arch}</strong>
                      <span style={{ fontSize: 11.5, color: COLORS.muted }}>{cuantos} punto(s)</span>

                      <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11.5, color: COLORS.muted }}>
                        todos son:
                        <select
                          style={{ ...inputStyle, padding: "4px 8px", minWidth: 170 }}
                          value={comun}
                          onChange={(e) => e.target.value && clasificarTodoElArchivo(arch, e.target.value)}
                        >
                          {/* Con tipos mezclados no se elige ninguno: mostrar
                              uno haría creer que están todos iguales. */}
                          {comun === "" && <option value="">(mezclados)</option>}
                          {TIPOS_DE_PUNTO.map((t) => (
                            <option key={t.id} value={t.id}>{t.nombre}</option>
                          ))}
                        </select>
                      </label>

                      {pista && comun === TIPO_POR_DEFECTO && (
                        <span style={{ fontSize: 11, color: COLORS.amber }}>
                          parece{pista.operativo ? "n" : ""} {pista.nombre.toLowerCase()}
                        </span>
                      )}

                      <button onClick={() => borrarPorArchivo(arch)} title="Borrar todos los puntos de este archivo"
                        style={{ marginLeft: "auto", background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.muted, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11.5 }}>
                        Borrar
                      </button>
                    </div>
                  );
                })}

                {/* El resultado se dice: sin el conteo, el operador no sabe si
                    el selector hizo algo. */}
                {aviso && (
                  <p style={{ fontSize: 11.5, color: COLORS.cyan, margin: 0 }} role="status">{aviso}</p>
                )}
              </div>
            )}

            {misPuntos.length === 0 ? (
              <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0 }}>Todavía no hay puntos guardados.</p>
            ) : (
              <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                  <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}>
                    <th>Nombre</th><th>Tipo</th><th>Lat</th><th>Lon</th><th>Origen</th><th>Usar en la ruta</th><th></th>
                  </tr></thead>
                  <tbody>{misPuntos.map((x) => (
                    <tr key={x.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                      <td><input style={{ ...inputStyle, padding: "3px 6px", width: 190 }} value={x.nombre} onChange={(e) => actualizarPunto(x.id, { nombre: e.target.value.toUpperCase() })} /></td>
                      {/* ═══ QUÉ ES ESTE PUNTO ═══

                          Un KMZ de 41 estructuras entra como 41 filas iguales:
                          ni el mapa ni la tabla distinguen una torre de la zona
                          de acopio. En línea larga eso no es cosmético — la
                          zona donde se puede posar y la torre donde no se puede
                          son el mismo punto en pantalla.

                          Un punto sin clasificar queda GENÉRICO y no afirma
                          nada: no saber no es saber que no. */}
                      <td>
                        <select
                          style={{ ...inputStyle, padding: "3px 6px", width: 160 }}
                          value={x.tipo || TIPO_POR_DEFECTO}
                          onChange={(e) => actualizarPunto(x.id, { tipo: e.target.value })}
                          title={(TIPOS_DE_PUNTO.find((t) => t.id === (x.tipo || TIPO_POR_DEFECTO)) || {}).descripcion}
                        >
                          {TIPOS_DE_PUNTO.map((t) => (
                            <option key={t.id} value={t.id}>{t.nombre}</option>
                          ))}
                        </select>
                      </td>
                      <td><CampoNumero step="0.0001" style={{ ...inputStyle, padding: "3px 6px", width: 105 }} valor={x.lat} alCambiar={(n) => actualizarPunto(x.id, { lat: n })} /></td>
                      <td><CampoNumero step="0.0001" style={{ ...inputStyle, padding: "3px 6px", width: 105 }} valor={x.lon} alCambiar={(n) => actualizarPunto(x.id, { lon: n })} /></td>
                      <td style={{ fontSize: 11, color: COLORS.muted }}>{x.origen === "archivo" ? x.archivo : x.origen}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button onClick={() => usarPuntoEnRuta(x, "origin")} style={botonPuntoStyle}>Despegue</button>{" "}
                        <button onClick={() => usarPuntoEnRuta(x, "destination")} style={botonPuntoStyle}>Aterrizaje</button>{" "}
                        <button onClick={() => usarPuntoEnRuta(x, "waypoint")} style={botonPuntoStyle}>+ Waypoint</button>
                      </td>
                      <td><button onClick={() => borrarPunto(x.id)} title="Borrar punto" style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.muted, borderRadius: 6, padding: "3px 8px", cursor: "pointer" }}>✕</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel eyebrow="Crucero" title="Nivel de vuelo y velocidad" right={
            <button onClick={() => {
              setCruiseAltFt(Math.round((origin.elevFt + destination.elevFt) / 2 / 100) * 100);
              setCruiseTempC(Math.round((origin.tempC + destination.tempC) / 2));
            }} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>
              ↺ usar datos de origen/destino
            </button>
          }>
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0, marginBottom: 12 }}>
              Performance y W&B ya usan la elevación/temperatura de cada waypoint directamente. Combustible y el resumen
              de la ruta usan este par altitud/temperatura de crucero — pulsa "usar datos de origen/destino" para
              igualarlo al promedio de {origin.name} y {destination.name}, tal como en el Excel original (una sola
              condición de vuelo), o ajústalo a mano si tu crucero real es distinto (p. ej. cruce de cordillera).
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
              <Field label="Altitud de presión — Hp (ft)" hint={sobreTopeDeCarta(cruiseAltFt) ? `Por encima de ${topeDeCartaTexto()}: sin carta digitalizada` : undefined}>
                <CampoNumero step={500} max={ROUTE_CEILING} style={inputStyle} valor={cruiseAltFt} alCambiar={(n) => setCruiseAltFt(n)} />
              </Field>
              <Field label="Temperatura crucero (°C)" hint={atmosferaCrucero ? `Pronóstico a ${cruiseAltFt.toLocaleString()} ft` : undefined}>
                <CampoNumero style={inputStyle} valor={cruiseTempC} alCambiar={(n) => setCruiseTempC(n)} />
              </Field>
              {/* La velocidad es el tercer parámetro del crucero, junto a la
                  altitud y la temperatura: los tres definen la condición de
                  vuelo y el piloto los ajusta en el mismo lugar. Estaba en
                  otra sección, lejos de los dos con los que se decide. */}
              <Field
                label={`Velocidad de crucero — TAS (kt, máx ${VELOCIDAD_CRUCERO_MAXIMA_KT})`}
                hint={eteConViento
                  ? `Sobre el suelo: ${velocidadSueloEfectivaKt} kt con el viento en altura`
                  : "Sin viento cargado se supone calma"}
              >
                <CampoNumero
                  style={inputStyle} max={VELOCIDAD_CRUCERO_MAXIMA_KT} min={0}
                  valor={groundspeedKt} alCambiar={(n) => setGroundspeedKt(n)}
                />
              </Field>
            </div>

            {chequeoMasa.dentro === false && (
              <Banner tone="INTOLERABLE">{chequeoMasa.motivo}</Banner>
            )}
            {velocidadPedida.recortada && (
              <Banner tone="TOLERABLE">{velocidadPedida.motivo}</Banner>
            )}
            {!velocidadPedida.kt && (
              <Banner tone="TOLERABLE">{velocidadPedida.motivo}</Banner>
            )}

            {/* Atmósfera en el nivel de crucero, del pronóstico en altura. */}
            <div style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                <span style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>
                  Atmósfera al nivel de crucero
                </span>
                <button
                  onClick={traerAtmosferaEnRuta}
                  disabled={atmosferaCargando}
                  style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 11.5 }}
                >
                  {atmosferaCargando ? "Consultando…" : "🌡 Traer temperatura y viento en altura"}
                </button>
                {atmosferaCrucero && (
                  <button
                    onClick={() => setCruiseTempC(Math.round(atmosferaCrucero.temperaturaC))}
                    style={{ background: COLORS.cyan, border: "none", color: "#04201D", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 11.5, fontWeight: 600 }}
                  >
                    Usar {Math.round(atmosferaCrucero.temperaturaC)} °C en los cálculos
                  </button>
                )}
              </div>

              {atmosferaRuta && !atmosferaRuta.ok && (
                <p style={{ fontSize: 11.5, color: COLORS.caution, margin: 0 }}>{atmosferaRuta.error}</p>
              )}

              {atmosferaCrucero && (
                <>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Readout label={`Temperatura a ${cruiseAltFt.toLocaleString()} ft`} value={atmosferaCrucero.temperaturaC} unit="°C" />
                    <Readout label="Desviación ISA" value={`${atmosferaCrucero.desviacionIsaC > 0 ? "+" : ""}${atmosferaCrucero.desviacionIsaC.toFixed(1)}`} unit="°C" tone={atmosferaCrucero.desviacionIsaC > 10 ? "TOLERABLE" : "ACEPTABLE"} />
                    <Readout label="Viento en altura" value={atmosferaCrucero.vientoKt === null ? "s/d" : `${String(atmosferaCrucero.direccionGrados).padStart(3, "0")}° / ${atmosferaCrucero.vientoKt}`} unit="kt" />
                    {vientoEnRuta && (
                      <>
                        <Readout
                          label={vientoEnRuta.frontal > 0 ? "Viento de frente" : "Viento de cola"}
                          value={Math.abs(vientoEnRuta.frontal)} unit="kt"
                          tone={vientoEnRuta.frontal > 0 ? "TOLERABLE" : "ACEPTABLE"}
                        />
                        <Readout label="Viento cruzado" value={Math.abs(vientoEnRuta.cruzado)} unit={`kt por ${vientoEnRuta.cruzado >= 0 ? "derecha" : "izquierda"}`} />
                        <Readout label="TAS pedida" value={tasKt} unit="kt" />
                        <Readout
                          label="Velocidad sobre el suelo"
                          value={vientoEnRuta.velocidadSueloKt ?? "s/d"}
                          unit="kt"
                          tone={vientoEnRuta.velocidadSueloKt < tasKt ? "TOLERABLE" : "ACEPTABLE"}
                        />
                        <Readout label="ETE con viento" value={vientoEnRuta.eteConVientoMin ?? "s/d"} unit="min" tone={vientoEnRuta.eteConVientoMin > eteMin ? "TOLERABLE" : "ACEPTABLE"} />
                      </>
                    )}
                  </div>
                  <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "8px 0 0", lineHeight: 1.55 }}>
                    Pronóstico de modelo en {atmosferaCrucero.punto}, {atmosferaCrucero.horaUtc} UTC — no es una
                    observación. El nivel se eligió por su <strong>altura geopotencial real del día</strong>, no
                    por la nominal: la altitud de un nivel de presión varía cientos de metros según el sistema
                    de presión.
                    {vientoEnRuta?.variosTramos && " El viento se aplica al rumbo directo; con waypoints intermedios cada tramo tiene el suyo."}
                    {" "}La velocidad sobre el suelo sale del triángulo de velocidades: con viento cruzado hay que
                    apuntar contra la deriva y eso también resta.
                  </p>
                </>
              )}
            </div>
            <div style={{ display: "none" }}>
            </div>

            {/* Temperatura de superficie en cada punto, traída de Open-Meteo.
                Performance y peso y balance ya leen la temperatura de cada
                waypoint, así que estas dos son las que realmente entran al
                cálculo en despegue y aterrizaje. */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              {[["origin", "Punto de despegue", origin], ["destination", "Punto de aterrizaje", destination]].map(([slot, titulo, wp]) => {
                const m = meteoPuntos[slot];
                return (
                  <div key={slot} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                      {titulo} — {wp?.lat !== null ? wp.name : "sin fijar"}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "end", flexWrap: "wrap" }}>
                      <Field label="Temperatura (°C)">
                        <CampoNumero style={{ ...inputStyle, width: 90 }}
                          disabled={!wp || wp.lat === null}
                          valor={wp?.tempC ?? ""}
                          alCambiar={(n) => updateWp(wp.id, { tempC: n })} />
                      </Field>
                      <button
                        onClick={() => traerTemperatura(slot)}
                        disabled={!wp || wp.lat === null || meteoCargando === slot}
                        style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "7px 11px", cursor: "pointer", fontSize: 12 }}
                      >
                        {meteoCargando === slot ? "Consultando…" : "🌡 Traer de Open-Meteo"}
                      </button>
                    </div>
                    {m && !m.ok && <p style={{ fontSize: 11, color: COLORS.caution, margin: "8px 0 0" }}>{m.error}</p>}
                    {m && m.ok && (
                      <div style={{ fontSize: 10.5, color: COLORS.muted, marginTop: 8, lineHeight: 1.55 }}>
                        <div>{m.temperaturaC} °C · {m.horaUtc} UTC · {m.fuente}</div>
                        {(() => {
                          const v = evaluarViento(m.vientoParaRiesgoKt, VIENTO_MAXIMO_KT);
                          const color = v.superaLimite === null ? COLORS.amber : v.superaLimite ? COLORS.danger : COLORS.ok;
                          return (
                            <div style={{ marginTop: 5, padding: "5px 8px", borderRadius: 6, background: `${color}1E`, border: `1px solid ${color}66`, color }}>
                              <strong>
                                Viento {m.vientoKt ?? "s/d"} kt
                                {m.direccionGrados !== null ? ` del ${String(m.direccionGrados).padStart(3, "0")}°` : ""}
                                {m.rafagaKt !== null ? ` · ráfagas ${m.rafagaKt} kt` : ""}
                              </strong>
                              <div style={{ fontSize: 10 }}>
                                {v.superaLimite === true && "SOBRE el máximo de 25 kt — no se sale a volar."}
                                {v.superaLimite === false && "Dentro del máximo aprobado de 25 kt."}
                                {v.superaLimite === null && "Sin dato de viento."}
                                {" "}Medido a 10 m sobre el terreno del modelo, no en la plataforma ni a la altura del rotor.
                              </div>
                            </div>
                          );
                        })()}
                        {m.diferenciaElevacionFt !== null && Math.abs(m.diferenciaElevacionFt) >= 300 && (
                          <div style={{ color: COLORS.amber, marginTop: 3 }}>
                            El modelo sitúa el terreno en {pies(m.elevacionModeloFt)} y este punto está a{" "}
                            {pies(wp.elevFt)}: {Math.abs(m.diferenciaElevacionFt).toLocaleString()} ft de diferencia.
                            Por gradiente estándar la temperatura real sería unos {m.correccionSugeridaC > 0 ? "+" : ""}
                            {m.correccionSugeridaC} °C respecto de la informada. <strong>No se aplicó sola</strong> — corregila
                            a mano si corresponde.
                          </div>
                        )}
                      </div>
                    )}

                    {/* METAR textual del aeródromo.

                        La temperatura de arriba viene de Open-Meteo, que es un
                        PRONÓSTICO DE MODELO. Esto es una OBSERVACIÓN: alguien
                        midió eso, en ese aeródromo, a esa hora. Para el registro
                        de planificación auditable (BARS) hace falta el informe
                        textual con su hora, no un resumen. */}
                    <PanelMetar
                      oaci={slot === "origin" ? oaciDespegue : oaciAterrizaje}
                      crudo={metar[slot === "origin" ? oaciDespegue : oaciAterrizaje]?.crudo ?? null}
                      motivoFalla={metar[slot === "origin" ? oaciDespegue : oaciAterrizaje]?.motivo ?? null}
                      cargando={metarCargando}
                      alActualizar={consultarMetar}
                    />
                  </div>
                );
              })}
            </div>
            <div style={{ display: "none" }}>
              {/* Se ajusta arriba, junto a altitud y temperatura: un mismo dato
                  con dos controles termina con dos valores. */}
              <Field label="Velocidad de crucero — TAS (kt)" hint="Se ajusta arriba, en «Nivel de vuelo y velocidad».">
                <input type="number" readOnly value={tasKt} style={soloLecturaStyle} />
              </Field>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Readout label="Distancia total" value={totalNm.toFixed(1)} unit="NM" />
              <Readout label="ETE" value={eteMin.toFixed(0)} unit="min" />
              <Readout label="Elev. despegue" value={origin.elevFt} unit="ft" />
              <Readout label="Elev. aterrizaje" value={destination.elevFt} unit="ft" />
            </div>

            {/* Peso máximo en cada punto, con la temperatura de ese punto. Es la
                pregunta que se hace el piloto antes de cargar, y estaba solo en
                Análisis de vuelo. */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
              {[["origin", "Peso máximo en el despegue", origin, perfOrigin, limiteOrigen],
                ["destination", "Peso máximo en el aterrizaje", destination, perfDestination, limiteDestino]].map(
                ([slot, titulo, wp, perf, lim]) => (
                <div key={slot} style={{ background: COLORS.panelAlt, border: `1px solid ${lim.limiteKg !== null && totalKg > lim.limiteKg ? COLORS.danger : COLORS.hairline}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                    {titulo} — {wp.lat !== null ? wp.name : "sin fijar"}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Readout label="OGE" value={perf.oge.outOfRange ? "s/d" : fmtWeight(perf.oge.value).toFixed(0)} unit={weightUnitLabel} />
                    <Readout label="IGE" value={perf.ige.outOfRange ? "s/d" : fmtWeight(perf.ige.value).toFixed(0)} unit={weightUnitLabel} />
                    <Readout
                      label={`Manda${lim.limitante ? ` — ${lim.limitante}` : ""}`}
                      value={lim.limiteKg === null ? "s/d" : fmtWeight(lim.limiteKg).toFixed(0)}
                      unit={weightUnitLabel}
                      tone={lim.limiteKg !== null && totalKg > lim.limiteKg ? "INTOLERABLE" : "ACEPTABLE"}
                    />
                  </div>
                  <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "8px 0 0", lineHeight: 1.5 }}>
                    A {pies(wp.elevFt)} y {grados(wp.tempC)}
                    {meteoPuntos[slot]?.ok ? " · temperatura de Open-Meteo" : " · temperatura cargada a mano"}.
                    {" "}Peso bruto actual: {fmtWeight(totalKg).toFixed(0)} {weightUnitLabel}.
                  </p>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ---------------- MODO VUELO (mapa en movimiento con GPS) ---------------- */}
      {tab === "vuelo" && (
        <React.Suspense fallback={<div style={{ padding: 24, color: COLORS.muted, fontSize: 13 }}>Cargando modo vuelo…</div>}>
          <ModoVuelo
            /* EL MISMO cronómetro que Operaciones: arrancar en cualquiera de
               los dos arranca el único que hay, y el tiempo que va al libro
               del piloto es el que va al duty time. */
            crono={cronometro}
            setCrono={setCronometro}
            waypoints={waypoints}
            combustibleKg={fuelKg}
            /* El consumo sale de la carta, no de un campo fijo del perfil: es el
               mismo número con el que se calculó el combustible requerido del
               vuelo. Si la carta no llega a esa altitud, va null y el modo
               vuelo muestra guiones en vez de un consumo inventado. */
            consumoKgH={fuelRateAt.outOfRange ? null : fuelRateAt.value}
            matricula={matricula}
            tipoAeronave={activeProfile?.tipo || ""}
            aircraftId={fleet.find((f) => f.matricula === matricula)?.id || null}
            pilotId={assignedPilotId || null}
            /* El asiento llega ARMADO desde el cronómetro, con el flight time
               y el air time medidos y marcados como tales. Antes ese número
               moría en pantalla y alguien lo tecleaba de memoria. */
            /* Del cierre salen DOS asientos con destinos distintos:
                 el FT al libro del piloto y a su duty time,
                 el AT a la bitácora de mantenimiento de la aeronave.
               Confundirlos infla las horas de la aeronave o le descuenta horas
               al piloto, y ninguno de los dos errores se ve en pantalla. */
            etiquetaOperacion={etiquetaOperacion}
            onCerrarVuelo={(asientoDeLaCabina) => {
              /* El asiento de la cabina trae lo que se MIDIÓ. Para quién se voló
                 no se mide: se elige, y por eso es lo único que hay que
                 agregarle acá. */
              const resuelta = conLaOperacion(asientoDeLaCabina, parDeOperacion(operacion));
              /* El panel de cierre ya lo mostró antes de firmar, así que llegar
                 acá sin empresa es raro: se avisa y NO se guarda a medias. */
              if (!resuelta.ok) { setMotivoOperacion(resuelta.motivo); return; }
              const asiento = resuelta.asiento;
              const { piloto, aeronave } = repartirVuelo(asiento);
              if (aeronave) setFlightLog((l) => [{ ...asiento, ...aeronave }, ...l]);
              if (piloto) setLibroPiloto((l) => [piloto, ...l]);
            }}
            elevacionTerrenoFt={cotaMaximaRutaFt}
            /* Las dos usan lo mismo que el mapa de planificación: `insertar`
               ubica el punto por geografía —sin depender de ningún id, que fue
               lo que falló ahí— y `alFinal` lo cuelga al final. */
            onInsertarPunto={(lat, lon, nombre) => setWaypoints((ws) => insertarEnTramoMasCercano(
              ws, { id: nuevoIdPunto(), lat, lon, elevFt: null, name: nombre },
            ))}
            onAgregarAlFinal={(lat, lon, nombre) => addPointToRoute(lat, lon, null, nombre, "waypoint")}
          />
        </React.Suspense>
      )}

      {/* ---------------- AERÓDROMOS (administración de base de datos) ---------------- */}
      {/* ---------------- W&B ---------------- */}
      {tab === "resumen" && (
        <div style={{ marginTop: 20, borderTop: `2px solid ${COLORS.hairline}`, paddingTop: 16 }}>
          <div style={{ fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: COLORS.cyan, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, marginBottom: 10 }}>
            Hoja de vuelo
          </div>
        </div>
      )}
      {tab === "resumen" && (
        <React.Suspense fallback={<div style={{ padding: 24, color: COLORS.muted, fontSize: 13 }}>Cargando…</div>}>
          <HojaReferencia
            datos={{
              matricula,
              tipoAeronave: activeProfile.tipo,
              origen: origin?.name || null,
              destino: destination?.name || null,
              distanciaNm: totalNm,
              eteMin,
              cotaMaximaFt: cotaMaximaRutaFt,
              altitudCruceroFt: cruiseAltFt,
              temperaturaC: cruiseTempC,
              pesoBrutoKg: totalKg,
              // Las condiciones REALES con las que se leyó cada OGE. La hoja decía
              // "condición evaluada: <altitud de crucero>, <temperatura de
              // crucero>", pero el OGE se lee en la plataforma: a la elevación y
              // temperatura de cada punto. Un piloto que leía 2 100 kg creía que
              // era a 4 500 ft cuando era a 1 555 ft, y son cifras distintas.
              elevOrigenFt: origin?.elevFt ?? null,
              tempOrigenC: Number.isFinite(origin?.tempC) ? origin.tempC : null,
              elevDestinoFt: destination?.elevFt ?? null,
              tempDestinoC: Number.isFinite(destination?.tempC) ? destination.tempC : null,
              ogeOrigenKg: perfOrigin.oge.outOfRange ? null : perfOrigin.oge.value,
              igeOrigenKg: perfOrigin.ige.outOfRange ? null : perfOrigin.ige.value,
              igeDestinoKg: perfDestination.ige.outOfRange ? null : perfDestination.ige.value,
              // El límite que realmente manda: el menor entre carta y máximo
              // estructural. El número de la carta solo no es el límite del vuelo.
              limiteOrigenKg: limiteOrigen.limiteKg,
              limiteDestinoKg: limiteDestino.limiteKg,
              limitanteOrigen: limiteOrigen.limitante,
              limitanteDestino: limiteDestino.limitante,
              ogeDestinoKg: perfDestination.oge.outOfRange ? null : perfDestination.oge.value,
              dentroEnvolvente: insideEnvelope,
              // El aterrizaje es una condición aparte: despegar dentro de
              // envolvente no garantiza llegar dentro.
              pesoAterrizajeKg: wbAterrizaje.valido ? wbAterrizaje.pesoTotalKg : null,
              cgAterrizajeM: wbAterrizaje.valido ? wbAterrizaje.cg : null,
              dentroEnvolventeAterrizaje: wbAterrizaje.valido ? wbAterrizaje.dentroDeEnvolvente : null,
              motivoAterrizaje: wbAterrizaje.motivo,
              combustibleKg: fuelKg,
              requeridoKg: requiredKg,
              margenKg: fuelMargin,
              autonomiaMin: autonomyMin,
              porcentajeTanque: fuelPct,
              nivelRiesgo: worst.nivel,
              factorRiesgo: worst.idx,
              factoresEnElPeorNivel: worst.empatados,
              detalleRiesgo: worst.detalle,
              pesoVacioKg: pesoVacio.kg,
              pesoVacioDelPesaje: pesoVacio.pesoDelPesaje,
              brazoVacioDelPesaje: pesoVacio.brazoDelPesaje,
              fechaPesaje: pesoVacio.fecha,
              horasProximoMantenimiento: mantenimientoActivo.horas,
              itemProximoMantenimiento: mantenimientoActivo.itemHoras,
              diasProximoVencimiento: mantenimientoActivo.dias,
              itemProximoVencimiento: mantenimientoActivo.itemDias,
              mantenimientoConDatos: mantenimientoActivo.hayDatos,
              discrepanciasAbiertas: discrepancies.filter((d) => !d.cerrada).length,
              unidadPeso: weightUnitLabel,
              unidadVolumen: volUnitLabel,
              aPeso: fmtWeight,
              aVolumen: fmtVol,
              densidad: activeProfile.fuelDensity,
              // Ningún perfil está verificado hasta que alguien coteje sus cartas
              // contra el RFM y lo deje registrado. Ver AD-005.
              perfilVerificado: Boolean(activeProfile.verificadoPor),
            }}
          />
        </React.Suspense>
      )}

      {tab === "espacio" && (
        <React.Suspense fallback={<div style={{ padding: 24, color: COLORS.muted, fontSize: 13 }}>Cargando…</div>}>
          <DigitalizadorAIP />
        </React.Suspense>
      )}

      {tab === "analisis" && (
        <div style={{ display: "grid", gap: 16 }}>
          <Panel eyebrow="Configuración" title="Perfil de aeronave">
            <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap", marginBottom: 14 }}>
              <Field label="Matrícula"><input style={inputStyle} value={matricula} onChange={(e) => setMatricula(e.target.value.toUpperCase())} /></Field>
              <Field label="Tipo de aeronave">
                <select style={{ ...inputStyle, minWidth: 220 }} value={activeProfileId} onChange={(e) => setActiveProfileId(e.target.value)}>
                  {profiles.map((p) => <option key={p.id} value={p.id}>{p.tipo}</option>)}
                </select>
              </Field>
              <button onClick={addProfile} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "7px 12px", cursor: "pointer" }}>+ nuevo tipo</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              <Field label="Nombre del tipo"><input style={inputStyle} value={activeProfile.tipo} onChange={(e) => updateProfile({ tipo: e.target.value })} /></Field>
              <Field
                label={`Peso vacío (kg) — ${pesoVacio.pesoDelPesaje ? "pesaje de " + matricula : "genérico del tipo"}`}
                hint={pesoVacio.pesoDelPesaje
                  ? `Del pesaje${pesoVacio.fecha ? " del " + pesoVacio.fecha : ""}. Se edita en Mantenimiento → «Pesaje por matrícula», no acá.`
                  : `${matricula} no tiene pesaje cargado. Cargalo en Mantenimiento → «Pesaje por matrícula».`}
              >
                <input type="number" readOnly value={pesoVacio.kg} style={soloLecturaStyle} />
              </Field>
              <Field
                label={`Brazo vacío (m) — ${pesoVacio.brazoDelPesaje ? "acta de pesaje" : "genérico del tipo"}`}
                hint={pesoVacio.brazoDelPesaje
                  ? "Del acta de pesaje de esta matrícula. Se edita en Mantenimiento → «Pesaje por matrícula»."
                  : "NO viene del acta de pesaje de esta matrícula: el CG es una estimación. Datum a 3.40 m (133.8 in) adelante del eje del rotor principal (RFM 6.2.2)."}
              >
                <input type="number" readOnly value={pesoVacio.arm} style={soloLecturaStyle} />
              </Field>
              <Field label="Densidad combustible (kg/L)"><CampoNumero step="0.01" style={inputStyle} valor={activeProfile.fuelDensity} alCambiar={(n) => updateProfile({ fuelDensity: n })} /></Field>
              <Field label="Capacidad utilizable (L)" hint="Base del cálculo por porcentaje. Utilizable, no total."><CampoNumero step="1" style={inputStyle} valor={activeProfile.fuelCapacityL ?? 540} alCambiar={(n) => updateProfile({ fuelCapacityL: n })} /></Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginTop: 12 }}>
              {Object.entries(activeProfile.arms).map(([k, v]) => (
                <Field key={k} label={`Brazo ${k}`}><CampoNumero step="0.01" style={inputStyle} valor={v} alCambiar={(n) => updateArm(k, n)} /></Field>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 12 }}>
              {activeProfile.cargoCompartments.map((c) => (
                <Field key={c.key} label={`Límite compartimento ${c.label} (kg)`}>
                  <CampoNumero style={inputStyle} valor={c.maxKg} alCambiar={(n) => updateCargoCompartment(c.key, n)} />
                </Field>
              ))}
            </div>
            {!activeProfile.ogeTable && (
              <Banner tone="TOLERABLE">Este tipo aún no tiene cartas de performance/combustible cargadas — las pestañas Performance y Combustible mostrarán "sin dato" hasta digitalizar sus tablas.</Banner>
            )}
          </Panel>

          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16 }}>
            <Panel eyebrow="Carga" title="Pesos">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="Piloto (kg)"><CampoNumero style={inputStyle} valor={pilotKg} alCambiar={(n) => setPilotKg(n)} /></Field>
                <Field label="Carga externa (kg)"><CampoNumero style={inputStyle} valor={externalCargoKg} alCambiar={(n) => setExternalCargoKg(n)} /></Field>
                {/* Solo lectura: el combustible se ajusta en un único lugar, el
                    panel de autonomía. Dos controles para el mismo dato es cómo
                    se termina cargando en litros y leyendo en kilos. */}
                <Field label="Combustible (litros)" hint="Se ajusta abajo, en «Consumo en crucero».">
                  <input type="number" readOnly value={Math.round(fuelLiters * 10) / 10} style={soloLecturaStyle} />
                </Field>
              </div>
              <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 0 }}>
                Pax delante/atrás y equipaje ya no se escriben aquí — se suman solos desde el "Manifiesto de pasajeros"
                de abajo (nombre, documento, peso y equipaje de cada uno).
              </p>
              <table style={{ width: "100%", marginTop: 14, fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Partida</th><th>{weightUnitLabel}</th><th>Brazo</th><th>Momento (kg·m)</th></tr></thead>
                <tbody>{items.map((it) => (
                  <tr key={it.label} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                    <td style={{ padding: "4px 0" }}>{it.label}</td><td>{fmtWeight(it.kg).toFixed(1)}</td><td>{it.arm}</td><td>{(it.kg * it.arm).toFixed(1)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </Panel>

            <div style={{ display: "grid", gap: 16, alignContent: "start" }}>
              <Panel eyebrow="Resultado" title="Peso bruto y CG">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <Readout label="Peso bruto" value={fmtWeight(totalKg).toFixed(0)} unit={weightUnitLabel} />
                  <span style={{ fontSize: 12, color: COLORS.muted }}>≈ {esImperial ? `${totalKg.toFixed(0)} kg` : `${(totalKg * LB_PER_KG).toFixed(0)} lb`}</span>
                  <Readout label="Centro de gravedad" value={cg.toFixed(3)} unit="m" />
                  {insideEnvelope !== null && <div style={{ display: "flex", alignItems: "center" }}><StatusPill status={insideEnvelope ? "ACEPTABLE" : "INTOLERABLE"} /></div>}
                </div>
                <p style={{ margin: "10px 0 0", fontSize: 12, lineHeight: 1.5, color: pesoVacio.brazoDelPesaje ? COLORS.muted : COLORS.amber }}>
                  {pesoVacio.pesoDelPesaje ? (
                    <>Peso vacío <strong>{fmtWeight(pesoVacio.kg).toFixed(0)} {weightUnitLabel}</strong> tomado del pesaje de {matricula}
                      {pesoVacio.fecha ? ` del ${pesoVacio.fecha}` : ""}.</>
                  ) : (
                    <>Peso vacío <strong>{fmtWeight(pesoVacio.kg).toFixed(0)} {weightUnitLabel}</strong> genérico del tipo {activeProfile.tipo}: {matricula} no tiene pesaje cargado.</>
                  )}{" "}
                  {pesoVacio.brazoDelPesaje
                    ? `Brazo en vacío ${pesoVacio.arm.toFixed(3)} m, del mismo pesaje.`
                    : `El brazo en vacío (${pesoVacio.arm.toFixed(3)} m) NO viene del pesaje: es el valor genérico del tipo. El peso bruto es correcto; el CG es una estimación hasta cargar el brazo del acta de pesaje.`}
                </p>
              </Panel>
              <Panel eyebrow={`Origen: ${origin.name}`} title="Cruce con performance">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <Readout label="Máx. OGE" value={perfOrigin.oge.outOfRange ? "s/d" : fmtWeight(perfOrigin.oge.value).toFixed(0)} unit={weightUnitLabel} />
                  <Readout label="Máx. IGE" value={perfOrigin.ige.outOfRange ? "s/d" : fmtWeight(perfOrigin.ige.value).toFixed(0)} unit={weightUnitLabel} />
                  <Readout
                    label={`Límite que manda${limiteOrigen.limitante ? ` — ${limiteOrigen.limitante}` : ""}`}
                    value={limiteOrigen.limiteKg === null ? "s/d" : fmtWeight(limiteOrigen.limiteKg).toFixed(0)}
                    unit={weightUnitLabel}
                    tone={limiteOrigen.limiteKg !== null && totalKg > limiteOrigen.limiteKg ? "INTOLERABLE" : "ACEPTABLE"}
                  />
                  <Readout
                    label="Margen sobre el límite"
                    value={limiteOrigen.limiteKg === null ? "s/d" : fmtWeight(limiteOrigen.limiteKg - totalKg).toFixed(0)}
                    unit={weightUnitLabel}
                    tone={limiteOrigen.limiteKg === null ? "TOLERABLE" : totalKg > limiteOrigen.limiteKg ? "INTOLERABLE" : "ACEPTABLE"}
                  />
                  <div style={{ display: "flex", alignItems: "center" }}><StatusPill status={wbStatus(perfOrigin)} /></div>
                </div>
                <p style={{ fontSize: 11, color: COLORS.muted, margin: "10px 0 0", lineHeight: 1.5 }}>
                  Calculado con <strong>{pies(origin.elevFt)}</strong> y{" "}
                  <strong>{origin.tempC} °C</strong>
                  {meteoPuntos.origin?.ok ? " (temperatura traída de Open-Meteo)" : " (temperatura cargada a mano)"}.
                  {" "}El OGE es la condición exigente; el IGE solo vale con efecto suelo.
                  {limiteOrigen.soloIGE && " Sin carta OGE en esta condición: el margen se está midiendo con la carta menos exigente."}
                </p>
              </Panel>
              <Panel eyebrow={`Destino: ${destination.name}`} title="Cruce con performance">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <Readout label="Máx. OGE" value={perfDestination.oge.outOfRange ? "s/d" : fmtWeight(perfDestination.oge.value).toFixed(0)} unit={weightUnitLabel} />
                  <Readout label="Máx. IGE" value={perfDestination.ige.outOfRange ? "s/d" : fmtWeight(perfDestination.ige.value).toFixed(0)} unit={weightUnitLabel} />
                  <Readout
                    label={`Límite que manda${limiteDestino.limitante ? ` — ${limiteDestino.limitante}` : ""}`}
                    value={limiteDestino.limiteKg === null ? "s/d" : fmtWeight(limiteDestino.limiteKg).toFixed(0)}
                    unit={weightUnitLabel}
                    tone={limiteDestino.limiteKg !== null && totalKg > limiteDestino.limiteKg ? "INTOLERABLE" : "ACEPTABLE"}
                  />
                  <Readout
                    label="Margen sobre el límite"
                    value={limiteDestino.limiteKg === null ? "s/d" : fmtWeight(limiteDestino.limiteKg - totalKg).toFixed(0)}
                    unit={weightUnitLabel}
                    tone={limiteDestino.limiteKg === null ? "TOLERABLE" : totalKg > limiteDestino.limiteKg ? "INTOLERABLE" : "ACEPTABLE"}
                  />
                  <div style={{ display: "flex", alignItems: "center" }}><StatusPill status={wbStatus(perfDestination)} /></div>
                </div>
                <p style={{ fontSize: 11, color: COLORS.muted, margin: "10px 0 0", lineHeight: 1.5 }}>
                  Calculado con <strong>{pies(destination.elevFt)}</strong> y{" "}
                  <strong>{destination.tempC} °C</strong>
                  {meteoPuntos.destination?.ok ? " (temperatura traída de Open-Meteo)" : " (temperatura cargada a mano)"}.
                  {" "}El OGE es la condición exigente; el IGE solo vale con efecto suelo.
                  {limiteDestino.soloIGE && " Sin carta OGE en esta condición: el margen se está midiendo con la carta menos exigente."}
                </p>
              </Panel>
            </div>
          </div>

          <Panel eyebrow="Manifiesto" title="Manifiesto de pasajeros y carga" right={<button className="no-print" onClick={addPassenger} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ pasajero</button>}>
            {(() => {
              const canasta = compartmentTotals.find((c) => c.key === "canasta");
              const definicion = activeProfile.cargoCompartments.find((c) => c.key === "canasta");
              return (
                <div style={{ background: COLORS.panelAlt, border: `1px solid ${canasta?.overweight ? COLORS.danger : COLORS.hairline}`, borderRadius: 8, padding: 12, marginBottom: 14 }}>
                  <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                    Accesorio — canasta ({matricula})
                  </div>
                  {!enmiendaAplicable && (
                    <p style={{ fontSize: 11.5, color: COLORS.amber, margin: "0 0 10px", lineHeight: 1.5 }}>
                      {matricula} no tiene enmienda de peso y balance cargada, así que activar la canasta no
                      puede cambiar el peso vacío. Carga la enmienda de esta matrícula en Mantenimiento.
                    </p>
                  )}
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
                    <Field label="Canasta (Heli-Utility Basket)">
                      <select style={inputStyle} disabled={!enmiendaAplicable} value={canastaInstalada ? "con" : "sin"} onChange={(e) => setCanastaInstalada(e.target.value === "con")}>
                        <option value="con">Instalada — suma {enmienda?.accesorio?.pesoKg ?? "—"} kg al peso vacío</option>
                        <option value="sin">Desinstalada — resta {enmienda?.accesorio?.pesoKg ?? "—"} kg</option>
                      </select>
                    </Field>
                    <Readout label="Peso vacío resultante" value={configuracionCanasta.pesoKg === null ? "s/d" : fmtWeight(configuracionCanasta.pesoKg).toFixed(1)} unit={weightUnitLabel} />
                    <Readout label="Brazo en vacío" value={configuracionCanasta.brazoM === null ? "s/d" : configuracionCanasta.brazoM.toFixed(3)} unit="m" />
                    {canasta && (
                      <Readout
                        label={`Carga en canasta (máx ${definicion?.maxKg ?? "—"} kg)`}
                        value={fmtWeight(canasta.kg).toFixed(1)} unit={weightUnitLabel}
                        tone={canasta.overweight ? "INTOLERABLE" : "ACEPTABLE"}
                      />
                    )}
                  </div>
                  {!canastaInstalada && (canasta?.kg || 0) > 0 && (
                    <p style={{ fontSize: 11.5, color: COLORS.danger, margin: "8px 0 0" }}>
                      Hay {canasta.kg} kg asignados a la canasta pero la configuración es SIN canasta. Ese peso
                      está entrando al centrado sin que el accesorio esté a bordo: o se instala, o se mueve la
                      carga a otro compartimento.
                    </p>
                  )}
                  <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "8px 0 0", lineHeight: 1.55 }}>
                    La canasta figura siempre entre los compartimentos de carga, junto a derecha, izquierda y
                    trasero: cualquier pasajero puede asignarle su equipaje. Su límite es {definicion?.maxKg ?? 100} kg
                    y comparte estación de centrado con el compartimento derecho. Lo que cambia el interruptor de
                    arriba es el <strong>peso vacío</strong>: instalada suma el peso del accesorio, desinstalada lo resta.
                    {enmiendaAplicable && ` Peso vacío y brazo salen de la ${enmienda.referencia}.`}
                  </p>
                </div>
              );
            })()}

            {passengers.length === 0 ? (
              <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0 }}>Sin pasajeros cargados — agrega uno con "+ pasajero".</p>
            ) : (
              <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                  <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Nombre</th><th>N° documento</th><th>Estación</th><th>Peso (kg)</th><th>Equipaje (kg)</th><th>Compartimento</th><th></th></tr></thead>
                  <tbody>{passengers.map((p) => (
                    <tr key={p.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                      <td style={{ padding: "4px 0" }}><input style={{ ...inputStyle, padding: "3px 6px", minWidth: 140 }} value={p.nombre} onChange={(e) => setPassengers((ps) => ps.map((x) => (x.id === p.id ? { ...x, nombre: e.target.value } : x)))} /></td>
                      <td><input style={{ ...inputStyle, padding: "3px 6px", width: 120 }} value={p.documento} onChange={(e) => setPassengers((ps) => ps.map((x) => (x.id === p.id ? { ...x, documento: e.target.value } : x)))} /></td>
                      <td>
                        <select style={{ ...inputStyle, padding: "3px 6px" }} value={p.station} onChange={(e) => setPassengers((ps) => ps.map((x) => (x.id === p.id ? { ...x, station: e.target.value } : x)))}>
                          {/* Las estaciones del TIPO activo. El AS350 tiene dos;
                              el BK117, nueve con brazos de 3,492 a 5,229 m. */}
                          {estacionesDelTipo.map((e) => (
                            <option key={e.key} value={e.key}>{e.label} ({e.brazoM} m)</option>
                          ))}
                        </select>
                      </td>
                      <td><CampoNumero style={{ ...inputStyle, padding: "3px 6px", width: 70 }} valor={p.pesoKg} alCambiar={(n) => setPassengers((ps) => ps.map((x) => (x.id === p.id ? { ...x, pesoKg: n } : x)))} /></td>
                      <td>
                        <CampoNumero style={{ ...inputStyle, padding: "3px 6px", width: 70 }}
                          valor={p.equipajeKg} max={remainingCapacityFor(p.id, p.compartimento)}
                          alCambiar={(n) => {
                            const max = remainingCapacityFor(p.id, p.compartimento);
                            const val = Math.max(0, Math.min(n || 0, max));
                            setPassengers((ps) => ps.map((x) => (x.id === p.id ? { ...x, equipajeKg: val } : x)));
                          }} />
                        <div style={{ fontSize: 9.5, color: COLORS.muted }}>libre: {remainingCapacityFor(p.id, p.compartimento).toFixed(0)} kg</div>
                      </td>
                      <td>
                        <select
                          style={{ ...inputStyle, padding: "3px 6px" }} value={p.compartimento}
                          onChange={(e) => {
                            const nuevoCompartimento = e.target.value;
                            const max = remainingCapacityFor(p.id, nuevoCompartimento);
                            setPassengers((ps) => ps.map((x) => (x.id === p.id ? { ...x, compartimento: nuevoCompartimento, equipajeKg: Math.min(Number(x.equipajeKg) || 0, max) } : x)));
                          }}
                        >
                          {activeProfile.cargoCompartments.map((c) => <option key={c.key} value={c.key}>{c.label} (máx {c.maxKg} kg)</option>)}
                        </select>
                      </td>
                      <td className="no-print"><button onClick={() => removePassenger(p.id)} style={{ background: "transparent", border: "none", color: COLORS.danger, cursor: "pointer" }}>✕</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
              {/* Un total por estación, con su brazo: es la tabla que cambia
                  entre tipos. Con dos estaciones se ven dos; con nueve, nueve. */}
              {estacionesDelTipo.map((e) => (
                <Readout
                  key={e.key}
                  label={`${e.label} · ${e.brazoM} m`}
                  value={fmtWeight(pesoPorEstacion[e.key] || 0).toFixed(1)}
                  unit={weightUnitLabel}
                />
              ))}
              <Readout label="Equipaje total" value={fmtWeight(totalBaggageKg).toFixed(1)} unit={weightUnitLabel} />
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
              {compartmentTotals.map((c) => (
                <Readout
                  key={c.key}
                  label={`Compartimento ${c.label} (máx ${fmtWeight(c.maxKg).toFixed(0)} ${weightUnitLabel})`}
                  value={fmtWeight(c.kg).toFixed(1)}
                  unit={weightUnitLabel}
                  tone={c.overweight ? "INTOLERABLE" : c.kg > c.maxKg * 0.85 ? "TOLERABLE" : "ACEPTABLE"}
                />
              ))}
            </div>
            {compartmentTotals.some((c) => c.overweight) && (
              <Banner tone="INTOLERABLE">
                ⚠ Sobrepeso en {compartmentTotals.filter((c) => c.overweight).map((c) => `${c.label} (${fmtWeight(c.kg).toFixed(1)}/${fmtWeight(c.maxKg).toFixed(0)} ${weightUnitLabel})`).join(", ")} — mueve equipaje a otro compartimento con espacio antes de volar.
              </Banner>
            )}
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 0 }}>
              Cada pasajero elige su compartimento de equipaje directamente en el manifiesto — no se reparte
              automático. Los compartimentos siempre parten en 0 kg al iniciar una planificación nueva; sus límites
              (Derecha 100 kg, Izquierda 120 kg, Trasero 80 kg) se pueden ajustar por tipo de aeronave más abajo, en
              el perfil.
            </p>
          </Panel>

          <Panel eyebrow="Figura de peso y balance" title="Envolvente de centro de gravedad — AS350 B3, carga interna (manual de vuelo real)">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              <Readout label="Máx. carga interna" value={fmtWeight(activeProfile.maxInternalKg).toFixed(0)} unit={weightUnitLabel} tone={!usingExternal && totalKg > activeProfile.maxInternalKg ? "INTOLERABLE" : undefined} />
              <Readout label="Máx. con carga externa" value={fmtWeight(activeProfile.maxExternalKg).toFixed(0)} unit={weightUnitLabel} tone={usingExternal && totalKg > activeProfile.maxExternalKg ? "INTOLERABLE" : undefined} />
              <Readout label="Configuración activa" value={usingExternal ? "Carga externa" : "Interna"} unit="" tone={usingExternal ? "TOLERABLE" : "ACEPTABLE"} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 16 }}>
              <div>
                <p style={{ fontSize: 12, color: COLORS.muted, marginTop: 0, lineHeight: 1.6 }}>
                  {activeProfile.datumNote}
                </p>
                <p style={{ fontSize: 12, color: COLORS.muted, lineHeight: 1.6 }}>
                  El gráfico muestra los dos ábacos cerrados del manual en el mismo trazo: <b style={{ color: COLORS.amber }}>carga interna</b>
                  {" "}(≤2250 kg, línea punteada gruesa) y <b style={{ color: COLORS.cyan }}>con carga externa/liberable</b> (≤2800 kg, línea
                  punteada fina) — ambos comparten el mismo límite delantero hasta 2250 kg y luego el de carga externa
                  se extiende hasta 3.29 m / 2800 kg, con el límite trasero llegando a ≈3.43 m.
                </p>
                {usingExternal && (
                  <p style={{ fontSize: 11.5, color: COLORS.caution, lineHeight: 1.6 }}>
                    Llevas carga externa: el chequeo ACEPTABLE/INTOLERABLE de abajo usa el ábaco de 2800 kg
                    (línea fina cian) como límite real para tu configuración actual.
                  </p>
                )}
              </div>
              <div style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 6, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={COLORS.hairline} />
                    <XAxis type="number" dataKey="x" name="CG (m)" stroke={COLORS.muted} tick={{ fontSize: 11 }} domain={["dataMin - 0.02", "dataMax + 0.02"]} />
                    <YAxis type="number" dataKey="y" name={`Peso (${weightUnitLabel})`} stroke={COLORS.muted} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}` }} cursor={{ strokeDasharray: "3 3" }} />
                    <Scatter name="Envolvente (carga interna, ≤2250 kg)" data={closedEnvelopePoints.map((p) => ({ x: p.x, y: fmtWeight(p.y) }))} line={{ stroke: COLORS.amber, strokeDasharray: "6 4", strokeWidth: 1.6 }} fill="none" shape={() => null} />
                    <Scatter name="Envolvente (con carga externa, ≤2800 kg)" data={closedEnvelopeExternalPoints.map((p) => ({ x: p.x, y: fmtWeight(p.y) }))} line={{ stroke: COLORS.cyan, strokeDasharray: "2 3", strokeWidth: 1.4 }} fill="none" shape={() => null} />
                    {/* La línea entre los dos puntos es el recorrido del CG a
                        medida que se quema el combustible: es lo que hay que
                        mirar para saber si en algún momento se sale. */}
                    {wbAterrizaje.valido && (
                      <Scatter
                        name="Recorrido del CG en vuelo"
                        data={[{ x: cg, y: fmtWeight(totalKg) }, { x: wbAterrizaje.cg, y: fmtWeight(wbAterrizaje.pesoTotalKg) }]}
                        line={{ stroke: COLORS.magenta, strokeWidth: 1.6, strokeDasharray: "4 3" }}
                        fill="none" shape={() => null}
                      />
                    )}
                    <Scatter name="CG al despegue ★" data={[{ x: cg, y: fmtWeight(totalKg) }]} fill={insideEnvelope === false ? COLORS.danger : COLORS.ok} shape="star" />
                    {wbAterrizaje.valido && (
                      <Scatter
                        name="CG al aterrizaje ▲"
                        data={[{ x: wbAterrizaje.cg, y: fmtWeight(wbAterrizaje.pesoTotalKg) }]}
                        fill={wbAterrizaje.dentroDeEnvolvente === false ? COLORS.danger : wbAterrizaje.dentroDeEnvolvente === null ? COLORS.caution : COLORS.cyan}
                        shape="triangle"
                      />
                    )}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 10, lineHeight: 1.5 }}>
              Envolvente real, tomada del Manual de Vuelo Eurocopter AS350 B3 Arriel 2B1 (Fig. 2-1) — los seis puntos
              del contorno (3.17/124.8, 3.21/126.4, 3.425/134.8, 3.49/137.4, 3.50/137.8 in) son los valores impresos
              en la carta, no una aproximación.
            </p>
          </Panel>
        </div>
      )}

      {/* ---------------- PERFORMANCE ---------------- */}
      {tab === "analisis" && (
        <div style={{ display: "grid", gap: 16 }}>
          {/* ═══ OEI — ESPACIO PROPIO ═══
              Es específico de un BIMOTOR: en un monomotor la pregunta es «¿puedo
              sostener el estacionario?»; acá hay una segunda que decide la
              operación: «si se me para un motor AHORA, ¿el que queda alcanza?».

              El módulo `domain/oei.js` estaba escrito, probado y sin que nadie
              lo importara. */}
          <PanelOEI
            pesoBrutoKg={totalKg}
            altitudPresionFt={cruiseAltFt}
            oatC={cruiseTempC}
            altitudDensidadFt={altitudDensidad(cruiseAltFt, cruiseTempC)}
            matricula={matricula}
            serie={fleet.find((f) => f.matricula === matricula)?.serie || null}
            /* El dato que decide si el análisis corresponde. Sale del PERFIL,
               no de la matrícula: es una característica del tipo. */
            motores={activeProfile?.motores ?? null}
          />
        </div>
      )}

      {tab === "analisis" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: 16 }}>
          <Panel eyebrow="Nivel de vuelo (de Planificación de ruta)" title="Altitud / temperatura de crucero">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
              <Field label="Altitud presión — Hp (ft)">
                <input type="range" min={0} max={ROUTE_CEILING} step={100} value={cruiseAltFt} onChange={(e) => setCruiseAltFt(Number(e.target.value))} />
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>{cruiseAltFt.toLocaleString()} ft</div>
              </Field>
              <Field label="Temperatura (°C)">
                <input type="range" min={-20} max={50} step={1} value={cruiseTempC} onChange={(e) => setCruiseTempC(Number(e.target.value))} />
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>{cruiseTempC} °C</div>
              </Field>
            </div>
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: -8, marginBottom: 14 }}>
              Este es el mismo nivel de crucero de "Planificación de ruta de vuelo" — cambiarlo aquí también lo cambia
              allá, y el cálculo de combustible de más abajo parte de este mismo valor automáticamente.
            </p>
            <div style={{ display: "grid", gap: 10 }}>
              <Readout label="Peso máx. OGE" value={ogeExpl.outOfRange ? "—" : fmtWeight(ogeExpl.value).toFixed(0)} unit={weightUnitLabel} tone={ogeExpl.outOfRange ? "TOLERABLE" : undefined} />
              <Readout label="Peso máx. IGE" value={igeExpl.outOfRange ? "—" : fmtWeight(igeExpl.value).toFixed(0)} unit={weightUnitLabel} tone={igeExpl.outOfRange ? "TOLERABLE" : undefined} />
              {IGE_IS_ESTIMATE && !igeExpl.outOfRange && (
                <p style={{ fontSize: 10.5, color: COLORS.caution, margin: 0 }}>IGE: estimado de gráfico (nomograma "5.6 Hover in Ground Effect") — verificar contra el manual con puntos de anclaje reales antes de usar en operación.</p>
              )}
              {(ogeExpl.outOfRange || igeExpl.outOfRange) && (
                <p style={{ fontSize: 11.5, color: COLORS.caution }}>
                  {!activeProfile.ogeTable ? "Este tipo no tiene cartas de performance cargadas: carga las del manual de tu aeronave." : sobreTopeDeCarta(cruiseAltFt) ? `Por encima de ${topeDeCartaTexto()} no hay carta digitalizada para este tipo.` : "Combinación fuera de la envolvente publicada."}
                </p>
              )}
            </div>
          </Panel>
          <Panel eyebrow={`Curva a ${cruiseTempC} °C`} title="Peso máximo vs. altitud">
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={COLORS.hairline} />
                  <XAxis dataKey="alt" stroke={COLORS.muted} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000}k`} />
                  <YAxis stroke={COLORS.muted} tick={{ fontSize: 11 }} domain={[1500, 2850]} />
                  <Tooltip contentStyle={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}` }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="oge" name="OGE" stroke={COLORS.cyan} dot={false} strokeWidth={2} connectNulls={false} />
                  {/* La curva IGE solo se dibuja si HAY carta. Sin ella, una
                      línea ausente se lee igual que «no alcanza», y son cosas
                      opuestas: una es no tener el dato, la otra que el dato
                      diga que no. */}
                  {activeProfile.igeTable && (
                    <Line type="monotone" dataKey="ige" name="IGE" stroke={COLORS.amber} dot={false} strokeWidth={2} connectNulls={false} />
                  )}
                  {!ogeExpl.outOfRange && <ReferenceDot x={cruiseAltFt} y={ogeExpl.value} r={5} fill={COLORS.cyan} stroke="none" />}
                  {activeProfile.igeTable && !igeExpl.outOfRange && <ReferenceDot x={cruiseAltFt} y={igeExpl.value} r={5} fill={COLORS.amber} stroke="none" />}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Sin carta IGE se DICE, con el tipo de aeronave y qué hacer.
                Un gráfico con una sola curva y sin explicación deja al piloto
                creyendo que el efecto suelo no aporta nada en esta máquina. */}
            {!activeProfile.igeTable && (
              <p style={{
                margin: "8px 0 0", padding: "8px 10px",
                background: "rgba(255,210,63,0.10)",
                borderLeft: `3px solid ${COLORS.caution}`,
                borderRadius: 4, fontSize: 12, lineHeight: 1.5, color: COLORS.text,
              }}>
                <strong>Sin carta IGE para {activeProfile.tipo}.</strong>{" "}
                El estacionario CON efecto suelo no se puede calcular: no hay curva
                que dibujar. La curva OGE —sin efecto suelo— es la exigente de las
                dos, así que planificar con ella es conservador, pero no reemplaza
                a la carta IGE del manual.
              </p>
            )}
          </Panel>
        </div>
      )}

      {/* ---------------- COMBUSTIBLE ---------------- */}
      {tab === "analisis" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Panel eyebrow="Autonomía" title="Consumo en crucero">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
              <Field label="Reserva mínima (min)"><CampoNumero style={inputStyle} valor={reserveMin} alCambiar={(n) => setReserveMin(n)} /></Field>
              <Field label={`Combustible embarcado (${fuelInputLabel})`}>
                <CampoNumero
                  step="0.1" style={inputStyle}
                  valor={fuelInputValue}
                  alCambiar={(n) => setFuelFromInput(n)}
                />
              </Field>
              <Field label="Unidad del combustible" hint="Solo cambia cómo se escribe y se lee: el dato se guarda en litros.">
                <select style={{ ...inputStyle, width: "100%" }} value={unidadCombustible} onChange={(e) => setUnidadCombustible(e.target.value)}>
                  {UNIDADES_COMBUSTIBLE.map((u) => <option key={u.clave} value={u.clave}>{u.etiqueta} ({u.simbolo})</option>)}
                </select>
              </Field>
            </div>

            {/* Desglose por tanque, en los tipos que tienen más de uno.
                El formato del BK117 carga principal y supply por separado
                porque cada uno tiene su propia lectura de cabina. */}
            {Array.isArray(activeProfile.tanques) && activeProfile.tanques.length > 1 && (
              <div style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 10, marginBottom: 12 }}>
                <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                  Tanques de {activeProfile.tipo}
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {activeProfile.tanques.map((t) => (
                    <Readout
                      key={t.key}
                      label={t.label}
                      value={enUnidadCrucero(t.capacidadKg / (densidadKgL || 0.8))}
                      unit={`${unidadActual.simbolo} máx`}
                    />
                  ))}
                  <Readout
                    label="Total utilizable"
                    value={enUnidadCrucero(fuelCapacityL)}
                    unit={unidadActual.simbolo}
                    tone="ACEPTABLE"
                  />
                </div>
                <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "8px 0 0", lineHeight: 1.5 }}>
                  {activeProfile.fuelCapacityKg} kg entre los dos tanques. El deslizador de abajo carga el total;
                  el reparto entre principal y supply se anota en el formato de peso y balance.
                </p>
              </div>
            )}

            {/* Deslizador de 0 a 100 % del utilizable. Mismo gesto que los de
                altitud y temperatura, para cargar rápido sin teclear. */}
            <Field label="Carga del tanque (% del utilizable)">
              <input
                type="range" min={0} max={100} step={1}
                value={Math.round(fuelPct)}
                onChange={(e) => setFuelFromPct(e.target.value)}
                className="hc-rango-combustible"
              />
              <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>
                <span style={{ color: "#FF3EA5" }}>{fuelPct.toFixed(0)} %</span>
                <span style={{ fontSize: 12, color: COLORS.muted }}>0 % — 100 % ({fuelCapacityL} L utilizables)</span>
              </div>
            </Field>

            {/* La misma cantidad en las cuatro unidades. Es lo que se pide en
                plataforma según quién sirva el combustible. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, margin: "12px 0" }}>
              {UNIDADES_COMBUSTIBLE.map((u) => (
                <div key={u.clave} style={{ background: COLORS.panelAlt, border: `1px solid ${u.clave === unidadCombustible ? "#FF3EA5" : COLORS.hairline}`, borderRadius: 6, padding: "6px 8px" }}>
                  <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>{u.simbolo}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>
                    {embarcadoTodas[u.clave] === null ? "s/d" : embarcadoTodas[u.clave].toFixed(1)}
                  </div>
                  <div style={{ fontSize: 9.5, color: COLORS.muted }}>
                    máx {capacidadTodas[u.clave] === null ? "s/d" : capacidadTodas[u.clave].toFixed(1)}
                  </div>
                </div>
              ))}
            </div>

            {!chequeoTanque.cabe && chequeoTanque.excedeL > 0 && (
              <Banner tone="INTOLERABLE">{chequeoTanque.motivo}</Banner>
            )}

            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 4, marginBottom: 14 }}>
              Densidad usada: {activeProfile.fuelDensity} kg/L — verificar contra el combustible real, cambia con
              la temperatura. Los factores de conversión son los exactos (1 gal US = 3,785411784 L;
              1 kg = 2,20462262 lb): con 2,2 lb/kg redondeado, 432 kg darían 950,4 lb en vez de 952,4, y esa
              diferencia de 1 kg entra al peso y balance.
            </p>
            <div style={{ display: "grid", gap: 10 }}>
              <Readout label="Consumo estimado" value={fuelRateAt.outOfRange ? "—" : fmtWeight(fuelRateAt.value).toFixed(1)} unit={`${weightUnitLabel}/h`} />
              {!fuelRateAt.outOfRange && (() => {
                const c = consumoEnTodasLasUnidades(fuelRateAt.value, densidadKgL);
                return (
                  <p style={{ fontSize: 11, color: COLORS.muted, margin: "-4px 0 0" }}>
                    ≈ {c.L === null ? "s/d" : c.L.toFixed(1)} L/h · {c.gal === null ? "s/d" : c.gal.toFixed(1)} gal US/h ·{" "}
                    {c.kg.toFixed(1)} kg/h · {c.lb.toFixed(1)} lb/h
                  </p>
                );
              })()}
              <Readout label="Autonomía total" value={autonomyMin === null ? "—" : (autonomyMin / 60).toFixed(2)} unit="h" />
              <Readout label="Autonomía utilizable" value={usableMin === null ? "—" : (usableMin / 60).toFixed(2)} unit="h (con reserva)" tone={usableMin !== null && usableMin < 0 ? "INTOLERABLE" : undefined} />
            </div>
            {fuelRateAt.outOfRange && <p style={{ fontSize: 11.5, color: COLORS.caution, marginTop: 10 }}>{!activeProfile.fuelTable ? "No hay carta de combustible cargada para este tipo." : sobreTopeDeCarta(cruiseAltFt) ? `Por encima de ${topeDeCartaTexto()} no hay carta de combustible digitalizada.` : "Combinación fuera de la carta de combustible."}</p>}
          </Panel>
          <Panel eyebrow="Vs. ruta planeada" title="Combustible requerido">
            <div style={{ display: "grid", gap: 10 }}>
              <Readout label="ETE + reserva" value={((eteMin + reserveMin) / 60).toFixed(2)} unit="h" />
              <Readout label="Combustible requerido" value={requiredKg === null ? "—" : fmtWeight(requiredKg).toFixed(0)} unit={weightUnitLabel} />
              {requiredKg !== null && (() => {
                const r = enTodasLasUnidades(requiredKg / (densidadKgL || 1), densidadKgL);
                return (
                  <p style={{ fontSize: 11, color: COLORS.muted, margin: "-4px 0 0" }}>
                    ≈ {r.L.toFixed(1)} L · {r.gal.toFixed(1)} gal US · {r.kg === null ? "s/d" : r.kg.toFixed(1)} kg · {r.lb === null ? "s/d" : r.lb.toFixed(1)} lb
                  </p>
                );
              })()}
              <Readout label="Margen sobre lo embarcado" value={fuelMargin === null ? "—" : fmtWeight(fuelMargin).toFixed(0)} unit={weightUnitLabel} tone={fuelMargin !== null ? (fuelMargin < 0 ? "INTOLERABLE" : fuelMargin < 30 ? "TOLERABLE" : "ACEPTABLE") : undefined} />
              {fuelMargin !== null && <p style={{ fontSize: 11, color: COLORS.muted, margin: "-4px 0 0" }}>≈ {(fuelMargin / activeProfile.fuelDensity).toFixed(1)} L · {(fuelMargin / activeProfile.fuelDensity / GAL_TO_L).toFixed(1)} gal US</p>}
            </div>
          </Panel>
        </div>
      )}

      {/* ---------------- FRAT ---------------- */}
      {tab === "frat" && (
        <div style={{ display: "grid", gap: 16 }}>
          <Banner tone="TOLERABLE">
            Cota máxima del terreno en ruta: <b>{cotaMaximaRutaFt === null ? "sin dato — falta planificar la ruta" : `${cotaMaximaRutaFt.toLocaleString()} ft`}</b>.
            {" "}Topografía derivada: <b>{topografiaAuto.etiqueta}</b>
            {topografiaAuto.derivado ? " (del perfil de terreno y las cotas de despegue/aterrizaje)" : " — sin ruta no se puede clasificar, el factor queda en ámbar"}.
            {" "}Umbrales: montaña 5 000–10 000 ft, alta montaña sobre 10 000 ft, techo de servicio 23 000 ft.
            <br />Piloto: <b>{pilots.find((p) => p.id === assignedPilotId)?.nombre || "—"}</b>
            {pilots.find((p) => p.id === assignedPilotId)?.licencia ? ` (Lic. ${pilots.find((p) => p.id === assignedPilotId).licencia})` : ""} · Aeronave: <b>{matricula}</b>
          </Banner>
          <Panel eyebrow="Variables del vuelo (desde Análisis de vuelo)" title="Altitud de nivel de vuelo y combustible">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
              <Readout label="Nivel de vuelo (Hp)" value={cruiseAltFt.toLocaleString()} unit="ft" tone={sobreTopeDeCarta(cruiseAltFt) ? "TOLERABLE" : undefined} />
              <Readout label="Peso bruto" value={fmtWeight(totalKg).toFixed(0)} unit={weightUnitLabel} tone={insideEnvelope === false ? "INTOLERABLE" : undefined} />
              <Readout label="Combustible embarcado" value={fmtWeight(fuelKg).toFixed(0)} unit={weightUnitLabel} />
              <Readout label="Margen de combustible" value={fuelMargin === null ? "—" : fmtWeight(fuelMargin).toFixed(0)} unit={weightUnitLabel} tone={fuelMargin !== null ? (fuelMargin < 0 ? "INTOLERABLE" : fuelMargin < 30 ? "TOLERABLE" : "ACEPTABLE") : undefined} />
            </div>
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 0 }}>
              Estas cuatro variables vienen directo de "Análisis de vuelo" — si el margen de combustible es negativo o
              el nivel de vuelo supera la carta digitalizada (18 000 ft), el índice agregado de abajo sube a
              INTOLERABLE automáticamente, aunque los demás factores estén en verde.
            </p>
          </Panel>
          {(() => {
            const veredicto = veredictoDespacho(worst.nivel);
            const tono = !veredicto.permite ? "INTOLERABLE" : veredicto.requiereMitigacion ? "TOLERABLE" : "ACEPTABLE";
            return (
              <div style={{
                background: `${STATUS_COLOR[tono]}18`, border: `2px solid ${STATUS_COLOR[tono]}`,
                borderRadius: 10, padding: "16px 20px",
              }}>
                <div style={{ fontSize: 10.5, color: COLORS.muted, letterSpacing: 1.2, textTransform: "uppercase", fontFamily: "'Barlow Condensed', sans-serif" }}>
                  Veredicto de despacho
                </div>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 30, letterSpacing: 0.5, color: STATUS_COLOR[tono], lineHeight: 1.2 }}>
                  {veredicto.titulo}
                </div>
                <p style={{ fontSize: 12.5, color: COLORS.text, margin: "6px 0 0", lineHeight: 1.5 }}>
                  {veredicto.detalle}
                </p>
                <p style={{ fontSize: 12, color: COLORS.muted, margin: "8px 0 0", lineHeight: 1.5 }}>
                  Nivel resultante <StatusPill status={worst.nivel} /> — lo determina el peor factor de todos:{" "}
                  <strong>{worst.idx || "ninguno por encima de ACEPTABLE"}</strong>.{" "}
                  {reservaAuto.etiqueta}.
                </p>
                <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "10px 0 0", lineHeight: 1.5 }}>
                  Herramienta de apoyo a la decisión según la metodología Eurocopter IN 2255-I-00. No reemplaza
                  al RFM, al manual de operaciones ni a la autoridad aeronáutica. La decisión final es del
                  piloto al mando.
                </p>
              </div>
            );
          })()}

          <Panel eyebrow="Índice agregado (matriz SGS)" title="Resultado del análisis de riesgo" right={<StatusPill status={worst.nivel} />}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10, marginBottom: 14 }}>
              <Readout
                label={`Carga inicial · ${promedioInicial.total} factores`}
                value={promedioInicial.indice === null ? "—" : `${promedioInicial.indice} %`}
                unit={promedioInicial.nivelEquivalente || ""}
                tone={promedioInicial.nivelEquivalente || undefined}
              />
              <Readout
                label="Carga residual — tras las medidas"
                value={promedioFrat.indice === null ? "—" : `${promedioFrat.indice} %`}
                unit={promedioFrat.nivelEquivalente || ""}
                tone={promedioFrat.nivelEquivalente || undefined}
              />
              <Readout label="Peor factor — decide el despacho" value={worst.nivel} unit="" tone={worst.nivel} />
              <Readout
                label="Factores que aún exigen medida"
                value={factoresQueExigenMedida.length} unit=""
                tone={factoresQueExigenMedida.length > 0 ? "TOLERABLE" : "ACEPTABLE"}
              />
              {["ACEPTABLE", "MEDIO", "GRAVE", "INACEPTABLE", "INACEPTABLE+"]
                .filter((n) => promedioFrat.conteo[n])
                .map((n) => (
                  <Readout key={n} label={`Factores en ${n}`} value={promedioFrat.conteo[n]} unit="" tone={n} />
                ))}
            </div>
            <p style={{ fontSize: 11, color: COLORS.muted, margin: "0 0 14px", lineHeight: 1.55 }}>
              La <strong>carga promedio</strong> es el promedio de los {promedioFrat.total} factores sobre el
              máximo posible, y sirve para ver el cuadro completo: once factores en MEDIO no se ven igual que
              uno solo en MEDIO y diez en ACEPTABLE.
              {" "}
              <strong style={{ color: COLORS.amber }}>
                El veredicto de despacho lo sigue determinando el peor factor, no el promedio.
              </strong>{" "}
              Un promedio diluye: diez factores en MEDIO y uno en INACEPTABLE+ promedian GRAVE, y la
              IN 2255-I-00 dice que INACEPTABLE significa que la actividad no puede proseguir en el estado
              actual. Ningún promedio cambia eso, igual que diez sistemas en verde no compensan un motor en
              llamas.
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {selectedRisks.filter((r) => r.nivel !== "ACEPTABLE").length === 0
                ? <p style={{ color: COLORS.muted, fontSize: 13 }}>Todos los factores están en su condición más favorable.</p>
                : selectedRisks.filter((r) => r.nivel !== "ACEPTABLE").map((r) => (
                  <div key={r.group.key} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: "8px 12px", fontSize: 12.5 }}>
                    {r.group.label}: <b>{r.opt.label}</b> · <StatusPill status={r.nivel} />
                  </div>
                ))}
            </div>
          </Panel>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {selectedRisks.map(({ group: g, opt, medidas, mit, nivel }) => {
              const indice = indiceDeGrupo(g);
              return (
              <Panel key={g.key} title={g.label}>
                <select
                  style={{ ...inputStyle, width: "100%", ...(g.derivable ? soloLecturaStyle : {}) }}
                  value={indice}
                  disabled={g.derivable}
                  onChange={(e) => {
                    setRiskChoices((c) => ({ ...c, [g.key]: Number(e.target.value) }));
                    /* Tocar la casilla la vuelve MANUAL: desde acá el METAR ya
                       no la pisa. El piloto vio el cielo. */
                    if (g.key === "meteoSalida" || g.key === "meteoDestino") {
                      setMetarManual((m) => ({ ...m, [g.key]: true }));
                    }
                  }}
                >
                  {g.options.map((o, i) => <option key={o.label} value={i}>{o.label}</option>)}
                </select>
                {g.key === "topografia" && (
                  <p style={{ fontSize: 10.5, color: topografiaAuto.derivado ? COLORS.cyan : COLORS.amber, margin: "4px 0 0" }}>
                    {topografiaAuto.derivado
                      ? `Derivado de la cota máxima en ruta (${topografiaAuto.cota.toLocaleString()} ft). No se edita a mano: cambia la ruta.`
                      : "Sin ruta planificada. El factor no puede quedar en verde mientras no se sepa sobre qué terreno se vuela."}
                  </p>
                )}
                {(g.key === "meteoSalida" || g.key === "meteoDestino") && (() => {
                  const p = propuestasMeteo[g.key];
                  const esAutomatica = Boolean(p) && !metarManual[g.key];

                  if (esAutomatica) {
                    return (
                      <div style={{ margin: "6px 0 0" }}>
                        <span style={{
                          display: "inline-block", padding: "1px 6px", borderRadius: 4,
                          background: "rgba(91,209,215,0.15)", color: COLORS.cyan,
                          fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase",
                        }}>
                          Automático · METAR
                        </span>
                        {/* De dónde salió el número: sin esto, una casilla
                            completada sola es indistinguible de una
                            contestada a mano, y el registro de auditoría no
                            dice quién decidió. */}
                        <p style={{ fontSize: 10.5, color: COLORS.cyan, margin: "4px 0 0" }}>
                          {explicacionDe(p)}
                        </p>
                        <button
                          type="button"
                          onClick={() => setMetarManual((m) => ({ ...m, [g.key]: true }))}
                          style={{
                            marginTop: 6, padding: "4px 10px", background: "none",
                            border: `1px solid ${COLORS.hairline}`, borderRadius: 6,
                            color: COLORS.muted, fontSize: 11, cursor: "pointer",
                          }}
                        >
                          Contestar a mano
                        </button>
                      </div>
                    );
                  }

                  if (p && metarManual[g.key]) {
                    return (
                      <p style={{ fontSize: 10.5, color: COLORS.amber, margin: "6px 0 0" }}>
                        Contestada a mano. El METAR propone «{p.etiqueta}».{" "}
                        <button
                          type="button"
                          onClick={() => setMetarManual((m) => ({ ...m, [g.key]: false }))}
                          style={{ background: "none", border: "none", color: COLORS.cyan, cursor: "pointer", textDecoration: "underline", padding: 0, fontSize: 10.5 }}
                        >
                          Volver al METAR
                        </button>
                      </p>
                    );
                  }

                  return (
                    <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "6px 0 0" }}>
                      Sin METAR para este aeródromo. Consultalo en Análisis de vuelo
                      y la casilla se completa sola.
                    </p>
                  );
                })()}
                {g.key === "meteoRuta" && (
                  /* Se dice POR QUÉ esta queda a mano: si el piloto ve salida y
                     destino automáticos y ruta no, tiene que saber que es
                     deliberado y no un fallo. */
                  <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "6px 0 0" }}>
                    No se completa con un METAR: un informe describe un aeródromo,
                    no el tramo entre los dos.
                  </p>
                )}
                {g.key === "reserva" && (
                  <p style={{ fontSize: 10.5, color: COLORS.cyan, margin: "4px 0 0" }}>{reservaAuto.detalle}</p>
                )}
                {g.key === "viento" && (
                  <p style={{ fontSize: 10.5, color: vientoAuto.superaLimite ? COLORS.danger : vientoAuto.superaLimite === null ? COLORS.amber : COLORS.cyan, margin: "4px 0 0" }}>
                    {vientoAuto.detalle} Se consulta en Análisis de vuelo, en los bloques de despegue y aterrizaje.
                  </p>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 10, fontSize: 11.5, color: COLORS.muted, alignItems: "center", flexWrap: "wrap" }}>
                  <span>Gravedad: {SMS_GRAVEDAD[mit.gravedadResidual ?? opt.gravIdx]}</span><span>·</span>
                  <span>Probabilidad: {SMS_PROBABILIDAD[mit.probabilidadResidual ?? opt.probIdx]}</span>
                  {mit.reduccion > 0 && (
                    <>
                      <StatusPill status={mit.nivelInicial} />
                      <span style={{ color: COLORS.cyan }}>→</span>
                    </>
                  )}
                  <StatusPill status={nivel} />
                </div>

                {exigeMitigacion(mit.nivelInicial) && (
                  <div style={{ marginTop: 10, borderTop: `1px solid ${COLORS.hairline}`, paddingTop: 10 }}>
                    <p style={{ fontSize: 10.5, color: COLORS.amber, margin: "0 0 8px", lineHeight: 1.5 }}>
                      Este factor está en <strong>{mit.nivelInicial}</strong>. La IN 2255-I-00 pide una medida que
                      lo baje al menos a MEDIO. Escribila: sin texto no reduce nada.
                    </p>
                    <Field label="Prevención — reduce la PROBABILIDAD" hint="Qué se hace para que no ocurra.">
                      <textarea
                        style={{ ...inputStyle, width: "100%", minHeight: 42, fontFamily: "inherit" }}
                        placeholder="Ej.: altitud de seguridad sobre el terreno con zonas de aterrizaje de emergencia identificadas en ruta"
                        value={medidas.prevencion || ""}
                        onChange={(e) => editarMitigacion(g.key, "prevencion", e.target.value)}
                      />
                    </Field>
                    <Field label="Protección — reduce la GRAVEDAD" hint="Qué limita las consecuencias si ocurre.">
                      <textarea
                        style={{ ...inputStyle, width: "100%", minHeight: 42, fontFamily: "inherit" }}
                        placeholder="Ej.: chalecos y balsa a bordo, briefing de evacuación, seguimiento satelital"
                        value={medidas.proteccion || ""}
                        onChange={(e) => editarMitigacion(g.key, "proteccion", e.target.value)}
                      />
                    </Field>
                    {exigeMitigacion(nivel) && (
                      <p style={{ fontSize: 10.5, color: COLORS.danger, margin: "6px 0 0" }}>
                        Con lo declarado el factor sigue en {nivel}. La medida no alcanza.
                      </p>
                    )}
                  </div>
                )}
              </Panel>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------------- SGS / SMS ---------------- */}
      {tab === "sms" && (
        <div style={{ display: "grid", gap: 16 }}>
          {/* ═══ REGISTRO DEL SGS ═══
              Reemplaza al registro anterior, que guardaba con
              `usePersistedState` sin mirar el error: si la escritura fallaba,
              el reporte seguía en pantalla y se perdía al cerrar.

              La pantalla nueva confirma cada escritura, marca la integridad y
              monta el canal de seguimiento del manual del operador. El resto
              de la pestaña —la metodología de la IN 2255-I-00— se conserva
              debajo: son cosas distintas y las dos hacen falta. */}
          <PanelSgs quien={pilots.find((p) => p.id === assignedPilotId)?.nombre || ""} />

          <Banner tone="TOLERABLE">
            Sistema de gestión de la seguridad completo, siguiendo la Information Notice de Eurocopter N° 2255-I-00
            ("Apoyo para la creación de un SGS — metodología de gestión del riesgo operativo"): identificación de
            peligros, análisis de causas y consecuencias, matriz de riesgo, defensas en profundidad (control,
            recuperación, mitigación), decisión coste/beneficio de las medidas, diagrama pajarita/corbatín, y la
            biblioteca completa de 51 Eventos Indeseables del Anexo 1.
          </Banner>

          <Panel eyebrow="Panel de control" title="Indicadores de seguridad (KPIs del SGS)">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
              <Readout label="Eventos registrados" value={smsKpis.total} unit="" />
              <Readout label="Medidas implementadas" value={smsKpis.implementadosSi} unit="" tone="ACEPTABLE" />
              <Readout label="Implementación parcial" value={smsKpis.implementadosParcial} unit="" tone="MEDIO" />
              <Readout label="Sin implementar" value={smsKpis.implementadosNo} unit="" tone={smsKpis.implementadosNo > 0 ? "GRAVE" : "ACEPTABLE"} />
              <Readout label="Reportes sin procesar" value={smsKpis.reportesAbiertos} unit="" tone={smsKpis.reportesAbiertos > 0 ? "MEDIO" : "ACEPTABLE"} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Riesgo inicial vs. actual (cuántos eventos por nivel)</div>
                <table style={{ fontSize: 11.5, borderCollapse: "collapse", width: "100%" }}>
                  <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Nivel</th><th>Inicial</th><th>Actual</th></tr></thead>
                  <tbody>
                    {Object.keys(SMS_COLOR).map((nivel) => (
                      <tr key={nivel} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                        <td style={{ padding: "3px 0" }}><StatusPill status={nivel} /></td>
                        <td>{smsNivelCounts("inicial")[nivel]}</td>
                        <td>{smsNivelCounts("actual")[nivel]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Eventos por categoría de Evento Límite</div>
                <div style={{ display: "grid", gap: 4 }}>
                  {SMS_EU_CATEGORIES.map((c) => (
                    <div key={c.key} style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, background: COLORS.panelAlt, padding: "3px 8px", borderRadius: 4 }}>
                      <span>{c.key}</span><b>{smsEuCounts()[c.key]}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 0 }}>
              Si "riesgo actual" muestra menos eventos en Grave/Inaceptable(+) que "riesgo inicial", las defensas
              aplicadas están funcionando — esto es lo que un auditor de SGS revisa primero.
            </p>
          </Panel>

          {(() => {
            const rep = construirReporteSGS({
              eventos: smsEvents, reportes: smsReports, politica: smsPolicy,
              desde: periodoReporte.desde || null, hasta: periodoReporte.hasta || null,
              categoriasEu: SMS_EU_CATEGORIES, operador: periodoReporte.operador,
            });
            const celda = { border: "1px solid #999", padding: "4px 6px", verticalAlign: "top", fontSize: 10.5 };
            const lista = (arr) => (arr.length === 0
              ? <em style={{ color: "#999" }}>sin registrar</em>
              : <ul style={{ margin: 0, paddingLeft: 14 }}>{arr.map((x, i) => <li key={i}>{x}</li>)}</ul>);
            return (
              <Panel eyebrow="Reporte" title="Seguimiento del riesgo operativo — Anexo 2 + bow-tie" right={
                <button className="no-print" onClick={() => window.print()} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}>
                  🖨 Imprimir reporte
                </button>
              }>
                <style>{`@media print {
                  .app-tabs, .no-print { display: none !important; }
                  body { background: #fff !important; color: #000 !important; }
                  .reporte-a2 { background: #fff !important; color: #000 !important; }
                  .reporte-a2 table { page-break-inside: auto; }
                  .reporte-a2 .ficha { page-break-inside: avoid; }
                }`}</style>

                <div className="no-print" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end", marginBottom: 14 }}>
                  <Field label="Operador"><input style={inputStyle} value={periodoReporte.operador} onChange={(e) => setPeriodoReporte((p) => ({ ...p, operador: e.target.value }))} /></Field>
                  <Field label="Desde"><input type="date" style={inputStyle} value={periodoReporte.desde} onChange={(e) => setPeriodoReporte((p) => ({ ...p, desde: e.target.value }))} /></Field>
                  <Field label="Hasta"><input type="date" style={inputStyle} value={periodoReporte.hasta} onChange={(e) => setPeriodoReporte((p) => ({ ...p, hasta: e.target.value }))} /></Field>
                  {[["mes", "Mes en curso"], ["anio", "Año en curso"], ["todo", "Todo el histórico"]].map(([k, t]) => (
                    <button key={k} onClick={() => aplicarPeriodo(k)} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "7px 11px", cursor: "pointer", fontSize: 12.5 }}>{t}</button>
                  ))}
                </div>

                {rep.avisos.map((a, i) => (
                  <p key={i} className="no-print" style={{ fontSize: 11.5, color: COLORS.amber, margin: "3px 0" }}>⚠ {a}</p>
                ))}

                <div className="reporte-a2" style={{ background: "#fff", color: "#111", borderRadius: 6, padding: 18, marginTop: 12, fontFamily: "'Inter', sans-serif" }}>
                  <h2 style={{ margin: 0, fontSize: 17 }}>{rep.encabezado.titulo}</h2>
                  <p style={{ margin: "2px 0 10px", fontSize: 10.5, color: "#555" }}>{rep.encabezado.subtitulo}</p>
                  <table style={{ borderCollapse: "collapse", fontSize: 10.5, marginBottom: 14 }}>
                    <tbody>
                      <tr><td style={celda}><b>Operador</b></td><td style={celda}>{rep.encabezado.operador || "—"}</td>
                          <td style={celda}><b>Responsable del SGS</b></td><td style={celda}>{rep.encabezado.responsableSgs || "—"}</td></tr>
                      <tr><td style={celda}><b>Período</b></td><td style={celda}>{rep.encabezado.desde || "sin límite"} — {rep.encabezado.hasta || "sin límite"}</td>
                          <td style={celda}><b>Emitido</b></td><td style={celda}>{new Date(rep.encabezado.emitido).toLocaleString("es")}</td></tr>
                    </tbody>
                  </table>

                  {rep.encabezado.politica && (
                    <p style={{ fontSize: 10.5, fontStyle: "italic", borderLeft: "3px solid #999", paddingLeft: 8, margin: "0 0 14px" }}>{rep.encabezado.politica}</p>
                  )}

                  <table style={{ borderCollapse: "collapse", fontSize: 10.5, marginBottom: 16, width: "100%" }}>
                    <tbody>
                      <tr>
                        <td style={celda}><b>Eventos</b><br />{rep.resumen.eventos}</td>
                        <td style={celda}><b>Implementadas</b><br />{rep.resumen.implementadas}</td>
                        <td style={celda}><b>Parciales</b><br />{rep.resumen.parciales}</td>
                        <td style={celda}><b>Sin implementar</b><br />{rep.resumen.sinImplementar}</td>
                        <td style={celda}><b>Sin responsable</b><br />{rep.resumen.sinResponsable}</td>
                        <td style={celda}><b>Integridad bow-tie</b><br />{rep.resumen.integridadMedia} %</td>
                        <td style={celda}><b>ASR sin procesar</b><br />{rep.resumen.asrSinProcesar} de {rep.resumen.reportesAsr}</td>
                      </tr>
                    </tbody>
                  </table>

                  {rep.fichas.length === 0 && <p style={{ fontSize: 11 }}>Sin eventos indeseables registrados en el período.</p>}

                  {rep.fichas.map((f, idx) => (
                    <div key={idx} className="ficha" style={{ marginBottom: 18 }}>
                      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 10.5 }}>
                        <tbody>
                          <tr>
                            <td style={{ ...celda, width: 150, background: "#eee" }}><b>EVENTO INDESEABLE</b></td>
                            <td style={celda} colSpan={2}><b>{f.eventoIndeseable}</b>{f.ref ? ` (${f.ref})` : ""}</td>
                            <td style={{ ...celda, width: 110, background: "#eee" }}><b>Revisado el</b></td>
                            <td style={celda}>{f.revisadoEl || "—"}</td>
                          </tr>
                          <tr>
                            <td style={{ ...celda, background: "#eee" }}><b>PELIGRO</b><br /><span style={{ fontWeight: 400, color: "#666" }}>(bow-tie)</span></td>
                            <td style={celda} colSpan={2}>{f.peligro || <em style={{ color: "#b06" }}>sin nombrar — el bow-tie no tiene nudo izquierdo</em>}</td>
                            <td style={{ ...celda, background: "#eee" }}><b>Nivel de riesgo</b></td>
                            <td style={celda}>
                              Inicial: <b>{f.nivelRiesgo.inicial || "—"}</b><br />
                              Actual: <b>{f.nivelRiesgo.actual || "—"}</b>
                              {f.nivelRiesgo.reduccion !== null && (
                                <span> ({f.nivelRiesgo.reduccion > 0 ? `bajó ${f.nivelRiesgo.reduccion} nivel(es)` : f.nivelRiesgo.reduccion === 0 ? "sin cambio" : "SUBIÓ"})</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ ...celda, background: "#eee" }}><b>EVENTOS LÍMITE RELACIONADOS</b></td>
                            <td style={celda} colSpan={4}>{lista(f.eventosLimite)}</td>
                          </tr>
                          <tr style={{ background: "#eee" }}>
                            <td style={celda}><b>Precursores</b></td>
                            <td style={celda}><b>Defensas — Control</b></td>
                            <td style={celda}><b>Defensas — Recuperación</b></td>
                            <td style={celda}><b>Medidas de protección (mitigación)</b></td>
                            <td style={celda}><b>Responsable / Implementado</b></td>
                          </tr>
                          <tr>
                            <td style={celda}>{lista(f.precursores)}</td>
                            <td style={celda}>{lista(f.defensas.control)}</td>
                            <td style={celda}>{lista(f.defensas.recuperacion)}</td>
                            <td style={celda}>{lista(f.defensas.mitigacion)}</td>
                            <td style={celda}>
                              {f.responsable || <em style={{ color: "#b06" }}>sin asignar</em>}<br />
                              <b>{f.implementado}</b>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ ...celda, background: "#eee" }}><b>Factores de escalada</b><br /><span style={{ fontWeight: 400, color: "#666" }}>¿cómo pueden fallar las barreras?</span></td>
                            <td style={celda} colSpan={2}>{lista(f.factoresEscalada)}</td>
                            <td style={{ ...celda, background: "#eee" }}><b>¿Qué asegura que no fallen?</b></td>
                            <td style={celda}>{lista(f.controlDeEscalada)}</td>
                          </tr>
                          {f.integridad.porcentaje < 100 && (
                            <tr>
                              <td style={{ ...celda, background: "#fff4e0" }} colSpan={5}>
                                <b>Ficha incompleta ({f.integridad.porcentaje} %).</b> Sin responder: {f.integridad.faltantes.join(" ")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      <p style={{ fontSize: 9, color: "#777", margin: "3px 0 0" }}>
                        La asociación entre cada precursor y la defensa que lo combate no está registrada en la
                        aplicación: las defensas se listan por familia, sin atribuirlas a un precursor concreto.
                      </p>
                    </div>
                  ))}

                  <p style={{ fontSize: 9.5, color: "#555", borderTop: "1px solid #999", paddingTop: 8, marginTop: 16, lineHeight: 1.5 }}>
                    {rep.procedencia}
                  </p>
                </div>
              </Panel>
            );
          })()}

          <Panel eyebrow="Pilar 1 — Política" title="Política de seguridad y responsable del SGS">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
              <Field label="Responsable de seguridad (Safety Manager)"><input style={inputStyle} value={smsPolicy.responsable} onChange={(e) => setSmsPolicy((p) => ({ ...p, responsable: e.target.value }))} /></Field>
              <Field label="Declaración de política de seguridad">
                <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: "inherit" }} placeholder="Ej.: La dirección de [empresa] se compromete a identificar peligros y gestionar los riesgos de seguridad operacional como prioridad, fomentando una cultura justa de reporte." value={smsPolicy.declaracion} onChange={(e) => setSmsPolicy((p) => ({ ...p, declaracion: e.target.value }))} />
              </Field>
            </div>
          </Panel>

          {/* ═══ EL PANEL DE REPORTES VIEJO SE QUITÓ ═══

              Seguía en pantalla con su botón «+ nuevo reporte», así que cada vez
              que alguien lo usaba se creaba un registro en el depósito ANTERIOR —
              y el sistema nunca terminaba de consolidarse por más que se migrara.

              Migrar sin quitar la vía vieja no consolida nada: repone el problema
              en cada uso.

              El registro único vive arriba, en <PanelSgs>. Los datos del depósito
              viejo NO se borraron: se migran desde ahí. */}
          <Panel eyebrow="Pilar 2 — Fuentes internas" title="Reportes voluntarios de seguridad (ASR)">
            <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0, lineHeight: 1.6 }}>
              Los reportes se cargan y se gestionan en el <strong>registro único</strong>, al
              principio de esta pestaña: ahí se marcan las dimensiones del Sistema Integrado,
              se analizan las <strong>barreras</strong> y se sigue el canal hasta el cierre.
            </p>
            {smsReports.length > 0 && (
              <p style={{ fontSize: 12, color: COLORS.amber, marginBottom: 0 }}>
                Quedan {smsReports.length} reportes en el sistema anterior. El aviso de arriba
                permite unificarlos; sus datos no se borran al migrar.
              </p>
            )}
          </Panel>

        </div>
      )}

      {/* ═══ LA REFERENCIA SALE DEL FLUJO DE REPORTE ═══

          El SGS tenía TRECE paneles, y cinco de ellos eran material de
          consulta —matrices, criterios, escenarios, glosario— metidos entre
          los formularios. **Nada de lo que sale de acá es ingreso de datos**:
          son tablas que se miran mientras se clasifica un evento.

          Quedan en Consulta, que es un proceso distinto: no se llena nada, se
          mira. El SGS queda con lo que sí se hace: reportar, seguir, cerrar y
          medir. */}
      {tab === "consulta" && (
        <div style={{ display: "grid", gap: 16 }}>
          <Panel eyebrow="Metodología" title="Las 6 preguntas del proceso (secciones 5-9 del documento)">
            <ol style={{ fontSize: 12.5, color: COLORS.text, lineHeight: 1.9, margin: 0, paddingLeft: 20 }}>
              <li>¿Qué podría pasar en mi actividad? — <i>identificación del peligro (Evento Indeseable)</i></li>
              <li>¿Cómo podría ocurrir esto? — <i>identificación de las causas (precursores)</i></li>
              <li>¿Cuáles podrían ser sus consecuencias? — <i>Eventos Límite (EU1-EU7)</i></li>
              <li>¿Qué hay que hacer para que esto no suceda, o limitar su probabilidad? — <i>reducción del riesgo (prevención — medidas de Control)</i></li>
              <li>¿Qué hay que hacer para eliminar o mitigar las consecuencias? — <i>protección (medidas de Recuperación y Mitigación)</i></li>
              <li>¿Cómo aplicar estas medidas para reducir los riesgos? — <i>responsable, estado de implementación, y decisión coste/beneficio</i></li>
            </ol>
          </Panel>

          <Panel eyebrow="Referencia" title="Matriz de riesgo (Gravedad × Probabilidad)">
            <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", fontSize: 11.5 }}>
                <thead>
                  <tr>
                    <th></th>
                    {SMS_PROBABILIDAD.map((p) => <th key={p} style={{ padding: "4px 8px", color: COLORS.muted, fontWeight: 500 }}>{p}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[...SMS_GRAVEDAD].reverse().map((g, revIdx) => {
                    const gravIdx = SMS_GRAVEDAD.length - 1 - revIdx;
                    return (
                      <tr key={g}>
                        <td style={{ padding: "4px 8px", color: COLORS.muted, textAlign: "right", whiteSpace: "nowrap" }}>{g}</td>
                        {SMS_PROBABILIDAD.map((_, probIdx) => {
                          const level = smsRiskLevel(gravIdx, probIdx);
                          return <td key={probIdx} style={{ width: 90, height: 26, background: SMS_COLOR[level], textAlign: "center", fontSize: 10, color: "#0A1420", fontWeight: 700 }}>{level}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 12, display: "grid", gap: 4 }}>
              {["ACEPTABLE", "MEDIO", "GRAVE", "INACEPTABLE", "INACEPTABLE+"].map((nivel) => (
                <p key={nivel} style={{ fontSize: 11, color: COLORS.muted, margin: 0 }}>
                  <span style={{ color: SMS_COLOR[nivel], fontWeight: 700 }}>{nivel}</span> — {SIGNIFICADO_NIVEL[nivel]}
                </p>
              ))}
            </div>
          </Panel>

          <Panel eyebrow="Referencia" title="Cómo clasificar — criterios de la IN 2255-I-00">
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0 }}>
              Sin estos criterios a la vista, "Mayor" y "Crítico" son etiquetas vacías y dos personas clasifican
              el mismo evento distinto. La gravedad se mide por cuatro consecuencias en paralelo y manda la peor
              de las cuatro.
            </p>
            <div className="hc-tabla-ancha" style={{ overflowX: "auto", marginBottom: 16 }}>
              <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}>
                <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}>
                  <th style={{ padding: "4px 8px" }}>Gravedad</th><th>Personas</th><th>Entorno</th><th>Material</th><th>Imagen pública</th>
                </tr></thead>
                <tbody>{CRITERIOS_GRAVEDAD.map((c) => (
                  <tr key={c.nivel} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                    <td style={{ padding: "4px 8px", fontWeight: 600 }}>{c.nivel}</td>
                    <td>{c.personas}</td><td>{c.entorno}</td><td>{c.material}</td><td>{c.imagen}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <p style={{ fontSize: 11.5, color: COLORS.amber, margin: "0 0 8px" }}>
              La probabilidad se mide contra la historia de ESTA empresa, no contra una tasa teórica.
              "Improbable" no significa "no me parece que pase": significa que nunca pasó en la aviación.
            </p>
            <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}>
                <tbody>{CRITERIOS_PROBABILIDAD.map((c) => (
                  <tr key={c.nivel} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                    <td style={{ padding: "4px 8px", fontWeight: 600, whiteSpace: "nowrap" }}>{c.nivel}</td>
                    <td>{c.criterio}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Panel>

          <Panel eyebrow="Referencia" title="Matriz de decisión Coste / Beneficio (Figura 3)">
            <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", fontSize: 11.5 }}>
                <thead><tr><th></th>{SMS_BENEFICIO.map((b) => <th key={b} style={{ padding: "4px 10px", color: COLORS.muted }}>Beneficio {b}</th>)}</tr></thead>
                <tbody>
                  {SMS_COSTE.map((c, ci) => (
                    <tr key={c}>
                      <td style={{ padding: "4px 10px", color: COLORS.muted, whiteSpace: "nowrap" }}>Coste {c}</td>
                      {SMS_BENEFICIO.map((_, bi) => {
                        const score = SMS_COSTE_BENEFICIO_MATRIX[ci][bi];
                        const color = score <= 2 ? SMS_COLOR.ACEPTABLE : score === 3 ? SMS_COLOR.GRAVE : SMS_COLOR.INACEPTABLE;
                        return <td key={bi} style={{ width: 60, height: 26, background: color, textAlign: "center", fontWeight: 700, color: "#0A1420" }}>{score}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 0 }}>
              1-2: la medida se puede adoptar tal cual. 3: conviene reducir su coste si se puede. 4-5: hay que
              volver a analizar el riesgo para buscar otra solución — el coste de la protección nunca debe superar
              el coste de las consecuencias del riesgo que evita.
            </p>
          </Panel>

          <Panel eyebrow="Referencia" title="Escenarios de accidente pre-armados (Anexo 3)">
            <div style={{ display: "grid", gap: 8 }}>
              {SMS_ANEXO3_SCENARIOS.map((s) => (
                <div key={s.id} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                  <b>Escenario {s.id} ({s.eu}):</b> {s.nombre}
                  <div style={{ color: COLORS.muted, marginTop: 4, fontSize: 11.5, lineHeight: 1.5 }}>{s.cadena}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel eyebrow="Referencia" title="Glosario">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8, fontSize: 12 }}>
              {[
                ["ASR", "Air Safety Report — informe de seguridad aérea"],
                ["CFIT", "Controlled Flight Into Terrain — impacto contra el terreno sin pérdida de control"],
                ["EI", "Evento Indeseable — evento precursor que podría originar una secuencia accidental"],
                ["EU", "Evento Límite — el accidente en sí"],
                ["IHST/EHEST", "International/European Helicopter Safety Team"],
                ["Peligro", "Condición u objeto con potencial de causar lesiones o daños"],
                ["Gestión de la seguridad", "Gestión de riesgos vinculados específicamente a la seguridad, no financieros/legales"],
                ["Criticidad (C = P × S)", "Combinación de Probabilidad y Severidad de un riesgo"],
              ].map(([term, def]) => (
                <div key={term} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 6, padding: "6px 10px" }}>
                  <b>{term}</b>: <span style={{ color: COLORS.muted }}>{def}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel eyebrow="Biblioteca completa" title="Eventos Indeseables — Anexo 1 (51 de 51)">
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0, marginBottom: 10 }}>
              El Anexo 1 completo del documento, con sus 51 eventos indeseables y su categoría de Evento Límite
              real. Elige uno para agregarlo al registro con su categoría ya cargada, o crea uno propio con
              "+ evento nuevo" abajo.
            </p>
            <div style={{ display: "grid", gap: 6 }}>
              {SMS_EI_LIBRARY.map((ei) => (
                <div key={ei.ref} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 6, padding: "6px 10px", fontSize: 12 }}>
                  <span><b>{ei.ref}</b> — {ei.desc} <span style={{ color: COLORS.muted }}>({ei.eu.join(", ")})</span></span>
                  <button onClick={() => addSmsEvent(ei)} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" }}>+ agregar</button>
                </div>
              ))}
            </div>
          </Panel>

        </div>
      )}

      {tab === "sms" && (
        <div style={{ display: "grid", gap: 16 }}>
          <Panel eyebrow="Registro" title="Eventos indeseables en seguimiento" right={<button className="no-print" onClick={() => addSmsEvent(null)} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ evento nuevo</button>}>
            {smsEvents.length === 0 ? (
              <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0 }}>Sin eventos registrados — agrega uno de la biblioteca de arriba o crea uno nuevo.</p>
            ) : (
              <div style={{ display: "grid", gap: 14 }}>
                {smsEvents.map((e) => {
                  const nivelInicial = smsRiskLevel(e.gravedadInicial, e.probabilidadInicial);
                  const nivelActual = smsRiskLevel(e.gravedadActual, e.probabilidadActual);
                  return (
                    <div key={e.id} style={{ border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                        <input style={{ ...inputStyle, flex: 1, minWidth: 220, fontWeight: 600 }} value={e.descripcion} onChange={(ev) => updateSmsEvent(e.id, { descripcion: ev.target.value })} />
                        <button onClick={() => removeSmsEvent(e.id)} className="no-print" style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.danger, borderRadius: 6, padding: "5px 10px", cursor: "pointer" }}>✕</button>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                        {SMS_EU_CATEGORIES.map((c) => {
                          const active = e.euCategorias.includes(c.key);
                          return (
                            <button
                              key={c.key} title={c.label}
                              onClick={() => updateSmsEvent(e.id, { euCategorias: active ? e.euCategorias.filter((k) => k !== c.key) : [...e.euCategorias, c.key] })}
                              style={{ background: active ? COLORS.cyan : "transparent", color: active ? "#0A1420" : COLORS.muted, border: `1px solid ${active ? COLORS.cyan : COLORS.hairline}`, borderRadius: 12, padding: "3px 9px", cursor: "pointer", fontSize: 10.5 }}
                            >{c.key}</button>
                          );
                        })}
                      </div>
                      <Field label="Peligro (dónde empieza el bow-tie)" hint="El evento temido es el momento en que se pierde el control de este peligro.">
                        <input style={{ ...inputStyle, width: "100%", marginBottom: 10 }} placeholder="Ej.: combustible a bordo, carga externa suspendida, terreno en ruta" value={e.peligro || ""} onChange={(ev) => updateSmsEvent(e.id, { peligro: ev.target.value })} />
                      </Field>
                      <Field label="Precursores (causas — una por línea, para el diagrama)"><textarea style={{ ...inputStyle, minHeight: 46, fontFamily: "inherit", marginBottom: 10 }} placeholder={"Ej.:\nFallo de componente interno\nErrores durante mantenimiento"} value={e.precursores} onChange={(ev) => updateSmsEvent(e.id, { precursores: ev.target.value })} /></Field>
                      <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Defensas (3 familias, Figura 1 del documento)</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                        <Field label="Control (evitar el EI)"><textarea style={{ ...inputStyle, minHeight: 46, fontFamily: "inherit" }} value={e.medidasControl} onChange={(ev) => updateSmsEvent(e.id, { medidasControl: ev.target.value })} /></Field>
                        <Field label="Recuperación (volver al control)"><textarea style={{ ...inputStyle, minHeight: 46, fontFamily: "inherit" }} value={e.medidasRecuperacion} onChange={(ev) => updateSmsEvent(e.id, { medidasRecuperacion: ev.target.value })} /></Field>
                        <Field label="Mitigación (limitar consecuencias)"><textarea style={{ ...inputStyle, minHeight: 46, fontFamily: "inherit" }} value={e.medidasMitigacion} onChange={(ev) => updateSmsEvent(e.id, { medidasMitigacion: ev.target.value })} /></Field>
                      </div>
                      <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Factores de escalada (bow-tie — SKYbrary/EUROCONTROL)</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                        <Field label="¿Cómo podrían fallar estos controles?"><textarea style={{ ...inputStyle, minHeight: 46, fontFamily: "inherit" }} placeholder="Ej.: el piloto omite la verificación por presión de tiempo" value={e.factoresEscalada || ""} onChange={(ev) => updateSmsEvent(e.id, { factoresEscalada: ev.target.value })} /></Field>
                        <Field label="¿Qué asegura que NO fallen?"><textarea style={{ ...inputStyle, minHeight: 46, fontFamily: "inherit" }} placeholder="Ej.: doble verificación firmada antes de despegar" value={e.controlEscalada || ""} onChange={(ev) => updateSmsEvent(e.id, { controlEscalada: ev.target.value })} /></Field>
                      </div>
                      {(() => {
                        const bt = evaluarBowTie(e);
                        return (
                          <div style={{ background: COLORS.panelAlt, border: `1px solid ${bt.porcentaje === 100 ? COLORS.ok : COLORS.hairline}`, borderRadius: 6, padding: "6px 10px", marginBottom: 10, fontSize: 11 }}>
                            <b style={{ color: bt.porcentaje === 100 ? COLORS.ok : COLORS.amber }}>Bow-tie {bt.porcentaje} %</b>
                            {bt.faltantes.length > 0 && <span style={{ color: COLORS.muted }}> — falta responder: {bt.faltantes.map((f) => f.pregunta).join(" ")}</span>}
                          </div>
                        );
                      })()}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 10 }}>
                        <Field label="Responsable"><input style={inputStyle} value={e.responsable} onChange={(ev) => updateSmsEvent(e.id, { responsable: ev.target.value })} /></Field>
                        <Field label="Implementado">
                          <select style={inputStyle} value={e.implementado} onChange={(ev) => updateSmsEvent(e.id, { implementado: ev.target.value })}>
                            <option value="No">No</option><option value="Parcial">Parcial</option><option value="Sí">Sí</option>
                          </select>
                        </Field>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
                        <div>
                          <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Riesgo inicial</div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <select style={{ ...inputStyle, fontSize: 11.5 }} value={e.gravedadInicial} onChange={(ev) => updateSmsEvent(e.id, { gravedadInicial: Number(ev.target.value) })}>
                              {SMS_GRAVEDAD.map((g, i) => <option key={g} value={i}>{g}</option>)}
                            </select>
                            <select style={{ ...inputStyle, fontSize: 11.5 }} value={e.probabilidadInicial} onChange={(ev) => updateSmsEvent(e.id, { probabilidadInicial: Number(ev.target.value) })}>
                              {SMS_PROBABILIDAD.map((p, i) => <option key={p} value={i}>{p}</option>)}
                            </select>
                          </div>
                          <div style={{ marginTop: 6, display: "inline-block", background: SMS_COLOR[nivelInicial], color: "#0A1420", fontWeight: 700, fontSize: 11, padding: "3px 10px", borderRadius: 4 }}>{nivelInicial}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Riesgo actual (con defensas aplicadas)</div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <select style={{ ...inputStyle, fontSize: 11.5 }} value={e.gravedadActual} onChange={(ev) => updateSmsEvent(e.id, { gravedadActual: Number(ev.target.value) })}>
                              {SMS_GRAVEDAD.map((g, i) => <option key={g} value={i}>{g}</option>)}
                            </select>
                            <select style={{ ...inputStyle, fontSize: 11.5 }} value={e.probabilidadActual} onChange={(ev) => updateSmsEvent(e.id, { probabilidadActual: Number(ev.target.value) })}>
                              {SMS_PROBABILIDAD.map((p, i) => <option key={p} value={i}>{p}</option>)}
                            </select>
                          </div>
                          <div style={{ marginTop: 6, display: "inline-block", background: SMS_COLOR[nivelActual], color: "#0A1420", fontWeight: 700, fontSize: 11, padding: "3px 10px", borderRadius: 4 }}>{nivelActual}</div>
                        </div>
                      </div>
                      <div style={{ borderTop: `1px solid ${COLORS.hairline}`, paddingTop: 10 }}>
                        <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Matriz coste/beneficio de la medida (Figura 3)</div>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <select style={{ ...inputStyle, fontSize: 11.5 }} value={e.costeIdx} onChange={(ev) => updateSmsEvent(e.id, { costeIdx: Number(ev.target.value) })}>
                            {SMS_COSTE.map((c, i) => <option key={c} value={i}>Coste: {c}</option>)}
                          </select>
                          <select style={{ ...inputStyle, fontSize: 11.5 }} value={e.beneficioIdx} onChange={(ev) => updateSmsEvent(e.id, { beneficioIdx: Number(ev.target.value) })}>
                            {SMS_BENEFICIO.map((b, i) => <option key={b} value={i}>Beneficio: {b}</option>)}
                          </select>
                          <span style={{ fontSize: 12, color: COLORS.text }}>
                            Resultado: <b>{SMS_COSTE_BENEFICIO_MATRIX[e.costeIdx][e.beneficioIdx]}</b> — {smsCosteBeneficioResultado(SMS_COSTE_BENEFICIO_MATRIX[e.costeIdx][e.beneficioIdx])}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            )}
          </Panel>

          <Panel eyebrow="Herramienta visual" title="Diagrama pajarita / corbatín (Figura 2 del documento)">
            {smsEvents.length === 0 ? (
              <p style={{ fontSize: 12.5, color: COLORS.muted, margin: 0 }}>Agrega al menos un evento en el registro de arriba para poder verlo aquí como diagrama.</p>
            ) : (
              <>
                <Field label="Evento a diagramar">
                  <select style={inputStyle} value={smsBowTieId || smsEvents[0].id} onChange={(e) => setSmsBowTieId(e.target.value)}>
                    {smsEvents.map((e) => <option key={e.id} value={e.id}>{e.ref ? `${e.ref} — ` : ""}{e.descripcion}</option>)}
                  </select>
                </Field>
                <div style={{ marginTop: 12 }}>
                  <BowTieDiagram event={smsEvents.find((e) => e.id === (smsBowTieId || smsEvents[0].id))} euCategories={SMS_EU_CATEGORIES} />
                </div>
                <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 0 }}>
                  El lado izquierdo toma cada línea que escribiste en "Precursores" (una causa por línea, o separadas
                  por ";") y las conecta al Evento Temido central; el lado derecho muestra los Eventos Límite que
                  marcaste como consecuencia. Escribe/edita el evento en el registro de arriba y el diagrama se
                  actualiza solo.
                </p>
              </>
            )}
          </Panel>
        </div>
      )}

      {/* ---------------- MANTENIMIENTO Y OPERACIONES ---------------- */}
      {/* ---------------- IMPORTAR AERONAVE ---------------- */}
      {tab === "importar" && (
        <ImportarAeronave
          COLORS={COLORS}
          profiles={profiles}
          setProfiles={setProfiles}
          fleet={fleet}
          setFleet={setFleet}
          onImportado={(aeronave) => {
            // La matrícula recién importada pasa a ser la activa: importarla y
            // que la app siga calculando con la anterior confunde.
            if (aeronave?.matricula) setMatricula(aeronave.matricula);
          }}
        />
      )}

      {tab === "mantenimiento" && (
        <div style={{ display: "grid", gap: 16 }} className="no-print-wrap">
          <style>{`@media print {
            .app-tabs, .app-header-actions, .no-print { display: none !important; }
            body, .print-area { background: #fff !important; color: #000 !important; }
          }`}</style>
          <Banner tone="TOLERABLE">
            El registro de inspecciones y el plan de reemplazo de componentes se cargaron desde el status real de
            del status report de la aeronave — son una muestra representativa, no la totalidad;
            agrega los ítems que falten con "+ ítem". El estado de cada uno (AL DÍA/PRÓXIMO/VENCIDO) se recalcula
            solo cuando registras un vuelo en la pestaña "Operaciones" — no hay que actualizarlo a mano.
          </Banner>

          <div className="no-print" style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={() => window.print()} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}>🖨 Generar reporte imprimible</button>
          </div>

          {fleet.map((ac) => {
            const report = buildStatusReport(ac.id);
            const vencidos = report.filter((r) => r.estado === "INTOLERABLE").length;
            const proximos = report.filter((r) => r.estado === "TOLERABLE").length;
            return (
              <Panel key={ac.id} eyebrow="Informe resumen de status" title={`${ac.matricula} — ${ac.tipo} (S/N ${ac.serie || "s/d"})`}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
                  <Readout label="Fecha del informe" value={new Date().toLocaleDateString()} unit="" />
                  {/* `ac.tsn.toFixed()` daba por hecho que toda aeronave tiene
                      contadores cargados. Una matrícula recién incorporada
                      todavía no los tiene, y eso tiraba abajo la aplicación
                      entera al abrir Mantenimiento. */}
                  <Readout label="TSN aeronave" value={hs(ac.tsn)} unit="h" tone={Number.isFinite(ac.tsn) ? "ACEPTABLE" : "TOLERABLE"} />
                  <Readout label="TSN motor" value={hs(ac.motor?.tsn)} unit="h" tone={Number.isFinite(ac.motor?.tsn) ? "ACEPTABLE" : "TOLERABLE"} />
                  <Readout label="Ítems vencidos" value={vencidos} unit="" tone={vencidos > 0 ? "INTOLERABLE" : "ACEPTABLE"} />
                  <Readout label="Ítems próximos" value={proximos} unit="" tone={proximos > 0 ? "TOLERABLE" : "ACEPTABLE"} />
                </div>
                <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                    <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Categoría</th><th>Ítem</th><th>Dato</th><th>Estado</th></tr></thead>
                    <tbody>{report.map((r, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                        <td style={{ padding: "4px 0" }}>{r.grupo}</td>
                        <td style={{ minWidth: 220 }}>{r.item}</td>
                        <td>{r.dato}</td>
                        <td><StatusPill status={r.estado} /></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </Panel>
            );
          })}

          <Panel eyebrow="Flota" title="Aeronave y motor (TSN/CSN en vivo)" right={<button className="no-print" onClick={() => setFleet((f) => [...f, { id: `f_${Date.now()}`, matricula: "NUEVA", tipo: activeProfile.tipo, serie: "", profileId: activeProfileId, tsn: 0, csn: 0, ldg: 0, cargas: 0, motor: { tipo: "", serie: "", tsn: 0, cyNG: 0, cyNF: 0 }, pesoBasicoVacioKg: null, brazoVacioM: null, fechaPesaje: "" }])} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ aeronave</button>}>
            <table style={{ width: "100%", fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
              <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Matrícula</th><th>Tipo</th><th>S/N aeronave</th><th>TSN (h)</th><th>CSN (ciclos)</th><th>LDG</th><th>Cargas</th><th>Motor</th><th>S/N motor</th><th>TSN motor (h)</th><th>Cy NG</th><th>Cy NF</th></tr></thead>
              <tbody>{fleet.map((f) => (
                <tr key={f.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                  <td style={{ padding: "5px 0" }}><input style={{ ...inputStyle, padding: "3px 6px", width: 80 }} value={f.matricula} onChange={(e) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, matricula: e.target.value.toUpperCase() } : x)))} /></td>
                  <td><input style={{ ...inputStyle, padding: "3px 6px", width: 90 }} value={f.tipo} onChange={(e) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, tipo: e.target.value } : x)))} /></td>
                  <td><input style={{ ...inputStyle, padding: "3px 6px", width: 70 }} value={f.serie} onChange={(e) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, serie: e.target.value } : x)))} /></td>
                  <td><CampoNumero step="0.1" style={{ ...inputStyle, padding: "3px 6px", width: 80 }} valor={f.tsn} alCambiar={(n) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, tsn: n } : x)))} /></td>
                  <td><CampoNumero style={{ ...inputStyle, padding: "3px 6px", width: 80 }} valor={f.csn} alCambiar={(n) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, csn: n } : x)))} /></td>
                  <td><CampoNumero style={{ ...inputStyle, padding: "3px 6px", width: 70 }} valor={f.ldg || 0} alCambiar={(n) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, ldg: n } : x)))} /></td>
                  <td><CampoNumero style={{ ...inputStyle, padding: "3px 6px", width: 70 }} valor={f.cargas || 0} alCambiar={(n) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, cargas: n } : x)))} /></td>
                  <td><input style={{ ...inputStyle, padding: "3px 6px", width: 90 }} value={f.motor?.tipo ?? ""} onChange={(e) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, motor: { ...x.motor, tipo: e.target.value } } : x)))} /></td>
                  <td><input style={{ ...inputStyle, padding: "3px 6px", width: 70 }} value={f.motor?.serie ?? ""} onChange={(e) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, motor: { ...x.motor, serie: e.target.value } } : x)))} /></td>
                  <td><CampoNumero step="0.1" style={{ ...inputStyle, padding: "3px 6px", width: 80 }} valor={f.motor?.tsn ?? null} alCambiar={(n) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, motor: { ...x.motor, tsn: n } } : x)))} /></td>
                  <td><CampoNumero step="0.01" style={{ ...inputStyle, padding: "3px 6px", width: 80 }} valor={f.motor?.cyNG ?? null} alCambiar={(n) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, motor: { ...x.motor, cyNG: n } } : x)))} /></td>
                  <td><CampoNumero step="0.01" style={{ ...inputStyle, padding: "3px 6px", width: 80 }} valor={f.motor?.cyNF ?? null} alCambiar={(n) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, motor: { ...x.motor, cyNF: n } } : x)))} /></td>
                </tr>
              ))}</tbody>
            </table>
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 8 }}>Estos valores suben solos cuando registras un vuelo más abajo, en "Registro de Bitácora — Operaciones".</p>
          </Panel>

          {/* ── COTEJO CONTRA LA BITÁCORA FÍSICA, POR HELICÓPTERO ────────── */}
          <Panel eyebrow="Cotejo" title="Bitácora virtual contra bitácora de papel, por helicóptero">
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0, lineHeight: 1.55 }}>
              La bitácora de papel <strong>no se edita ni se transcribe acá</strong>: es un documento manuscrito
              y firmado, y modificarlo destruiría el único registro que vale como prueba. Lo que se hace es
              cotejarla: el técnico compara campo por campo lo cargado virtualmente contra lo escrito a mano.
              {" "}Un asiento marcado como discrepante <strong>deja de alimentar el status de mantenimiento</strong>:
              un dato que se sabe distinto del papel no puede seguir sumando horas de inspección.
            </p>

            {fleet.map((f) => {
              const asientos = flightLog.filter((v) => v.aircraftId === f.id);
              const acum = acumuladoDeAeronave(flightLog, f.id);
              const base = estadoInicialDe(f);
              // Sin línea base registrada no hay contra qué comparar. Usar el
              // valor ACTUAL como base daba un desvío igual al aporte entero de
              // la bitácora, en rojo, y no significaba nada.
              const arrastre = base ? verificarArrastre(base, f, acum) : null;
              return (
                <div key={f.id} style={{ marginBottom: 20, border: `1px solid ${arrastre && !arrastre.coincide ? COLORS.danger : COLORS.hairline}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", marginBottom: 8 }}>
                    <strong style={{ fontSize: 14 }}>{f.matricula}</strong>
                    <span style={{ fontSize: 11.5, color: COLORS.muted }}>{f.tipo} · S/N {f.serie}</span>
                    <StatusPill status={!arrastre ? "TOLERABLE" : arrastre.coincide ? "ACEPTABLE" : "INTOLERABLE"} />
                    <span style={{ fontSize: 11.5, color: !arrastre ? COLORS.amber : arrastre.coincide ? COLORS.muted : COLORS.danger }}>
                      {arrastre ? arrastre.mensaje : "Sin estado inicial registrado: no hay línea base contra la que verificar el arrastre. Carga el status report de esta matrícula."}
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                    <Readout label="Asientos" value={asientos.length} unit="" />
                    <Readout label="Sin cotejar" value={acum.sinCotejar} unit="" tone={acum.sinCotejar > 0 ? "TOLERABLE" : "ACEPTABLE"} />
                    <Readout label="Excluidos por discrepancia" value={acum.excluidosPorDiscrepancia} unit="" tone={acum.excluidosPorDiscrepancia > 0 ? "INTOLERABLE" : "ACEPTABLE"} />
                    <Readout label="Horas aportadas" value={acum.horasAeronave} unit="h" />
                    <Readout label="Aterrizajes aportados" value={acum.ldg} unit="" />
                  </div>

                  {arrastre && <div className="hc-tabla-ancha" style={{ overflowX: "auto", marginBottom: 10 }}>
                    <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                      <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}>
                        <th>Campo</th><th>Al cargar el status</th><th>Aporte de bitácora</th><th>Esperado</th><th>En la app</th><th>Desvío</th>
                      </tr></thead>
                      <tbody>{arrastre.filas.map((fila) => (
                        <tr key={fila.campo} style={{ borderTop: `1px solid ${COLORS.hairline}`, color: fila.coincide ? COLORS.text : COLORS.danger }}>
                          <td style={{ padding: "3px 0" }}>{fila.campo}</td>
                          <td>{fila.inicial}</td><td>+{fila.aporte}</td><td>{fila.esperado}</td><td>{fila.real}</td>
                          <td>{fila.coincide ? "—" : (fila.desvio > 0 ? `+${fila.desvio}` : fila.desvio)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>}

                  {/* Bitácoras manuscritas de esta matrícula. Solo se agregan y
                      se consultan: no se editan. El PDF escaneado es el
                      documento firmado, y modificarlo destruiría la prueba. */}
                  <div style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 10, marginBottom: 10 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>
                        Bitácoras manuscritas escaneadas
                      </span>
                      <label className="no-print" style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "5px 9px", cursor: "pointer", fontSize: 11.5 }}>
                        + adjuntar PDF
                        <input type="file" accept="application/pdf,image/*" multiple style={{ display: "none" }}
                          onChange={(e) => adjuntarBitacoras(f.id, e.target.files)} />
                      </label>
                    </div>
                    {(bitacorasEscaneadas[f.id] || []).length === 0 ? (
                      <p style={{ fontSize: 11, color: COLORS.muted, margin: "6px 0 0" }}>Sin bitácoras adjuntas para esta matrícula.</p>
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        {Object.entries(
                          (bitacorasEscaneadas[f.id] || []).reduce((acc, b) => {
                            const per = (b.periodo || "sin fecha");
                            (acc[per] = acc[per] || []).push(b);
                            return acc;
                          }, {}),
                        ).sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([periodo, docs]) => (
                          <div key={periodo} style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10.5, color: COLORS.amber, letterSpacing: 0.5 }}>{periodo}</div>
                            <ul style={{ margin: "2px 0", paddingLeft: 18, fontSize: 11.5 }}>
                              {docs.map((b) => (
                                <li key={b.id}>
                                  <a href={b.datos} download={b.nombre} style={{ color: COLORS.cyan }}>{b.nombre}</a>
                                  <span style={{ color: COLORS.muted }}> · {(b.tamanoKb || 0).toLocaleString()} kB · adjuntado {String(b.subidoEl).slice(0, 10)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                    <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "6px 0 0", lineHeight: 1.5 }}>
                      Se agrupan por año y mes, por matrícula. <strong>No se editan</strong>: el escaneo es el
                      documento firmado y modificarlo destruiría la única prueba. Lo que se corrige, si hace
                      falta, es el asiento virtual — y queda registrado en el cotejo de abajo.
                    </p>
                  </div>

                  {asientos.length === 0 ? (
                    <p style={{ fontSize: 11.5, color: COLORS.muted, margin: 0 }}>Sin asientos de bitácora para esta matrícula.</p>
                  ) : (
                    <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                        <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}>
                          <th>Fecha</th><th>N° vuelo</th>
                          {CAMPOS_COTEJABLES.map((c) => <th key={c.clave}>{c.etiqueta}</th>)}
                          <th>Técnico</th><th>Estado</th>
                        </tr></thead>
                        <tbody>{asientos.map((v) => {
                          const cot = evaluarCotejo(v.cotejo);
                          const tono = cot.estado === ESTADOS_COTEJO.COINCIDE ? "ACEPTABLE"
                            : cot.estado === ESTADOS_COTEJO.DISCREPA ? "INTOLERABLE" : "TOLERABLE";
                          return (
                            <tr key={v.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                              <td style={{ padding: "4px 0", whiteSpace: "nowrap" }}>{String(v.fecha || "").slice(0, 10) || "s/f"}</td>
                              <td>{v.nVuelo || "—"}</td>
                              {CAMPOS_COTEJABLES.map((c) => {
                                const marca = v.cotejo?.campos?.[c.clave];
                                return (
                                  <td key={c.clave} style={{ whiteSpace: "nowrap" }}>
                                    <span style={{ color: COLORS.muted }}>{v[c.clave] ?? 0}</span>{" "}
                                    <button className="no-print" title="Coincide con el papel"
                                      onClick={() => marcarCotejo(v.id, c.clave, true)}
                                      style={{ background: marca === true ? COLORS.ok : "transparent", border: `1px solid ${COLORS.hairline}`, color: marca === true ? "#0C1116" : COLORS.muted, borderRadius: 4, cursor: "pointer", fontSize: 10, padding: "0 4px" }}>✓</button>
                                    <button className="no-print" title="NO coincide con el papel"
                                      onClick={() => marcarCotejo(v.id, c.clave, false)}
                                      style={{ background: marca === false ? COLORS.danger : "transparent", border: `1px solid ${COLORS.hairline}`, color: marca === false ? "#fff" : COLORS.muted, borderRadius: 4, cursor: "pointer", fontSize: 10, padding: "0 4px", marginLeft: 2 }}>✕</button>
                                  </td>
                                );
                              })}
                              <td><input style={{ ...inputStyle, padding: "2px 5px", width: 90 }} value={v.cotejo?.tecnico || ""} placeholder="quién cotejó" onChange={(e) => editarCotejo(v.id, "tecnico", e.target.value)} /></td>
                              <td style={{ whiteSpace: "nowrap" }}><StatusPill status={tono} /></td>
                            </tr>
                          );
                        })}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </Panel>


          {/* Qué sabe la app de este TIPO. Un tipo recién dado de alta tiene el
              peso y balance completo pero las cartas de performance vacías, y
              eso hay que verlo antes de planificar, no al llegar al cálculo. */}
          {(() => {
            const faltan = [
              !activeProfile.ogeTable && "OGE",
              !activeProfile.igeTable && "IGE",
              !activeProfile.fuelTable && "consumo",
              activeProfile.bimotor && !activeProfile.oeiHogeTable && "OEI",
            ].filter(Boolean);
            if (faltan.length === 0) return null;
            return (
              <Banner tone="TOLERABLE">
                <strong>{activeProfile.tipo}</strong> — el peso y balance está cargado, pero
                {" "}<strong>faltan las cartas de {faltan.join(", ")}</strong>. Están publicadas como nomograma
                en el RFM y no se digitalizaron: la app dice «sin carta» en vez de dar un peso máximo.
                {activeProfile.bimotor && " Al ser bimotor, la performance con un motor inoperativo es una condición de despacho aparte."}
              </Banner>
            );
          })()}

          <Panel eyebrow="Peso y balance" title="Pesaje por matrícula — origen del peso vacío de toda la app">
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0, marginBottom: 12 }}>
              Este es el único lugar donde se carga el peso vacío. Análisis de vuelo y la Hoja de vuelo lo leen
              de acá; allá no se puede editar. El peso sale de la hoja de datos de la aeronave; el brazo, del
              acta de pesaje. Sin brazo cargado el peso bruto es correcto pero el CG queda estimado con el
              valor genérico del tipo, y la app lo advierte en cada pantalla.
            </p>
            <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}>
                  <th>Matrícula</th><th>Peso básico vacío (kg)</th><th>Brazo en vacío (m)</th>
                  <th>Fecha del pesaje</th><th>Próximo pesaje</th><th>Estado</th>
                </tr></thead>
                <tbody>{fleet.map((f) => {
                  const tienePeso = Number.isFinite(f.pesoBasicoVacioKg);
                  const tieneBrazo = Number.isFinite(f.brazoVacioM);
                  return (
                    <tr key={f.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                      <td style={{ padding: "5px 0" }}>{f.matricula}</td>
                      <td><CampoNumero step="0.1" placeholder={`${activeProfile.emptyKg} (tipo)`} style={{ ...inputStyle, padding: "3px 6px", width: 110 }} valor={f.pesoBasicoVacioKg ?? ""} alCambiar={(n) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, pesoBasicoVacioKg: n === null ? null : n } : x)))} permitirVacio /></td>
                      <td><CampoNumero step="0.001" placeholder={`${activeProfile.emptyArm} (tipo)`} style={{ ...inputStyle, padding: "3px 6px", width: 110 }} valor={f.brazoVacioM ?? ""} alCambiar={(n) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, brazoVacioM: n === null ? null : n } : x)))} permitirVacio /></td>
                      <td><input type="date" style={{ ...inputStyle, padding: "3px 6px" }} value={f.fechaPesaje || ""} onChange={(e) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, fechaPesaje: e.target.value } : x)))} /></td>
                      <td><input type="date" style={{ ...inputStyle, padding: "3px 6px" }} value={f.proximoPesaje || ""} onChange={(e) => setFleet((fs) => fs.map((x) => (x.id === f.id ? { ...x, proximoPesaje: e.target.value } : x)))} /></td>
                      <td><StatusPill status={tienePeso && tieneBrazo ? "ACEPTABLE" : tienePeso ? "TOLERABLE" : "INTOLERABLE"} />{" "}
                        <span style={{ fontSize: 10, color: COLORS.muted }}>
                          {tienePeso && tieneBrazo ? "peso y CG del pesaje" : tienePeso ? "peso real, CG estimado" : "sin pesaje: usa el tipo"}
                        </span>
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </Panel>

          <Panel eyebrow="Certificaciones y AD's — alertas" title="Programa de mantenimiento por horas (célula, motor, AD's repetitivos)" right={<button className="no-print" onClick={addHourComplianceItem} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ ítem</button>}>
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0, marginBottom: 12 }}>
              Extraído de las hojas "CERTIFICACIONES" y "AD REPETITIVOS" del status report — el
              reporte estaba al mismo TSN que tu flota actual, así que el remanente de cada ítem se
              recalcula solo cada vez que registras un vuelo. Es una extracción representativa de esas dos hojas
              (que entre ambas tienen cientos de filas), no la totalidad — agrega los que falten con "+ ítem".
            </p>
            <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Ítem</th><th>Célula/Motor</th><th>Intervalo (h)</th><th>Remanente HOY</th><th>Estado</th><th></th></tr></thead>
                <tbody>{hourComplianceItems.map((m) => {
                  const ac = fleet.find((f) => f.id === m.aircraftId);
                  const currentTsn = m.motor ? ac?.motor?.tsn : ac?.tsn;
                  const remanenteHoy = !Number.isFinite(m.remHorasAlReporte) ? null
                    : currentTsn !== undefined ? m.remHorasAlReporte - (currentTsn - REPORT_TSN_REF) : m.remHorasAlReporte;
                  const { estado, label: estadoLabel } = remanenteHoy === null
                    ? { estado: "TOLERABLE", label: "SIN DATO" }
                    : hourAlarmStatus(remanenteHoy);
                  return (
                    <tr key={m.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                      <td style={{ padding: "4px 0", minWidth: 260 }}>{m.item}</td>
                      <td>{m.motor ? "Motor" : "Célula"}</td>
                      <td>{m.intervaloHoras}</td>
                      <td>{remanenteHoy === null ? "s/d" : `${remanenteHoy.toFixed(1)} h`}</td>
                      <td><StatusPill status={estado} /> <span style={{ fontSize: 10, color: COLORS.muted }}>{estadoLabel}</span></td>
                      <td className="no-print"><button onClick={() => removeHourComplianceItem(m.id)} style={{ background: "transparent", border: "none", color: COLORS.danger, cursor: "pointer" }}>✕</button></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </Panel>

          <Panel eyebrow="Certificaciones y AD's — alertas" title="Equipos e ítems por fecha calendario" right={<button className="no-print" onClick={addDateComplianceItem} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ ítem</button>}>
            <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Ítem</th><th>Vence</th><th>Días restantes</th><th>Estado</th><th></th></tr></thead>
                <tbody>{dateComplianceItems.map((m) => {
                  const diasRest = m.fecha ? Math.ceil((new Date(m.fecha).getTime() - Date.now()) / 86400000) : null;
                  const { estado, label: estadoLabel } = dateAlarmStatus(diasRest);
                  return (
                    <tr key={m.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                      <td style={{ padding: "4px 0", minWidth: 220 }}>{m.item}</td>
                      <td><input type="date" style={{ ...inputStyle, padding: "3px 6px" }} value={m.fecha} onChange={(e) => setDateComplianceItems((ms) => ms.map((x) => (x.id === m.id ? { ...x, fecha: e.target.value } : x)))} /></td>
                      <td>{diasRest === null ? "—" : `${diasRest} d`}</td>
                      <td><StatusPill status={estado} /> <span style={{ fontSize: 10, color: COLORS.muted }}>{estadoLabel}</span></td>
                      <td className="no-print"><button onClick={() => removeDateComplianceItem(m.id)} style={{ background: "transparent", border: "none", color: COLORS.danger, cursor: "pointer" }}>✕</button></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </Panel>

          <Panel eyebrow="Registro de cumplimiento de inspecciones" title="Programa de inspecciones (por hora, según AMM/ALS)" right={<button className="no-print" onClick={addInspectionItem} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ ítem</button>}>
            <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
              <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Ref.</th><th>Designación</th><th>Frec. (h)</th><th>Últ. cumpl. (h)</th><th>Fecha</th><th>Próximo (h)</th><th>Remanente</th><th>Estado</th><th>Doc.</th></tr></thead>
              <tbody>{inspectionItems.map((m) => {
                const ac = fleet.find((f) => f.id === m.aircraftId);
                const remanente = m.proximoHoras - (ac ? ac.tsn : 0);
                const { estado, label: estadoLabel } = hourAlarmStatus(remanente);
                return (
                  <tr key={m.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                    <td style={{ padding: "4px 0" }}><input style={{ ...inputStyle, padding: "2px 5px", width: 55 }} value={m.ref} onChange={(e) => setInspectionItems((ms) => ms.map((x) => (x.id === m.id ? { ...x, ref: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "2px 5px", minWidth: 220 }} value={m.designacion} onChange={(e) => setInspectionItems((ms) => ms.map((x) => (x.id === m.id ? { ...x, designacion: e.target.value } : x)))} /></td>
                    <td><CampoNumero style={{ ...inputStyle, padding: "2px 5px", width: 55 }} valor={m.frecuenciaHoras} alCambiar={(n) => setInspectionItems((ms) => ms.map((x) => (x.id === m.id ? { ...x, frecuenciaHoras: n } : x)))} /></td>
                    <td><CampoNumero step="0.1" style={{ ...inputStyle, padding: "2px 5px", width: 65 }} valor={m.ultimoHoras} alCambiar={(n) => setInspectionItems((ms) => ms.map((x) => (x.id === m.id ? { ...x, ultimoHoras: n } : x)))} /></td>
                    <td><input type="date" style={{ ...inputStyle, padding: "2px 5px" }} value={m.ultimoFecha} onChange={(e) => setInspectionItems((ms) => ms.map((x) => (x.id === m.id ? { ...x, ultimoFecha: e.target.value } : x)))} /></td>
                    <td><CampoNumero step="0.1" style={{ ...inputStyle, padding: "2px 5px", width: 65 }} valor={m.proximoHoras} alCambiar={(n) => setInspectionItems((ms) => ms.map((x) => (x.id === m.id ? { ...x, proximoHoras: n } : x)))} /></td>
                    <td>{remanente.toFixed(1)} h</td>
                    <td><StatusPill status={estado} /> <span style={{ fontSize: 10, color: COLORS.muted }}>{estadoLabel}</span></td>
                    <td className="no-print">
                      <input type="file" accept="image/*,.pdf" style={{ fontSize: 10, width: 90 }} onChange={async (e) => {
                        const file = e.target.files[0]; if (!file) return;
                        const dataUrl = await readFileAsDataURL(file);
                        attachInspectionDoc(m.id, { name: file.name, dataUrl, uploadedAt: new Date().toISOString() });
                        e.target.value = "";
                      }} />
                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                        {(m.documentos || []).map((d, i) => <a key={i} href={d.dataUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.cyan, fontSize: 10 }}>📎{i + 1}</a>)}
                      </div>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </Panel>

          <Panel eyebrow="Plan de reemplazo" title="Componentes con vida límite / TBO (P/N + S/N)" right={<button className="no-print" onClick={addLifeComponent} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ componente</button>}>
            <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
              <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>ATA</th><th>Descripción</th><th>P/N</th><th>S/N</th><th>Método</th><th>Límite (h)</th><th>TSN instalación</th><th>Restantes</th><th>Estado</th><th>Doc.</th></tr></thead>
              <tbody>{lifeComponents.map((m) => {
                const ac = fleet.find((f) => f.id === m.aircraftId);
                const usadas = (ac ? ac.tsn : 0) - m.tsnInstalacion;
                const restantes = m.limiteHoras ? m.limiteHoras - usadas : null;
                let estado, estadoLabel;
                if (m.metodo === "OTL") { estado = "TOLERABLE"; estadoLabel = "SEGÚN CONDICIÓN (OTL)"; }
                else if (restantes === null) { estado = "TOLERABLE"; estadoLabel = "DEFINIR LÍMITE"; }
                else { const a = hourAlarmStatus(restantes); estado = a.estado; estadoLabel = a.label; }
                return (
                  <tr key={m.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                    <td style={{ padding: "4px 0" }}><input style={{ ...inputStyle, padding: "2px 5px", width: 55 }} value={m.ata} onChange={(e) => setLifeComponents((ms) => ms.map((x) => (x.id === m.id ? { ...x, ata: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "2px 5px", minWidth: 160 }} value={m.descripcion} onChange={(e) => setLifeComponents((ms) => ms.map((x) => (x.id === m.id ? { ...x, descripcion: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "2px 5px", width: 100 }} value={m.pn} onChange={(e) => setLifeComponents((ms) => ms.map((x) => (x.id === m.id ? { ...x, pn: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "2px 5px", width: 90 }} value={m.sn} onChange={(e) => setLifeComponents((ms) => ms.map((x) => (x.id === m.id ? { ...x, sn: e.target.value } : x)))} /></td>
                    <td>
                      <select style={{ ...inputStyle, padding: "2px 5px" }} value={m.metodo} onChange={(e) => setLifeComponents((ms) => ms.map((x) => (x.id === m.id ? { ...x, metodo: e.target.value } : x)))}>
                        <option value="OTL">OTL</option><option value="SLL">SLL (vida límite)</option><option value="OC">OC</option>
                      </select>
                    </td>
                    <td><CampoNumero style={{ ...inputStyle, padding: "2px 5px", width: 65 }} valor={m.limiteHoras ?? ""} alCambiar={(n) => setLifeComponents((ms) => ms.map((x) => (x.id === m.id ? { ...x, limiteHoras: n === null ? null : n } : x)))} permitirVacio /></td>
                    <td><CampoNumero step="0.1" style={{ ...inputStyle, padding: "2px 5px", width: 65 }} valor={m.tsnInstalacion} alCambiar={(n) => setLifeComponents((ms) => ms.map((x) => (x.id === m.id ? { ...x, tsnInstalacion: n } : x)))} /></td>
                    <td>{restantes === null ? "—" : `${restantes.toFixed(1)} h`}</td>
                    <td><StatusPill status={estado} /> <span style={{ fontSize: 10, color: COLORS.muted }}>{estadoLabel}</span></td>
                    <td className="no-print">
                      <input type="file" accept="image/*,.pdf" style={{ fontSize: 10, width: 90 }} onChange={async (e) => {
                        const file = e.target.files[0]; if (!file) return;
                        const dataUrl = await readFileAsDataURL(file);
                        attachComponentDoc(m.id, { name: file.name, dataUrl, uploadedAt: new Date().toISOString() });
                        e.target.value = "";
                      }} />
                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                        {(m.documentos || []).map((d, i) => <a key={i} href={d.dataUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.cyan, fontSize: 10 }}>📎{i + 1}</a>)}
                      </div>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </Panel>
        </div>
      )}

      {/* ---------------- OPERACIONES ---------------- */}
      {tab === "operaciones" && (
        <div style={{ display: "grid", gap: 16, marginBottom: 16 }}>
          {/* Los mapas se bajan EN TIERRA, con wifi. Por eso el panel vive en
              Operaciones y no en el modo vuelo: en vuelo ya es tarde. */}
          <PanelDescargaMapas />
        </div>
      )}

      {tab === "operaciones" && (
        <div style={{ display: "grid", gap: 16, marginBottom: 16 }}>
          {/* El libro del piloto: se llena solo al cerrar el cronómetro, con el
              FLIGHT TIME —el número del piloto, no el de mantenimiento—. */}
          {/* El filtro incluye los asientos SIN piloto asignado.

              Antes los escondía: un vuelo cerrado sin piloto en el perfil
              quedaba guardado y no aparecía en ninguna vista, así que sus
              horas existían en la base y no en el libro de nadie. */}
          {/* EL LIBRO ELIGE SU PILOTO.

              Antes dependía del piloto asignado al VUELO: sin vuelo cargado no
              había libro, y para ver el de otro piloto había que cambiar la
              asignación del vuelo. Son dos cosas distintas — quién vuela hoy y
              de quién es el libro que estoy llenando. */}
          <LibroDeVuelo
            todos={libroPiloto}
            pilotos={pilots}
            pilotoPorDefecto={assignedPilotId || null}
            onAgregar={(asiento) => setLibroPiloto((l) => [asiento, ...l])}
            /* Corregir y quitar reemplazan la lista entera: el módulo devuelve
               la lista nueva y acá se guarda, como en el resto del proyecto. */
            onReemplazarTodos={(lista) => setLibroPiloto(lista)}
            onNuevoPiloto={addPilot}
          />
        </div>
      )}

      {tab === "operaciones" && (
        <div style={{ display: "grid", gap: 16 }} className="no-print-wrap">
          <style>{`@media print {
            .app-tabs, .app-header-actions, .no-print { display: none !important; }
            body, .print-area { background: #fff !important; color: #000 !important; }
          }`}</style>
          <Banner tone="TOLERABLE">
            Los umbrales de descanso por país son valores de referencia editables, no la norma oficial vigente.
            Ajusta cada campo con el reglamento aplicable (RAP en Perú, RDAC en Ecuador, DAR en Chile, 14 CFR Part 135 en EE. UU.)
            antes de usar esta pestaña como control real de cumplimiento. Cada vuelo que registres aquí abajo suma
            horas/ciclos a la aeronave y al motor de la pestaña "Mantenimiento" — el estado de cada inspección y
            componente de vida límite se recalcula solo con esos datos, sin que tengas que tocar nada más allá.
          </Banner>

          <Panel eyebrow="Tripulación" title="Pilotos, licencia, apto médico y descanso" right={<button className="no-print" onClick={addPilot} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ piloto</button>}>
            <table style={{ width: "100%", fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
              <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Nombres</th><th>Apellidos</th><th>Licencia</th><th>Apto médico vence</th><th>Horas totales</th><th>Últ. 30 d</th><th>Fin servicio anterior</th><th>Jurisdicción</th><th>Descanso</th><th></th></tr></thead>
              <tbody>{pilots.map((p) => {
                const jornada = jornadaDe(p);
                const horasDesdeFin = horasDescansoDe(p);
                const restStatus = horasDesdeFin === null ? "TOLERABLE"
                  : !Number.isFinite(jornada.descansoRequeridoH) ? "TOLERABLE"
                    : horasDesdeFin >= jornada.descansoRequeridoH ? "ACEPTABLE" : "INTOLERABLE";
                let medStatus = null, medLabel = "";
                if (p.aptoMedicoVencimiento) {
                  const diasRest = (new Date(p.aptoMedicoVencimiento).getTime() - Date.now()) / 86400000;
                  medStatus = diasRest <= 0 ? "INTOLERABLE" : diasRest <= 30 ? "TOLERABLE" : "ACEPTABLE";
                  medLabel = diasRest <= 0 ? "VENCIDO" : diasRest <= 30 ? `${Math.ceil(diasRest)} d` : "vigente";
                }
                return (
                  <tr key={p.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                    <td style={{ padding: "5px 0" }}><input style={{ ...inputStyle, padding: "3px 6px", width: 90 }} value={p.nombre} onChange={(e) => setPilots((ps) => ps.map((x) => (x.id === p.id ? { ...x, nombre: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "3px 6px", width: 90 }} value={p.apellido || ""} onChange={(e) => setPilots((ps) => ps.map((x) => (x.id === p.id ? { ...x, apellido: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "3px 6px", width: 90 }} placeholder="N° licencia" value={p.licencia} onChange={(e) => setPilots((ps) => ps.map((x) => (x.id === p.id ? { ...x, licencia: e.target.value } : x)))} /></td>
                    <td>
                      <input type="date" style={{ ...inputStyle, padding: "3px 6px" }} value={p.aptoMedicoVencimiento || ""} onChange={(e) => setPilots((ps) => ps.map((x) => (x.id === p.id ? { ...x, aptoMedicoVencimiento: e.target.value } : x)))} />
                      {medStatus && <div style={{ marginTop: 3 }}><StatusPill status={medStatus} /> <span style={{ fontSize: 10, color: COLORS.muted }}>{medLabel}</span></div>}
                    </td>
                    <td><CampoNumero style={{ ...inputStyle, padding: "3px 6px", width: 80 }} valor={p.horasTotales} alCambiar={(n) => setPilots((ps) => ps.map((x) => (x.id === p.id ? { ...x, horasTotales: n } : x)))} /></td>
                    <td><CampoNumero style={{ ...inputStyle, padding: "3px 6px", width: 70 }} valor={p.horasUltimos30d} alCambiar={(n) => setPilots((ps) => ps.map((x) => (x.id === p.id ? { ...x, horasUltimos30d: n } : x)))} /></td>
                    <td><input type="datetime-local" style={{ ...inputStyle, padding: "3px 6px" }} value={p.finServicioISO} onChange={(e) => setPilots((ps) => ps.map((x) => (x.id === p.id ? { ...x, finServicioISO: e.target.value } : x)))} /></td>
                    <td>
                      <select style={{ ...inputStyle, padding: "3px 6px" }} value={p.jurisdiccion} onChange={(e) => setPilots((ps) => ps.map((x) => (x.id === p.id ? { ...x, jurisdiccion: e.target.value } : x)))}>
                        {JURISDICCIONES.map((j) => <option key={j} value={j}>{j}</option>)}
                      </select>
                    </td>
                    <td>{restStatus ? <StatusPill status={restStatus} /> : <span style={{ color: COLORS.muted }}>sin dato</span>}</td>
                    <td className="no-print"><button onClick={() => removePilot(p.id)} title="Eliminar piloto" style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.danger, borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>✕</button></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </Panel>

          {/* ── DUTY TIME por piloto, imprimible ─────────────────────────── */}
          <Panel
            eyebrow="Duty Time"
            title="Control de jornada y descanso por piloto"
            right={<button className="no-print" onClick={() => window.print()} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "7px 12px", cursor: "pointer", fontWeight: 600 }}>🖨 Imprimir Duty Time</button>}
          >
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0, lineHeight: 1.55 }}>
              Las horas y los <strong>días volados</strong> por mes y por año salen del registro de bitácora, no
              se escriben acá. Los días se cuentan por fecha distinta: tres vuelos el mismo día son un día
              volado. Confundirlos infla el conteo que va al descanso periódico —los 10 días al mes de Chile,
              las 24 h cada 7 días de Ecuador y EE.UU.—, que son días de calendario, no salidas.
              {" "}Cada piloto se evalúa contra la norma de <strong>su</strong> jurisdicción. Hasta ahora los cuatro
              países tenían las mismas reglas cargadas (10 h de descanso, 14 de servicio, 8 de vuelo) y elegir
              país no cambiaba nada.
            </p>
            <Banner tone="TOLERABLE">
              Los umbrales están transcritos de un <strong>cuadro comparativo</strong>, no del texto oficial de
              cada reglamento. No reemplazan a la norma ni al manual de operaciones aprobado, y ninguna cifra
              está verificada contra la fuente primaria. Cada país tiene excepciones y regímenes especiales que
              un cuadro de una página no contiene.
            </Banner>

            <div className="hc-tabla-ancha" style={{ overflowX: "auto", marginTop: 12 }}>
              <table style={{ width: "100%", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}>
                  <th>Piloto</th><th>Jurisdicción</th><th>Tipo de operación</th><th>Pilotos</th>
                  <th>Vuelo jornada (h)</th><th>PSV (h)</th><th>Límite</th><th>Restan</th>
                  <th>Descanso req.</th><th>Descanso real</th><th>Mes (h · días)</th><th>Año (h · días)</th><th>Estado</th>
                </tr></thead>
                <tbody>{pilots.map((p) => {
                  const d = dutyTime[p.id] || {};
                  const j = jornadaDe(p);
                  const descansoReal = horasDescansoDe(p);
                  const tono = !j.valido ? "INTOLERABLE"
                    : j.hallazgos.some((h) => h.nivel === "alto") ? "INTOLERABLE"
                      : j.hallazgos.length > 0 ? "TOLERABLE" : "ACEPTABLE";
                  return (
                    <tr key={p.id} style={{ borderTop: `1px solid ${COLORS.hairline}`, verticalAlign: "top" }}>
                      <td style={{ padding: "5px 0" }}>{p.nombre} {p.apellido}</td>
                      <td>{p.jurisdiccion}</td>
                      <td>
                        <select className="no-print" style={{ ...inputStyle, padding: "3px 6px" }}
                          value={d.tipoOperacion || "estandar"}
                          onChange={(e) => editarDuty(p.id, "tipoOperacion", e.target.value)}>
                          {TIPOS_OPERACION_JORNADA.map((t) => <option key={t.clave} value={t.clave}>{t.etiqueta}</option>)}
                        </select>
                      </td>
                      <td>
                        <select className="no-print" style={{ ...inputStyle, padding: "3px 6px", width: 60 }}
                          value={d.cantidadPilotos || 1}
                          onChange={(e) => editarDuty(p.id, "cantidadPilotos", Number(e.target.value))}>
                          <option value={1}>1</option><option value={2}>2</option>
                        </select>
                      </td>
                      <td>
                        {(() => {
                          const j = horasDeJornada(libroPiloto, p.id, new Date().toISOString().slice(0, 10), d.horasVueloJornada);
                          return (
                            <>
                              <input
                                type="number" step="0.1"
                                style={{ ...inputStyle, padding: "3px 6px", width: 70 }}
                                value={d.horasVueloJornada ?? (j.fuente === FUENTE_JORNADA.libro ? j.horas : "")}
                                placeholder={j.fuente === FUENTE_JORNADA.ausente ? "sin dato" : ""}
                                onChange={(e) => editarDuty(p.id, "horasVueloJornada", e.target.value)}
                              />
                              {/* De dónde salió la cifra. Una derivada del libro y
                                  una recordada no valen lo mismo. */}
                              <div style={{
                                fontSize: 9.5, marginTop: 2, lineHeight: 1.3,
                                color: j.fuente === FUENTE_JORNADA.libro ? COLORS.cyan
                                  : (j.fuente === FUENTE_JORNADA.ausente ? COLORS.muted : COLORS.amber),
                              }}>
                                {explicacionDeJornada(j)}
                              </div>
                              {j.avisos.length > 0 && (
                                <div style={{ fontSize: 9.5, marginTop: 2, lineHeight: 1.3, color: COLORS.caution }}>
                                  {j.avisos[0]}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td><input type="number" step="0.1" style={{ ...inputStyle, padding: "3px 6px", width: 70 }} value={d.horasPsv ?? ""} onChange={(e) => editarDuty(p.id, "horasPsv", e.target.value)} /></td>
                      <td>{j.limiteVueloH ?? "s/d"} h</td>
                      <td style={{ color: j.horasRestantesH !== null && j.horasRestantesH < 0 ? COLORS.danger : COLORS.text }}>
                        {j.horasRestantesH === null ? "s/d" : `${j.horasRestantesH} h`}
                      </td>
                      <td>{j.descansoRequeridoH ?? "s/d"} h<br /><span style={{ fontSize: 9.5, color: COLORS.muted }}>base: {j.baseDescanso}</span></td>
                      <td>{descansoReal === null ? "s/d" : `${descansoReal} h`}</td>
                      {(() => {
                        const r = resumenMesYAnio(flightLog, p.id);
                        return (
                          <>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {r.mes.horas} h · {r.mes.diasVolados} d
                              <br /><span style={{ fontSize: 9.5, color: COLORS.muted }}>{r.etiquetaMes}</span>
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {r.anio.horas} h · {r.anio.diasVolados} d
                              <br /><span style={{ fontSize: 9.5, color: COLORS.muted }}>{r.etiquetaAnio}</span>
                            </td>
                          </>
                        );
                      })()}
                      <td><StatusPill status={tono} /></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>

            {pilots.map((p) => {
              const j = jornadaDe(p);
              if (!j.valido || j.hallazgos.length === 0) return null;
              return (
                <div key={p.id} style={{ marginTop: 12, borderLeft: `3px solid ${COLORS.amber}`, paddingLeft: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{p.nombre} {p.apellido} — {j.norma}</div>
                  {j.hallazgos.map((h, i) => (
                    <p key={i} style={{ fontSize: 11.5, margin: "3px 0", color: h.nivel === "alto" ? COLORS.danger : COLORS.amber }}>
                      {h.nivel === "alto" ? "✕" : "⚠"} {h.texto}
                    </p>
                  ))}
                </div>
              );
            })}

            <div style={{ marginTop: 16, borderTop: `1px solid ${COLORS.hairline}`, paddingTop: 12 }}>
              <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.6 }}>
                Reglas aplicadas y descanso periódico
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
                {JURISDICCIONES.map((jur) => {
                  const r = REGLAS_JORNADA[jur];
                  return (
                    <div key={jur} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{jur}</div>
                      <div style={{ fontSize: 10.5, color: COLORS.cyan, marginBottom: 6 }}>{r.norma}</div>
                      <div style={{ fontSize: 11, color: COLORS.muted, lineHeight: 1.6 }}>
                        Vuelo diario: {r.vueloMaximoH.estandar} h estándar
                        {r.vueloMaximoH.cargaExterna !== r.vueloMaximoH.estandar && ` · ${r.vueloMaximoH.cargaExterna} h carga externa`}
                        {r.vueloMaximoH.combinado !== r.vueloMaximoH.estandar && ` · ${r.vueloMaximoH.combinado} h combinado`}
                        {r.vueloMaximoDosPilotosH && ` · ${r.vueloMaximoDosPilotosH} h con dos pilotos`}
                        <br />
                        {r.descansoPorPsv && `Descanso por PSV: ${r.descansoPorPsv.map((t) => `≤${t.hastaPsvH} h → ${t.descansoH} h`).join(" · ")}`}
                        {r.descansoPorVuelo && `Descanso por vuelo: <8 h → 9 h · 8–9 h → 10 h · >9 h → 11 h`}
                        {!r.descansoPorPsv && !r.descansoPorVuelo && `Descanso mínimo: ${r.descansoMinimoH} h`}
                        <div style={{ marginTop: 5, color: COLORS.text }}>{r.periodico}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Panel>


          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Panel eyebrow="Tripulación" title="Mecánicos" right={<button className="no-print" onClick={addMechanic} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ mecánico</button>}>
              <table style={{ width: "100%", fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Nombres</th><th>Apellidos</th><th>Licencia</th><th></th></tr></thead>
                <tbody>{mechanics.map((m) => (
                  <tr key={m.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                    <td style={{ padding: "5px 0" }}><input style={{ ...inputStyle, padding: "3px 6px" }} value={m.nombre} onChange={(e) => setMechanics((ms) => ms.map((x) => (x.id === m.id ? { ...x, nombre: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "3px 6px" }} value={m.apellido} onChange={(e) => setMechanics((ms) => ms.map((x) => (x.id === m.id ? { ...x, apellido: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "3px 6px" }} placeholder="N° licencia" value={m.licencia} onChange={(e) => setMechanics((ms) => ms.map((x) => (x.id === m.id ? { ...x, licencia: e.target.value } : x)))} /></td>
                    <td className="no-print"><button onClick={() => removeMechanic(m.id)} style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.danger, borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>✕</button></td>
                  </tr>
                ))}</tbody>
              </table>
            </Panel>
            <Panel eyebrow="Tripulación" title="Supervisores / CC" right={<button className="no-print" onClick={addSupervisor} style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.cyan, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 }}>+ supervisor</button>}>
              <table style={{ width: "100%", fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Nombres</th><th>Apellidos</th><th>Licencia</th><th></th></tr></thead>
                <tbody>{supervisors.map((s) => (
                  <tr key={s.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                    <td style={{ padding: "5px 0" }}><input style={{ ...inputStyle, padding: "3px 6px" }} value={s.nombre} onChange={(e) => setSupervisors((ss) => ss.map((x) => (x.id === s.id ? { ...x, nombre: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "3px 6px" }} value={s.apellido} onChange={(e) => setSupervisors((ss) => ss.map((x) => (x.id === s.id ? { ...x, apellido: e.target.value } : x)))} /></td>
                    <td><input style={{ ...inputStyle, padding: "3px 6px" }} placeholder="N° licencia" value={s.licencia} onChange={(e) => setSupervisors((ss) => ss.map((x) => (x.id === s.id ? { ...x, licencia: e.target.value } : x)))} /></td>
                    <td className="no-print"><button onClick={() => removeSupervisor(s.id)} style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.danger, borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>✕</button></td>
                  </tr>
                ))}</tbody>
              </table>
            </Panel>
          </div>

          <Panel eyebrow="Bitácora" title="Discrepancias de mantenimiento y acción correctiva (con adjuntos escaneados)">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
              <Field label="Aeronave">
                <select style={inputStyle} value={newDiscrepancy.aircraftId} onChange={(e) => setNewDiscrepancy((d) => ({ ...d, aircraftId: e.target.value }))}>
                  {fleet.map((f) => <option key={f.id} value={f.id}>{f.matricula}</option>)}
                </select>
              </Field>
              <Field label="Folio"><input style={inputStyle} value={newDiscrepancy.folio} onChange={(e) => setNewDiscrepancy((d) => ({ ...d, folio: e.target.value }))} /></Field>
              <Field label="Fecha"><input type="date" style={inputStyle} value={newDiscrepancy.fecha} onChange={(e) => setNewDiscrepancy((d) => ({ ...d, fecha: e.target.value }))} /></Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
              <Field label="Discrepancia"><input style={inputStyle} value={newDiscrepancy.descripcion} onChange={(e) => setNewDiscrepancy((d) => ({ ...d, descripcion: e.target.value }))} /></Field>
              <Field label="Acción correctiva"><input style={inputStyle} value={newDiscrepancy.accionCorrectiva} onChange={(e) => setNewDiscrepancy((d) => ({ ...d, accionCorrectiva: e.target.value }))} /></Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 12, alignItems: "end" }}>
              <Field label="Mecánico">
                <select style={inputStyle} value={newDiscrepancy.mecanicoId} onChange={(e) => setNewDiscrepancy((d) => ({ ...d, mecanicoId: e.target.value }))}>
                  {mechanics.map((m) => <option key={m.id} value={m.id}>{m.nombre} {m.apellido}</option>)}
                </select>
              </Field>
              <Field label="Supervisor / CC">
                <select style={inputStyle} value={newDiscrepancy.supervisorId} onChange={(e) => setNewDiscrepancy((d) => ({ ...d, supervisorId: e.target.value }))}>
                  {supervisors.map((s) => <option key={s.id} value={s.id}>{s.nombre} {s.apellido}</option>)}
                </select>
              </Field>
              <button onClick={addDiscrepancy} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}>Registrar</button>
            </div>
            <table style={{ width: "100%", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
              <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Folio</th><th>Fecha</th><th>Aeronave</th><th>Discrepancia</th><th>Acción correctiva</th><th>Mecánico / Sup.</th><th>Adjuntos (firma escaneada, etc.)</th></tr></thead>
              <tbody>{discrepancies.map((d) => (
                <tr key={d.id} style={{ borderTop: `1px solid ${COLORS.hairline}`, verticalAlign: "top", opacity: d.cerrada ? 0.55 : 1 }}>
                  <td style={{ padding: "4px 0" }}>
                    <button
                      type="button"
                      onClick={() => toggleDiscrepancyCerrada(d.id, d.cerrada ? "" : (prompt("¿Quién levanta esta discrepancia?") || ""))}
                      title={d.cerrada ? `Levantada por ${d.cerradaPor} el ${(d.cerradaEl || "").slice(0, 10)} — pulsa para reabrir` : "Marcar como levantada"}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginRight: 6,
                               color: d.cerrada ? COLORS.ok : COLORS.caution, fontWeight: 700 }}
                    >
                      {d.cerrada ? "LEVANTADA" : "ABIERTA"}
                    </button>
                    {d.folio}
                  </td>
                  <td>{d.fecha}</td>
                  <td>{fleet.find((f) => f.id === d.aircraftId)?.matricula}</td>
                  <td>{d.descripcion}</td>
                  <td>{d.accionCorrectiva}</td>
                  <td>{mechanics.find((m) => m.id === d.mecanicoId)?.nombre} {mechanics.find((m) => m.id === d.mecanicoId)?.apellido}{d.supervisorId ? ` / ${supervisors.find((s) => s.id === d.supervisorId)?.nombre || ""}` : ""}</td>
                  <td className="no-print">
                    <input type="file" accept="image/*,.pdf" style={{ fontSize: 10, width: 130 }} onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      const dataUrl = await readFileAsDataURL(file);
                      attachDiscrepancyDoc(d.id, { name: file.name, dataUrl, uploadedAt: new Date().toISOString() });
                      e.target.value = "";
                    }} />
                    <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                      {d.documentos.map((doc, i) => (
                        doc.dataUrl.startsWith("data:image") ? (
                          <a key={i} href={doc.dataUrl} target="_blank" rel="noreferrer"><img src={doc.dataUrl} alt={doc.name} style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, border: `1px solid ${COLORS.hairline}` }} /></a>
                        ) : (
                          <a key={i} href={doc.dataUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.cyan, fontSize: 10 }}>📄 {doc.name}</a>
                        )
                      ))}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 10 }}>
              El adjunto guarda la imagen/PDF escaneado en la sesión del navegador (igual al modelo de la bitácora en
              papel, con firma de piloto/mecánico/supervisor visible). En producción esto debe subirse a almacenamiento
              en la nube (no quedar embebido en la base de datos) y quedar vinculado por folio.
            </p>
          </Panel>

          <Panel eyebrow="Cronómetro" title="Tiempo de vuelo — arranque, tierra, aire, apagado">
            <Cronometro
              estado={cronometro}
              onCambio={setCronometro}
              onGuardarVuelo={(datos) => setVuelosDelDia((vs) => [...vs, vueloDesdeCronometro(datos, datos)])}
            />
          </Panel>

          {/* ── TOTALES DE LA JORNADA ────────────────────────────────────── */}
          <Panel
            eyebrow="Día de vuelo"
            title="Vuelos de la jornada y gran total"
            right={
              <div style={{ display: "flex", gap: 8 }}>
                <button className="no-print" onClick={() => window.print()} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "7px 12px", cursor: "pointer", fontWeight: 600 }}>🖨 Imprimir el día</button>
                {vuelosDelDia.length > 0 && (
                  <button
                    className="no-print"
                    onClick={() => { if (window.confirm("¿Empezar un día nuevo? Los vuelos de la jornada actual se borran del cronómetro. Pasalos antes a la bitácora si corresponde.")) setVuelosDelDia([]); }}
                    style={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, color: COLORS.muted, borderRadius: 6, padding: "7px 12px", cursor: "pointer" }}
                  >
                    Día nuevo
                  </button>
                )}
              </div>
            }
          >
            <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap", marginBottom: 12 }}>
              <Field label="Unidad del combustible" hint="Solo cambia cómo se escribe y se lee: el dato se guarda en litros.">
                <select
                  style={{ ...inputStyle, width: 180 }}
                  value={unidadCombustibleDia}
                  onChange={(e) => setUnidadCombustibleDia(e.target.value)}
                >
                  {UNIDADES_COMBUSTIBLE.map((u) => <option key={u.clave} value={u.clave}>{u.etiqueta} ({u.simbolo})</option>)}
                </select>
              </Field>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <Readout label="Vuelos" value={totalDelDia.vuelos} unit="" />
              <Readout label="Arranques" value={totalDelDia.arranques} unit="" />
              <Readout label="Aterrizajes" value={totalDelDia.aterrizajes} unit="" />
              <Readout label="FT total" value={totalDelDia.ftHhMm ?? "—"} unit={totalDelDia.ft !== null ? `${totalDelDia.ft.toFixed(2)} h` : ""} />
              <Readout label="AT total" value={totalDelDia.atHhMm ?? "—"} unit={totalDelDia.at !== null ? `${totalDelDia.at.toFixed(2)} h` : ""} tone="ACEPTABLE" />
              <Readout label="En tierra" value={totalDelDia.tierraHhMm ?? "—"} unit="" />
              <Readout
                label="Ciclos NG" value={totalDelDia.ngCiclos ?? "incompleto"} unit=""
                tone={totalDelDia.ngCompleto ? "ACEPTABLE" : "TOLERABLE"}
              />
              <Readout
                label="Ciclos NF" value={totalDelDia.nfCiclos ?? "incompleto"} unit=""
                tone={totalDelDia.nfCompleto ? "ACEPTABLE" : "TOLERABLE"}
              />
              <Readout
                label="Combustible consumido"
                value={totalDelDia.combustibleConsumidoL === null ? "incompleto" : enUnidad(totalDelDia.combustibleConsumidoL)}
                unit={totalDelDia.combustibleConsumidoL === null ? "" : simboloDia}
                tone={totalDelDia.consumoCompleto ? "ACEPTABLE" : "TOLERABLE"}
              />
              {vuelosEliminados > 0 && (
                <Readout label="Registros eliminados" value={vuelosEliminados} unit="" tone="TOLERABLE" />
              )}
            </div>

            {totalDelDia.avisos.map((a, i) => (
              <p key={i} style={{ fontSize: 11.5, color: COLORS.caution, margin: "3px 0" }}>⚠ {a}</p>
            ))}

            {vuelosDelDia.length > 0 && (
              <div className="hc-tabla-ancha" style={{ overflowX: "auto", marginTop: 12 }}>
                <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                  <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}>
                    <th style={{ paddingRight: 10 }}>#</th>
                    <th style={{ paddingRight: 10 }}>Arranque</th>
                    <th style={{ paddingRight: 10 }}>Apagado</th>
                    <th style={{ paddingRight: 10 }}>Aterrizajes (hora)</th>
                    <th style={{ paddingRight: 10 }}>FT</th>
                    <th style={{ paddingRight: 10 }}>AT</th>
                    <th style={{ paddingRight: 10 }}>Tierra</th>
                    <th style={{ paddingRight: 10 }}>Ldg</th>
                    <th style={{ paddingRight: 10 }}>NG</th>
                    <th style={{ paddingRight: 10 }}>NF</th>
                    <th>Comb. inicial ({simboloDia})</th>
                    <th>Comb. final ({simboloDia})</th>
                    <th>Consumo ({simboloDia})</th>
                    <th>Observaciones</th><th></th>
                  </tr></thead>
                  <tbody>{vuelosDelDia.map((v, i) => {
                    const hhmm = (ms) => `${Math.floor(Math.round(ms / 60000) / 60)}:${String(Math.round(ms / 60000) % 60).padStart(2, "0")}`;
                    return (
                      <tr key={v.id} style={{ borderTop: `1px solid ${COLORS.hairline}`, opacity: v.anulado ? 0.45 : 1 }}>
                        <td style={{ padding: "4px 0" }}>{i + 1}</td>
                        <td style={{ paddingRight: 10, whiteSpace: "nowrap" }}>{hora(v.horaArranque ?? v.fecha)}</td>
                        <td style={{ paddingRight: 10, whiteSpace: "nowrap", color: COLORS.text }}>{hora(v.horaApagado)}</td>
                        <td style={{ fontSize: 10.5, color: COLORS.muted, maxWidth: 130 }}>
                          {(v.horasAterrizaje || []).map(hora).join(" · ") || "—"}
                        </td>
                        <td style={{ paddingRight: 10 }}>{hhmm(v.ftMs)}</td>
                        <td style={{ color: COLORS.cyan, paddingRight: 10 }}>{hhmm(v.atMs)}</td>
                        <td style={{ color: COLORS.muted, paddingRight: 10 }}>{hhmm(v.tierraMs)}</td>
                        <td><input type="number" min={0} disabled={v.anulado} value={v.aterrizajes} onChange={(e) => editarVueloDelDia(v.id, { aterrizajes: Number(e.target.value) || 0 })} style={{ ...inputStyle, padding: "3px 6px", width: 62 }} /></td>
                        {/* Los ciclos admiten decimales: el conteo del Arriel
                            incluye ciclos parciales, no solo enteros. */}
                        <td><input type="number" step="0.01" min={0} disabled={v.anulado} value={v.ngCiclos ?? ""} onChange={(e) => editarVueloDelDia(v.id, { ngCiclos: e.target.value === "" ? null : Number(e.target.value) })} style={{ ...inputStyle, padding: "3px 6px", width: 76, borderColor: v.ngCiclos === null ? COLORS.caution : COLORS.hairline }} /></td>
                        <td><input type="number" step="0.01" min={0} disabled={v.anulado} value={v.nfCiclos ?? ""} onChange={(e) => editarVueloDelDia(v.id, { nfCiclos: e.target.value === "" ? null : Number(e.target.value) })} style={{ ...inputStyle, padding: "3px 6px", width: 76, borderColor: v.nfCiclos === null ? COLORS.caution : COLORS.hairline }} /></td>
                        {/* Combustible: se escribe en la unidad elegida arriba y se
                            guarda siempre en litros, para que cambiar de unidad no
                            arrastre redondeo. */}
                        <td><input type="number" step="0.1" min={0} disabled={v.anulado} value={enUnidad(v.combustibleInicialL)} onChange={(e) => editarVueloDelDia(v.id, { combustibleInicialL: aLitrosDesdeUnidad(e.target.value) })} style={{ ...inputStyle, padding: "3px 6px", width: 96, borderColor: v.combustibleInicialL === null ? COLORS.caution : COLORS.hairline }} /></td>
                        <td><input type="number" step="0.1" min={0} disabled={v.anulado} value={enUnidad(v.combustibleFinalL)} onChange={(e) => editarVueloDelDia(v.id, { combustibleFinalL: aLitrosDesdeUnidad(e.target.value) })} style={{ ...inputStyle, padding: "3px 6px", width: 96, borderColor: v.combustibleFinalL === null ? COLORS.caution : COLORS.hairline }} /></td>
                        <td style={{
                          whiteSpace: "nowrap",
                          color: consumoVuelo(v) === null ? COLORS.muted : consumoVuelo(v) < 0 ? COLORS.danger : COLORS.text,
                        }}>
                          {consumoVuelo(v) === null
                            ? "—"
                            : consumoVuelo(v) < 0
                              ? `+${enUnidad(-consumoVuelo(v))} ⚠`
                              : enUnidad(consumoVuelo(v))}
                        </td>
                        <td><input disabled={v.anulado} value={v.observaciones || ""} onChange={(e) => editarVueloDelDia(v.id, { observaciones: e.target.value })} style={{ ...inputStyle, padding: "2px 5px", width: 150 }} /></td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {/* Eliminar de verdad. Es el registro de trabajo del
                              cronómetro, todavía no un asiento de bitácora: un
                              vuelo mal cronometrado se borra y se vuelve a
                              tomar. Lo que sí queda es el CONTADOR de
                              eliminados, que sale en el papel del día para que
                              nadie crea que la lista está completa. */}
                          <button
                            className="no-print"
                            onClick={() => {
                              if (!window.confirm(`¿Eliminar el vuelo ${i + 1} del día? Se pierde su tiempo cronometrado y no se puede recuperar.`)) return;
                              setVuelosDelDia((vs) => vs.filter((x) => x.id !== v.id));
                              setVuelosEliminados((n) => n + 1);
                            }}
                            style={{ background: "transparent", border: "none", color: COLORS.danger, cursor: "pointer", fontSize: 11 }}
                          >
                            eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                className="no-print"
                disabled={!totalDelDia.cerrable}
                onClick={() => {
                  const detalle =
                    `¿Pasar el total del día al asiento de bitácora?\n\n` +
                    `  Vuelos:      ${totalDelDia.vuelos}\n` +
                    /* Se dice A DÓNDE VA CADA UNO. Mostrar los dos números sin
                       decir su destino fue lo que dejó pasar que el FT
                       estuviera yendo a mantenimiento. */
                    `  FT total:    ${totalDelDia.ftHhMm} (${totalDelDia.ft} h) → libro del piloto\n` +
                    `  AT total:    ${totalDelDia.atHhMm} (${totalDelDia.at} h) → mantenimiento\n` +
                    `  Aterrizajes: ${totalDelDia.aterrizajes}\n` +
                    `  Ciclos NG:   ${totalDelDia.ngCiclos}\n` +
                    `  Ciclos NF:   ${totalDelDia.nfCiclos}\n` +
                    `  Combustible: ${totalDelDia.combustibleConsumidoL === null ? "incompleto" : `${enUnidad(totalDelDia.combustibleConsumidoL)} ${simboloDia}`}\n\n` +
                    `Aceptar = SÍ, rellena los campos del asiento.\n` +
                    `Cancelar = NO, no toca nada.\n\n` +
                    `No lo guarda: el asiento sigue necesitando que alguien lo confirme.`;
                  if (!window.confirm(detalle)) return;
                  setNewLog((l) => ({
                    ...l,
                    /* ═══ AT A MANTENIMIENTO, FT AL PILOTO ═══

                       Acá iba `totalDelDia.ft` en los dos: mantenimiento
                       recibía el tiempo con rotores en tierra incluido.

                       Con los números del operador —FT 0:09, AT 0:04— eran
                       nueve minutos donde la aeronave voló cuatro: **las
                       inspecciones se adelantarían un 125 %**.

                       Es la misma regla que ya se corrigió en el cierre de
                       vuelo y en el alta manual, y volvió a romperse acá
                       porque este camino se escribió después. */
                    horasAeronave: totalDelDia.at,
                    /* Las horas de motor van con el AIR TIME: el motor gira en
                       tierra, pero el programa de mantenimiento cuenta lo
                       mismo que la célula salvo que el manual diga otra cosa.

                       PENDIENTE DEL OPERADOR: confirmar si su programa cuenta
                       las horas de motor por air time o por flight time. Hasta
                       que lo declare, se usa el AT, que es el criterio
                       conservador para la célula. */
                    horasMotor: totalDelDia.at,
                    /* Y el FT al libro del piloto, que es su tiempo. */
                    flightTime: totalDelDia.ft,
                    ldg: totalDelDia.aterrizajes,
                    ciclosNG: totalDelDia.ngCiclos,
                    ciclosNF: totalDelDia.nfCiclos,
                    combustibleKg: totalDelDia.combustibleConsumidoL === null
                      ? l.combustibleKg
                      : Math.round(totalDelDia.combustibleConsumidoL * (densidadKgL || 0.8)),
                  }));
                }}
                style={{
                  background: totalDelDia.cerrable ? COLORS.cyan : COLORS.panelAlt,
                  border: `1px solid ${totalDelDia.cerrable ? COLORS.cyan : COLORS.hairline}`,
                  color: totalDelDia.cerrable ? "#04201D" : COLORS.muted,
                  borderRadius: 8, padding: "12px 16px",
                  cursor: totalDelDia.cerrable ? "pointer" : "not-allowed", fontWeight: 600, minHeight: 48,
                }}
              >
                Pasar el total del día a la bitácora
              </button>
              {!totalDelDia.cerrable && (
                <span style={{ fontSize: 11.5, color: COLORS.caution }}>
                  Falta completar los ciclos de todos los vuelos antes de poder cerrar el día.
                </span>
              )}
            </div>

            <p style={{ fontSize: 10.5, color: COLORS.muted, marginTop: 12, lineHeight: 1.55 }}>
              El consumo de cada vuelo es <strong>lo que había al arrancar menos lo que quedó al apagar</strong>.
              Si entre dos vuelos se reabastece, el inicial del siguiente será mayor que el final del anterior:
              es normal, cada vuelo se mide contra sí mismo. Si falta el dato de algún vuelo el total no se
              suma, porque un consumo parcial haría creer que se gastó menos.
              {vuelosEliminados > 0 && ` Se eliminaron ${vuelosEliminados} registro(s) del cronómetro en esta jornada.`}
              {" "}El total pasa al asiento de bitácora como precarga; ese asiento sigue necesitando
              confirmación y cotejo contra el papel.
            </p>
          </Panel>

          <Panel eyebrow="Registro de Bitácora — Operaciones" title="Asientos de vuelo que alimentan el status de mantenimiento">
            <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0, marginBottom: 12 }}>
              Mismos campos que la bitácora física de vuelo y mantenimiento (folio, horas aeronave/motor, ciclos NG/NF,
              LDG, cargas) — se suman directo a la aeronave por matrícula/serie en "Mantenimiento". Adjunta la foto o
              PDF liviano de la bitácora firmada para poder cruzar en cualquier momento el dato físico con el virtual.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8, alignItems: "end", marginBottom: 10 }}>
              <Field label="Aeronave">
                <select style={inputStyle} value={newLog.aircraftId} onChange={(e) => setNewLog((l) => ({ ...l, aircraftId: e.target.value }))}>
                  {fleet.map((f) => <option key={f.id} value={f.id}>{f.matricula} (S/N {f.serie})</option>)}
                </select>
              </Field>
              <Field label="Piloto">
                <select style={inputStyle} value={newLog.pilotId} onChange={(e) => setNewLog((l) => ({ ...l, pilotId: e.target.value }))}>
                  {pilots.map((p) => <option key={p.id} value={p.id}>{p.nombre} {p.apellido}</option>)}
                </select>
              </Field>
              <Field label="N° Vuelo"><input style={inputStyle} value={newLog.nVuelo} onChange={(e) => setNewLog((l) => ({ ...l, nVuelo: e.target.value }))} /></Field>
              {/* AIR TIME, no flight time. El programa de mantenimiento cuenta
                  tiempo en el aire; el flight time del piloto incluye rotores
                  girando en tierra y es un número mayor. Anotar el FT acá
                  adelantaría las inspecciones. */}
              <Field label="Air time (mantenimiento)">
                <input type="number" step="0.1" style={inputStyle} value={newLog.horasAeronave} onChange={(e) => setNewLog((l) => ({ ...l, horasAeronave: e.target.value }))} />
                <div style={{ fontSize: 9.5, marginTop: 2, color: COLORS.muted, lineHeight: 1.3 }}>
                  Tiempo en el aire. Es lo que cuenta para las inspecciones.
                </div>
              </Field>
              <Field label="Flight time (piloto)">
                <input type="number" step="0.1" style={inputStyle} value={newLog.flightTime ?? ""} onChange={(e) => setNewLog((l) => ({ ...l, flightTime: e.target.value }))} />
                <div style={{ fontSize: 9.5, marginTop: 2, color: COLORS.muted, lineHeight: 1.3 }}>
                  Incluye rotores en tierra. Va al libro del piloto y a su jornada.
                </div>
              </Field>
              <Field label="Horas motor"><input type="number" step="0.1" style={inputStyle} value={newLog.horasMotor} onChange={(e) => setNewLog((l) => ({ ...l, horasMotor: e.target.value }))} /></Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 8, alignItems: "end", marginBottom: 12 }}>
              <Field label="Ciclos NG"><input type="number" step="0.01" style={inputStyle} value={newLog.ciclosNG} onChange={(e) => setNewLog((l) => ({ ...l, ciclosNG: e.target.value }))} /></Field>
              <Field label="Ciclos NF"><input type="number" step="0.01" style={inputStyle} value={newLog.ciclosNF} onChange={(e) => setNewLog((l) => ({ ...l, ciclosNF: e.target.value }))} /></Field>
              <Field label="LDG"><input type="number" style={inputStyle} value={newLog.ldg} onChange={(e) => setNewLog((l) => ({ ...l, ldg: e.target.value }))} /></Field>
              <Field label="Cargas"><input type="number" style={inputStyle} value={newLog.cargas} onChange={(e) => setNewLog((l) => ({ ...l, cargas: e.target.value }))} /></Field>
              <Field label="Resultado FRAT">
                <select style={inputStyle} value={newLog.frat} onChange={(e) => setNewLog((l) => ({ ...l, frat: e.target.value }))}>
                  <option value="ACEPTABLE">ACEPTABLE</option><option value="TOLERABLE">TOLERABLE</option><option value="INTOLERABLE">INTOLERABLE</option>
                </select>
              </Field>
              <Field label="Adjunto (foto/PDF bitácora)">
                <input type="file" accept="image/*,.pdf" style={{ fontSize: 11 }} onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const dataUrl = await readFileAsDataURL(file);
                  setNewLog((l) => ({ ...l, adjunto: { name: file.name, dataUrl, uploadedAt: new Date().toISOString() } }));
                }} />
              </Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, alignItems: "end", marginBottom: 12 }}>
              <Field label="Pasajeros transportados"><input type="number" style={inputStyle} value={newLog.pasajeros} onChange={(e) => setNewLog((l) => ({ ...l, pasajeros: e.target.value }))} /></Field>
              <Field label={`Carga interna (${weightUnitLabel})`}><input type="number" style={inputStyle} value={newLog.cargaInternaKg} onChange={(e) => setNewLog((l) => ({ ...l, cargaInternaKg: e.target.value }))} /></Field>
              <Field label={`Carga externa (${weightUnitLabel})`}><input type="number" style={inputStyle} value={newLog.cargaExternaKg} onChange={(e) => setNewLog((l) => ({ ...l, cargaExternaKg: e.target.value }))} /></Field>
              <Field label={`Combustible usado (${weightUnitLabel})`}><input type="number" style={inputStyle} value={newLog.combustibleUsadoKg} onChange={(e) => setNewLog((l) => ({ ...l, combustibleUsadoKg: e.target.value }))} /></Field>
              <Field label="Tipo de operación">
                <select style={inputStyle} value={newLog.tipoOperacion} onChange={(e) => setNewLog((l) => ({ ...l, tipoOperacion: e.target.value }))}>
                  {RISK_GROUPS.find((g) => g.key === "tarea")?.options.map((o) => <option key={o.label} value={o.label}>{o.label}</option>)}
                </select>
              </Field>
            </div>
            {/* ═══ PARA QUIÉN SE VOLVIÓ ═══

                Va antes del botón y no después: es un dato del asiento, y un
                asiento se firma con lo que dice arriba. */}
            <div style={{ marginBottom: 12 }}>
              <SelectorDeOperacion
                empresas={identidad?.empresas || []}
                clientes={clientes}
                empresaId={parDeOperacion(newLog).empresaId}
                clienteId={newLog.clienteId}
                alCambiar={(par) => {
                  setMotivoOperacion(null);
                  setNewLog((l) => ({ ...l, ...par }));
                }}
                motivo={motivoOperacion}
                leyenda="Para quién se volvió"
              />
            </div>

            {/* Confirmación con el detalle a la vista: este asiento SÍ suma al
                status de mantenimiento, y una vez confirmado no se edita. */}
            <button
              onClick={() => {
                const f = fleet.find((x) => x.id === newLog.aircraftId);
                const detalle =
                  `¿Registrar este vuelo en la bitácora?\n\n` +
                  `  Aeronave:   ${f?.matricula ?? "sin elegir"}\n` +
                  `  Horas célula: ${newLog.horasAeronave}\n` +
                  `  Horas motor:  ${newLog.horasMotor}\n` +
                  `  Aterrizajes:  ${newLog.ldg}\n` +
                  `  Ciclos NG/NF: ${newLog.ciclosNG} / ${newLog.ciclosNF}\n\n` +
                  `Aceptar = SÍ, queda registrado.\n` +
                  `Cancelar = NO, no se registra.\n\n` +
                  `Suma al status de mantenimiento de esa matrícula y no se puede editar después.`;
                if (window.confirm(detalle)) addLogEntry();
              }}
              style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}
            >
              Registrar vuelo
            </button>
            <div style={{ display: "flex", gap: 10, margin: "14px 0" }}>
              <Readout label="Aceptable" value={smsTally.ACEPTABLE || 0} unit="" tone="ACEPTABLE" />
              <Readout label="Medio" value={smsTally.MEDIO || 0} unit="" tone="MEDIO" />
              <Readout label="Grave" value={smsTally.GRAVE || 0} unit="" tone="GRAVE" />
              <Readout label="Inaceptable" value={(smsTally.INACEPTABLE || 0) + (smsTally["INACEPTABLE+"] || 0)} unit="" tone="INACEPTABLE" />
            </div>
            <div className="hc-tabla-ancha" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
              <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Fecha</th><th>N° Vuelo</th><th>Aeronave</th><th>Piloto</th><th>Operación</th><th>Hrs A/C</th><th>Hrs Mot</th><th>NG</th><th>NF</th><th>LDG</th><th>Cargas</th><th>Pax</th><th>C.Int</th><th>C.Ext</th><th>Combust.</th><th>FRAT</th><th>Adjunto</th></tr></thead>
              <tbody>{flightLog.map((l) => (
                <tr key={l.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                  <td style={{ padding: "4px 0" }}>{l.fecha}</td>
                  <td>{l.nVuelo}</td>
                  <td>{fleet.find((f) => f.id === l.aircraftId)?.matricula}</td>
                  <td>{pilots.find((p) => p.id === l.pilotId)?.nombre}</td>
                  <td>{l.tipoOperacion || "—"}</td>
                  <td>{l.horasAeronave}</td>
                  <td>{l.horasMotor}</td>
                  <td>{l.ciclosNG}</td>
                  <td>{l.ciclosNF}</td>
                  <td>{l.ldg}</td>
                  <td>{l.cargas}</td>
                  <td>{l.pasajeros || 0}</td>
                  <td>{l.cargaInternaKg || 0}</td>
                  <td>{l.cargaExternaKg || 0}</td>
                  <td>{l.combustibleUsadoKg || 0}</td>
                  <td><StatusPill status={l.frat} /></td>
                  <td>{l.adjunto ? <a href={l.adjunto.dataUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.cyan }}>📎 ver</a> : "—"}</td>
                </tr>
              ))}</tbody>
            </table>
            </div>
          </Panel>
        </div>
      )}
      {tab === "resumen" && (
        <div style={{ display: "grid", gap: 16 }}>
          <Panel
            eyebrow="Decisión"
            title={`${origin.name} → ${destination.name}`}
            right={<StatusPill status={[wbStatus(perfOrigin), wbStatus(perfDestination), worst.nivel, usableMin !== null && usableMin < 0 ? "INTOLERABLE" : "ACEPTABLE"].sort((a, b) => RANK[b] - RANK[a])[0]} />}
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 14 }}>
              <Readout label="Peso bruto" value={fmtWeight(totalKg).toFixed(0)} unit={weightUnitLabel} />
              <Readout label="Distancia / ETE" value={`${totalNm.toFixed(1)} NM`} unit={`${eteMin.toFixed(0)} min`} />
              <Readout label="Autonomía utilizable" value={usableMin === null ? "—" : (usableMin / 60).toFixed(2)} unit="h" tone={usableMin !== null && usableMin < 0 ? "INTOLERABLE" : undefined} />
              {/* Antes mostraba `worst.idx`, que es el NOMBRE DEL GRUPO del FRAT
                  ("Tarea", "Topografía"…), no la respuesta. Decía "Tarea" cuando
                  lo que hace falta saber es cuál tarea: traslado, mantenimiento,
                  línea larga… */}
              <Readout
                label="Tipo de tarea"
                value={tipoDeTarea}
                unit=""
              />
              <Readout
                label="FRAT (SGS)"
                value={worst.nivel === "ACEPTABLE" ? "OK" : `${worst.nivel}${worst.idx ? ` — ${worst.idx}` : ""}`}
                unit="" tone={worst.nivel}
              />
            </div>
            {/* Para quién se vuela se elige ACÁ, en la preparación, y no al
                final: es lo que el cierre del cronómetro va a heredar. */}
            <div style={{ marginTop: 12 }}>
              <SelectorDeOperacion
                empresas={identidad?.empresas || []}
                clientes={clientes}
                empresaId={parDeOperacion(operacion).empresaId}
                clienteId={operacion?.clienteId || null}
                alCambiar={(par) => {
                  setMotivoOperacion(null);
                  setOperacion(par);
                }}
                motivo={motivoOperacion}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, borderTop: `1px solid ${COLORS.hairline}`, paddingTop: 12 }}>
              <span style={{ fontSize: 12, color: COLORS.muted }}>
                {activeProfile.tipo} — {matricula} · Piloto: {pilots.find((p) => p.id === assignedPilotId)?.nombre || "—"}
              </span>
              <button onClick={() => {
                const resuelta = conLaOperacion({}, parDeOperacion(operacion));
                if (!resuelta.ok) return setMotivoOperacion(resuelta.motivo);
                setMotivoOperacion(null);
                const horas = Number((eteMin / 60).toFixed(2));
                const targetAircraft = fleet.find((f) => f.matricula === matricula) || fleet[0];
                const tareaLabel = RISK_GROUPS.find((g) => g.key === "tarea")?.options[riskChoices.tarea]?.label || "";
                const entry = {
                  id: `l_${Date.now()}`, fecha: new Date().toISOString().slice(0, 10), aircraftId: targetAircraft?.id, pilotId: assignedPilotId,
                  nVuelo: "", horasAeronave: horas, horasMotor: horas, ciclosNG: 1, ciclosNF: 1, ldg: 1, cargas: 0, frat: worst.nivel,
                  ruta: `${origin.name} → ${destination.name}`, matricula, tipo: activeProfile.tipo, tipoAeronave: activeProfile.tipo,
                  pesoBrutoKg: Math.round(totalKg), pesoAterrizajeKg: wbAterrizaje.valido ? wbAterrizaje.pesoTotalKg : null, cgAterrizajeM: wbAterrizaje.valido ? wbAterrizaje.cg : null, fratIdx: worst.idx || "", tipoDeTarea, combustibleReqKg: requiredKg === null ? null : Math.round(requiredKg),
                  pasajeros: passengers.length, cargaInternaKg: Math.round(paxFrontKg + paxRearKg + totalBaggageKg),
                  cargaExternaKg: Math.round(externalCargoKg), combustibleUsadoKg: Math.round(fuelKg), tipoOperacion: tareaLabel,
                  empresaId: resuelta.asiento.empresaId,
                  clienteId: resuelta.asiento.clienteId,
                };
                entry.fecha = entry.fecha || new Date().toISOString();
                entry.cotejo = entry.cotejo || { campos: {}, tecnico: "", fechaCotejo: null, observacion: "" };
                setFlightLog((l) => [entry, ...l]);
                setFleet((fs) => fs.map((f) => (f.id === entry.aircraftId ? {
                  ...f,
                  tsn: sumarContador(f.tsn, entry.horasAeronave),
                  csn: sumarContador(f.csn, entry.ldg),
                  ldg: (f.ldg || 0) + entry.ldg,
                  cargas: (f.cargas || 0) + entry.cargas,
                  motor: {
                    ...f.motor,
                    tsn: sumarContador(f.motor?.tsn, entry.horasMotor),
                    cyNG: sumarContador(f.motor?.cyNG, entry.ciclosNG),
                    cyNF: sumarContador(f.motor?.cyNF, entry.ciclosNF),
                  },
                } : f)));
                setPilots((ps) => ps.map((p) => (p.id === entry.pilotId ? { ...p, horasTotales: p.horasTotales + ftDePiloto(entry), horasUltimos30d: p.horasUltimos30d + ftDePiloto(entry) } : p)));
              }} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}>
                📋 Registrar este vuelo (ruta + aeronave + piloto + W&B + combustible + FRAT)
              </button>
            </div>
            <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 8 }}>
              Queda guardado en "Operaciones → SMS", y suma horas a la aeronave, al motor y al
              piloto seleccionados igual que un registro manual — con todos los datos de este vuelo adjuntos.
            </p>
          </Panel>

          <Panel eyebrow="Auditoría estilo BARS" title="Informe completo del análisis de esta operación">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
              <Readout label="Aeronave" value={matricula} unit={activeProfile.tipo} />
              <Readout label="Ruta" value={`${origin.name} → ${destination.name}`} unit={`${totalNm} NM`} />
              <Readout label="Máx. OGE origen" value={perfOrigin.oge.outOfRange ? "s/d" : fmtWeight(perfOrigin.oge.value).toFixed(0)} unit={weightUnitLabel} />
              <Readout label="Máx. OGE destino" value={perfDestination.oge.outOfRange ? "s/d" : fmtWeight(perfDestination.oge.value).toFixed(0)} unit={weightUnitLabel} />
              <Readout label="Máx. IGE (Hp/temp crucero)" value={igeExpl.outOfRange ? "s/d" : fmtWeight(igeExpl.value).toFixed(0)} unit={weightUnitLabel} />
              <Readout label="Combustible embarcado" value={fmtWeight(fuelKg).toFixed(0)} unit={weightUnitLabel} />
            </div>
            {passengers.length > 0 && (
              <div className="hc-tabla-ancha" style={{ overflowX: "auto", marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Manifiesto de pasajeros</div>
                <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                  <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Nombre</th><th>N° documento</th><th>Estación</th><th>Peso ({weightUnitLabel})</th><th>Equipaje ({weightUnitLabel})</th></tr></thead>
                  <tbody>{passengers.map((p) => (
                    <tr key={p.id} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                      <td style={{ padding: "4px 0" }}>{p.nombre || "—"}</td>
                      <td>{p.documento || "—"}</td>
                      <td>{p.station === "delante" ? "Delante" : "Atrás"}</td>
                      <td>{fmtWeight(p.pesoKg).toFixed(1)}</td>
                      <td>{fmtWeight(p.equipajeKg).toFixed(1)}</td>
                    </tr>
                  ))}</tbody>
                </table>
                <p style={{ fontSize: 11, color: COLORS.muted, marginTop: 6, marginBottom: 0 }}>
                  Equipaje total {fmtWeight(totalBaggageKg).toFixed(1)} {weightUnitLabel} — {compartmentTotals.map((c) => `${c.label} ${fmtWeight(c.kg).toFixed(1)}/${fmtWeight(c.maxKg).toFixed(0)}${c.overweight ? " ⚠ SOBREPESO" : ""}`).join(" · ")}.
                </p>
              </div>
            )}
            {compartmentTotals.some((c) => c.overweight) && (
              <Banner tone="INTOLERABLE">⚠ Compartimento(s) con sobrepeso: {compartmentTotals.filter((c) => c.overweight).map((c) => c.label).join(", ")}.</Banner>
            )}
            <div className="hc-tabla-ancha" style={{ overflowX: "auto", marginBottom: 14 }}>
              <table style={{ width: "100%", fontSize: 11.5, fontFamily: "'JetBrains Mono', monospace", borderCollapse: "collapse" }}>
                <thead><tr style={{ color: COLORS.muted, textAlign: "left" }}><th>Factor</th><th>Condición</th><th>Gravedad</th><th>Probabilidad</th><th>Nivel (matriz SGS)</th></tr></thead>
                <tbody>{selectedRisks.map((r) => (
                  <tr key={r.group.key} style={{ borderTop: `1px solid ${COLORS.hairline}` }}>
                    <td style={{ padding: "4px 0" }}>{r.group.label}</td>
                    <td>{r.opt.label}</td>
                    <td>{SMS_GRAVEDAD[r.opt.gravIdx]}</td>
                    <td>{SMS_PROBABILIDAD[r.opt.probIdx]}</td>
                    <td><StatusPill status={r.nivel} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" }}>
              <div>
                <div style={{ fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Piloto al mando</div>
                <div style={{ fontSize: 13 }}>{pilots.find((p) => p.id === assignedPilotId)?.nombre} {pilots.find((p) => p.id === assignedPilotId)?.apellido} — Lic. {pilots.find((p) => p.id === assignedPilotId)?.licencia || "s/d"}</div>
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 10, marginBottom: 4 }}>Firma</div>
                <SignaturePad value={signatureDataUrl} onChange={setSignatureDataUrl} />
              </div>
              <div>
                <p style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 0 }}>
                  "Guardar informe" congela una foto de todo lo de arriba (ruta, pesos OGE/IGE, combustible, la matriz
                  de riesgo completa, aeronave y piloto) junto con la firma, como respaldo verificable de esta
                  operación específica — separado del plan de vuelo guardado y del registro de horas de mantenimiento.
                </p>
                <button onClick={() => {
                  const report = {
                    id: `a_${Date.now()}`, fecha: new Date().toISOString(),
                    matricula, tipo: activeProfile.tipo,
                    piloto: `${pilots.find((p) => p.id === assignedPilotId)?.nombre || ""} ${pilots.find((p) => p.id === assignedPilotId)?.apellido || ""}`.trim(),
                    licencia: pilots.find((p) => p.id === assignedPilotId)?.licencia || "",
                    ruta: `${origin.name} → ${destination.name}`, distanciaNm: totalNm, eteMin: Math.round(eteMin),
                    pesoBrutoKg: Math.round(totalKg), pesoAterrizajeKg: wbAterrizaje.valido ? wbAterrizaje.pesoTotalKg : null, cgAterrizajeM: wbAterrizaje.valido ? wbAterrizaje.cg : null, ogeOrigenKg: perfOrigin.oge.outOfRange ? null : perfOrigin.oge.value,
                    ogeDestinoKg: perfDestination.oge.outOfRange ? null : perfDestination.oge.value,
                    igeKg: igeExpl.outOfRange ? null : igeExpl.value, combustibleKg: Math.round(fuelKg),
                    cruiseAltFt, cruiseTempC, fratResultado: worst.nivel, fratIdx: worst.idx || "", tipoDeTarea,
                    factoresRiesgo: selectedRisks.map((r) => ({ factor: r.group.label, condicion: r.opt.label, gravedad: SMS_GRAVEDAD[r.opt.gravIdx], probabilidad: SMS_PROBABILIDAD[r.opt.probIdx], nivel: r.nivel })),
                    manifiesto: passengers.map((p) => ({ nombre: p.nombre, documento: p.documento, station: p.station, pesoKg: p.pesoKg, equipajeKg: p.equipajeKg })),
                    firma: signatureDataUrl,
                  };
                  persistAuditReports([report, ...auditReports]);
                }} style={{ background: COLORS.amber, border: "none", color: "#1A1204", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}>
                  💾 Guardar informe de auditoría
                </button>
              </div>
            </div>

            {auditReports.length > 0 && (
              <div style={{ marginTop: 16, borderTop: `1px solid ${COLORS.hairline}`, paddingTop: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Informes guardados</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {auditReports.map((r) => (
                    <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                      <span>{new Date(r.fecha).toLocaleString()} · {r.matricula} · {r.ruta} · <StatusPill status={r.fratResultado} /></span>
                      <button onClick={() => deleteAuditReport(r.id)} style={{ background: "transparent", border: `1px solid ${COLORS.hairline}`, color: COLORS.danger, borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          <Panel eyebrow="Figura" title="Peso y balance — envolvente de CG">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 6, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={COLORS.hairline} />
                    <XAxis type="number" dataKey="x" name="CG (m)" stroke={COLORS.muted} tick={{ fontSize: 11 }} domain={["dataMin - 0.05", "dataMax + 0.05"]} />
                    <YAxis type="number" dataKey="y" name={`Peso (${weightUnitLabel})`} stroke={COLORS.muted} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: COLORS.panelAlt, border: `1px solid ${COLORS.hairline}` }} />
                    <Scatter name="Envolvente (carga interna, ≤2250 kg)" data={closedEnvelopePoints.map((p) => ({ x: p.x, y: fmtWeight(p.y) }))} line={{ stroke: COLORS.amber, strokeDasharray: "6 4", strokeWidth: 1.6 }} fill="none" shape={() => null} />
                    <Scatter name="Envolvente (con carga externa, ≤2800 kg)" data={closedEnvelopeExternalPoints.map((p) => ({ x: p.x, y: fmtWeight(p.y) }))} line={{ stroke: COLORS.cyan, strokeDasharray: "2 3", strokeWidth: 1.4 }} fill="none" shape={() => null} />
                    {/* La línea entre los dos puntos es el recorrido del CG a
                        medida que se quema el combustible: es lo que hay que
                        mirar para saber si en algún momento se sale. */}
                    {wbAterrizaje.valido && (
                      <Scatter
                        name="Recorrido del CG en vuelo"
                        data={[{ x: cg, y: fmtWeight(totalKg) }, { x: wbAterrizaje.cg, y: fmtWeight(wbAterrizaje.pesoTotalKg) }]}
                        line={{ stroke: COLORS.magenta, strokeWidth: 1.6, strokeDasharray: "4 3" }}
                        fill="none" shape={() => null}
                      />
                    )}
                    <Scatter name="CG al despegue ★" data={[{ x: cg, y: fmtWeight(totalKg) }]} fill={insideEnvelope === false ? COLORS.danger : COLORS.ok} shape="star" />
                    {wbAterrizaje.valido && (
                      <Scatter
                        name="CG al aterrizaje ▲"
                        data={[{ x: wbAterrizaje.cg, y: fmtWeight(wbAterrizaje.pesoTotalKg) }]}
                        fill={wbAterrizaje.dentroDeEnvolvente === false ? COLORS.danger : wbAterrizaje.dentroDeEnvolvente === null ? COLORS.caution : COLORS.cyan}
                        shape="triangle"
                      />
                    )}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "grid", gap: 10, alignContent: "start" }}>
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11.5, color: COLORS.text, cursor: "pointer", lineHeight: 1.5 }}>
                  <input
                    type="checkbox" style={{ marginTop: 2 }}
                    checked={consumoElectricoAlto}
                    onChange={(e) => setConsumoElectricoAlto(e.target.checked)}
                  />
                  <span>
                    Consumo eléctrico sobre 50 A (faro de búsqueda, calefacción, cabrestante).{" "}
                    <span style={{ color: COLORS.muted }}>
                      La carta IGE del RFM resta <strong>50 kg</strong> al peso máximo en esa condición. La app no
                      lo modelaba: mostraba el número de la carta como si fuera el límite.
                    </span>
                  </span>
                </label>
                <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8 }}>★ Despegue</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <Readout label="Peso bruto" value={fmtWeight(totalKg).toFixed(0)} unit={weightUnitLabel} />
                  <Readout label="CG" value={cg.toFixed(3)} unit="m" />
                  {insideEnvelope !== null && <Readout label="En envolvente" value={insideEnvelope ? "SÍ" : "NO"} unit="" tone={insideEnvelope ? "ACEPTABLE" : "INTOLERABLE"} />}
                </div>

                <div style={{ fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.8, marginTop: 6 }}>▲ Aterrizaje en {destination.name}</div>
                {!wbAterrizaje.valido ? (
                  <p style={{ fontSize: 11.5, color: COLORS.caution, margin: 0, lineHeight: 1.5 }}>{wbAterrizaje.motivo}</p>
                ) : (
                  <>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <Readout label="Peso de aterrizaje" value={fmtWeight(wbAterrizaje.pesoTotalKg).toFixed(0)} unit={weightUnitLabel} />
                      <Readout label="CG al aterrizaje" value={wbAterrizaje.cg.toFixed(3)} unit="m" />
                      <Readout
                        label="En envolvente"
                        value={wbAterrizaje.dentroDeEnvolvente === null ? "s/d" : wbAterrizaje.dentroDeEnvolvente ? "SÍ" : "NO"}
                        unit=""
                        tone={wbAterrizaje.dentroDeEnvolvente === null ? "TOLERABLE" : wbAterrizaje.dentroDeEnvolvente ? "ACEPTABLE" : "INTOLERABLE"}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <Readout label="Combustible de la etapa" value={fmtWeight(wbAterrizaje.combustibleConsumidoKg).toFixed(0)} unit={weightUnitLabel} />
                      <Readout label="Queda a bordo" value={wbAterrizaje.combustibleRestanteKg === null ? "s/d" : fmtWeight(wbAterrizaje.combustibleRestanteKg).toFixed(0)} unit={weightUnitLabel} />
                      <Readout label="El CG se corre" value={`${Math.abs(wbAterrizaje.desplazamientoCgM * 1000).toFixed(0)} mm`} unit={wbAterrizaje.sentido} />
                    </div>
                    {wbAterrizaje.motivo && <Banner tone="INTOLERABLE">{wbAterrizaje.motivo}</Banner>}
                  </>
                )}
                <p style={{ fontSize: 10.5, color: COLORS.muted, margin: 0, lineHeight: 1.5 }}>
                  Solo se descuenta el combustible de la etapa, no la reserva: la reserva llega al destino dentro
                  del tanque y sigue pesando. La envolvente se relee al peso de llegada, porque sus límites
                  cambian con el peso.
                </p>
              </div>
            </div>
          </Panel>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Panel title={`Performance en ${origin.name}`}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <Readout label="Máx. OGE" value={perfOrigin.oge.outOfRange ? "s/d" : fmtWeight(perfOrigin.oge.value).toFixed(0)} unit={weightUnitLabel} />
                <Readout label="Máx. IGE" value={perfOrigin.ige.outOfRange ? "s/d" : fmtWeight(perfOrigin.ige.value).toFixed(0)} unit={weightUnitLabel} />
                {/* El número de la carta no es el límite del vuelo: la carta de
                    estacionario llega a 2800 kg porque contempla carga externa.
                    Mostrar "máx. OGE 2776 kg" a secas invita a cargar media
                    tonelada de más sobre el máximo estructural. */}
                <Readout
                  label={`Límite que manda${limiteOrigen.limitante ? ` — ${limiteOrigen.limitante}` : ""}`}
                  value={limiteOrigen.limiteKg === null ? "s/d" : fmtWeight(limiteOrigen.limiteKg).toFixed(0)}
                  unit={weightUnitLabel}
                  tone={limiteOrigen.limiteKg !== null && totalKg > limiteOrigen.limiteKg ? "INTOLERABLE" : "ACEPTABLE"}
                />
                <Readout
                  label="Margen sobre el límite"
                  value={limiteOrigen.limiteKg === null ? "s/d" : fmtWeight(limiteOrigen.limiteKg - totalKg).toFixed(0)}
                  unit={weightUnitLabel}
                  tone={limiteOrigen.limiteKg === null ? "TOLERABLE" : totalKg > limiteOrigen.limiteKg ? "INTOLERABLE" : "ACEPTABLE"}
                />
                <StatusPill status={wbStatus(perfOrigin)} />
              </div>
              <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "8px 0 0", lineHeight: 1.5 }}>
                A {origin.elevFt?.toLocaleString() ?? "s/d"} ft y {origin.tempC ?? "s/d"} °C. El OGE es la condición
                exigente; el IGE solo vale con efecto suelo.
                {limiteOrigen.soloIGE && " Sin carta OGE en esta condición: el margen se mide con la carta menos exigente."}
                {consumoElectricoAlto && " Incluye la resta de 50 kg por consumo eléctrico sobre 50 A."}
              </p>
            </Panel>
            <Panel title={`Performance en ${destination.name}`}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <Readout label="Máx. OGE" value={perfDestination.oge.outOfRange ? "s/d" : fmtWeight(perfDestination.oge.value).toFixed(0)} unit={weightUnitLabel} />
                <Readout label="Máx. IGE" value={perfDestination.ige.outOfRange ? "s/d" : fmtWeight(perfDestination.ige.value).toFixed(0)} unit={weightUnitLabel} />
                {/* El número de la carta no es el límite del vuelo: la carta de
                    estacionario llega a 2800 kg porque contempla carga externa.
                    Mostrar "máx. OGE 2776 kg" a secas invita a cargar media
                    tonelada de más sobre el máximo estructural. */}
                <Readout
                  label={`Límite que manda${limiteDestino.limitante ? ` — ${limiteDestino.limitante}` : ""}`}
                  value={limiteDestino.limiteKg === null ? "s/d" : fmtWeight(limiteDestino.limiteKg).toFixed(0)}
                  unit={weightUnitLabel}
                  tone={limiteDestino.limiteKg !== null && totalKg > limiteDestino.limiteKg ? "INTOLERABLE" : "ACEPTABLE"}
                />
                <Readout
                  label="Margen sobre el límite"
                  value={limiteDestino.limiteKg === null ? "s/d" : fmtWeight(limiteDestino.limiteKg - totalKg).toFixed(0)}
                  unit={weightUnitLabel}
                  tone={limiteDestino.limiteKg === null ? "TOLERABLE" : totalKg > limiteDestino.limiteKg ? "INTOLERABLE" : "ACEPTABLE"}
                />
                <StatusPill status={wbStatus(perfDestination)} />
              </div>
              <p style={{ fontSize: 10.5, color: COLORS.muted, margin: "8px 0 0", lineHeight: 1.5 }}>
                A {destination.elevFt?.toLocaleString() ?? "s/d"} ft y {destination.tempC ?? "s/d"} °C. El OGE es la condición
                exigente; el IGE solo vale con efecto suelo.
                {limiteDestino.soloIGE && " Sin carta OGE en esta condición: el margen se mide con la carta menos exigente."}
                {consumoElectricoAlto && " Incluye la resta de 50 kg por consumo eléctrico sobre 50 A."}
              </p>
            </Panel>
          </div>

          <Panel title="Aviso operacional">
            <p style={{ fontSize: 12.5, color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>
              Este resumen combina datos digitalizados con interpolación bilineal y una envolvente de CG editable.
              Es una ayuda de planificación — la decisión final debe validarse contra el RFM/POH certificado y el
              SMS del operador, especialmente por encima de {topeDeCartaTexto()} donde no hay carta
              digitalizada, y en los umbrales de descanso de tripulación, que aquí son valores de referencia editables.
            </p>
          </Panel>
        </div>
      )}
      </Cabina>
    </div>
  );
}

// Red de seguridad: si algo dentro de la app tira un error en tiempo de ejecución (por ejemplo, al abrir una
// pestaña con datos inesperados), React sin esto desmonta TODO el árbol y la pantalla queda en blanco sin
// ningún aviso. Con este límite de error, en vez de blanco se ve un mensaje con el motivo y un botón para
// reintentar sin tener que recargar toda la página.
class HeliCalcErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("HeliCalc — error de ejecución:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", background: "#0C1116", color: "#E6EDF3", fontFamily: "'Inter', sans-serif", padding: 28 }}>
          <h2 style={{ color: "#E4573D", marginTop: 0 }}>HeliCalc encontró un error y se detuvo</h2>
          <p style={{ color: "#9AA5B1", maxWidth: 640, lineHeight: 1.6 }}>
            Esto evita que la pantalla se quede en blanco sin explicación — el detalle técnico de abajo ayuda a
            identificar qué lo causó. Prueba "Reintentar"; si vuelve a pasar justo con la misma acción, esa acción es la que hay que revisar.
          </p>
          <p style={{ background: "#3A1410", border: "1px solid #E4573D", borderRadius: 8, padding: "12px 14px",
                      maxWidth: 760, fontSize: 14, fontFamily: "'JetBrains Mono', monospace", color: "#FFD9D2" }}>
            {String((this.state.error && this.state.error.message) || this.state.error)}
          </p>
          <button
            onClick={() => navigator.clipboard?.writeText(String((this.state.error && (this.state.error.message + "\n" + this.state.error.stack)) || this.state.error))}
            style={{ background: "#2B3641", border: "1px solid #3A4653", color: "#E6EDF3", borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12 }}
          >
            Copiar error completo
          </button>
          <pre style={{ whiteSpace: "pre-wrap", background: "#151B22", border: "1px solid #2A333D", borderRadius: 8, padding: 14, fontSize: 11, maxWidth: 760, overflowX: "auto", color: "#93A3B3" }}>
            {String((this.state.error && this.state.error.stack) || "")}
          </pre>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: 14, background: "#FFB020", border: "none", color: "#1A1204", borderRadius: 6, padding: "9px 16px", cursor: "pointer", fontWeight: 700 }}
          >
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function HeliCalcProtoRoot({ alRegistrarIr } = {}) {
  return (
    <HeliCalcErrorBoundary>
      {/* ═══ EL AVISO VA EN EL CASCARÓN ═══

          Acá cubre a las 29 llamadas a `usePersistedState` y también a la que
          todavía no existe. Ninguna de ellas recoge el error de escritura —
          todas destructuran `[valor, setValor]`— así que un vuelo que no se
          guardaba se seguía viendo en pantalla como guardado. */}
      <AvisoFallasEscritura />
      <HeliCalcProto alRegistrarIr={alRegistrarIr} />
    </HeliCalcErrorBoundary>
  );
}
